/**
 * `P0-UIK-007`: the `fr-ML` catalogue, section 06, 6.5.1.
 *
 * **One catalogue, keys namespaced by app and screen.** Not because a second
 * locale is coming , 6.5.1 is explicit that none is planned, and that written
 * Bambara is not the interface this market reads , but because *"the catalogue
 * exists so that adding one is a file, not a refactor"*, and because a string
 * that lives in one place can be corrected in one place. A French sentence
 * spelled two ways across two screens is a product that looks assembled by two
 * companies.
 *
 * **What does NOT belong here.** Three things, each with its own home, and
 * putting any of them here would create the second source this package exists
 * to prevent:
 *
 *   1. **Stage labels and the presentation vocabulary** , `@hakili/domain-types`
 *      (9.2's row, and `hakili/no-literal-label` enforces it). This module
 *      imports them rather than restating them, so the catalogue cannot drift
 *      from the vocabulary.
 *   2. **Server-rendered French** , guard messages and error `message` fields
 *      are *"the exact French sentence the UI displays, stored with the guard"*
 *      (6.5.1). A screen renders what the server sent; it does not look the
 *      sentence up and risk showing a different refusal than the one that
 *      happened.
 *   3. **Enum values** , never translated (`DECISION ADDED 05-01`):
 *      `refuseBanque` is an identifier, not a word.
 */

import { etapeLabel, quiLabel, type EtapeDossier } from "@hakili/domain-types";

import { pl } from "./accord.js";

/** A key is `<app>.<screen>.<what>`, so its home is readable from the key. */
export type CatalogueKey = `${string}.${string}.${string}`;

/**
 * The phase-0 catalogue. It starts deliberately small: the shell strings every
 * app needs before it has a screen. Screens add their own keys as they arrive,
 * which is the growth 6.5.1 describes rather than a big-bang translation file.
 */
export const FR_ML: Readonly<Record<string, string>> = {
  "common.action.enregistrer": "Enregistrer",
  "common.action.annuler": "Annuler",
  "common.action.reessayer": "Réessayer",
  "common.action.fermer": "Fermer",
  "common.state.chargement": "Chargement…",
  "common.state.vide": "Rien à afficher",
  "common.error.reseau": "La connexion a échoué. Vérifiez votre réseau et réessayez.",
  "common.error.inattendue": "Une erreur inattendue est survenue.",
  // `P0-DLW-006`. Two apps now need this sentence , `bank.session.inattendue`
  // has carried it since `P1-BKW-001` , so it moves to `common` rather than
  // being spelled a third time. A French sentence spelled two ways across two
  // screens is a product that looks assembled by two companies.
  "common.error.reponseInattendue": "Réponse inattendue du serveur.",
  "common.error.connexionEchouee": "La connexion a échoué.",
  // `P0-CNW-028` / `P0-DLW-004`, `00-SPINE` 6.6: each surface admits one kind
  // of organisation, and says so rather than redirecting in silence.
  "common.surface.refusConsole":
    "Cet espace est réservé aux équipes Hakili. Connectez-vous sur l'application de votre organisation.",
  "common.surface.refusConcession":
    "Cet espace est réservé aux équipes des concessions. Connectez-vous sur l'application de votre organisation.",

  // `P0-UIK-029`, `00-SPINE` 6.3. Shared and not per app: ONE screen accepts
  // an invitation, whichever surface mounts it, so its words are shared too.
  "common.invitation.titre": "Votre invitation",
  "common.invitation.motDePasse": "Choisissez un mot de passe",
  "common.invitation.accepter": "Activer mon compte",
  "common.invitation.motDePasseRequis": "Un mot de passe est requis.",
  // Used ONLY when the API refused without a sentence of its own. Its
  // refusals are the ones that tell a holder whether to ask for a new
  // invitation or simply to sign in, so they are rendered verbatim.
  "common.invitation.refusParDefaut": "Cette invitation ne peut pas être utilisée.",

  // `P1-UIK-025`, round 203: the marked-stub convention, shared by every app.
  // The words are the mockups' own `DemandeFonctionnaliteModal`, moved here so
  // six front ends cannot spell the same affordance six ways.
  "ui.stub.marque": "à venir",
  "ui.demande.titre": "Demander cette fonctionnalité",
  "ui.demande.ouvrir": "Demander cette fonctionnalité",
  "ui.demande.faire": "Qu'essayiez-vous de faire ?",
  "ui.demande.manque": "Qu'est-ce qui manquait ?",
  "ui.demande.permettrait": "Que vous permettrait cette fonctionnalité ?",
  "ui.demande.envoyer": "Transmettre",
  "ui.demande.annuler": "Annuler",
  "ui.demande.fermer": "Fermer",
  "ui.demande.transmise": "Demande transmise",
  "ui.demande.merci":
    "C'est noté. Votre demande rejoint la file des fonctionnalités avec l'écran d'où elle vient.",
  // `OD-18`, `round F5`: the modal now calls a DOOR, so it can be refused, and
  // a refusal has to be readable. « Demande transmise » shown over a demand the
  // api turned away would be the same lie the inert control avoided.
  "ui.demande.echec": "La demande n'a pas pu être transmise.",
  "ui.demande.envoiEnCours": "Transmission…",
  // `P0-UIK-014`: the five views of 03, 2.0 and the eleven screens of their
  // nav rail. The labels are canon's own words, and they live here rather than
  // in the app so the founder can rename a view once.
  "console.vue.admin": "Administration",
  "console.vue.resp": "Responsable des dossiers",
  "console.vue.collecte": "Chargé de dossier",
  "console.vue.banques": "Relation banques",
  "console.vue.concess": "Relation concessionnaires",
  "console.nav.collecte": "Dossiers",
  /** `CN-A.0`: the rail RENAMES this item for the chargé alone , `hakili-console.jsx`'s `NavRail`. */
  "console.nav.collecteMien": "Mes dossiers",
  "console.nav.arrivees": "Arrivées",
  "console.nav.priorites": "Priorités",
  "console.nav.partenaires": "Partenaires",
  "console.fiche.intro": "Nommez l'administrateur de cette concession. Il recevra une invitation par e-mail.",
  "console.fiche.nom": "Nom complet de l'administrateur",
  "console.fiche.email": "Adresse e-mail",
  "console.fiche.nommer": "Nommer et inviter",
  "console.fiche.envoye": "Invitation envoyée à",
  // `P0-PTA-001`, round I-A. The dealership's address, on the fiche.
  "console.fiche.adresseACompleter": "Adresse à compléter.",
  "console.fiche.modifierAdresse": "Modifier l'adresse",
  "console.fiche.ville": "Ville",
  "console.fiche.quartier": "Quartier",
  "console.fiche.villeChoisir": "Choisir une ville",
  "console.fiche.quartierChoisir": "Choisir un quartier",
  "console.fiche.telephone": "Téléphone",
  "console.fiche.nonEnvoye": "Le compte est créé, mais l'e-mail n'est pas parti pour",
  "console.fiche.enAttente": "Le compte est créé. L'e-mail n'est pas encore parti : Hakili réessaie pour",
  "console.fiche.renvoyer": "Renvoyer",
  "console.fiche.renvoiNonParti":
    "L'e-mail n'est toujours pas parti. Le lien précédent a été révoqué ; réessayez.",
  "console.fiche.erreurChamps": "Le nom complet et une adresse e-mail valide sont requis.",
  "console.concession.titre": "Ouvrir une concession",
  "console.concession.intro":
    "L'ouverture est réservée à Hakili. Une concession ne peut pas s'inscrire elle-même.",
  "console.concession.nom": "Nom de la concession",
  "console.concession.identiteVerifiee": "J'ai rencontré le responsable et vérifié son identité",
  "console.concession.conventionRecue": "J'ai reçu la convention signée",
  "console.concession.ouvrir": "Ouvrir la concession",
  "console.concession.ouverte": "Concession ouverte.",
  "console.concession.prochaineEtape": "Vous pourrez nommer son administrateur depuis sa fiche.",
  "console.concession.erreurNom": "Le nom est requis.",
  "console.concession.erreurSession": "Votre session a expiré. Reconnectez-vous.",
  "console.concession.erreurReseau": "Le serveur est injoignable. Réessayez.",
  "console.concession.erreurInattendue": "L'ouverture a échoué.",
  "console.nav.developpement": "Prospects",
  "console.nav.expertises": "Expertises",
  "console.nav.equipe": "Équipe",
  "console.nav.comptes": "Comptes",
  "console.nav.supervision": "Supervision",
  "console.nav.fonctionnalites": "Idées",
  "console.nav.profil": "Profil",
  "console.panel.conversation": "Conversation",
  // `P0-UIK-015`: the console login screen and the profile screen (WF-CON-43).
  "console.connexion.titre": "Connexion",
  "console.connexion.email": "Adresse e-mail",
  "console.connexion.motDePasse": "Mot de passe",
  "console.connexion.code": "Code à six chiffres",
  "console.connexion.entrer": "Se connecter",
  "console.connexion.enCours": "Connexion…",
  // `P0-UIK-021`: what the console says while it is finding out who you are,
  // and what it says when the server refuses in a way the envelope did not
  // spell out. The server's OWN sentence is preferred wherever there is one ,
  // 4.5.5 writes it beside the guard that produced it , and these are the
  // fallbacks for when there is not.
  "console.session.chargement": "Chargement…",
  "console.session.inattendue": "Réponse inattendue du serveur.",
  /**
   * `round CN-A`, `CN-A.0`: **the scope banner, in the drawing's own words.**
   *
   * `hakili-console.jsx`'s `PorteeBanner` prints, under the person's name and
   * role, what this role does and , as importantly , what it does NOT. Four of
   * the five views carry one; the administration's is `null` in the drawing,
   * because a scope that is everything is not a scope and a paragraph saying so
   * would be noise on the one screen that has none.
   *
   * Copied to the letter. These are the founder's sentences about who may see
   * what, and a paraphrase of a boundary is a different boundary.
   */
  "console.portee.resp":
    "Vous voyez tous les dossiers en collecte et l'équipe qui les traite. Côté finances, vous suivez les frais de dossier et les commissions d'assurance ; les banques, les comptes partenaires et les données financières globales de la plateforme ne relèvent pas de ce rôle. Vous animez la relation avec les assureurs du panel, comptes de votre portefeuille : convention, délai d'émission des attestations, renouvellements et échanges ; les banques et les concessionnaires relèvent des gestionnaires relation.",
  "console.portee.collecte":
    "Vous voyez vos dossiers et ce que la banque demande sur chacun, jamais l'intérieur de son espace de travail. Chaque consultation de pièce d'identité est journalisée.",
  "console.portee.banques":
    "Vous suivez VOTRE portefeuille de banques : celles que vous ouvrez y entrent automatiquement, l'administration voit l'ensemble de la plateforme. Vous animez la relation de bout en bout : convention et délais tenus, santé du partenariat, échanges et actions sur la fiche de chaque banque, prospection dans Prospects. Ce que vous apprenez des refus revient dans le pré-filtre. Vous invitez le premier responsable, qui crée lui-même les comptes de son équipe ; le détail consolidé des comptes relève de l'administration. Les dossiers en collecte et les concessionnaires ne relèvent pas de votre périmètre.",
  "console.portee.concess":
    "Vous suivez VOTRE portefeuille de concessionnaires : ceux que vous ouvrez y entrent automatiquement, l'administration voit l'ensemble de la plateforme. Vous animez la relation de bout en bout : convention et commission, qualité des expertises, solde prépayé, échanges et actions sur la fiche de chaque concessionnaire, prospection dans Prospects. Vous invitez le premier responsable, qui crée lui-même les comptes de son équipe ; le détail consolidé des comptes relève de l'administration. Les banques et les dossiers en collecte ne relèvent pas de votre périmètre.",
  /** The rail's own word under the mark, `hakili-console.jsx`'s `NavRail`. */
  "console.nav.interne": "interne",
  /** `WF-CON-24`'s feedback control, in the rail and visible from every screen. */
  "console.nav.uneIdee": "Une idée ?",
  "console.nav.demanderFonctionnalite": "Demander une fonctionnalité",
  "console.nav.notifications": "Notifications",
  /**
   * `round CN-A`, `CN-A.0`: an entitled screen the bundle does not serve says
   * WHICH ROUND builds it. « Pas encore construit » tells a founder the thing
   * is missing; it does not tell him whether it is coming, and the difference
   * between « forgotten » and « next » is the whole of what he wants to know.
   */
  "console.session.ecranRond": "Bientôt disponible",
  /** `round CN-C`, `CN-C.1`: « Priorités », WF-CON-38, the drawing's own words. */
  "console.priorites.titre": "Vos priorités",
  "console.priorites.sousTitreBanques": "Votre portefeuille de banques partenaires et prospects",
  "console.priorites.sousTitreConcess": "Votre portefeuille de concessionnaires partenaires et prospects",
  "console.priorites.sousTitreAdmin":
    "Les deux portefeuilles de la plateforme, banques et concessionnaires, avec leur gestionnaire",
  "console.priorites.filtreTous": "Tous les partenaires",
  "console.priorites.filtreBanques": "Banques",
  "console.priorites.filtreConcess": "Concessionnaires",
  "console.priorites.filtreAssureurs": "Assureurs",
  "console.priorites.kpiPartenairesActifs": "Partenaires actifs",
  "console.priorites.kpiPartenairesSous": "sur {n} partenaires du portefeuille",
  "console.priorites.kpiSoumis": "Dossiers soumis ce mois",
  "console.priorites.kpiMisEnLigne": "Véhicules mis en ligne ce mois",
  "console.priorites.kpiMisEnLigneSous": "tous concessionnaires suivis",
  "console.priorites.kpiProspects": "Prospects en cours",
  "console.priorites.kpiProspectsSous": "dans le pipeline de développement",
  "console.priorites.signauxTitre": "Ce que Hakili vous signale cette semaine",
  "console.priorites.signauxSous":
    "Classé par ce qui coûte le plus cher si personne n'agit. Chaque ligne vient des données du portefeuille : délais tenus, échanges, expertises, soldes, motifs de refus.",
  "console.priorites.signauxVide": "Rien ne remonte cette semaine sur votre portefeuille.",
  "console.priorites.groupeBanques": "Banques",
  "console.priorites.groupeConcess": "Concessionnaires",
  "console.priorites.groupeAssureurs": "Assureurs",
  "console.priorites.enMotsTitre": "Signaux que leur module ne dérive pas encore",
  "console.priorites.reservationsTitre": "Réservations",
  "console.priorites.reservationsSous":
    "Les véhicules réservés fermement dont le dossier attend : le client au-delà du terme, ou la réservation trop ancienne. Rien n'est libéré par la plateforme.",
  "console.priorites.reservationsVide": "Aucune réservation ne dépasse ses termes.",
  "console.priorites.reservationRelancer": "Relancer le client",
  "console.priorites.reservationVoir": "Voir le dossier",
  "console.priorites.reservationClore": "Clore sans suite",
  "console.priorites.enMotsPorte": "Ce signal n'est pas encore disponible.",
  "console.priorites.suiviPar": "Suivi par {nom}",
  "console.priorites.voirPartenaire": "Voir le partenaire",
  "console.priorites.actionsTitre": "Vos rendez-vous et actions",
  "console.priorites.actionsSous":
    "Même liste que sur la fiche de chaque partenaire : ce que vous ajoutez ici s'y retrouve, et l'inverse.",
  "console.priorites.actionsVide": "Aucun rendez-vous programmé sur votre portefeuille.",
  "console.priorites.gesteConsigne": "Échange consigné au journal du compte.",
  /** `CN-C.2`: « Prospects » (was « Développement » until `D-1019-1`), WF-CON-39. The title is the rail's word: one place, one name. */
  "console.developpement.titre": "Prospects",
  "console.developpement.sousTitreBanques":
    "Les banques que vous travaillez, du premier contact à la première soumission",
  "console.developpement.sousTitreConcess":
    "Les concessionnaires que vous travaillez, du premier contact à la mise en ligne",
  "console.developpement.sousTitreAdmin": "Les deux pipelines d'acquisition, avec leur gestionnaire",
  "console.developpement.stadeProspect": "Prospect",
  "console.developpement.stadePremierContact": "Premier contact",
  "console.developpement.stadeNdaSigne": "NDA signé",
  "console.developpement.stadeAccordPilote": "Accord pilote",
  "console.developpement.stadeParametrage": "Paramétrage",
  "console.developpement.stadeActif": "Actif",
  "console.developpement.colCompte": "Compte et contact",
  "console.developpement.colPotentiel": "Potentiel et prochaine action",
  "console.developpement.colDernier": "Dernier échange",
  "console.developpement.colStade": "Stade",
  "console.developpement.colAvancer": "Faire avancer",
  "console.developpement.faireAvancer": "Faire avancer",
  "console.developpement.auPortefeuille": "Au portefeuille",
  "console.developpement.dejaAuPortefeuille": "Déjà au portefeuille",
  "console.developpement.ilYA": "il y a {n} j",
  "console.developpement.ajouterEchange": "Ajouter un échange",
  "console.developpement.masquer": "Masquer",
  "console.developpement.vide": "Aucun compte dans ce pipeline.",
  "console.developpement.prospectsRond":
    "Les prospects entrent par une porte que le record ne nomme pas encore : cette liste porte les partenaires depuis leur propre source.",
  /** `CN-C.3`: « Supervision », WF-CON-41. */
  "console.supervision.titre": "Supervision de l'agent",
  "console.supervision.sousTitre":
    "Ce que Hakili traite seul, et ce qui demande encore une intervention humaine",
  "console.supervision.kpiFiltres": "Dossiers filtrés",
  "console.supervision.kpiFiltresSous": "ce mois",
  "console.supervision.kpiSansIntervention": "Traités sans intervention",
  "console.supervision.kpiSansInterventionSous": "objectif 95 %",
  "console.supervision.kpiExceptions": "Exceptions à traiter",
  "console.supervision.kpiExceptionsSous": "en attente humaine",
  "console.supervision.kpiDelai": "Délai moyen de filtrage",
  "console.supervision.kpiDelaiSous": "de la complétude à la décision",
  "console.supervision.issueTitre": "Issue du filtre",
  "console.supervision.issueQualifies": "Qualifiés et soumis",
  "console.supervision.issueRenvoyes": "Renvoyés au client pour correction",
  "console.supervision.issueRefuses": "Refusés au filtre",
  "console.supervision.issueRond": "L'issue du filtre n'est pas encore disponible.",
  "console.supervision.buteTitre": "Où l'agent bute",
  "console.supervision.buteVide": "Aucune intervention humaine demandée ce mois.",
  "console.supervision.feuilleDeRoute":
    "{n} interventions ce mois pour {cause} : c'est ce chiffre qui décidera du passage à l'ingestion automatique des documents. Tant qu'il reste bas, le lien de dépôt envoyé avec la relance suffit.",
  "console.supervision.exceptionsTitre": "Exceptions en attente",
  "console.supervision.exceptionsVide": "Aucune exception en attente d'un humain.",
  "console.supervision.motifsTitre": "Motifs de clôture sans suite",
  "console.supervision.motifsSous": "Les dossiers clos sans suite, comptés par le motif codé du chargé.",
  "console.supervision.motifsVide": "Aucun dossier clos sans suite avec un motif codé.",
  "console.supervision.traiter": "Traiter",
  "console.supervision.traiterMotif": "Bientôt disponible",
  "console.supervision.gravite": "Gravité",
  /** `CN-C.4`: « Idées » (was « Fonctionnalités » until `D-1019-1`), WF-CON-42. The title is the rail's word: one place, one name. */
  "console.fonctionnalites.titre": "Idées",
  "console.fonctionnalites.sousTitre":
    "Ce que les utilisateurs demandent depuis chaque application, et ce que Hakili en fait",
  "console.fonctionnalites.ongletDemandes": "Demandes détaillées",
  "console.fonctionnalites.ongletConsolidation": "Consolidation Hakili",
  "console.fonctionnalites.colQui": "Qui",
  "console.fonctionnalites.colApp": "Application et écran",
  "console.fonctionnalites.colFaire": "Qu'essayait-il de faire",
  "console.fonctionnalites.colCapture": "Capture",
  "console.fonctionnalites.colDate": "Date",
  "console.fonctionnalites.ecran": "écran {ecran}",
  "console.fonctionnalites.captureJointe": "capture jointe",
  "console.fonctionnalites.manque": "Ce qui manque :",
  "console.fonctionnalites.permettrait": "Ce que cela permettrait :",
  "console.fonctionnalites.vide": "Aucune demande reçue pour le moment.",
  "console.fonctionnalites.consolidationRond":
    "La consolidation en paniers classés n'est pas encore disponible. Sa règle : le rang d'un panier pèse le volume, la portée, l'alignement avec le modèle, l'effort et le risque, jamais le seul nombre de demandes.",
  /** `CN-C.6`: the two blocks on « Dossiers », WF-CON-44 and 45. */
  "console.dossiers.encaissementsVide": "Aucun mandat accepté : rien à encaisser pour le moment.",
  "console.dossiers.encColClient": "Client",
  "console.dossiers.colBanque": "Banque",
  "console.dossiers.colFinance": "Montant financé",
  "console.dossiers.encColEtape": "Étape",
  "console.dossiers.colMandat": "Mandat accepté le",
  "console.dossiers.colFrais": "Frais Hakili",
  "console.dossiers.colStatut": "Statut",
  "console.dossiers.statutAttente": "En attente",
  "console.dossiers.statutEncaisse": "Encaissé",
  // `T-1c` (WF-CON-45, `P2-PAY-003`): the two states the doors add , the buyer's word, the bank's financing.
  "console.dossiers.statutDeclare": "Déclaré réglé",
  "console.dossiers.statutFinance": "Financé par la banque",
  // `T-2b` (`P2-PAY-037`): the fee's refund on a file that ended without a disbursement.
  "console.dossiers.statutRemboursementOrdonne": "Remboursement ordonné",
  "console.dossiers.statutRembourse": "Remboursé le {date}",
  "console.dossiers.declarationLigne": "Déclaré le {date} · {means} · réf. {reference}",
  "console.dossiers.confirmerEncaissement": "Confirmer sur preuve",
  "console.dossiers.confirmerEncaissementMotifFinance":
    "Ces frais sont financés par la banque et ne s'encaissent pas : ils se règlent au décaissement.",
  "console.dossiers.confirmerEncaissementMotifEncaisse": "Encaissés sur preuve le {date}.",
  "console.dossiers.preuveTitre": "La preuve du règlement (reçu, capture du virement) :",
  "console.dossiers.preuveRequise": "La preuve de l'encaissement est requise.",
  // `T-1d` (WF-DLR-13 step 3 as amended, `P2-PAY-008`): the dealers' top-ups, declared by their
  // door and confirmed here on evidence , their own block, WF-CON-45's neighbour.
  "console.dossiers.rechargesTitre": "Recharges de solde déclarées",
  "console.dossiers.rechargesSous":
    "Virements déclarés par les concessionnaires sur le compte de Hakili ; le solde est crédité à la confirmation sur preuve",
  "console.dossiers.rechargesVide": "Aucune recharge déclarée pour le moment.",
  "console.dossiers.rechargeColConcession": "Concession",
  "console.dossiers.rechargeColVirement": "Virement",
  "console.dossiers.rechargeColMontant": "Montant",
  "console.dossiers.rechargeDeclareeLigne": "Déclarée le {date} par {name} · réf. {reference}",
  "console.dossiers.rechargeStatutDeclaree": "Déclarée",
  "console.dossiers.rechargeStatutConfirmee": "Confirmée · {reference}",
  "console.dossiers.rechargeConfirmeeMotif": "Confirmée sur preuve le {date}, mouvement {reference}.",
  "console.dossiers.rechargePreuveTitre": "La preuve de réception du virement (avis de crédit, relevé) :",
  "console.dossiers.rechargePreuveRequise": "La preuve de la recharge est requise.",
  "console.dossiers.marquerEncaisse": "Marquer encaissé",
  "console.dossiers.marquerEncaisseMotif": "Bientôt disponible",
  "console.dossiers.placementsRond":
    "Polices actives, commissions à la signature, renouvellements à vérifier ce mois et commissions de renouvellement : pas encore disponibles.",
  "console.dossiers.placementsPolices": "Polices actives",
  "console.dossiers.placementsSignature": "Commissions à la signature",
  "console.dossiers.placementsRenouvellements": "Renouvellements à vérifier",
  "console.dossiers.placementsCommissions": "Commissions de renouvellement",
  /**
   * `round CN-A`, `CN-A.3`: **the Expertises screen**, `hakili-console.jsx`'s
   * `ExpertisesScreen`, `WF-CON-30` to `34` as amended on 10 September. Every
   * sentence is the drawing's; the séquestre's are the order's, in words.
   */
  /**
   * The founder's finding of 11 September on row 39: each type of partner has
   * its own tab, as the drawing draws it (`sousOnglets`, `WF-CON-35`).
   */
  "console.partenaires.titre": "Partenaires",
  "console.partenaires.sousTitre":
    "Banques, concessionnaires, experts et assureurs : annuaires, onboarding et état de chaque partenariat",
  "console.partenaires.ongletBanques": "Banques",
  "console.partenaires.ongletConcessionnaires": "Concessionnaires",
  "console.partenaires.ongletExperts": "Experts",
  "console.partenaires.ongletAssureurs": "Assureurs",
  "console.partenaires.banquesTitre": "Banques partenaires",
  "console.partenaires.banquesSous": "État du partenariat, paramétrage des critères et volumes traités",
  "console.partenaires.ajouterBanque": "Ajouter une banque",
  "console.partenaires.assureursTitre": "Assureurs partenaires",
  "console.partenaires.assureursSous":
    "Le panel proposé aux clients, sa commission unique et ses polices actives",
  "console.partenaires.ajouterAssureur": "Ajouter un assureur",
  "console.partenaires.ajouterConcessionnaire": "Ajouter un concessionnaire",
  "console.partenaires.concessionnairesTitre": "Concessionnaires partenaires",
  "console.partenaires.concessionnairesSous": "Vendeurs professionnels, leur fiabilité et leur solde prépayé",
  "console.partenaires.fermer": "Fermer",
  /**
   * `round CN-B`, `CN-B.1`: « Équipe », WF-CON-25 to 29, the drawing's words
   * (`hakili-console.jsx`, `EquipeScreen` and its two modals).
   */
  "console.equipe.titre": "Équipe Hakili",
  "console.equipe.sousTitre": "Qui accompagne les clients et les partenaires, et ce à quoi chacun accède",
  "console.equipe.inviterCollaborateur": "Inviter un collaborateur",
  "console.equipe.inviterCharge": "Inviter un chargé de dossier",
  "console.equipe.inviterMotif":
    "Créer un compte Hakili est un droit de l'administration et, pour un chargé de dossier de son équipe, de la responsable des dossiers.",
  "console.equipe.kpiCharges": "Chargés de dossier",
  "console.equipe.kpiChargesSous": "en activité",
  "console.equipe.kpiDossiers": "Dossiers suivis",
  "console.equipe.kpiDossiersSous": "répartis dans l'équipe",
  "console.equipe.kpiReponse": "Taux de réponse client",
  "console.equipe.kpiReponseSous": "moyenne de l'équipe",
  "console.equipe.kpiSatisfaction": "Satisfaction client",
  "console.equipe.kpiSatisfactionSous": "échanges notés",
  "console.equipe.collaborateurs": "Collaborateurs",
  "console.equipe.colPersonne": "Personne",
  "console.equipe.colRole": "Rôle et périmètre",
  "console.equipe.colDossiers": "Dossiers",
  "console.equipe.colRelances": "Relances",
  "console.equipe.colReponse": "Réponse",
  "console.equipe.colSatisfaction": "Satisfaction",
  "console.equipe.colStatut": "Statut",
  "console.equipe.aucun": "Personne dans l'équipe pour l'instant.",
  "console.equipe.aucunCharge": "Hakili n'a aucun chargé disponible à proposer",
  "console.equipe.roleAdministration": "Administration",
  "console.equipe.roleResponsable": "Responsable des dossiers",
  "console.equipe.roleCharge": "Chargé de dossier",
  "console.equipe.roleRelationBanques": "Relation banques",
  "console.equipe.roleRelationConcessionnaires": "Relation concessionnaires",
  "console.equipe.roleRelationAssurances": "Relation assurances",
  "console.equipe.roleProduitDev": "Produit / Dev",
  "console.equipe.perimetreAdministration": "Toute la plateforme",
  "console.equipe.perimetreResponsable": "Équipe des chargés de dossier",
  "console.equipe.perimetreCharge": "Ses dossiers clients",
  "console.equipe.perimetreBanques": "Banques",
  "console.equipe.perimetreConcessionnaires": "Concessionnaires",
  "console.equipe.invitePar": "invité par {nom} le {date}",
  "console.equipe.reaffectePar": "réaffecté par {nom}",
  "console.equipe.depuisLe": "Dans l'équipe depuis le {date}",
  "console.equipe.disponible": "Disponible",
  "console.equipe.indisponibleJusquAu": "Indisponible jusqu'au {date}",
  "console.equipe.compteDesactiveLe": "Compte désactivé le {date}",
  "console.equipe.invitationEnAttente": "Invitation en attente",
  "console.equipe.chargeTitre": "Charge et disponibilité",
  "console.equipe.chargeSous":
    "Charge pondérée, pas un nombre de dossiers : une prise en charge ou une collecte pèse 3 points, une vérification ou une analyse 2, toute étape après l'analyse 1, et chaque pièce manquante ajoute 1. Un dossier décaissé ou sorti du parcours ne pèse plus rien.",
  "console.equipe.pointUn": "point",
  "console.equipe.points": "points",
  "console.equipe.dossierOuvertUn": "dossier ouvert",
  "console.equipe.dossiersOuverts": "dossiers ouverts",
  "console.equipe.rendreDisponible": "Rendre disponible",
  "console.equipe.marquerAbsent": "Marquer une absence",
  "console.equipe.voirDossiers": "Voir ses dossiers ({n})",
  "console.equipe.masquerDossiers": "Masquer ses dossiers",
  "console.equipe.transfererTout": "Transférer tous ses dossiers",
  "console.equipe.retirer": "Retirer de l'équipe",
  "console.equipe.enCoursDe": "Ce que {nom} a en cours",
  "console.equipe.stagnation": "{jours} jours à cette étape, {attendus} attendus",
  "console.equipe.hakiliProposerait": "Hakili proposerait {nom} pour ce dossier",
  "console.equipe.nonRetenus": "Non retenus :",
  "console.equipe.confirmerReaffectation": "Confirmer la réaffectation",
  "console.equipe.deCharge": "de charge",
  "console.equipe.portefeuilleNote":
    "Une réaffectation ponctuelle se fait ici, dossier par dossier ; un transfert complet du portefeuille se fait avec le bouton du dessus, pour une absence ou un départ.",
  "console.equipe.absencePhrase":
    "{nom} ne recevra plus de dossier jusqu'à son retour. Ses dossiers en cours ne bougent pas : vous décidez, avec le transfert de portefeuille.",
  "console.equipe.retourLe": "Retour le",
  "console.equipe.confirmerAbsence": "Confirmer l'absence",
  "console.equipe.annuler": "Annuler",
  "console.equipe.sortieImpossible":
    "Sortie impossible : {n} dossiers ouverts doivent d'abord être transférés.",
  "console.equipe.transfererPortefeuille": "Transférer son portefeuille",
  "console.equipe.siegeTransferer": "Transférer le portefeuille",
  "console.equipe.siegeRetirer": "Retirer de l'équipe",
  "console.equipe.siegeSortieImpossible":
    "Sortie impossible : {n} partenaires en portefeuille doivent d'abord être transférés.",
  "console.equipe.portefeuilleTitre": "Transférer le portefeuille de {nom}",
  "console.equipe.portefeuillePhrase":
    "{n} partenaires en portefeuille à transférer, en une seule fois. Chaque partenaire garde au journal la trace de sa passation, et l'ancien chargé ne le voit plus.",
  "console.equipe.portefeuilleCible": "Confier le portefeuille à",
  "console.equipe.portefeuilleAdministration": "À l'administration (intérim)",
  "console.equipe.portefeuilleConfirmer": "Transférer le portefeuille",
  "console.equipe.portefeuilleFait":
    "Portefeuille transféré : {n} partenaires confiés à {cible}. {nom} n'a plus de partenaire en portefeuille.",
  "console.equipe.portefeuilleAucun": "Aucun partenaire en portefeuille : rien à transférer.",
  "console.equipe.chargeNote":
    "Une absence ne déplace aucun dossier par elle-même : elle sort la personne des candidats de l'algorithme, et c'est vous qui décidez si son portefeuille doit être transféré. Un compte retiré reste dans la liste, grisé et daté, pour que la trace de qui portait quoi ne disparaisse jamais.",
  "console.equipe.lectureCharge":
    "{plus} porte la charge la plus lourde de l'équipe, {plusPoints}, et {moins} la plus légère, {moinsPoints}.",
  "console.equipe.lectureAucune": "La lecture de la charge se fait à partir de deux chargés en activité.",
  "console.equipe.ouvrirPortefeuilleDe": "Ouvrir le portefeuille de {nom}",
  "console.equipe.lectureNote":
    "La note client se lit avec le taux de réponse et le délai de complétude, jamais seule : un chargé qui hérite des dossiers les plus difficiles obtient mécaniquement de moins bonnes notes. Ces indicateurs mesurent le traitement des dossiers, pas les personnes.",
  "console.equipe.droitsTitre": "Droits par rôle",
  "console.equipe.droitsSous":
    "L'accès est séparé par domaine de données, pas par ancienneté. Les droits d'ouverture d'organisation sont limités au type de partenaire dont chacun a la charge.",
  "console.equipe.droitsRond": "La matrice des droits par rôle n'est pas encore disponible.",
  "console.equipe.droitsNote1":
    "Personne chez Hakili ne crée les comptes des employés d'un partenaire : nous ouvrons l'organisation et invitons son premier responsable, qui invite ensuite sa propre équipe. Un chargé de dossier voit ce que la banque demande sur son dossier, jamais l'intérieur de son espace de travail. L'accès d'assistance suppose l'accord du partenaire, il est limité dans le temps et journalisé.",
  "console.equipe.droitsNote2":
    "Un rôle Finance reste à créer lorsque les frais commenceront à circuler : celui qui relance un client ne doit pas être celui qui confirme l'encaissement de ses frais.",
  "console.equipe.transfererTitre": "Transférer le portefeuille de {nom}",
  "console.equipe.transfererPhrase":
    "{n} dossiers ouverts à transférer. Chaque client est prévenu du changement de chargé, et chaque dossier garde au journal la trace de sa passation.",
  "console.equipe.transfereFait":
    "Portefeuille transféré. {nom} n'a plus de dossier ouvert, et chaque chargé retrouve les siens signalés comme nouveaux.",
  "console.equipe.propositionMotivee": "Proposition de Hakili, motivée dossier par dossier",
  "console.equipe.aucunChargeDisponible": "aucun chargé disponible",
  "console.equipe.transfererLesN": "Transférer les {n} dossiers",
  "console.equipe.transfererLeDossier": "Transférer le dossier",
  "console.equipe.equipeHakili": "Équipe Hakili",
  "console.equipe.role": "Rôle",
  "console.equipe.perimetre": "Périmètre",
  "console.equipe.perimetreNote":
    "Le périmètre limite ce que la personne voit et les organisations qu'elle peut ouvrir.",
  "console.equipe.adressePro": "Adresse professionnelle",
  "console.equipe.invitationNote":
    "La personne reçoit un lien d'invitation et définit son mot de passe. Les droits du rôle et du périmètre s'appliquent dès sa première connexion.",
  "console.equipe.entreraDansEquipe":
    "{nom} entrera dans l'équipe des chargés et recevra des dossiers dès sa première connexion.",
  "console.equipe.envoyerInvitation": "Envoyer l'invitation",
  /**
   * `round CN-B`, `CN-B.2`: Partenaires behind the founder's bar, WF-CON-35
   * and 36, the drawing's words (`PartenairesScreen`, `ConcessionnairesDirectory`,
   * `ExpertsDirectory`, `PartenaireScreen`, `ligneComptesOrg`).
   */
  "console.partenaires.sousTitreResp":
    "Les assureurs du panel : convention, délai d'émission des attestations, renouvellements et journal de la relation",
  "console.partenaires.ficheInconnue": "Ce partenaire n'est pas dans votre portefeuille.",
  /** `CN-B.2`: Assureurs in words, the insurance round named. */
  "console.partenaires.assureursRond": "Bientôt disponible",
  "console.partenaires.statutActive": "Active",
  "console.partenaires.statutInvitationEnvoyee": "Invitation envoyée",
  "console.partenaires.statutVerifications": "Vérifications en cours",
  "console.partenaires.statutSuspendue": "Suspendue",
  "console.partenaires.compteActifUn": "Compte du responsable actif ({nom})",
  "console.partenaires.comptesActifs": "{n} comptes actifs",
  "console.partenaires.invitationDepuis":
    "Invitation du responsable envoyée le {date}, compte actif dès qu'il définit son mot de passe",
  "console.partenaires.invitationEnvoyeeSansDate":
    "Invitation du responsable envoyée, compte actif dès qu'il définit son mot de passe",
  "console.partenaires.aucunCompteVerifications": "Pas encore de compte : vérifications hors ligne en cours",
  "console.partenaires.aucunCompte": "Aucun compte : partenariat non commencé",
  "console.partenaires.suiviPar": "Suivi par {nom}",
  "console.partenaires.comptesOrganisation": "Comptes de l'organisation",
  "console.partenaires.vosBanques":
    "Vos banques · {n} · celles que vous ouvrez entrent automatiquement dans votre portefeuille",
  "console.partenaires.ouvrirMotif":
    "Ouvrir une organisation est un droit de l'administration et de la relation banques.",
  "console.partenaires.banquesAucune":
    "Votre portefeuille est vide : les banques que vous ouvrez y entrent automatiquement.",
  // Round `FX-3`, `M3` (`S2-4`): the two gestures a pending invitation owes on the partner's card , the
  // sitting met « Inviter le responsable » greyed with neither beside it, and one of the tokens in a
  // screenshot.
  "console.partenaires.invitationRenvoyer": "Renvoyer l'invitation",
  "console.partenaires.invitationRevoquer": "Révoquer l'invitation",
  "console.partenaires.invitationRevoquerAide":
    "Le lien cesse de fonctionner immédiatement. Vous pourrez inviter de nouveau, la même personne ou une autre.",
  "console.partenaires.invitationRevoquee":
    "Invitation révoquée : le lien ne fonctionne plus. Invitez de nouveau le responsable.",
  "console.partenaires.invitationRenvoyee": "Invitation renvoyée.",
  "console.partenaires.contactAVenir": "Contact de la direction du crédit à renseigner sur la fiche",
  "console.partenaires.parametrer": "Paramétrer",
  "console.partenaires.tuileUtilisateurs": "Utilisateurs",
  "console.partenaires.tuileSoumis": "Dossiers soumis",
  "console.partenaires.tuileRetours": "Retours pour complément",
  "console.partenaires.tuileDecides": "Décidés",
  "console.partenaires.tuileFinances": "Financés",
  "console.partenaires.tuileParametrage": "Paramétrage",
  "console.partenaires.configComplete": "Critères paramétrés",
  "console.partenaires.configAParametrer": "Critères à paramétrer",
  "console.partenaires.configSansBanque": "Panel à ouvrir",
  "console.partenaires.banquesNote":
    "Chaque banque a sa grille tarifaire, ses seuils de délégation et ses règles éliminatoires. Ce paramétrage alimente le filtre appliqué en amont : un dossier peut passer chez l'une et être écarté chez l'autre, ce qui détermine à qui il est soumis.",
  "console.partenaires.invitationEnvoyee": "Invitation envoyée",
  "console.partenaires.banqueAjoutee":
    "{nom} apparaît dans les banques partenaires. Son responsable définit son mot de passe et invite lui-même son équipe ; le paramétrage des critères commence dès l'activation.",
  "console.partenaires.ajouteeAuPortefeuille":
    "Ajoutée à votre portefeuille : vous suivez cette relation de bout en bout.",
  "console.partenaires.ajouterBanqueSous": "Ouvre l'organisation de la banque et invite son responsable",
  /**
   * Round `L-1`, `M0 (d)`: « Inviter le responsable » on the bank's card, by
   * identity's first-admin door (06 row 21), as the cabinet's card carries it.
   */
  "console.partenaires.inviterResponsable": "Inviter le responsable",
  "console.partenaires.responsableNomme": "Responsable nommé : {nom}",
  "console.partenaires.statutComptesActifs": "Comptes actifs",
  "console.partenaires.panelAu": "Au panel",
  "console.partenaires.panelHors": "Hors panel",
  "console.partenaires.panelInconnu": "Panel inconnu",
  "console.partenaires.inviterDroitMotif":
    "Réservé à l'administration et aux relations banques (droit inviterPremierResponsable, ligne 21).",
  "console.partenaires.inviterTitre": "Inviter le responsable de la banque",
  "console.partenaires.inviterPour":
    "La direction du crédit de {bank} reçoit un lien d'invitation par e-mail ; elle choisit son mot de passe et invite ensuite ses analystes.",
  "console.partenaires.inviterParti": "Invitation envoyée à {email}.",
  "console.partenaires.nomBanque": "Nom de la banque",
  "console.partenaires.responsableInvite": "Responsable invité par courriel",
  "console.partenaires.nomDeduit": "Le nom du responsable se déduit de son adresse.",
  "console.partenaires.banqueModalNote":
    "Hakili ouvre l'organisation et invite son premier responsable, qui définit son mot de passe et crée lui-même les comptes de son équipe. La banque entre dans le portefeuille de celui qui l'ouvre.",
  "console.partenaires.contactEnMots": "Le contact de la direction du crédit n'est pas encore disponible.",
  "console.partenaires.creerEtInviter": "Créer et inviter",
  "console.partenaires.typeBanque": "Banque",
  "console.partenaires.typeConcessionnaire": "Concessionnaire",
  "console.partenaires.typeCabinet": "Cabinet d'expertise",
  "console.partenaires.typeAssureur": "Assureur",
  "console.partenaires.retourAnnuaire": "Retour à l'annuaire",
  "console.partenaires.conventionTitre": "Convention",
  "console.partenaires.conventionAucune": "Rien de signé pour l'instant.",
  // `T-1c`, `M3` (I31 amended): the dealer's commission term against the standard, on its fiche.
  "console.partenaires.commissionStandard": "Commission Hakili : {taux} % du prix de vente (taux standard)",
  "console.partenaires.commissionNegociee": "Commission Hakili : {taux} % négocié · standard {standard} %",
  "console.partenaires.commissionModifier": "Modifier le taux négocié",
  "console.partenaires.commissionMotifDroit":
    "La convention d'un concessionnaire se modifie par le chargé de relation de son portefeuille ou par l'administration.",
  "console.partenaires.commissionChampTaux": "Taux négocié (% du prix de vente, vide = standard)",
  "console.partenaires.commissionChampNote": "Ce qui a été négocié",
  "console.partenaires.conventionNonSignee": "Convention non signée : pas de SLA, pas d'escalade (I29).",
  "console.partenaires.conventionSigneeLe": "Signée le {date} · échéance {echeance}",
  "console.partenaires.conventionSla": "SLA de réponse : {jours} jours ouvrés",
  "console.partenaires.fraisDossierBanque": "Frais de dossier : {taux}",
  "console.partenaires.fraisDossierDefaut": "par défaut",
  "console.partenaires.fraisDossierTaux": "{taux} %",
  "console.partenaires.conventionModifier": "Modifier la convention",
  "console.partenaires.conventionEnregistrer": "Enregistrer la convention",
  "console.partenaires.conventionMotifDroit":
    "La convention d'une banque se paramètre avec ses critères, depuis la relation banques ou l'administration.",
  "console.partenaires.conventionChampSignee": "Signée le",
  "console.partenaires.conventionChampEcheance": "Échéance",
  "console.partenaires.conventionChampSla": "SLA (jours ouvrés)",
  "console.partenaires.conventionChampFrais": "Frais de dossier de la banque (%)",
  "console.partenaires.conventionChampNote": "Négocié par rapport au standard",
  /**
   * `CONVENTION-TYPE`: the words are the first-admin door's own (`organisations.controller.ts`: « son type
   * renseigné (pilote ou contrat complet) », and the convention door's « Le type de convention doit être … »),
   * capitalised for a control; none is new.
   */
  "console.partenaires.conventionChampType": "Type de convention",
  "console.partenaires.conventionTypePilote": "Pilote",
  "console.partenaires.conventionTypeComplet": "Contrat complet",
  // `PRET-BANKS`: the panel, a real act on the bank's sheet. The chip above stays about accounts.
  "console.partenaires.panelTitre": "Panel Hakili",
  "console.partenaires.panelOui":
    "Cette banque est dans le panel : le filtre Hakili la considère et peut lui proposer des dossiers.",
  "console.partenaires.panelNon":
    "Cette banque n'est pas dans le panel : le filtre Hakili ne la considère pas et aucun dossier ne lui est proposé.",
  "console.partenaires.panelAdmettre": "Admettre au panel",
  "console.partenaires.panelRetirer": "Retirer du panel",
  "console.partenaires.panelRetirerConfirmation":
    "Retirer {nom} du panel ? Pour le filtre Hakili, elle ne recevra plus aucun dossier tant qu'elle n'y sera pas admise de nouveau.",
  "console.partenaires.panelRetirerConfirmer": "Oui, retirer du panel",
  "console.partenaires.panelRetirerAnnuler": "Non, la laisser dans le panel",
  "console.partenaires.criteresTitre": "Critères de la banque",
  "console.partenaires.criteresIntro":
    "Ce que Hakili sait de l'appétit de la banque. Le filtre n'écarte une banque que sur un critère renseigné : un champ vide ne s'applique pas.",
  "console.partenaires.criteresNonParametre": "non paramétré",
  "console.partenaires.criteresQuotite": "Quotité maximale : {valeur}",
  "console.partenaires.criteresLtv": "LTV maximale : {valeur}",
  "console.partenaires.criteresDuree": "Durée maximale : {valeur}",
  "console.partenaires.criteresAge": "Âge maximal du véhicule : {valeur}",
  "console.partenaires.criteresValeurPct": "{valeur} %",
  "console.partenaires.criteresValeurMois": "{valeur} mois",
  "console.partenaires.criteresValeurAns": "{valeur} ans",
  "console.partenaires.criteresModifier": "Modifier les critères",
  "console.partenaires.criteresEnregistrer": "Enregistrer les critères",
  "console.partenaires.criteresAnnuler": "Annuler",
  "console.partenaires.criteresMotifDroit":
    "Les critères d'une banque se paramètrent depuis la relation banques ou l'administration.",
  "console.partenaires.criteresChampQuotite": "Quotité maximale (%, 1 à 100)",
  "console.partenaires.criteresChampLtv": "LTV maximale (%, 1 à 150)",
  "console.partenaires.criteresChampDuree": "Durée maximale (mois, 1 à 60)",
  "console.partenaires.criteresChampAge": "Âge maximal du véhicule (années, 1 à 30)",
  "console.partenaires.criteresHorsBornes": "{champ} : entier entre {min} et {max}, ou vide.",
  "console.partenaires.santeTitre": "Santé du partenariat",
  "console.partenaires.santeSoumis": "Dossiers soumis",
  "console.partenaires.santeRetours": "Retours pour complément",
  "console.partenaires.santeDecides": "Décidés",
  "console.partenaires.santeOffres": "Offres",
  "console.partenaires.santeRefus": "Refus",
  "console.partenaires.santeDelai": "Délai moyen",
  "console.partenaires.santeVehicules": "Véhicules en ligne",
  "console.partenaires.santeControles": "Contrôles",
  "console.partenaires.santeNonConformes": "Non conformes",
  "console.partenaires.santeEcartPrix": "Écart de prix moyen",
  // `T-2c`, `M2 (5)`: signal 7 on the fiche, from the ledger's facts.
  "console.partenaires.soldeBas": "solde prépayé bas, sous {seuil}",
  "console.partenaires.santeSolde": "Solde prépayé",
  "console.partenaires.santeNote":
    "Chaque chiffre vient de la source qui le détient ; un tiret signale une source qui ne répond pas encore.",
  "console.partenaires.motifsTitre": "Motifs de refus",
  /** `CN-C.5`, WF-CON-37: the pre-filter's state on the fiche, and its adjustment. */
  "console.partenaires.prefiltreAjuste":
    "Pré-filtre ajusté le {date} : {libelle}, au-delà de {seuil} % de quotité. Ces dossiers ne partiront plus chez {banque}.",
  "console.partenaires.prefiltreAucun":
    "Aucun pré-filtre appris pour cette banque. Un motif de refus devient une règle ici, et le filtre opérationnel la lit à l'analyse et à la soumission.",
  "console.partenaires.prefiltreSeuil": "Seuil de quotité (%)",
  "console.partenaires.prefiltreLibelle": "Motif, dans les mots de la banque",
  "console.partenaires.prefiltreEnregistrer": "Enregistrer le pré-filtre",
  "console.partenaires.prefiltreMotifDroit":
    "Le pré-filtre d'une banque s'ajuste depuis la relation banques ou l'administration.",
  "console.partenaires.motifsRond": "Bientôt disponible",
  "console.partenaires.ajusterPrefiltre": "Ajuster le pré-filtre",
  "console.partenaires.journalTitre": "Journal des échanges",
  "console.partenaires.journalVide": "Aucun échange enregistré pour l'instant.",
  "console.partenaires.voirHistorique": "Voir tout l'historique ({n})",
  "console.partenaires.echangesHumains": "{n} échanges humains ; les lignes système ne comptent pas.",
  "console.partenaires.ligneSysteme": "ligne système",
  "console.partenaires.canal": "Canal",
  "console.partenaires.canalAppel": "Appel",
  "console.partenaires.canalVisite": "Visite",
  "console.partenaires.canalCourriel": "Courriel",
  "console.partenaires.canalComite": "Comité",
  "console.partenaires.canalWhatsapp": "WhatsApp",
  "console.partenaires.canalSysteme": "Hakili",
  "console.partenaires.echange": "Échange",
  "console.partenaires.echangePlaceholder": "Ce qui a été dit, et ce qui a été convenu",
  "console.partenaires.enregistrerEchange": "Enregistrer l'échange",
  "console.partenaires.actionsTitre": "Actions",
  "console.partenaires.actionsRond": "L'agenda partagé avec l'écran Priorités n'est pas encore disponible.",
  "console.partenaires.contactsTitre": "Contacts",
  "console.partenaires.contactAucun": "Aucun responsable nommé pour l'instant.",
  "console.dealerships.colConcession": "Concessionnaire et contact",
  "console.dealerships.colAdresse": "Adresse et ouverture",
  "console.dealerships.colFiabilite": "Fiabilité",
  "console.dealerships.colFinances": "Financés",
  "console.dealerships.colSolde": "Solde prépayé",
  "console.dealerships.colStatut": "Statut",
  "console.dealerships.colSuivi": "Suivi",
  "console.dealerships.note":
    "Le solde prépayé se recharge depuis l'espace concessionnaire ; la note de fiabilité est calculée par Hakili sur l'ensemble des transactions. Les vérifications se font hors ligne avant toute invitation, comme pour les experts et les assureurs.",
  "console.cabinets.colCabinet": "Cabinet et contact",
  "console.cabinets.colZone": "Zone et responsable",
  "console.cabinets.colHonoraires": "Honoraires",
  "console.cabinets.colParJour": "Par jour",
  "console.cabinets.colCharge": "Charge semaine · mois",
  "console.cabinets.colStatut": "Statut",
  "console.cabinets.chargeSemaineMois": "{semaine} cette semaine · {mois} ce mois",
  "console.cabinets.responsableAInviter": "Responsable du cabinet à inviter",
  "console.cabinets.responsableActif": "Compte du responsable actif ({nom})",
  "console.cabinets.responsableInvite": "Invitation du responsable envoyée ({nom})",
  "console.cabinets.inviterResponsable": "Inviter le responsable",
  "console.cabinets.inviterMotif": "L'espace du cabinet arrive avec C5.",
  // `round C5-2`, `M1`: the control is lit; the reason above stays for a
  // cabinet whose organisation the list does not name.
  "console.cabinets.inviterTitre": "Inviter le responsable du cabinet",
  "console.cabinets.inviterPour":
    "Le premier compte de {cabinet} : son responsable, qui invitera ensuite ses experts.",
  "console.cabinets.inviterNom": "Nom complet",
  "console.cabinets.inviterEmail": "Adresse e-mail",
  "console.cabinets.inviterTelephone": "Téléphone",
  "console.cabinets.inviterEnvoyer": "Envoyer l'invitation",
  "console.cabinets.inviterNote":
    "Le lien part sur l'espace cabinet ; il active le compte à la première connexion.",
  "console.cabinets.inviterParti": "Invitation envoyée à {email}.",
  "console.cabinets.titre": "Experts partenaires",
  "console.cabinets.sousTitre": "Cabinets mandatés pour les contrôles, leur zone et leur capacité par jeudi",
  "console.cabinets.ouvrir": "Ajouter un expert",
  "console.cabinets.aucun": "Aucun cabinet onboardé pour l'instant.",
  "console.cabinets.capacite": "capacité",
  "console.cabinets.charge": "charge semaine et mois :",
  "console.cabinets.actif": "Actif",
  "console.cabinets.enVerification": "En vérification",
  "console.cabinets.nom": "Nom du cabinet",
  "console.cabinets.contact": "Contact",
  "console.cabinets.telephone": "Téléphone",
  "console.cabinets.rive": "Rive",
  "console.cabinets.riveGauche": "Rive gauche",
  "console.cabinets.riveDroite": "Rive droite",
  "console.cabinets.zone": "Zone couverte",
  "console.cabinets.agrement": "Agrément",
  "console.cabinets.honoraires": "Honoraires (libellé)",
  // `round C5-2`, `OD-29`/`OD-30`: the fee as ONE figure, the capacity as the contractual minimum.
  "console.cabinets.honorairesMontant": "Honoraires (FCFA par véhicule)",
  "console.cabinets.capaciteJour": "Capacité minimale (contrat)",
  "console.cabinets.modifierTermes": "Modifier honoraires et minimum",
  "console.cabinets.termesEnregistres": "Termes enregistrés.",
  "console.cabinets.identiteVerifiee": "Identité vérifiée",
  "console.cabinets.contratRecu": "Contrat reçu",
  "console.cabinets.enregistrer": "Onboarder le cabinet",
  // Round `FX-3`, `M5` (`S2-10`): **every field understandable without reading the canon** , the founder's
  // own rule. Each hint says what the field IS and what reads it, and where a field looks like another's
  // duplicate it says which of the two the platform actually matches on.
  "console.cabinets.zoneAide":
    "Pour la feuille de tournée et pour la lecture humaine. L'affectation d'un contrôle se fait sur la RIVE ci-dessus, jamais sur ce texte.",
  "console.cabinets.agrementAide":
    "Le numéro d'agrément que porte le papier du cabinet. Il est imprimé sur la feuille de tournée et sur le rapport.",
  "console.cabinets.honorairesAide":
    "Le libellé que le cabinet emploie lui-même (par exemple « forfait par véhicule »). Il ne calcule rien.",
  "console.cabinets.honorairesMontantAide":
    "Ce que Hakili paie ce cabinet par contrôle. Renseigné, ce montant remplace les honoraires par défaut pour ce cabinet ; laissé vide, le cabinet est payé les honoraires par défaut de la plateforme (un seul montant pour tous les cabinets). L'administration voit et fixe ce défaut dans Profil, Paramètres de paiement.",
  "console.cabinets.capaciteAide":
    "Le nombre de véhicules par jeudi que la convention engage au minimum. La capacité réellement déclarée chaque semaine est celle du cabinet.",
  "console.cabinets.incompletChamps": "Il reste à renseigner : {champs}.",
  "console.cabinets.champNom": "le nom",
  "console.cabinets.champContact": "le contact",
  "console.cabinets.champTelephone": "le téléphone",
  "console.cabinets.champZone": "la zone couverte",
  "console.cabinets.champAgrement": "l'agrément",
  "console.cabinets.champCapacite": "la capacité minimale",
  "console.cabinets.incomplet":
    "Chaque champ est requis : le cabinet est identifié par ce que la feuille de tournée imprimera.",
  "console.cabinets.deuxPortes":
    "Deux portes, dans l'ordre : l'organisation est ouverte, puis le cabinet est onboardé sur elle. Il devient actif quand ses deux vérifications sont faites.",
  "console.expertises.titre": "Expertises",
  "console.expertises.sousTitre":
    "Contrôles des véhicules, experts mandatés et règlement des sommes séquestrées",
  "console.expertises.statAProgrammer": "À programmer",
  "console.expertises.statAProgrammerSous": "en attente d'un créneau",
  "console.expertises.statSequestre": "Sommes séquestrées",
  "console.expertises.statSequestreSous": "en attente d'issue",
  "console.expertises.statProgrammees": "Programmées",
  "console.expertises.statProgrammeesSous": "sur les jeudis à venir",
  "console.expertises.commentTitre": "Comment Hakili programme",
  "console.expertises.toutConfirmer": "Tout confirmer",
  "console.expertises.chezUn": "1 véhicule chez",
  "console.expertises.chez": "véhicules chez",
  "console.expertises.statConformite": "Taux de conformité",
  "console.expertises.statConformiteSous": "sur les contrôles du mois",
  "console.expertises.regrouperTitre": "Regrouper les déplacements",
  "console.expertises.regrouperTexte":
    "Un expert qui contrôle plusieurs véhicules au même endroit dans la même matinée coûte nettement moins cher qu'un déplacement par dossier. C'est le principal levier sur le prix du contrôle. Les propositions de tournée sont prêtes dans les jeudis d'expertise ci-dessous.",
  "console.expertises.joursTitre": "Jeudis d'expertise",
  "console.expertises.joursCapacite": "Capacité par jeudi : somme des capacités des cabinets actifs",
  "console.expertises.arretee": "Tournée arrêtée {label}, {hours} h avant le passage",
  "console.expertises.ouverte": "Tournée ouverte, arrêt {label}, soit {hours} h avant le passage",
  "console.expertises.rienNePart":
    "Aucune demande confirmée sur ce jeudi : rien ne part encore chez les cabinets.",
  "console.expertises.programmees": "{placed} sur {capacity} programmées",
  "console.expertises.complet": "Complet",
  "console.expertises.placeRestanteUne": "1 place restante",
  "console.expertises.placesRestantes": "places restantes",
  "console.expertises.voirQuiVaOu": "Voir qui va où",
  "console.expertises.masquerDetail": "Masquer le détail",
  "console.expertises.aucunArret": "Aucun passage programmé pour ce cabinet ce jeudi.",
  "console.expertises.vehiculeUn": "1 véhicule",
  "console.expertises.vehicules": "véhicules",
  "console.expertises.arretUn": "1 arrêt",
  "console.expertises.arrets": "arrêts",
  "console.expertises.controleDeStock": "contrôle de stock",
  // `T-2b`, `M1 (d)`: the dealer's mise à disposition on the tour's line (WF-DLR-07 step 3).
  "console.expertises.dispositionAttendue": "mise à disposition attendue du concessionnaire",
  "console.expertises.dispositionConfirmee": "mise à disposition confirmée",
  "console.expertises.apresArret": "programmé après l'arrêt, exception prise en connaissance de cause",
  /** `round CN-B`, `CN-B.0(d)`, WF-CON-31: the whole stop, and the record's own refusal. */
  "console.expertises.reaffecterA": "Réaffecter à {cabinet}",
  "console.expertises.capaciteInsuffisante":
    "Capacité insuffisante : il reste {places} places chez {cabinet} ce jeudi",
  "console.expertises.reaffecterSeul": "Aucun autre cabinet ne passe ce jeudi.",
  /** `CN-B.0(b)`: the séquestre's second line, what the office reads. */
  "console.expertises.demandeLe": "demandé le {date}",
  "console.expertises.coordonneesDemandeesLe": "coordonnées demandées le {date}",
  "console.expertises.declarePayeLe": "déclaré payé le {date}",
  "console.expertises.declareRef": " (réf. {ref})",
  "console.expertises.recuLe": "reçu le {date}",
  "console.expertises.envoyerFeuille": "Envoyer la feuille de tournée",
  "console.expertises.renvoyerFeuille": "Renvoyer, version {version}",
  "console.expertises.feuilleEnvoyee": "Version {version} envoyée à {cabinet}, en attente d'accusé",
  "console.expertises.feuilleConfirmee": "Version {version} confirmée le {date}",
  "console.expertises.feuillePartie": "Version {version} partie, la tournée a changé depuis",
  "console.expertises.accuseAncien": "Accusé de la version {acknowledged}, version en vigueur {version}",
  "console.expertises.feuilleGarantie":
    "Version {version} envoyée par {channel} le {date}, {count} à bord. Tant que le cabinet n'a pas ouvert son lien, rien ne garantit qu'il passera.",
  "console.expertises.feuillePortait":
    "La version {version} portait {sent}. La tournée en compte {live} aujourd'hui. Un ajout après l'arrêt {label} reste possible, mais c'est une exception : elle demande un renvoi et un nouvel accusé.",
  "console.expertises.lienTitre": "Le lien de consultation, à envoyer par WhatsApp",
  "console.expertises.lienTexte":
    "Ce que le cabinet reçoit : la liste de ses arrêts, dans l'ordre de la tournée, par WhatsApp avec un lien de consultation sans connexion. Le lien est une clé : il n'apparaît qu'une fois, ici, et n'est jamais dans une capture d'écran.",
  // `round C5.1`: the version's facts, folded under the drawing's control.
  "console.expertises.voirFeuille": "Voir la feuille de tournée",
  "console.expertises.masquerFeuille": "Masquer la feuille",
  "console.expertises.feuilleFaitsTitre": "Feuille de tournée · version {version} · {cabinet} · {day}",
  "console.expertises.feuilleFaitsTotal": "{vehicles} sur {stops} · {fee}\u00a0FCFA d'honoraires",
  "console.expertises.feuilleSansFaits":
    "Cette version est partie avant que la feuille ne porte ses faits : seule la liste des références est connue.",
  "console.expertises.surPlace": "Sur place :",
  "console.expertises.contactAPreciser": "contact à préciser",
  "console.expertises.km": "{n} km",
  "console.expertises.sansDemandeControleStock": "sans demande, contrôle de stock",
  "console.expertises.feuilleFaitsNote":
    "Le rapport de chaque véhicule se remplit depuis ce même lien, sans compte ni mot de passe. À l'étape 2, un espace cabinet remplacera le lien sans rien changer à ce contenu.",
  "console.expertises.lienCopier": "Copier le lien",
  "console.expertises.lienCopie": "Lien copié",
  "console.expertises.lienAfficher": "Afficher le lien",
  "console.expertises.lienMasquer": "Masquer le lien",
  "console.expertises.signalDivergente":
    "La tournée de {cabinet} a changé depuis la feuille version {version} : {gap}. {sentence} Renvoyez la feuille en version {next}.",
  "console.expertises.signalDivergenteAccusee":
    "Le cabinet a accusé une liste qu'il n'a plus : son accusé n'atteste plus rien.",
  "console.expertises.signalDivergenteNonAccusee": "Le cabinet lit une liste qui n'est plus la nôtre.",
  "console.expertises.ajouteUn": "1 véhicule ajouté",
  "console.expertises.ajoutes": "véhicules ajoutés",
  "console.expertises.retireUn": "1 véhicule retiré",
  "console.expertises.retires": "véhicules retirés",
  "console.expertises.signalAccuseAncien":
    "{cabinet} a accusé la version {acknowledged} de la feuille du {day}, mais la version en vigueur est la {version} : un accusé ne couvre que la version qu'il nomme.",
  "console.expertises.signalSansAccuse":
    "Feuille envoyée {since} à {cabinet}, sans accusé : le jeudi peut échouer sans que personne ne le sache. Le {day} est {when}, {count} en dépendent.",
  "console.expertises.aujourdhui": "aujourd'hui",
  "console.expertises.ilYA": "il y a",
  "console.expertises.jourUn": "1 jour",
  "console.expertises.jours": "jours",
  "console.expertises.dans": "dans",
  "console.expertises.fileTitre": "Demandes à programmer",
  "console.expertises.aConfirmerUne": "1 proposition à confirmer",
  "console.expertises.aConfirmer": "propositions à confirmer",
  "console.expertises.surCapacite": "{placed} sur {capacity}",
  "console.expertises.fileAucune":
    "Aucune demande en attente d'un créneau : toutes les demandes payées sont programmées.",
  "console.expertises.propositionHakili": "Proposition de Hakili",
  "console.expertises.choixEquipe": "Choix de l'équipe",
  "console.expertises.vousDecidez":
    "Vous décidez : cabinet et jeudi restent modifiables jusqu'à la confirmation",
  "console.expertises.recommandation": "Recommandation :",
  "console.expertises.confirmer": "Confirmer cette affectation",
  "console.expertises.confirmerApresArret": "Confirmer malgré l'arrêt, en connaissance de cause",
  "console.expertises.cabinetComplet": "complet ce jeudi",
  "console.expertises.jourArrete": "tournée arrêtée",
  "console.expertises.aucunCabinet": "Aucun cabinet actif : ouvrez-en un dans Partenaires.",
  // `round C5-2`, `M4`, `OD-30`: the cabinet's declaration on the composition.
  "console.expertises.capaciteDeclaree":
    "Capacité déclarée par le cabinet : {declared} (minimum contractuel {minimum}).",
  "console.expertises.sousMinimum": "{cabinet} sous le minimum contractuel ce jeudi",
  "console.expertises.depassement":
    "capacité déclarée {declared}, {confirmed} confirmés, {over} à réaffecter",
  // `round C5-2`, `M2`, decision D: a pass that failed, back on the queue with the cabinet's reason.
  "console.expertises.controleEmpeche": "Contrôle empêché par le cabinet : {reason}. À reprogrammer.",
  "console.expertises.empecheAbsent": "véhicule absent du parc",
  "console.expertises.empecheCles": "clés indisponibles",
  "console.expertises.empecheBatterie": "batterie à plat, démarrage impossible",
  "console.expertises.empecheInaccessible": "véhicule inaccessible",
  "console.expertises.empecheFerme": "parc fermé à l'heure convenue",
  "console.expertises.empecheAutre": "autre raison",
  "console.expertises.regles1":
    "Priorité à l'ancienneté : la demande dont les frais sont payés depuis le plus longtemps passe en premier.",
  "console.expertises.regles2":
    "Regroupement : les véhicules d'une même zone partent dans la même tournée du même cabinet, un passage au lieu de plusieurs.",
  "console.expertises.regles3":
    "Capacité : quand le jeudi est plein, la demande passe au jeudi suivant et le client en est informé aussitôt.",
  "console.expertises.regles4":
    "Arrêt : une tournée est arrêtée {label}, {hours} h avant le passage ; une demande arrivée après part au jeudi suivant, sauf exception prise en connaissance de cause.",
  "console.expertises.pied":
    "Hakili propose l'affectation (zone, tournée, capacité), l'équipe confirme. L'affectation passera en automatique quand le taux de correction sera proche de zéro, comme pour le filtre suivi dans l'écran Supervision.",
  "console.expertises.piedCadence":
    "Un seul jour d'expertise par semaine pour l'instant : la cadence passera à deux puis trois jours quand la demande le justifiera, sans changer ce modèle (les créneaux sont des données, pas une structure). Cadence hebdomadaire du jeudi en place depuis le 13 août ; les contrôles antérieurs étaient programmés à la demande.",
  "console.expertises.piedAnnuaire":
    "L'annuaire et l'onboarding des cabinets vivent dans l'onglet Partenaires, côté administration, avec la charge de chaque cabinet (semaine, mois).",
  "console.expertises.statutAprogrammer": "À programmer",
  "console.expertises.statutProgrammee": "Programmée",
  "console.expertises.statutReussie": "Conforme",
  "console.expertises.statutEcartprix": "Écart de prix",
  "console.expertises.statutEchouee": "Non conforme",
  "console.expertises.statutAnnulee": "Annulée",
  "console.expertises.rapportsTitre": "Rapports à saisir",
  "console.expertises.rapportsAucun": "Aucun passage programmé n'attend son rapport.",
  "console.expertises.saisirRapport": "Saisir le rapport à la place du cabinet",
  "console.expertises.rapportRecu": "Rapport enregistré",
  "console.expertises.documentMotif": "Bientôt disponible",
  "console.expertises.sequestreTitre": "Le séquestre",
  "console.expertises.sequestreSous":
    "Les frais de vérification, dossier par dossier : ce que le client a versé, par quel moyen, et où l'argent en est. Les fonds transitent par un compte de cantonnement chez un prestataire de paiement agréé, jamais par un compte d'exploitation de Hakili.",
  // Round `PAY-P`: the margin line per control and the period's total.
  "console.expertises.margeLigne": "Contrôle {price} · honoraires du cabinet {fee} · marge {margin}",
  "console.expertises.sequestreTotalUn":
    "Sur la période : {received} reçus pour {n} contrôle, {due} d'honoraires dus, marge {margin}.",
  "console.expertises.sequestreTotal":
    "Sur la période : {received} reçus pour {n} contrôles, {due} d'honoraires dus, marge {margin}.",
  // Found by the pair: a received control whose cabinet fee is not known makes the margin a DASH (R81).
  "console.expertises.sequestreTotalPartielUn":
    "Sur la période : {received} reçus pour {n} contrôles, honoraires dus connus sur {k} contrôle ({due}), marge -.",
  "console.expertises.sequestreTotalPartiel":
    "Sur la période : {received} reçus pour {n} contrôles, honoraires dus connus sur {k} contrôles ({due}), marge -.",
  "console.expertises.sequestreAucun": "Aucun frais de vérification demandé pour l'instant.",
  "console.expertises.etatAttente": "En attente",
  "console.expertises.etatRecu": "Reçus",
  "console.expertises.etatAcquis": "Acquis",
  "console.expertises.etatRembourse": "Remboursés",
  "console.expertises.moyenOrange": "Orange Money",
  "console.expertises.moyenWave": "Wave",
  "console.expertises.moyenMoov": "Moov Money",
  "console.expertises.moyenSama": "Sama Money",
  "console.expertises.moyenVirement": "Virement bancaire",
  "console.expertises.confirmerVirement": "Confirmer la réception du paiement",
  "console.expertises.virementMontant": "Montant reçu (FCFA)",
  "console.expertises.virementReference": "Référence de la transaction",
  "console.expertises.virementDate": "Date de réception",
  "console.expertises.virementEnregistrer": "Enregistrer la réception",
  "console.expertises.virementMotif":
    "Seul un paiement déclaré et en attente se confirme ici : aucun paiement n'est en attente sur ce dossier.",
  "console.expertises.sequestreDessin":
    "Le séquestre est décrit en mots ; sa paire attend le trait du designer.",
  /**
   * `round CN-A`, `CN-A.2`: **the Arrivées screen**, `hakili-console.jsx`'s
   * `ArriveesScreen`, `WF-CON-21` to `23`. Every sentence is the drawing's.
   */
  "console.arrivees.titre": "Arrivées",
  "console.arrivees.sousTitre":
    "Un dossier naît dès qu'un client s'engage sur un véhicule, et Hakili le confie aussitôt à un chargé, avec son motif.",
  "console.arrivees.regle":
    "Hakili affecte à l'arrivée : aucun dossier n'attend une validation. Vous corrigez si nécessaire.",
  "console.arrivees.exceptionUne":
    "1 dossier attend une affectation manuelle : aucun chargé de dossier n'était disponible à son arrivée.",
  "console.arrivees.exceptionPlusieurs":
    "dossiers attendent une affectation manuelle : aucun chargé de dossier n'était disponible à leur arrivée.",
  /**
   * Round `WF-CON-11-NEW-BANK-PIPELINE`, `M1` (`OD-42`): **the chargé's recommendations.** The empty sentence is the
   * founder's own, in the ruling's words; the rest is the screen's, drafted, and the founder's to reword.
   */
  "console.recommandations.enAttenteUne": "1 envoi recommandé attend votre accord",
  "console.recommandations.enAttentePlusieurs": "envois recommandés attendent votre accord",
  "console.recommandations.voir": "Voir les recommandations",
  "console.recommandations.titre": "Recommandations",
  "console.recommandations.sousTitre":
    "Des banques ont rejoint le panel après la soumission de vos dossiers : Hakili vous propose d'y envoyer ceux qui correspondent à leur politique. Rien ne part sans votre accord.",
  "console.recommandations.vide": "Aucun dossier recommandé pour l'instant",
  "console.recommandations.envoyer": "Envoyer à cette banque",
  "console.recommandations.ecarter": "Écarter",
  "console.recommandations.ouvrir": "Ouvrir le dossier",
  "console.recommandations.quotite": "Quotité",
  "console.recommandations.motif": "Pourquoi Hakili le propose",
  "console.arrivees.dernieres": "Dernières arrivées",
  "console.arrivees.compteUne": "1 arrivée · la plus récente en tête",
  "console.arrivees.comptePlusieurs": "arrivées · la plus récente en tête",
  "console.arrivees.compteAucune": "Aucune arrivée ces trente derniers jours.",
  "console.arrivees.achatComptant": "Achat comptant",
  "console.arrivees.financement": "Financement",
  "console.arrivees.orpheline": "Aucun chargé disponible : ce dossier attend une affectation manuelle",
  "console.arrivees.affecteeA": "Affectée à",
  "console.arrivees.chipAutomatique": "affectation automatique",
  "console.arrivees.chipCorrigee": "corrigée par",
  "console.arrivees.dejaDansLaListe":
    "Le dossier est déjà dans la liste de {charge}, le travail a commencé. Rien à valider.",
  "console.arrivees.corriger": "Corriger si nécessaire",
  "console.arrivees.affecterALaMain": "Affecter à la main",
  "console.arrivees.reaffecter": "Réaffecter",
  "console.arrivees.affecterCeDossier": "Affecter ce dossier",
  "console.arrivees.rienACorriger": "Rien à corriger : {charge} porte déjà ce dossier.",
  "console.arrivees.optionAbsent": "(indisponible)",
  "console.arrivees.absentJusquAu":
    "{charge} est indisponible jusqu'au {date} : l'affectation sera tracée comme faite malgré son absence.",
  "console.arrivees.rendreDisponible": "Rendre quelqu'un disponible dans Équipe",
  "console.arrivees.motifCorrection": "correction de l'affectation depuis les arrivées",
  "console.arrivees.motifMalgreAbsence": "affecté malgré son absence",
  "console.arrivees.algorithme":
    "L'algorithme retient d'abord les chargés actifs, rend un client qui revient à la personne qui le suivait déjà, puis compare la charge pondérée (une collecte pèse plus qu'un dossier déjà soumis, chaque pièce manquante ajoute un point) et départage à égalité par l'ancienneté de la dernière affectation.",
  "console.arrivees.algorithmeSuite":
    "Sa décision s'applique tout de suite : elle ne vous attend pas. Vous la corrigez quand elle ne vous convient pas, ici ou dossier par dossier depuis Équipe, et le dossier garde la trace de qui a tranché. Le seul cas où l'algorithme s'arrête est celui où personne n'est disponible : il ne devine pas à votre place.",
  /** `U-3b`, `M1` (WF-CON-21 step 1): the excluded, person by person, from the run that decided. */
  "console.arrivees.nonRetenus": "Non retenus : {gens}",
  "console.arrivees.nonRetenusAucun": "Aucun candidat écarté : toute l'équipe était disponible.",
  "console.arrivees.nonRetenusMotif":
    "Les candidats non retenus et leurs raisons ne sont pas disponibles pour ce dossier.",
  /**
   * `round CN-A`, `CN-A.1`: **the Dossiers screen**, `hakili-console.jsx`'s
   * `CollecteScreen`, `WF-CON-01` to `03`. Every sentence is the drawing's.
   */
  "console.dossiers.sousTitreCharge":
    "Les clients dont vous avez la charge, et ce qu'il leur reste à fournir",
  "console.dossiers.sousTitre":
    "Les dossiers que Hakili assemble avec les clients, avant toute soumission à une banque",
  "console.dossiers.nouveauDossier": "Nouveau dossier",
  "console.dossiers.nouveauDossierMotif": "Bientôt disponible",
  "console.dossiers.unNouveauConfie": "1 nouveau dossier vous a été confié",
  "console.dossiers.nouveauxConfies": "nouveaux dossiers vous ont été confiés",
  "console.dossiers.marquerVus": "Marquer comme vus",
  "console.dossiers.statEnCollecte": "Dossiers en collecte",
  "console.dossiers.statEnCollecteSous": "avant soumission bancaire",
  "console.dossiers.statRelances": "Relances envoyées",
  "console.dossiers.statRelancesSous": "ce mois",
  "console.dossiers.statSansReponse": "Sans réponse client",
  "console.dossiers.statSansReponseSous": "3 relances ou plus",
  "console.dossiers.statPrets": "Prêts à soumettre",
  "console.dossiers.statPretsSous": "filtre Hakili passé",
  "console.dossiers.bandeTitre": "Le portefeuille étape par étape",
  "console.dossiers.bandeSous":
    "Ce qui est en cours à chaque étape, et ce qui y traîne au-delà de la durée attendue",
  "console.dossiers.retirerFiltre": "Retirer le filtre d'étape",
  "console.dossiers.sorties": "Sorties du parcours",
  "console.dossiers.enRetard": "en retard",
  "console.dossiers.aLHeure": "à l'heure",
  "console.dossiers.aucunDossier": "aucun dossier",
  "console.dossiers.aucunRetard": "Aucun dossier ne dépasse la durée attendue de son étape.",
  "console.dossiers.pireTraine": "Le dossier qui traîne le plus :",
  "console.dossiers.pireJours": "jours à l'étape",
  "console.dossiers.pireContre": "contre",
  "console.dossiers.pireAttendus": "attendus, soit",
  "console.dossiers.pireDeTrop": "jours de trop.",
  "console.dossiers.portePar": "Porté par",
  "console.dossiers.sansCharge": "Aucun chargé affecté.",
  "console.dossiers.autresDepassent": "autres dossiers dépassent aussi la durée attendue de leur étape.",
  "console.dossiers.filtreEnCours": "En cours",
  "console.dossiers.filtreSansReponse": "Sans réponse",
  "console.dossiers.filtreSansReponseMotif": "Bientôt disponible",
  "console.dossiers.filtrePrets": "Prêts à soumettre",
  "console.dossiers.filtreTous": "Tous",
  "console.dossiers.rechercher": "Rechercher un client, une référence, un véhicule",
  "console.dossiers.colClient": "Client et référence",
  "console.dossiers.colVehicule": "Véhicule et vendeur",
  "console.dossiers.colMontant": "Montant",
  "console.dossiers.colEtape": "Étape",
  "console.dossiers.colContact": "Dernier contact",
  "console.dossiers.colCharge": "Chargé",
  "console.dossiers.badgeNouveau": "Nouveau",
  "console.dossiers.chargeDossier": "Chargé de dossier :",
  "console.dossiers.aucunChargeAffecte": "Aucun chargé affecté",
  "console.dossiers.listeVide": "Aucun dossier ne correspond à ces filtres.",
  "console.dossiers.listeVideCharge": "Aucun dossier ne vous est confié pour l'instant.",
  // `round CN-H`, `M1(b)`: the founder's word of 13 September , the responsable does not see the 2 % nor the dealer's leg.
  "console.dossiers.encaissementsAdministrationSeule":
    "Réservé à l'administration (droit accederDonneesFinancieres, ligne 24) : les encaissements ne sont pas lus depuis ce poste.",
  "console.dossiers.encaissementsTitre": "Frais de dossier à encaisser",
  "console.dossiers.encaissementsSous":
    "2 % du montant financé, dus une fois le prêt accordé, réglés à Hakili avant le rendez-vous de signature",
  "console.dossiers.placementsTitre": "Placements d'assurance",
  "console.dossiers.suiviBancaireTitre": "Suivi bancaire, banque par banque",
  "console.dossiers.suiviBancaireMots":
    "Le suivi bancaire, banque par banque, se lit sur la fiche du dossier soumis ; cette liste n'en porte que le compte.",
  /**
   * `round CN-A`, `CN-A.1`: **the file**, `hakili-console.jsx`'s `DossierScreen`
   * and `PanneauEtape`, `WF-CON-04` to `10`, `19`, `20`. The stage panels are in
   * the AMENDED order , `intake` waits on the money and offers no advance.
   */
  "console.dossier.retour": "Retour aux dossiers",
  "console.dossier.lectureSeule":
    "Lecture seule : ce dossier est porté par {charge}. Seul son chargé de dossier, la responsable des dossiers et l'administration peuvent le faire avancer.",
  "console.dossier.personnePourLInstant": "personne pour l'instant",
  "console.dossier.panneauTitre": "Où en est ce dossier",
  "console.dossier.prochaineEtape": "Prochaine étape :",
  "console.dossier.stagnationJourUn": "1 jour à cette étape,",
  "console.dossier.stagnationJours": "jours à cette étape,",
  "console.dossier.stagnationAttenduUn": "1 attendu",
  "console.dossier.stagnationAttendus": "attendus",
  "console.dossier.resteAFaire": "Ce qui reste à faire à cette étape",
  "console.dossier.recevoir": "Recevoir :",
  "console.dossier.fraisRegles": "Frais de vérification réglés",
  "console.dossier.fraisAttendus": "Frais de vérification en attente",
  "console.dossier.faireAvancer": "Faire avancer le dossier",
  "console.dossier.derniereEtape": "Dernière étape du dossier : il n'y a plus rien à faire après celle-ci.",
  "console.dossier.intakeAttenteVirement": "Frais de vérification en attente du virement.",
  "console.dossier.intakeAttentePush":
    "Frais de vérification demandés, en attente de la validation du client.",
  "console.dossier.intakeRecus": "Frais reçus le",
  "console.dossier.intakeAucuneDemande": "Aucune demande de paiement pour l'instant.",
  // Round `FX-3`, `M2 (d)` (`D6`): the chargé raises the awaited transfer; the confirmation lives with
  // row 28's right, on the séquestre, and is never offered here.
  "console.dossier.intakeSignalerControle": "Signaler au contrôle",
  "console.dossier.intakeSignalerAide":
    "Le virement se confirme par l'administration ou le responsable des dossiers, depuis le séquestre. Ce bouton les prévient ; il ne confirme rien.",
  "console.dossier.intakeSignale":
    "Signalé au contrôle : l'administration et le responsable des dossiers ont été prévenus.",
  "console.dossier.intakeVoirSequestre": "Confirmer depuis le séquestre",
  "console.dossier.intakeHakiliAttend":
    "Hakili attend. Le client lance les vérifications depuis son application une fois les frais reçus ; rien ne se fait avancer depuis cette console.",
  "console.dossier.programmerExpertise": "Programmer l'expertise",
  "console.dossier.saisirRapport": "Saisir le rapport à la place du cabinet",
  "console.dossier.saisirRapportSansDemande":
    "Aucune demande d'expertise ne porte encore sur ce dossier : le client n'a pas lancé les vérifications.",
  /** `round W-13`, `M4`: the chip and the next-step line follow the verdict once a report is in. */
  // `round C5-2`, `M3`: the report's provenance and the cabinet's own paper as a pièce.
  "console.dossier.provenanceEquipe": "équipe Hakili",
  "console.dossier.rapportPiece": "Rapport du cabinet, sur son papier",
  "console.dossier.rapportRecuConforme": "Rapport reçu · conforme",
  "console.dossier.rapportRecuEcartPrix": "Rapport reçu · écart de prix",
  "console.dossier.rapportRecuNonConforme": "Rapport reçu · non conforme",
  "console.dossier.prochaineEtapeEcartPrix": "la réponse du vendeur sur le prix.",
  "console.dossier.prochaineEtapeNonConforme": "la clôture, avec le remboursement de l'acheteur.",
  // Round `L-5`, `M3` (the ended file's words): the line on an all-expired file, and beside a failing verdict.
  "console.dossier.prochaineEtapeOffresExpirees":
    "la clôture sans suite, ou une nouvelle soumission par la chargée.",
  "console.dossier.prochaineEtapeFiltreEchoue":
    "écarter le dossier au filtre, ou relancer le filtre après correction.",
  "console.dossier.verdictTitre": "Verdict de la grille",
  "console.dossier.verdictConforme": "Conforme",
  "console.dossier.verdictEcartPrix": "Écart de prix",
  "console.dossier.verdictNonConforme": "Non conforme",
  "console.dossier.verdictAucun": "Aucun rapport enregistré.",
  "console.dossier.attenteRapport":
    "En attente du rapport du cabinet, dès que l'expertise sera programmée. Il le remplit depuis son lien de tournée ; le verdict sera calculé par la grille de conformité, il n'est jamais choisi ici.",
  "console.dossier.ecartPrixNote":
    "L'écart de prix n'avance pas le dossier : le vendeur doit répondre sur le prix avant toute collecte de pièces.",
  "console.dossier.envoyerAnalyse": "Envoyer à l'analyse",
  "console.dossier.clore": "Clore sans suite",
  // `P1-REST`: the exit is not a decision; the sentence that tells it from « Refuser ».
  "console.dossier.sortieTitre": "Sortie du dossier",
  "console.dossier.sortieMots":
    "Clore sans suite n'est ni un refus ni un verdict d'éligibilité : le véhicule est libéré et les frais sont traités.",
  "console.dossier.remettre": "Renvoyer en Collecte",
  "console.dossier.remettreMots":
    "Renvoie le dossier en collecte : les papiers du véhicule remplissent les pièces du vendeur, et le concessionnaire est averti de ce qui manque encore. L'envoi de l'acheteur est conservé, sauf si vous lui demandez un élément : il renvoie alors son dossier lui-même.",
  // `AA-3d2` (D-1018-AA2): the items editor, opened FIRST , what the client must complete, in her own words.
  "console.dossier.remettreItemsTitre": "Ce que le client doit compléter",
  "console.dossier.remettreItemsMots":
    "Choisissez et reformulez ce que le client doit faire : il le lit tel quel sur son dossier. Rien n'est obligatoire : sans élément ni message, le dossier revient en Collecte comme avant.",
  "console.dossier.remettreAjouter": "Ajouter un élément",
  "console.dossier.remettreAjouterPiece": "Redemander une pièce…",
  "console.dossier.remettrePieceLibre": "Élément libre (aucune pièce)",
  "console.dossier.remettreElementTexte": "Texte de l'élément",
  "console.dossier.remettreElementPiece": "Pièce concernée",
  "console.dossier.remettreRetirer": "Retirer",
  "console.dossier.remettreNote": "Votre message au client (facultatif)",
  "console.dossier.remettreElementVide": "Chaque élément porte un texte.",
  "console.dossier.remettreMotifInterneRequis": "Écrivez le motif interne.",
  "console.dossier.remettreMotif": "Motif interne (obligatoire, jamais montré au client)",
  "console.dossier.remettreConfirmer": "Renvoyer en Collecte",
  // `AA-3a2` (D-1018-1): the chargée decides at « Analyse », nothing without her.
  "console.dossier.envoyerEnAnalyse": "Envoyer en analyse",
  "console.dossier.valider": "Valider et passer à Prêt",
  "console.dossier.validerNonCalcule": "Le filtre Hakili n'a pas encore été calculé sur ce dossier.",
  "console.dossier.validerAucuneBanque":
    "Aucune banque éligible au dernier calcul du filtre Hakili : le dossier ne peut pas passer à Prêt.",
  "console.dossier.banqueEligibleUne": "1 banque éligible",
  "console.dossier.banquesEligibles": "{n} banques éligibles",
  "console.dossier.filtreCalcule": "Filtre Hakili : {eligibles} au dernier calcul.",
  "console.dossier.decisionsTitre": "Votre décision",
  "console.dossier.decisionsMots":
    "Rien ne part sans vous : vous validez et vous choisissez les banques au stade Prêt, ou vous renvoyez le dossier en Collecte, ou vous le refusez.",
  "console.dossier.refuser": "Refuser le dossier",
  // `P2` (architect's note): « Refuser le dossier » says what it does, in one sentence, beside the button.
  "console.dossier.validerSansAnalyse": "Il n'y a pas d'analyse terminée à valider : relancez l'analyse.",
  "console.dossier.refuserConsequence":
    "Le client est informé que son dossier n'est pas éligible, et aucune banque ne le recevra.",
  "console.dossier.refuserMots":
    "Refuse le dossier : il est clos. Votre motif est écrit au journal ; le client reçoit la phrase du dossier refusé, jamais votre motif.",
  "console.dossier.refuserMotif": "Motif du refus (obligatoire)",
  "console.dossier.refuserConfirmer": "Refuser le dossier",
  "console.dossier.refuserMotifRequis": "Écrivez le motif du refus.",
  "console.dossier.motifRequis": "Écrivez le motif.",
  "console.dossier.civiliteRequise": "Choisissez la civilité.",
  "console.dossier.titulaireRequis": "Écrivez le nom du titulaire.",
  "console.dossier.outilsHakili": "Lecture, analyse et filtre Hakili",
  "console.dossier.cloreCode": "Motif codé",
  "console.dossier.cloreCodeChoisir": "Choisir le motif",
  "console.dossier.cloreCodeClientInjoignable": "Client injoignable",
  "console.dossier.cloreCodeClientChangeAvis": "Client a changé d'avis",
  "console.dossier.cloreCodeVehiculeAcheteAilleurs": "Véhicule acheté ailleurs",
  "console.dossier.cloreCodeFinancementObtenuAilleurs": "Financement obtenu ailleurs",
  "console.dossier.cloreCodeAutre": "Autre",
  "console.dossier.tempsTitre": "Où passe le temps",
  "console.dossier.tempsSous":
    "Depuis la réservation ferme du véhicule, les jours par acteur et l'étape où chacun en est.",
  "console.dossier.tempsTotal": "{n} jours au total, véhicule réservé depuis le {date}",
  "console.dossier.tempsAbsent": "Le véhicule n'est pas réservé fermement : rien à décompter.",
  // `T-2b`, `M0 (c)`: a closed or ended file , the clock stopped at the end, the null is not an absent lock.
  "console.dossier.tempsClos": "Dossier clos : le décompte des jours s'est arrêté à la fin du dossier.",
  "console.dossier.tempsOwnerClient": "Client",
  "console.dossier.tempsOwnerHakili": "Hakili",
  "console.dossier.tempsOwnerBanque": "Banque",
  "console.dossier.tempsOwnerExpert": "Expert",
  "console.dossier.tempsOwnerAssureur": "Assureur",
  "console.dossier.tempsOwnerConcessionnaire": "Concessionnaire",
  "console.dossier.tempsJours": "{n} jours",
  "console.dossier.tempsEnCours": "le mouvement attendu",
  "console.dossier.cloreMotif": "Motif de la clôture",
  "console.dossier.cloreConfirmer": "Confirmer la clôture",
  "console.dossier.reattribuer": "Réattribuer",
  "console.dossier.reattribuerA": "Confier à",
  "console.dossier.reattribuerMotif": "Motif de la réattribution",
  "console.dossier.reattribuerConfirmer": "Confirmer la réattribution",
  "console.dossier.actionRefusee": "Le dossier n'a pas bougé.",
  "console.dossier.syntheseTitre": "Synthèse de la relation client",
  "console.dossier.syntheseAbsente": "Aucune synthèse : le dossier n'a pas pu être lu.",
  "console.dossier.syntheseRelances": "Relances",
  "console.dossier.syntheseDernierContact": "Dernier contact",
  "console.dossier.syntheseCanal": "Canal préféré",
  "console.dossier.syntheseMessages": "Messages",
  "console.dossier.syntheseASavoir": "Ce qu'il faut savoir",
  "console.dossier.syntheseMots": "La synthèse n'est pas encore disponible.",
  "console.dossier.piecesTitre": "Pièces",
  // `C14`: agreement is COMPUTED, never parenthesised , two keys, and the screen picks by count.
  "console.dossier.pieceManquanteUne": "pièce manquante",
  "console.dossier.piecesManquantesPlusieurs": "pièces manquantes",
  "console.dossier.pieceRecue": "Reçue",
  "console.dossier.pieceManquante": "Manquante",
  "console.dossier.pieceRelancee": "Relancée",
  "console.dossier.pieceProvenance": "par",
  "console.dossier.pieceLe": "le",
  "console.dossier.televerser": "Téléverser à la place du client",
  "console.dossier.televerserTitre": "Enregistrer une pièce reçue à la main",
  "console.dossier.televerserPiece": "Pièce",
  "console.dossier.televerserCanal": "Canal de réception",
  "console.dossier.televerserCanalRequis":
    "Le canal n'a pas de valeur par défaut : personne ne verse une pièce sans dire d'où elle vient.",
  "console.dossier.televerserAuteur": "Reçue par",
  "console.dossier.televerserNote": "Note (facultatif)",
  // `round S4-14`: la pièce ne bascule plus à « reçue » sans un fichier derrière.
  "console.dossier.televerserFichier": "Fichier",
  // `CONSOLE-MONTHS-QUESTION` (`PIECES-COUNT`, A1): the client's own question and words (`client.dossier.moisDuFichier`, approved by the A-H architect), asked by the chargée per file she files for him.
  "console.dossier.moisDuFichier": "Combien de mois ce fichier contient-il ?",
  "console.dossier.moisUn": "1 mois",
  "console.dossier.moisDeux": "2 mois",
  "console.dossier.moisTrois": "3 mois",
  "console.dossier.moisPlusieurs": "{n} mois",
  "console.dossier.televerserFichierRequis":
    "Aucun fichier n'a de valeur par défaut non plus : la pièce n'est enregistrée qu'avec le document derrière elle.",
  "console.dossier.televerserEchec": "Le fichier n'a pas pu être envoyé au stockage.",
  "console.dossier.televerserConfirmer": "Enregistrer la pièce",
  "console.dossier.televerserToutesRecues": "Toutes les pièces sont déjà reçues.",
  "console.dossier.canalWhatsapp": "WhatsApp",
  "console.dossier.canalAgence": "Au bureau",
  "console.dossier.canalCourriel": "Courriel",
  "console.dossier.relancer": "Relancer sur cette pièce",
  // `round C7`, `C7.2` and `C7.3`: the drawing's « Pièces » card, live.
  "console.dossier.relancerTout": "Tout relancer avec les liens",
  "console.dossier.sur": "sur",
  "console.dossier.recues": "reçues",
  "console.dossier.sourceClient": "Déposée par le client",
  "console.dossier.sourceLien": "Déposée par le lien de dépôt",
  "console.dossier.sourceWhatsapp": "Reçue par WhatsApp",
  "console.dossier.sourceAgence": "Remise en agence",
  "console.dossier.sourceCourriel": "Reçue par courriel",
  // `C7.6`: the seller's pièce, recorded by the chargé from the dealership.
  "console.dossier.sourceVendeur": "Reçue du vendeur",
  "console.dossier.sourceVendeurWhatsapp": "Reçue du vendeur par WhatsApp",
  "console.dossier.sourceVendeurAgence": "Remise en agence par le vendeur",
  "console.dossier.sourceVendeurCourriel": "Reçue du vendeur par courriel",
  "console.dossier.sourceVendeurLien": "Reçue du vendeur par le lien de dépôt",
  "console.dossier.sourceFicheVehicule": "Reçue de la fiche du véhicule",
  "console.dossier.sourceConcessionnaire": "Déposée par le concessionnaire",
  "console.dossier.sourceEmise": "Émise depuis l'espace concessionnaire",
  "console.dossier.pieceDuVendeur": "Pièce du vendeur",
  "console.dossier.pieceRetiree": "pièce retirée le {date}",
  "console.dossier.pieceRefusee": "Refusée",
  "console.dossier.pieceRefuser": "Refuser la pièce",
  "console.dossier.pieceRefusMotif": "Motif du refus",
  "console.dossier.pieceRefusTexte":
    "Le client lit ce motif sur sa ligne et dépose la pièce à nouveau ; la ligne redevient attendue.",
  "console.dossier.pieceRefusConfirmer": "Refuser et redemander",
  // `S4-9`: the pièce opened before judgement , a presigned GET, never the words it carries.
  "console.dossier.piecesVoir": "Voir la pièce",
  "console.dossier.televerserProvenance":
    "La pièce sera versée au dossier avec sa provenance : canal, qui l'enregistre, date.",
  "console.dossier.lienTitre": "Le lien de dépôt, à envoyer par WhatsApp",
  "console.dossier.lienTexte":
    "Il ouvre l'appareil photo sur cette pièce, sans connexion. Ce lien vaut identification : copiez-le sans l'afficher.",
  "console.dossier.piecesLienTexte":
    "Le lien ouvre l'appareil photo sur la pièce demandée, sans connexion : c'est ce qui évite le plus de dépôts manuels.",
  "console.dossier.piecesProvenanceTexte":
    "La provenance de chaque pièce est conservée : une banque qui contrôle le dossier doit pouvoir savoir d'où vient un document et qui l'a versé.",
  // `C7.4`: the civility, read off the identity document by the chargé.
  "console.dossier.civiliteLire": "Lire la civilité sur la pièce",
  "console.dossier.civiliteTitre": "Ce que dit la pièce d'identité",
  "console.dossier.civiliteTexte":
    "La civilité et le nom du titulaire, tels qu'ils sont écrits sur le document. Aucune banque ne reçoit le dossier sans.",
  "console.dossier.civiliteMadame": "Madame",
  "console.dossier.civiliteMonsieur": "Monsieur",
  "console.dossier.civiliteNom": "Nom du titulaire, tel qu'écrit",
  "console.dossier.civiliteConfirmer": "Enregistrer la civilité",
  "console.dossier.journalTitre": "Journal",
  "console.dossier.journalVide": "Rien au journal pour l'instant.",
  // `P1-CNW-064` THE DEALER-ACCESS FACTS PANEL (D-1031-18): who at the dealership read the client's identity and situation, and which pièces were added. The facts' own sentences are SERVED (`text`); only the frame is here.
  "console.dossier.accesTitre": "Accès de la concession",
  "console.dossier.accesSous":
    "Qui, à la concession, a consulté l'identité et la situation du client, et quelles pièces ont été ajoutées.",
  "console.dossier.accesVide": "Aucune consultation enregistrée.",
  "console.dossier.accesVisibilite":
    "Ces informations ne sont visibles que par le responsable de la concession et par Hakili, jamais par le client.",
  // `P45` (walk P5): the journal's channels in the chargée's words , « systeme » is Hakili.
  "console.dossier.journalCanalSysteme": "Hakili",
  "console.dossier.journalCanalSms": "SMS",
  "console.dossier.journalCanalAppel": "Appel",
  "console.dossier.journalCanalVisite": "Visite",
  "console.dossier.journalCanalComite": "Comité",
  "console.dossier.ouvrirConversation": "Ouvrir la conversation",
  "console.dossier.journalTout": "Voir tout l'historique ({n})",
  "console.dossier.conversationTitre": "Conversation",
  "console.dossier.fermer": "Fermer",
  "console.dossier.suiviTitre": "Suivi",
  "console.dossier.suiviCharge": "Chargé de dossier",
  "console.dossier.suiviTelephone": "Téléphone",
  "console.dossier.lectureTitre": "Lecture de Hakili",
  "console.dossier.lectureMots": "La lecture de Hakili n'est pas encore disponible.",
  "console.dossier.actionsTitre": "Actions",
  "console.dossier.contacterVendeur": "Contacter le vendeur",
  "console.dossier.contacterVendeurMotif": "Bientôt disponible",
  "console.dossier.rendezVousDepot": "Programmer un rendez-vous de dépôt",
  "console.dossier.rendezVousDepotMotif": "Bientôt disponible",
  "console.dossier.rapportTitre": "Rapport d'expertise, saisi par l'équipe",
  "console.dossier.rapportValeur": "Valeur de marché estimée (FCFA)",
  "console.dossier.rapportObservations": "Observations",
  "console.dossier.rapportCanal": "Canal de réception du rapport",
  "console.dossier.rapportEnregistrer": "Enregistrer le rapport",
  // Round `C6` (`OD-33`): the cabinet's paper is the document of record; its fingerprint and version on the panel.
  "console.dossier.rapportPapier": "Le rapport du cabinet, sur son propre papier (document de référence)",
  "console.dossier.rapportPapierRequis":
    "Joignez le rapport du cabinet, sur son propre papier : c'est le document de référence.",
  "console.dossier.rapportEmpreinte": "Version {version} · empreinte SHA-256 {sha}",
  // `L-1b`, R24 (c): both fingerprints short on the screen, the sheet's labelled, each with its date.
  "console.dossier.rapportEmpreinteCourte": "Version {version} · empreinte SHA-256 {sha} · reçu le {date}",
  "console.dossier.lectureEmpreinteCourte": "Lecture Hakili · empreinte SHA-256 {sha} · reçu le {date}",
  // `L-1b`, `M5` (b): the panel at `soumis` (WF-CON-14 step 1).
  "console.dossier.soumisTitre": "Réponses des banques",
  "console.dossier.soumissionEtude": "en étude",
  "console.dossier.soumissionRetour": "retour pour complément",
  // Round `L-3`, `M2` (WF-CON-04 step 3, `hakili-console.jsx`'s board): the return, as a return.
  "console.suiviBancaire.titre": "Suivi bancaire, banque par banque",
  "console.suiviBancaire.etudesEnCours": "{n} étude en cours",
  "console.suiviBancaire.etudesEnCoursPluriel": "{n} études en cours",
  "console.suiviBancaire.le": "le {date}",
  "console.suiviBancaire.chipEtude": "En étude",
  "console.suiviBancaire.chipRetour": "Retour pour complément",
  "console.suiviBancaire.chipOffre": "Offre transmise",
  "console.suiviBancaire.chipRefus": "Refus",
  "console.suiviBancaire.detailEtude": "Dossier en étude.",
  "console.suiviBancaire.detailOffre": "Offre transmise au client.",
  "console.suiviBancaire.detailOffreExpiree": "Offre expirée le {date}",
  "console.suiviBancaire.detailOffreDeclinee": "Offre déclinée (dossier clos)",
  "console.suiviBancaire.detailOffreNonRetenue": "Offre non retenue par le client",
  "console.suiviBancaire.detailRetiree": "Étude retirée (dossier clos)",
  "console.suiviBancaire.chipTerminee": "Terminée",
  "console.suiviBancaire.detailRefus": "Refus enregistré, motif conservé ici.",
  // Round `L-3b`, `M2` (WF-CON-14): the refused row's detail names the motive the console keeps.
  "console.suiviBancaire.detailRefusMotif": "Refusé · {motif}",
  // M0 (d), B13 (a drawing in words): « Enregistrer un refus » over a veil with the motive.
  "console.refus.titre": "Enregistrer un refus",
  "console.refus.phrase": "Le motif reste au dossier pour le calibrage ; il n'est jamais transmis au client.",
  "console.refus.motif": "Motif du refus",
  "console.refus.choisir": "Choisir un motif",
  "console.refus.confirmer": "Confirmer le refus",
  // WF-CON-14 step 4: the declaration, its guard sentence (05 §2.6.9's own), never typed twice.
  "console.refus.toutesMotif": "il reste des banques en étude ou une offre au dossier",
  // P1-DOS-008: the terminal panel's sentence and the branch's explanation, the drawing's words.
  // `L-4`, M3 (b) (WF-CON-12): the guard's own sentence on the greyed exit.
  "console.dossier.ecarterMotif": "le filtre Hakili n'a pas encore échoué sur ce dossier",
  // The verdict's own sentence on the panel, the journal's line said where the control stands.
  "console.dossier.filtreEchoue": "{motif}",
  "console.dossier.closSentence":
    "Dossier clos : il n'y a plus d'étape à franchir. Il reste consultable et compte dans l'historique du client.",
  "console.suiviBancaire.relancerPiece": "Relancer le client sur la pièce",
  "console.suiviBancaire.relanceEnvoyee": "Relance envoyée",
  "console.suiviBancaire.pieceRecue": "Pièce reçue, resoumettre",
  "console.suiviBancaire.traiterInterne": "Traiter en interne et resoumettre",
  "console.suiviBancaire.noteClient": "Le client voit la demande, jamais la banque qui la fait.",
  "console.suiviBancaire.noteInterne": "Vérification interne : le client n'en voit rien.",
  "console.suiviBancaire.pied":
    "Un retour n'est pas un refus : la banque veut une pièce ou une vérification de plus avant de se prononcer, ce qui est l'issue la plus fréquente en début de relation. Côté client : agrégat seulement ({banques}, {offres}). Les refus nominatifs et leurs motifs restent ici et nourrissent le calibrage banque par banque.",
  "console.suiviBancaire.banqueSaisie": "{n} banque saisie",
  "console.suiviBancaire.banquesSaisies": "{n} banques saisies",
  "console.suiviBancaire.offreRecue": "{n} offre reçue",
  "console.suiviBancaire.offresRecues": "{n} offres reçues",
  "console.dossier.soumissionOffre": "Offre reçue le {date}",
  "console.dossier.soumissionRefus": "Refus enregistré le {date} · motif conservé, non transmis au client",
  // Round `L-4` (B14, a drawing in words): the offer's end and the study's withdrawal, each from the row.
  "console.dossier.soumissionOffreExpiree": "Offre expirée le {date}",
  "console.dossier.soumissionOffreDeclinee": "Offre déclinée (dossier clos)",
  "console.dossier.soumissionOffreNonRetenue": "Offre non retenue par le client",
  "console.dossier.soumissionRetiree": "Étude retirée (dossier clos)",
  "console.dossier.soumisAucune": "Aucune banque saisie sur ce dossier.",
  "console.dossier.soumisRetenue": "Une offre retenue par le client",
  // Round `L-2`, `M3`: the retention is the buyer's own act (`M0 (d)`, OD-39); B10's form; the « Offre » panel.
  "console.dossier.offreFormulaireSous":
    "Les chiffres de la banque, tels qu'elle les a écrits sur son papier. Rien n'est calculé ici.",
  "console.dossier.offreDuree": "Durée (mois)",
  "console.dossier.offreTaux": "Taux nominal (%)",
  "console.dossier.offreApport": "Apport (%)",
  "console.dossier.offreTaeg": "TAEG (%)",
  "console.dossier.offreMontantVehicule": "Montant financé pour le véhicule (FCFA)",
  "console.dossier.offreGarantie": "Garantie mécanique financée (FCFA, 0 si aucune)",
  "console.dossier.offreFraisBanque": "Frais de dossier banque (FCFA)",
  "console.dossier.offreFraisHakili": "Frais de dossier Hakili (FCFA)",
  "console.dossier.offreMensualite": "Mensualité, assurance comprise (FCFA)",
  "console.dossier.offreAssurance": "Dont assurance emprunteur (FCFA)",
  "console.dossier.offreValidite": "Valable jusqu'au",
  "console.dossier.offreLettre": "Référence de la lettre",
  "console.dossier.offreAgence": "Agence (telle que la lettre la nomme)",
  "console.dossier.offreFraisBanqueFinances": "Frais de dossier banque financés",
  "console.dossier.offreFraisHakiliFinances": "Frais de dossier Hakili financés",
  "console.dossier.offreGarantieFinancee": "Garantie mécanique financée",
  "console.dossier.offreLettreNote":
    "Le papier de la banque sera joint au dossier quand une pièce de ce motif pourra être déposée ; d'ici là, sa référence est conservée sur l'événement de la soumission.",
  "console.dossier.offreEnregistrer": "Enregistrer",
  "console.dossier.offreTitre": "Ce qui est acquis",
  "console.dossier.offreRetenueLigne": "Offre retenue par le client, enregistrée au dossier",
  "console.dossier.offreAssuranceLigne": "Souscription d'assurance transmise à l'assureur",
  // Round `T-1`, `M2` (the insurance parked for the pilot, by the founder's word; 02 §2.4.1 and
  // WF-CON-15 amended by record): the « Offre » checklist's second line, the « Assurance du
  // dossier » card's two sentences (WF-CON-17's own words for the empty state).
  "console.dossier.offreAssuranceClient":
    "Assurance à la charge du client, attestation à présenter à la signature",
  "console.dossier.assuranceTitre": "Assurance du dossier",
  "console.dossier.assuranceAucune": "Aucune assurance à ce stade du dossier.",
  "console.dossier.assuranceClient": "Assurance à la charge du client",
  "console.dossier.prochaineEtapeOffreConditions":
    "le passage aux conditions préalables, l'assurance à la charge du client.",
  // Round `T-1b`, `M2` (WF-CON-18; `hakili-console.jsx`): the « Conditions » panel's words.
  // `T-1c`, `M4` (T-1b's booking (3)): the parked insurance on the stepper , bypassed, never passed.
  "console.dossier.etapeSansObjet": "sans objet (à la charge du client)",
  "console.dossier.conditionALever": "À lever :",
  "console.dossier.conditionLevee": "Levée :",
  "console.dossier.conditionRestante": "condition restante",
  "console.dossier.conditionsRestantes": "conditions restantes",
  "console.dossier.coteBanque": "Côté banque : {n}/{total} levées",
  "console.dossier.leveeLe": "levée le {date}",
  "console.dossier.editerBonRetrait": "Éditer le bon de retrait",
  /**
   * Round `C6`, `M4` (`OD-31`, `OD-33`): the file's panel opens the cabinet's
   * document and Hakili's sheet, lists the versions kept with their
   * fingerprints, and asks the cabinet for a correction , never corrects
   * itself. A missing sheet says why (A47); a standing request greys the ask.
   */
  "console.dossier.rapportDeRecordTitre": "Rapport du cabinet, document de référence",
  "console.dossier.ouvrirRapport": "Ouvrir le rapport",
  /** `LANE-E`, `M0` (b): the repair of a report that lacks Hakili's reading, the responsable's act (06 row 18). */
  "console.dossier.regenererLecture": "Régénérer la lecture Hakili",
  "console.dossier.lectureHakili": "Lecture Hakili",
  "console.dossier.lectureAbsente":
    "Hakili n'a pas pu conserver sa lecture de ce rapport ; le rapport du cabinet fait foi.",
  "console.dossier.papierAbsent": "Aucun document joint à ce rapport.",
  "console.dossier.versionsTitre": "Versions précédentes",
  "console.dossier.versionLigne": "Version {n} · {date} · empreinte SHA-256 {sha}",
  "console.dossier.versionsAucune": "Aucune version précédente.",
  "console.dossier.demanderCorrection": "Demander une correction au cabinet",
  "console.dossier.correctionMotifAide":
    "Ce qui doit être corrigé, en quelques mots ; le cabinet répond par une nouvelle version ou par un refus motivé.",
  "console.dossier.correctionEnvoyer": "Envoyer la demande",
  "console.dossier.correctionAnnuler": "Annuler",
  "console.dossier.correctionDemandee": "Correction demandée le {date} par {name} : {reason}",
  "console.dossier.correctionDejaDemandee": "Une demande de correction attend déjà la réponse du cabinet.",
  "console.dossier.correctionEnvoyee": "La demande de correction a été transmise au cabinet.",
  "console.dossier.provenanceTranscription": "transcription par {name}",
  "console.dossier.rapportMention": "L'expert constate, la grille décide.",
  "console.dossier.montantFinance": "Montant financé",
  "console.dossier.prixVehicule": "Prix du véhicule",
  /**
   * `U-1`, `M3`: « Lecture de Hakili » on the file , the first pass's reading, the chargée's two
   * acts, the trail. The drawing's block name (`hakili-console.jsx`, WF-CON-04 step 9); the words
   * the register's where the drawing is silent; the doors' own sentences where a door speaks.
   */
  // Round 11a (`OD-37`, D-1010-3): « Analyse de Hakili » on the dossier , the console run's register, grade and avis.
  "console.analyse.titre": "Analyse de Hakili",
  "console.analyse.lancer": "Lancer l'analyse Hakili",
  "console.analyse.relancer": "Relancer l'analyse Hakili",
  "console.analyse.horsEtape": "L'analyse Hakili se lance à l'étape d'analyse.",
  "console.analyse.absente":
    "Aucune analyse de Hakili sur ce dossier : lancez-la, Hakili lit les pièces puis démarre dès que vous avez confirmé tous les champs.",
  "console.analyse.enCours": "Analyse Hakili en cours",
  "console.analyse.echec": "L'analyse n'a pas abouti : {motif}",
  // `AA-3c`: the chain behind the one button , its steps, its state, the folded history.
  "console.analyse.progressionTitre": "Avancement de l'analyse",
  "console.analyse.etapeLecture": "Lecture des pièces",
  "console.analyse.etapeConfirmation": "Vos confirmations",
  "console.analyse.etapeAnalyse": "Analyse de Hakili",
  "console.analyse.etatLecture": "Lecture des pièces en cours",
  "console.analyse.etatAConfirmer": "En attente de vos confirmations",
  "console.analyse.etatAnalyse": "Analyse en cours",
  "console.analyse.etatTerminee": "Analyse terminée",
  "console.analyse.etatArretee": "Analyse arrêtée",
  "console.analyse.queueFiltre": "Filtre Hakili",
  "console.analyse.queueBanques": "Banques du panel",
  "console.analyse.ecoule": "Écoulé",
  "console.analyse.attendu": "Attendu",
  "console.analyse.estimation": "(estimation)",
  "console.analyse.environ": "environ {duree}",
  "console.analyse.dureeSecondes": "{n} s",
  "console.analyse.dureeMinSec": "{m} min {s} s",
  "console.analyse.dureeMinutes": "{n} minutes",
  "console.analyse.historiqueTitre": "Historique des analyses",
  "console.analyse.historiqueLecture": "Lecture",
  "console.analyse.historiqueAnalyse": "Analyse de Hakili",
  "console.analyse.historiqueReussie": "réussie",
  "console.analyse.historiqueEchec": "en échec",
  "console.analyse.historiqueEnCours": "en cours",
  "console.analyse.registreTitre": "Constats",
  "console.analyse.lecturesTitre": "Les lectures derrière la note",
  "console.analyse.capaciteObservee": "capacité observée",
  "console.analyse.charges": "charges mensuelles moyennes",
  "console.analyse.registreVide": "Aucun constat à lever.",
  "console.analyse.classeBloquant": "Bloquant",
  "console.analyse.classeALever": "À lever",
  "console.analyse.classeSignale": "Signalé",
  "console.analyse.noteTitre": "Note de solidité",
  "console.analyse.noteProvisoireUne": "Note provisoire, 1 lecture manquante",
  "console.analyse.noteProvisoirePlusieurs": "Note provisoire, {n} lectures manquantes",
  "console.analyse.avisTitre": "Avis de Hakili pour vous",
  "console.analyse.avisReserve": "À vérifier",
  // `P2` (the founder's walk): the verdict first, the detail folded, each finding naming its pièce.
  "console.analyse.detailTitre": "Le détail du calcul",
  "console.analyse.composantesDetail": "Les composantes de la note",
  "console.analyse.pieceUne": "Pièce : {pieces}",
  "console.analyse.piecesPlusieurs": "Pièces : {pieces}",
  "console.analyse.avisActe": "Avis {acte} par {auteur} le {date}",
  "console.analyse.acteConfirmee": "confirmé",
  "console.analyse.acteCorrigee": "corrigé",
  "console.analyse.propositionHakili": "Proposition de Hakili",
  "console.analyse.sansAnalyse": "Vous décidez sans l'analyse de Hakili.",
  "console.analyse.pieceNomIdentite": "Pièce d'identité",
  "console.analyse.pieceNomBulletins": "Bulletins de salaire",
  "console.analyse.pieceNomReleves": "Relevés bancaires",
  "console.analyse.pieceNomAttestation": "Attestation de travail",
  "console.analyse.pieceNomProforma": "Facture proforma",
  "console.analyse.pieceNomExpertise": "Rapport d'expertise",
  "console.analyse.avisFavorable": "Rien ne s'y oppose",
  "console.analyse.avisDefavorable": "Défavorable",
  // Round 10a (`OD-37`, workflow §1.5, WF-CON-13, decision D-1010-3): the employment verification, the chargée's own act.
  "console.dossier.soumettreBanques": "Soumettre aux banques",
  // `AA-4b` (`D-1018-AA2`): at Prêt the chargée chooses the banks herself, the filter's recommendation beside each.
  "console.soumission.titre": "Choisir les banques",
  "console.soumission.aide":
    "Hakili recommande les banques que le filtre retient. Cochez celles à qui vous envoyez le dossier, puis soumettez : l'envoi est votre décision.",
  "console.soumission.retenuesTitre": "Banques retenues par le filtre",
  "console.soumission.recommandee": "Recommandée",
  "console.soumission.ecarteesTitre": "Banques écartées par le filtre",
  "console.soumission.ecarteesAide":
    "Elles ne peuvent pas être cochées : la raison du filtre est écrite sous chaque banque.",
  "console.soumission.aucuneCochee": "aucune banque n'est cochée",
  // `PRET-BANKS` (A47): every bank the screen does not offer is named, with why.
  "console.soumission.incompletesTitre": "Banques aux critères incomplets",
  "console.soumission.incompletesAide":
    "Ces banques sont dans le panel, mais il leur manque le plafond de quotité : on ne peut pas leur envoyer ce dossier. Complétez leurs critères depuis leur fiche, dans Partenaires.",
  "console.soumission.horsPanelTitre": "Banques hors du panel",
  "console.soumission.horsPanelAide":
    "Le filtre ne considère que les banques du panel. Celles-ci n'y ont pas été admises : elles se règlent depuis leur fiche, dans Partenaires.",
  "console.verification.titre": "Vérification d'emploi",
  "console.verification.aide":
    "Appelez ou écrivez à l'employeur au contact imprimé sur l'attestation de travail. Posez les questions ouvertes sans donner les réponses, puis notez ce qu'on vous a dit.",
  "console.verification.methode": "Méthode",
  "console.verification.methodeAppel": "Appel",
  "console.verification.methodeCourriel": "Courriel",
  "console.verification.contact": "Contact utilisé, tel qu'imprimé sur l'attestation",
  "console.verification.contactObtenuPar": "Contact obtenu par",
  "console.verification.contactAttestation": "Attestation de travail",
  "console.verification.date": "Date et heure de l'échange",
  "console.verification.personne": "Personne jointe",
  "console.verification.fonction": "Fonction de cette personne",
  "console.verification.reponses": "Réponses de l'employeur",
  "console.verification.travaille": "Travaille-t-elle ici ?",
  "console.verification.oui": "Oui",
  "console.verification.non": "Non, elle ne travaille pas ici",
  "console.verification.depuis": "Depuis quand (AAAA-MM)",
  "console.verification.poste": "Poste occupé",
  "console.verification.salaire": "Salaire déclaré par l'employeur (FCFA)",
  "console.verification.observations": "Vos observations",
  "console.verification.enregistrer": "Enregistrer la vérification",
  "console.verification.nouvelle": "Enregistrer une nouvelle vérification",
  "console.verification.annuler": "Annuler",
  "console.verification.faite":
    "Vérification effectuée par {nom} : {methode}, {personne} ({fonction}), le {date}.",
  "console.verification.reponsesFaites": "Réponses notées : {reponses}.",
  "console.verification.nonTravaille": "elle ne travaille pas ici",
  "console.verification.travailleDepuis": "travaille ici depuis {depuis}, poste {poste}, salaire {salaire}",
  "console.verification.soumissionAttend": "« Soumettre aux banques » attend cette vérification : {motif}.",
  "console.verification.absente": "Aucune vérification d'emploi enregistrée sur ce dossier.",
  "console.lecture.titre": "Lecture de Hakili",
  "console.lecture.absente":
    "Aucune lecture de Hakili sur ce dossier : les pièces ont été transmises avant la mise en place de la lecture, ou la lecture n'a pas encore été demandée.",
  "console.lecture.enAttente":
    "Lecture en attente : le dossier vient d'être transmis, Hakili lit les pièces.",
  "console.lecture.enCours": "Lecture en cours.",
  "console.lecture.echec":
    "La lecture de Hakili a échoué : {motif} Le filtre reste à lancer à la main, sur les chiffres que vous relevez.",
  // `G5-2`: the run a panel shows is dated, so an old run never reads as current.
  "console.lecture.derniere": "Dernière lecture : {quand}",
  "console.lecture.lancee": "Lecture lancée : {quand}",
  // `G3-1(e)` (A47): no reader configured , nobody was asked, so nobody failed to answer.
  "console.lecture.nonActivee":
    "La lecture automatique n'est pas activée sur cet environnement. Le filtre reste à lancer à la main, sur les chiffres que vous relevez.",
  "console.lecture.completudeTitre": "Pièces requises",
  "console.lecture.complete": "Dossier complet : toutes les pièces requises sont présentes.",
  "console.lecture.incompleteUne": "1 pièce requise absente.",
  "console.lecture.incompletePlusieurs": "{n} pièces requises absentes.",
  "console.lecture.piecePresente": "présente",
  "console.lecture.pieceAbsente": "absente",
  "console.lecture.pieceSansObjet": "sans objet",
  // `VISITE-SANS-OBJET` (17 October 2026): the chargé marks the visite technique not applicable, with a reason, and undoes it.
  "console.dossier.pieceSansObjet": "Sans objet",
  "console.dossier.pieceSansObjetPar": "par",
  "console.dossier.sansObjetMarquer": "Marquer sans objet",
  "console.dossier.sansObjetAnnuler": "Annuler le « sans objet »",
  "console.dossier.sansObjetMotif": "Motif (obligatoire)",
  "console.dossier.sansObjetTexte":
    "En attendant que Bamako confirme si cette voiture a une visite technique. Le motif est écrit au journal du dossier ; le vendeur n'est plus relancé pour cette pièce.",
  "console.dossier.sansObjetTexteAnnuler":
    "La visite technique redevient attendue et le vendeur est de nouveau relancé. Le motif est écrit au journal du dossier.",
  "console.dossier.sansObjetConfirmer": "Marquer sans objet",
  "console.dossier.sansObjetConfirmerAnnuler": "Annuler le « sans objet »",
  "console.lecture.pieceLue": "lue par Hakili",
  "console.lecture.pieceIllisible": "illisible pour Hakili",
  "console.lecture.pieceNonLue": "non lue",
  "console.lecture.pieceEchec": "lecture en échec",
  "console.lecture.champsTitre": "Champs relevés",
  "console.lecture.aucunChamp": "Aucun champ relevé sur les pièces lues.",
  "console.lecture.valeurNulle": "non lu",
  "console.lecture.source":
    "Source : {piece}, page {page}, zone ({x} ; {y}) de la page. L'aperçu de la pièce n'existe pas encore sur cet écran.",
  "console.lecture.sourceSansZone": "Source : {piece}. Hakili n'a pas situé la mention sur la page.",
  "console.lecture.confiance": "confiance {pct} %",
  "console.lecture.confirmer": "Confirmer",
  "console.lecture.corriger": "Corriger",
  "console.lecture.enregistrerCorrection": "Enregistrer la correction",
  "console.lecture.annuler": "Annuler",
  "console.lecture.valeurCorrigee": "Valeur corrigée",
  "console.lecture.confirmeePar": "Confirmée par {nom}",
  "console.lecture.corrigeePar": "Corrigée par {nom} : {valeur} (Hakili proposait {proposee})",
  "console.lecture.contradictionsTitre": "Contradictions relevées",
  "console.lecture.aucuneContradiction":
    "Aucune contradiction entre les chiffres déclarés et les pièces lues.",
  "console.lecture.aucuneLecture":
    "Aucune pièce lue, aucune lecture effectuée : la comparaison n'a pas pu être faite.",
  "console.lecture.quotiteProposee": "Quotité proposée sur les pièces lues : {pct} %",
  "console.lecture.quotiteRetenue": "Quotité retenue : {pct} %",
  "console.lecture.quotiteRetenueAide":
    "Enregistrée à partir des chiffres que vous avez confirmés ou saisis.",
  "console.lecture.quotiteDeclaree": "sur les chiffres déclarés : {pct} %",
  "console.lecture.quotiteIndisponible": "Quotité non calculable sur les pièces lues.",
  "console.lecture.capacite": "Capacité mensuelle estimée sur les pièces : {montant}",
  "console.lecture.salaireMoyen": "Salaire net moyen relevé : {montant}",
  "console.lecture.prefiltreTitre": "Pré-filtre proposé",
  "console.lecture.prefiltreAbsent":
    "Aucune proposition : la quotité n'a pas pu être calculée sur les pièces lues.",
  "console.lecture.retenue": "retenue",
  "console.lecture.ecartee": "écartée",
  "console.lecture.restantUn": "1 champ de la lecture de Hakili reste à confirmer",
  "console.lecture.restantPlusieurs": "{n} champs de la lecture de Hakili restent à confirmer",
  "console.lecture.tousConfirmes": "Chaque champ porte votre acte : le filtre peut être lancé.",
  "console.lecture.voir": "Voir la lecture",
  "console.lecture.masquer": "Masquer la lecture",
  "console.lecture.etapesTitre": "Les étapes de la lecture",
  "console.lecture.etapeRegle": "règle {regle}",
  "console.lecture.etapeModele": "modèle {modele}, prompt {prompt} v{version}",
  "console.lecture.etapeEchec": "échec : {motif}",
  "console.lecture.etapeCompleteness": "Complétude",
  "console.lecture.etapeExtraction": "Lecture des pièces",
  "console.lecture.etapeArithmetic": "Arithmétique",
  "console.lecture.etapePrefilter": "Pré-filtre",
  "console.lecture.saisieManuelleTitre": "Champs à saisir à la main",
  "console.lecture.saisieManuelleAide":
    "Hakili n'a pas pu lire ces champs : la pièce est illisible, le lecteur n'a pas répondu pour cette pièce, ou elle n'a jamais été déposée. Entrez la valeur que vous lisez vous-même.",
  "console.lecture.enregistrerSaisie": "Enregistrer la saisie",
  // `P3` (walk 19 October 2026): the pièces as columns, one red sentence said once, the manual form per pièce.
  "console.lecture.colPiece": "Pièce",
  "console.lecture.colEtat": "État",
  "console.lecture.colRecue": "Reçue le",
  "console.lecture.colSource": "Source",
  "console.lecture.colLecture": "Lecture",
  "console.lecture.sourceVehicule": "Fiche du véhicule",
  "console.lecture.ouvrirPiece": "Ouvrir la pièce",
  "console.lecture.saisiLe": "Saisi par {nom}, le {quand}",
  "console.lecture.modifier": "Modifier",
  "console.lecture.lancer": "Lancer la lecture de Hakili",
  "console.lecture.relancer": "Relancer la lecture de Hakili",
  // `P45` (walk P4): the buyer's terms under a small heading, each figure labelled.
  "console.dossier.conditionsTitre": "Conditions demandées",
  "console.dossier.conditionsAcheteur": "Apport {apport} · Durée {duree}",
  "bank.dossier.dureeMois": "{duree} mois",
  "console.dossier.apportPct": "{apport} %",
  "console.dossier.dureeMois": "{duree} mois",
  "console.dossier.vendeur": "Vendeur",
  "console.session.ecranAbsent": "Bientôt disponible",
  /**
   * `round CN-B`, `CN-B.3`: « Comptes et accès » (WF-CON-40) and Profil
   * (WF-CON-43) to the drawing, and the administration's payment settings
   * by a drawing in words.
   */
  "console.comptes.titre": "Comptes et accès",
  "console.comptes.sousTitre":
    "Hakili ouvre les organisations et crée le premier compte. Chaque organisation invite ensuite ses propres équipes.",
  "console.comptes.ouvrirOrganisation": "Ouvrir une organisation",
  "console.comptes.note":
    "Personne ne s'inscrit seul sur la plateforme. Hakili ouvre l'organisation et crée le compte du responsable, qui reçoit un lien d'invitation et définit son mot de passe. Ce responsable invite ensuite ses collaborateurs, dans la limite de ses propres droits : un chef du crédit crée des analystes, pas d'autres chefs.",
  "console.comptes.organisations": "Organisations partenaires",
  "console.comptes.organisationsAucune": "Aucune organisation ouverte pour l'instant.",
  "console.comptes.membreUn": "1 membre",
  "console.comptes.membres": "{n} membres",
  "console.comptes.gerer": "Gérer",
  "console.comptes.gererMotif": "Bientôt disponible",
  "console.comptes.organisationsNote":
    "Les espaces banque et concessionnaire existent déjà ; les responsables des cabinets d'expertise et des assureurs ont un compte mais pas encore d'espace dédié (attestations et programmation y passeront demain), sans changer la structure des comptes.",
  "console.comptes.comptes": "Comptes des partenaires",
  "console.comptes.comptesQui": "personnel des banques, concessions, cabinets et assureurs",
  "console.comptes.comptesAucun": "Aucun compte partenaire pour l'instant.",
  "console.comptes.colPersonne": "Personne",
  "console.comptes.colOrganisation": "Organisation",
  "console.comptes.colRole": "Rôle",
  "console.comptes.colCreation": "Création",
  "console.comptes.colStatut": "Statut",
  "console.comptes.parHakili": "par Hakili",
  "console.comptes.inviteeParLeChef": "invitée par le chef",
  "console.comptes.statutActif": "Actif",
  "console.comptes.statutInvitation": "Invitation en attente",
  "console.comptes.statutDesactive": "Désactivé",
  "console.comptes.desactiver": "Désactiver",
  "console.comptes.desactiverMotif":
    "Seule l'administration Hakili ferme à sa place le compte d'un partenaire ; sinon, son propre responsable le ferme depuis l'espace du partenaire (I16).",
  "console.comptes.desactiverDeja": "Ce compte est déjà désactivé.",
  "console.comptes.desactiverTitre": "Désactiver le compte de {nom}",
  "console.comptes.desactiverPhrase":
    "La personne ne pourra plus se connecter et ses sessions ouvertes prendront fin. Ce qu'elle tient (dossiers, véhicules) passe au responsable de son organisation, qui en est informé. Ses décisions passées lui restent attribuées. Le motif est gardé dans le journal d'audit.",
  "console.comptes.desactiverFait": "Le compte de {nom} est désactivé.",
  /**
   * Partner accounts, `M2` (17 October 2026): the administration erases a partner's person. Same frictions as the client's
   * erasure (a reason, the full name typed back, the irreversibility box), the partner's own words.
   */
  "console.comptes.effacer": "Effacer",
  "console.comptes.effacerDabord":
    "Désactivez d'abord ce compte : l'effacement ne déplace aucun dossier, il détruit l'identité d'un compte déjà fermé.",
  "console.comptes.effacerSansDroit":
    "L'effacement a son propre droit, plus étroit que la désactivation : votre siège ne le détient pas.",
  "console.comptes.statutEfface": "Effacé",
  "console.comptes.effacerTitre": "Effacer définitivement le compte de {nom}",
  "console.comptes.effacerAvertissement":
    "Cette action est définitive. Elle ne peut être annulée par personne, pas même par vous.",
  "console.comptes.effacerVa":
    "Seront remplacés ou supprimés : le nom (remplacé par un nom anonymisé), l'adresse électronique, le téléphone, le mot de passe, le second facteur, les codes de secours et les traces des appareils des sessions.",
  "console.comptes.effacerReste":
    "Seront conservés : la ligne du compte, ses décisions passées et ses dossiers, qui restent attribués à la personne sous son nom anonymisé, et le journal d'audit.",
  "console.comptes.effacerNom": "Pour confirmer, saisissez le nom complet de la personne : {nom}",
  "console.comptes.effacerFait": "Le compte de {nom} a été effacé.",
  "console.comptes.comptesNote":
    "Un compte appartient à l'organisation, pas à la personne. Une désactivation réattribue ses dossiers, mais ses décisions passées lui restent attribuées dans le journal d'audit.",
  "console.comptes.roleChefCredit": "Chef du crédit",
  "console.comptes.roleAnalyste": "Analyste crédit",
  "console.comptes.roleResponsableConcession": "Responsable concession",
  "console.comptes.roleEmploye": "Employé de concession",
  "console.comptes.roleResponsableCabinet": "Responsable du cabinet",
  "console.comptes.roleResponsablePartenariats": "Responsable des partenariats",
  "console.comptes.roleExpertTerrain": "Expert de terrain",
  "console.comptes.ouvrirSous": "Crée l'organisation et invite son premier responsable",
  "console.comptes.typeOrganisation": "Type d'organisation",
  "console.comptes.nomOrganisation": "Nom de l'organisation",
  "console.comptes.domaineEnMots":
    "Le domaine de messagerie qui limitera les invitations n'est pas encore disponible.",
  "console.comptes.premierResponsable": "Premier responsable, invité par courriel",
  "console.comptes.organisationOuverte":
    "{nom} est ouverte et son responsable invité ; il définit son mot de passe et invite lui-même son équipe.",
  /**
   * Round 8, `M2` (WF-CON-40 extended, 16 October 2026): the administration finds a client and deactivates or
   * reactivates the account. Deactivation is not erasure: it says so in words, because the person clicking
   * should know nothing is deleted.
   */
  "console.comptes.clientsTitre": "Comptes clients",
  "console.comptes.clientsQui": "acheteurs inscrits dans l'application",
  "console.comptes.clientsSous":
    "Retrouvez un client par son nom ou son numéro de téléphone. Désactiver un compte coupe l'accès du client et ne supprime rien : le dossier, les pièces et les paiements restent intacts.",
  "console.comptes.clientsRecherche": "Nom ou numéro de téléphone",
  "console.comptes.clientsRechercher": "Rechercher",
  "console.comptes.clientsTropCourt": "Saisissez au moins deux caractères.",
  "console.comptes.clientsAucun": "Aucun client ne correspond.",
  "console.comptes.clientActif": "Actif",
  "console.comptes.clientDesactive": "Désactivé",
  "console.comptes.clientEfface": "Effacé",
  "console.comptes.clientDesactiveLe": "Désactivé le {date}",
  "console.comptes.clientMotifLigne": "Motif : {motif}",
  "console.comptes.clientDesactiver": "Désactiver le compte",
  "console.comptes.clientReactiver": "Réactiver le compte",
  "console.comptes.clientReactiverEfface":
    "Ce compte a été effacé à la demande du client : un effacement ne se défait pas.",
  "console.comptes.clientDesactiverTitre": "Désactiver le compte de {nom}",
  "console.comptes.clientReactiverTitre": "Réactiver le compte de {nom}",
  "console.comptes.clientDesactiverPhrase":
    "Le client ne pourra plus se connecter et ses sessions ouvertes prendront fin. Son dossier, ses pièces, ses paiements et ses messages restent exactement tels qu'ils sont, et l'équipe continue de traiter ses dossiers. Vous pourrez réactiver le compte.",
  "console.comptes.clientReactiverPhrase":
    "Le client pourra de nouveau se connecter avec son code. Ses sessions fermées ne reviennent pas.",
  "console.comptes.clientMotif": "Motif (obligatoire)",
  "console.comptes.clientAnnuler": "Annuler",
  "console.comptes.clientFait": "Le compte de {nom} est {etat}.",
  "console.comptes.clientEtatDesactive": "désactivé",
  "console.comptes.clientEtatActif": "actif de nouveau",
  /**
   * Round 9, `M3` (05 §6.3, third clause): the administration erases a client's account. The act has no undo, so the
   * dialog says what goes, what stays and why, in words, and asks for more than any other gesture on this screen.
   */
  "console.comptes.clientEffacer": "Effacer le compte",
  "console.comptes.clientEffacerSansDroit":
    "Effacer un compte est réservé aux personnes qui détiennent le droit d'effacer, distinct du droit de désactiver.",
  "console.comptes.clientEffacerTitre": "Effacer définitivement le compte de {nom}",
  "console.comptes.clientEffacerAvertissement":
    "Cette action est définitive. Elle ne peut être annulée par personne, pas même par vous.",
  "console.comptes.clientEffacerVa":
    "Seront remplacés ou supprimés : le nom, le numéro de téléphone (qui sera libéré), l'adresse électronique, le code, les sessions, les notifications, le texte des échanges et les documents des dossiers qui n'ont jamais été soumis à une banque.",
  "console.comptes.clientEffacerReste":
    "Seront conservés, parce que la loi l'impose : les dossiers déjà soumis à une banque avec leurs documents, les paiements et le journal d'audit.",
  "console.comptes.clientEffacerMotifNote":
    "Le motif est conservé dans le journal d'audit, qui ne s'efface jamais : n'y écrivez aucune donnée personnelle.",
  "console.comptes.clientEffacerNom": "Pour confirmer, saisissez le nom complet du client : {nom}",
  "console.comptes.clientEffacerComprends": "Je comprends que l'effacement est irréversible.",
  "console.comptes.clientEffacerBouton": "Effacer définitivement",
  "console.comptes.clientEffacerFait": "Le compte de {nom} a été effacé.",
  "console.profil.modifier": "Modifier",
  "console.profil.droitAdminOuvrir": "Ouvrir des organisations",
  "console.profil.droitAdminOuvrirValeur": "Tous types de partenaires",
  "console.profil.droitAdminPremierCompte": "Créer le premier compte",
  "console.profil.droitAdminPremierCompteValeur": "Responsable de chaque organisation",
  "console.profil.droitAdminDossiers": "Accès aux dossiers",
  "console.profil.droitAdminDossiersValeur": "Collecte, filtre et suivi bancaire des dossiers soumis",
  "console.profil.droitAdminBancaires": "Accès aux données bancaires",
  "console.profil.droitAdminBancairesValeur": "Volumes et décisions, jamais les délibérations internes",
  "console.profil.droitRespDossiers": "Dossiers en collecte",
  "console.profil.droitRespDossiersValeur": "Tous, avec réattribution",
  "console.profil.droitRespEquipe": "Équipe",
  "console.profil.droitRespEquipeValeur": "Les chargés de dossier et leur qualité de service",
  "console.profil.droitRespPortefeuille": "Portefeuille de partenaires",
  "console.profil.droitRespPortefeuilleValeur":
    "Les assureurs du panel : convention, délais, renouvellements, journal",
  "console.profil.droitRespBanques": "Banques et concessionnaires",
  "console.profil.droitRespBanquesValeur": "Aucun accès, ils relèvent des gestionnaires relation",
  "console.profil.droitRespFinancieres": "Données financières",
  "console.profil.droitRespFinancieresValeur": "Frais de dossier et commissions d'assurance, rien d'autre",
  "console.profil.droitChargeDossiers": "Dossiers en collecte",
  "console.profil.droitChargeDossiersValeur": "Les vôtres uniquement",
  "console.profil.droitChargePieces": "Pièces des clients",
  "console.profil.droitChargePiecesValeur": "Consultation journalisée",
  "console.profil.droitChargeBanques": "Espace de travail des banques",
  "console.profil.droitChargeBanquesValeur": "Aucun accès",
  "console.profil.droitChargeFinancieres": "Données financières",
  "console.profil.droitChargeFinancieresValeur": "Aucun accès",
  "console.profil.droitRelOuvrir": "Ouvrir des organisations",
  "console.profil.droitRelOuvrirBanques": "Banques uniquement",
  "console.profil.droitRelOuvrirConcess": "Concessionnaires uniquement",
  "console.profil.droitRelPortefeuille": "Portefeuille",
  "console.profil.droitRelPortefeuilleValeur":
    "Priorités, fiche de compte et pipeline de développement, sur vos partenaires",
  "console.profil.droitRelPrefiltre": "Pré-filtre d'une banque",
  "console.profil.droitRelPrefiltreValeur": "Ajustable depuis ses motifs de refus",
  "console.profil.droitRelQualite": "Qualité des concessionnaires",
  "console.profil.droitRelQualiteValeur": "Conformité des expertises et écarts de prix, en lecture",
  "console.profil.droitRelPremier": "Premier responsable",
  "console.profil.droitRelPremierValeur": "Invitation, jamais création des comptes de leurs équipes",
  "console.profil.droitRelDossiers": "Dossiers en collecte",
  "console.profil.droitRelDossiersValeur": "Aucun accès",
  "console.profil.droitRelEspace": "Espace de travail du partenaire",
  "console.profil.droitRelEspaceValeur": "Sur accord, limité dans le temps et journalisé",
  "console.profil.cloisonnementTitre": "Cloisonnement",
  "console.profil.cloisonnement":
    "Les données d'une banque ne sont jamais visibles par une autre. Hakili voit les volumes et les issues, ce qui sert au calibrage des notes, mais pas les échanges internes d'une direction du crédit.",
  "console.profil.ideeTitre": "Une idée pour améliorer la console ?",
  "console.profil.ideeSous":
    "Vos demandes rejoignent la vue consolidée, avec celles des clients et des partenaires",
  "console.profil.demanderFonctionnalite": "Demander une fonctionnalité",
  "console.profil.paiementsTitre": "Paramètres de paiement",
  "console.profil.paiementsSous":
    "Le taux Hakili, les moyens offerts aux clients et le compte qui reçoit les virements ; chaque publication est une version.",
  "console.profil.paiementsVersionSeed": "Version {version}, publiée par personne : la valeur d'amorçage.",
  "console.profil.paiementsVersion": "Version {version}, publiée par {par} le {date}.",
  // `T-1c` (`P2-PAY-012`): the dealer's standard rate beside Hakili's fee and the control's price.
  "console.profil.paiementsCommission": "Commission concessionnaire (% du prix de vente)",
  "console.profil.paiementsCommissionNote":
    "Le taux standard, pour chaque concessionnaire ; un taux négocié sur la convention d'un concessionnaire s'applique avant lui. Le taux appliqué est figé sur chaque vente avec sa source.",
  "console.profil.paiementsTaux": "Taux Hakili (frais de dossier, en %)",
  "console.profil.paiementsMoyens": "Moyens de paiement offerts",
  "console.profil.paiementsBanque": "Banque de Hakili",
  "console.profil.paiementsTitulaire": "Titulaire du compte",
  "console.profil.paiementsNumero": "Numéro de compte pour les virements",
  "console.profil.paiementsNumeroOrange":
    "Numéro Orange Money de Hakili (destination des paiements Orange Money)",
  "console.profil.paiementsNumeroMoov": "Numéro Moov Money de Hakili (destination des paiements Moov Money)",
  // Round `PAY-P`, `OD-29`: the control's ONE price and the reduced price, with the guard's state in words.
  "console.profil.paiementsTarif": "Tarif du contrôle (FCFA par véhicule, quel que soit le véhicule)",
  "console.profil.paiementsTarifReduit": "Tarif réduit (FCFA, rapport valide de moins de 90 jours)",
  "console.profil.paiementsMotif": "Motif de dérogation (si le tarif est sous les honoraires d'un cabinet)",
  "console.profil.paiementsTarifNonPublie":
    "Tarif non publié : la fiche et l'écran de paiement affichent un tiret.",
  "console.profil.paiementsTarifPublie": "Tarif publié : {amount}\u00a0FCFA par contrôle.",
  "console.profil.paiementsTarifDerogation": "Dérogation : {note}",
  "console.profil.paiementsGardePlafond":
    "Honoraires les plus élevés parmi les cabinets actifs : {amount}\u00a0FCFA ({cabinet}) ; un tarif inférieur est refusé sans motif de dérogation.",
  "console.profil.paiementsGardeAucun":
    "Aucun cabinet actif n'a d'honoraires saisis : aucune garde ne s'applique.",
  // Round `FX-3`, `M2 (a)` (`D2`, `D3`): the SECOND amount , what Hakili pays a cabinet by default.
  "console.profil.paiementsHonoraires":
    "Honoraires par défaut d'un cabinet (FCFA par contrôle, si sa convention n'en fixe pas)",
  // `S2-FINDINGS-2`, `M2` (`S2-10`, `D2` unchanged): the settings field's own hint , who is paid this figure.
  "console.profil.paiementsHonorairesAide":
    "Un seul montant pour tous les cabinets. Un cabinet dont le champ « Honoraires (FCFA par véhicule) » est vide à l'onboarding est payé ce montant ; un cabinet qui a saisi le sien est payé le sien.",
  "console.profil.paiementsHonorairesNonPublies":
    "Honoraires par défaut non saisis : un contrôle commandé maintenant ne porte aucun montant à payer au cabinet, et la marge ne peut pas être lue.",
  "console.profil.paiementsHonorairesPublies":
    "Honoraires par défaut : {amount}\u00a0FCFA par contrôle. La convention d'un cabinet qui fixe les siens l'emporte.",
  "console.profil.paiementsMarge": "Marge Hakili sur le tarif publié : {amount}\u00a0FCFA par contrôle.",
  "console.profil.paiementsMargeNegative":
    "Le tarif publié est inférieur aux honoraires par défaut : Hakili paierait {amount}\u00a0FCFA de plus qu'il ne reçoit sur chaque contrôle sans convention.",
  "console.profil.paiementsPublier": "Publier une nouvelle version",
  "console.profil.paiementsPublie": "Nouvelle version publiée.",
  "console.profil.reservationTitre": "Réservation",
  "console.profil.reservationSous":
    "Les deux termes du signal, en jours. Le premier compte les jours où le mouvement attendu est celui du client ; le second l'âge total de la réservation ferme. Aucun véhicule n'est libéré par la plateforme : le signal prévient, et c'est une personne qui décide.",
  "console.profil.reservationTermeClient":
    "Réservation : signal au chargé après N jours à la charge du client",
  "console.profil.reservationTermeTotal": "Réservation : signal au responsable après N jours au total",
  "console.profil.reservationVersion": "Version {version}, publiée le {date}.",
  "console.profil.reservationSeed": "Termes de départ, aucune version publiée encore.",
  "console.profil.reservationPublier": "Publier les termes",
  "console.profil.reservationPublie": "Termes publiés.",
  // `round K-2` (resumed), `M4`: the sanctions' terms beside the reservation's (WF-CON-43 amended,
  // `DECISION ADDED 08-17`); the penalty a dash while unpublished (R81).
  "console.profil.manquementTitre": "Manquement à la réservation",
  "console.profil.manquementSous":
    "Un véhicule réservé qui quitte la plateforme : l'acheteur est remboursé à la charge du concessionnaire, la pénalité fixe s'ajoute si elle est publiée, et la plateforme tient seule la rétrogradation et la suspension.",
  "console.profil.manquementSeed": "Termes de départ, aucune version publiée encore ; pénalité non publiée.",
  "console.profil.manquementVersion": "Version {version}, publiée le {date}.",
  "console.profil.manquementPenalite": "Pénalité fixe (FCFA, vide tant qu'elle n'est pas décidée)",
  "console.profil.manquementRetrogradation": "Rétrogradation dans la recherche pendant N jours",
  "console.profil.manquementSuspensionNombre": "Suspension au N-ième manquement",
  "console.profil.manquementSuspensionFenetre": "… survenus en N jours",
  "console.profil.manquementPublier": "Publier les termes",
  "console.profil.manquementPublie": "Termes publiés.",
  // `P1-CNW-063` EXPERTISE TOLERANCE: Hakili's one mileage tolerance, beside the payment settings (TRANSITIONAL: the « Paramètres de la
  // plateforme » screen does not exist yet). The sentences are PROPOSED by the architect; the figures, the dates and the names are served.
  "console.profil.toleranceTitre": "Expertise : tolérance",
  "console.profil.toleranceSous":
    "Le nombre de kilomètres dont un rapport d'expertise encore valable peut être en retard sur le compteur du véhicule et rester réutilisé. Un seul réglage pour toute la plateforme.",
  "console.profil.toleranceNonConfiguree": "Non configurée : seule la durée de validité du rapport compte.",
  "console.profil.toleranceValeur": "{km}\u00a0km",
  "console.profil.toleranceVersion": "Version {version}, en vigueur depuis le {date}, publiée par {nom}.",
  "console.profil.toleranceVersionSansNom": "Version {version}, en vigueur depuis le {date}.",
  "console.profil.toleranceChamp": "Nouvelle tolérance (kilomètres)",
  "console.profil.toleranceBornes": "Nombre entier de kilomètres, de {min} à {max}.",
  "console.profil.toleranceHistorique": "Historique",
  "console.profil.toleranceLigne": "Version {version} : {valeur}, depuis le {date}, publiée par {nom}.",
  "console.profil.toleranceLigneSansNom": "Version {version} : {valeur}, depuis le {date}.",
  "console.profil.tolerancePublier": "Publier la tolérance",
  "console.profil.tolerancePublie": "Tolérance publiée.",
  // `round K-2` (resumed), `M4`: the fiche's « Manquements » (WF-CON-36 amended).
  "console.partenaires.manquementsTitre": "Manquements",
  "console.partenaires.manquementsVide": "Aucun manquement constaté.",
  "console.partenaires.manquementUn": "manquement constaté",
  "console.partenaires.manquementPlusieurs": "manquements constatés",
  "console.partenaires.manquementLigne": "{vehicule} · {circonstance} · véhicule parti le {date}",
  "console.partenaires.manquementConstate": "Constaté le {date}",
  "console.partenaires.manquementPreuve": "Preuve déposée",
  "console.partenaires.manquementNote": "Note : {note}",
  "console.partenaires.circonstanceVenduATiers": "vendu à un tiers",
  "console.partenaires.circonstanceVenduAAcheteurHakiliHorsPlateforme":
    "vendu à l'acheteur Hakili hors plateforme",
  "console.partenaires.circonstanceRetireSansVente": "retiré sans vente",
  "console.partenaires.circonstanceAutre": "autre circonstance",
  "console.partenaires.creanceRemboursement": "Remboursement de l'acheteur",
  "console.partenaires.creancePenalite": "Pénalité de manquement",
  "console.partenaires.creanceOuverte": "Ouverte",
  "console.partenaires.creanceReglee": "Réglée depuis le solde · {reference}",
  "console.partenaires.creanceAbandonnee": "Abandonnée : {note}",
  "console.partenaires.creanceRegler": "Régler depuis le solde",
  "console.partenaires.creanceRenoncer": "Renoncer",
  "console.partenaires.creanceRenoncerMotif": "Le motif de l'abandon",
  "console.partenaires.creanceConfirmerAbandon": "Confirmer l'abandon",
  "console.partenaires.creanceAdministrationSeule":
    "Le règlement depuis le solde et l'abandon d'une créance sont l'acte de l'administration (droit accederDonneesFinancieres, ligne 24).",
  "console.partenaires.sanctionRetrogradation": "Véhicules rétrogradés dans la recherche jusqu'au {date}",
  "console.partenaires.sanctionSuspendu":
    "Organisation suspendue par la règle : {n} manquements dans la fenêtre publiée",
  "console.partenaires.sanctionAucune": "Aucune sanction en cours.",
  "console.profil.paiementsNote":
    "Aucun chiffre n'est écrit ici par l'écran : chaque valeur est celle que l'administration a saisie, et la version en vigueur est celle que le client lit.",
  "console.profil.droits": "Droits",
  "console.profil.titre": "Profil",
  "console.profil.role": "Rôle",
  "console.profil.organisation": "Organisation",
  "console.profil.portee": "Portée",
  "console.profil.ecrans": "Écrans",
  "console.profil.deconnexion": "Se déconnecter",
  "console.profil.nom": "Nom complet",
  "console.profil.enregistre": "Modifications enregistrées",
  "console.demande.ouvrir": "Une idée ?",
  "console.demande.titre": "Proposer une amélioration",
  "console.demande.envoyer": "Envoyer",
  // `P0-UIK-018`: the two demonstration switchers, which exist only behind the
  // `demo` flag (4.7.3). Their labels live here like any other, because a
  // string that ships in a staging demonstration is a string a founder reads.
  // `P0-IDN-044`: the demonstration choreography block. Its strings live here
  // like any other, because a string a founder reads in front of a bank is a
  // string this catalogue owns , and because the block must say plainly what it
  // is: a demonstration device, not a product feature.
  "console.choreographie.titre": "Code de démonstration",
  "console.choreographie.explication":
    "Dispositif de démonstration : lire le dernier code envoyé à un numéro, pour montrer le parcours client en salle. Absent hors démonstration.",
  "console.choreographie.numero": "Numéro du client",
  "console.choreographie.lire": "Lire le code",
  "console.choreographie.lecture": "Lecture…",
  "console.choreographie.aucun": "Aucun code pour ce numéro.",
  // `P0-IDN-046`'s console window, round 191. Beside the OTP card and worded the
  // same way: the founder reads a token he cannot otherwise reach, because
  // nothing sends the email that would carry it.
  "console.invitation.titre": "Lien d'invitation",
  "console.invitation.explication":
    "Aucun e-mail ne part encore. Cette carte lit le jeton de la dernière invitation envoyée à une adresse, pour le remettre en main propre pendant une démonstration.",
  "console.invitation.adresse": "Adresse e-mail invitée",
  "console.invitation.lire": "Lire le lien",
  "console.invitation.lecture": "Lecture…",
  "console.invitation.aucun": "Aucune invitation pour cette adresse.",
  "console.invitation.copie": "Lien à ouvrir",
  // `P1-IDN-047`, round 191: the invitation's own face on a partner host. The
  // five-minute onboarding's visible half, and the first screen a banker sees.
  "bank.invitation.titre": "Rejoindre votre espace",
  "bank.invitation.bienvenue": "Vous êtes invité",
  "bank.invitation.organisation": "Organisation",
  "bank.invitation.role": "Rôle",
  "bank.invitation.motDePasse": "Choisissez un mot de passe",
  "bank.invitation.confirmer": "Confirmez le mot de passe",
  "bank.invitation.accepter": "Activer mon compte",
  "bank.invitation.encours": "Activation…",
  "bank.invitation.differents": "Les deux mots de passe ne sont pas identiques.",
  "bank.invitation.reussite": "Votre compte est actif. Vous pouvez vous connecter.",
  "bank.invitation.introuvable": "Cette invitation n'est plus valable.",
  // `P1-BKW-020`, round 191: the bleed guard. A partner window that receives a
  // principal who is not that partner's staff says so and offers the door out.
  "bank.bleed.titre": "Ce compte n'appartient pas à cette banque",
  "bank.bleed.explication":
    "Votre compte existe, mais il n'a pas de rôle dans cet espace banque. Déconnectez-vous, puis utilisez la console qui correspond à votre rôle.",
  // `P0-PLT-038`: the four columns of the flag diagnostic. These strings are a
  // founder's only way to tell "flag off" from "environment excluded" from
  // "marker missing", so they say the difference out loud rather than in a
  // colour.
  "console.drapeaux.titre": "Diagnostic des drapeaux",
  "console.drapeaux.explication":
    "Pour chaque drapeau : la valeur brute, les environnements autorisés, le marqueur de cette base et le résultat dérivé. Un drapeau peut être vrai et rester inactif.",
  "console.drapeaux.lire": "Lire les drapeaux",
  "console.drapeaux.lecture": "Lecture…",
  "console.drapeaux.refuse": "Lecture refusée : ce compte n'a pas le droit d'administration des drapeaux.",
  "console.drapeaux.marqueur": "Marqueur de cette base :",
  "console.drapeaux.marqueurAbsent":
    "Marqueur absent : cette base ne sait pas quel environnement elle est, donc tous les drapeaux sont inactifs.",
  "console.drapeaux.colonneCle": "Clé",
  "console.drapeaux.colonneValeur": "Valeur brute",
  "console.drapeaux.colonneEnvironnements": "Environnements autorisés",
  "console.drapeaux.colonneMarqueur": "Marqueur",
  "console.drapeaux.colonneDerive": "Résultat dérivé",
  "console.drapeaux.vrai": "vrai",
  "console.drapeaux.faux": "faux",
  "console.drapeaux.absent": "absent",
  "console.drapeaux.actif": "actif",
  "console.drapeaux.inactif": "inactif",
  "console.drapeaux.aucunEnvironnement": "aucun",
  // `P0-CNW-029`: the dealerships directory on `/partenaires`. The KEY segments
  // are English per the founder's decision of 5 September 2026; the VALUES are
  // the product's own `fr-ML`, which that decision leaves untouched (06 6.5.1).
  // `P0-CNW-030`: the fiche of a dealership that already HAS an administrator
  // says so, rather than offering a naming form the api refuses.
  "console.fiche.introResponsable": "Le responsable de cette concession et l'état de son compte.",
  "console.dealerships.title": "Concessions",
  "console.dealerships.intro": "Les concessions ouvertes, et l'état du compte de leur responsable.",
  "console.dealerships.empty":
    "Votre portefeuille est vide : les concessionnaires que vous ouvrez y entrent automatiquement.",
  "console.dealerships.noAdministrator": "Aucun responsable nommé",
  "console.dealerships.open": "Ouvrir la fiche",
  // The api's four codes, one sentence each. `identity` decides which; this
  // file decides how it reads.
  "console.dealerships.stateActive": "Compte actif",
  "console.dealerships.stateInvitationPending": "Invitation en attente",
  "console.dealerships.stateInvitationRevoked": "Invitation révoquée",
  "console.dealerships.stateDeactivated": "Compte désactivé",
  "console.demo.vue": "Vue",
  "bank.demo.persona": "Persona",
  // `P0-UIK-017`: the two personas of 03, 3.0 and their screens.
  "bank.persona.analyste": "Analyste crédit",
  "bank.persona.chef": "Chef du crédit",
  "bank.nav.pipeline": "Pipeline",
  "bank.nav.dossiers": "Dossiers",
  "bank.nav.decisions": "Décisions",
  "bank.nav.comite": "Comité",
  "bank.nav.equipe": "Équipe",
  "bank.nav.pilotage": "Pilotage",
  "bank.nav.politique": "Politique",
  "bank.nav.profil": "Profil",
  // `P1-BKW-001` to `P1-BKW-003`, round 189: every sentence the analyst reads
  // between his login and Traoré's file. WF-BNK-01, WF-BNK-02, WF-BNK-03.
  "bank.login.titre": "Espace banque",
  "bank.login.email": "Adresse e-mail",
  "bank.login.motDePasse": "Mot de passe",
  "bank.login.entrer": "Se connecter",
  "bank.login.encours": "Connexion…",
  "bank.session.chargement": "Chargement…",
  "bank.session.inattendue": "Réponse inattendue du serveur.",
  "bank.session.deconnexion": "Se déconnecter",
  "dealer.session.deconnexion": "Se déconnecter",
  // **`round F`, `F2`: the dashboard.** `dealer.accueil.bienvenue` and
  // `dealer.accueil.suite` are GONE with the screen that carried them , a
  // welcome and a promise about what was coming, right for a product with one
  // other screen and false in a product with seven. `P0-DLW-009` had already
  // cut that sentence in half once for the same reason.
  //
  // The greeting is the DEALERSHIP's, as the drawing has it (`:1143`): two
  // people share this space and it belongs to the house. The person is named in
  // the rail, and once more here, quietly, because an invitee who has just set
  // a password lands on this screen and needs to see whose session he is in.
  "dealer.pieces.titre": "Pièces demandées par la banque pour ce dossier",
  "dealer.pieces.sousTitre": "La liste du dossier, la même que celle du client",
  "dealer.pieces.sur": "sur",
  "dealer.pieces.pretes": "pièces prêtes",
  "dealer.pieces.prete": "pièce prête",
  "dealer.pieces.photo": "Prendre en photo",
  "dealer.pieces.refaire": "Refaire la photo",
  "dealer.pieces.fichier": "Choisir un fichier",
  "dealer.pieces.astucesTitre": "Pour une photo lisible",
  "dealer.pieces.astuce1": "Page entière",
  "dealer.pieces.astuce2": "Pas de reflet",
  "dealer.pieces.astuce3": "Texte net",
  "dealer.pieces.voitureTitre": "Pièces de la voiture",
  "dealer.pieces.voitureSous": "Déjà au véhicule : rien à redéposer",
  "dealer.pieces.voitureVehicule": "Au dossier du véhicule, déposée le {date}",
  "dealer.pieces.voitureEmise": "Émise par votre concession le {date}",
  "dealer.pieces.voitureDeposee": "Déposée par vous le {date}",
  "dealer.pieces.valableJusqua": "Valable jusqu'au {date}",
  "dealer.accueil.bonjour": "Bonjour",
  "dealer.accueil.point": "point",
  "dealer.accueil.points": "points",
  "dealer.accueil.demande": "demande",
  "dealer.accueil.demandent": "demandent",
  "dealer.accueil.attention": "votre attention aujourd'hui",
  "dealer.accueil.vehiculeEnLigne": "véhicule en ligne",
  "dealer.accueil.vehiculesEnLigne": "véhicules en ligne",
  "dealer.accueil.enLigne": "Véhicules en ligne",
  "dealer.accueil.sur": "sur",
  "dealer.accueil.auCatalogue": "au catalogue",
  "dealer.accueil.vendus": "Vendus ce mois",
  // `R81`. The drawing says « Vendus ce mois »; `me/vehicles` carries the
  // LISTING date and no date of sale, so the month cannot be cut and the card
  // says which count it is really showing rather than mislabelling it.
  // `F2.3`: the drawing's own third line is « N autres en transaction », and it
  // is DERIVED , the stage says who is mid-sale. The « ce mois » of the tile's
  // title is the drawing's, and the month is the one thing this door cannot
  // cut: `me/vehicles` carries the listing's date and no date of sale. Said in
  // the tile's own third line rather than in a paragraph under the box.
  "dealer.accueil.vendusAutres": "autre en transaction",
  "dealer.accueil.vendusAutresPluriel": "autres en transaction",
  "dealer.accueil.solde": "Solde prépayé",
  // `round K-2` (resumed), `M3`/`M4`: the sanctions the platform holds, one banner each.
  "dealer.accueil.suspendu": "Compte suspendu par Hakili : contactez votre chargé de relation.",
  "dealer.accueil.retrograde":
    "Véhicules rétrogradés dans la recherche jusqu'au {date} : manquement à une réservation constaté par Hakili.",
  // **`R81` as the founder amended it on 7 September**: where a value is not
  // yet there, a DASH in its place , never a paragraph, never a number. The
  // tile keeps the drawing's exact anatomy , label, value, third line , and the
  // value is « - ». A paragraph in a tile is a tile that has stopped being one.
  "dealer.accueil.soldeSuite": "FCFA disponibles",
  "dealer.accueil.fiabilite": "Note de fiabilité",
  "dealer.accueil.fiabiliteSuite": "dossiers financés",
  /**
   * The dash itself, once, so every absent value is the same character.
   *
   * A plain hyphen and NOT an em dash: `hakili/no-forbidden-dash` bans U+2014
   * and U+2013 anywhere in source, and a placeholder is not worth an exemption
   * from a rule the whole repository keeps. The founder asked for a dash; this
   * is one.
   */
  "dealer.accueil.absent": "-",
  "dealer.accueil.ceQuiVousAttend": "Ce qui vous attend aujourd'hui",
  "dealer.accueil.derive": "dérivé de l'état de vos véhicules",
  // The five lines the STAGE alone can justify. Each names the car; the buyer's
  // name, the valuation and the tour's Thursday are not on this door.
  "dealer.accueil.prioriteVisite": "Visite demandée, à confirmer",
  "dealer.accueil.prioriteRapport": "Rapport d'expertise rendu, à examiner",
  "dealer.accueil.prioriteBon": "Fonds reçus : véhicule à remettre contre le bon de retrait",
  "dealer.accueil.prioriteReclamation": "Vendu hors plateforme : la commission de 10 % reste due",
  /**
   * `PRICE-GAP-DEALER`, M3: the two acts that are the dealership's alone, asked for and never inferred from a stage.
   * `{vehicule}` , the car's title and stock reference as the line's second row shows them.
   */
  "dealer.accueil.prioriteMiseADisposition": "Confirmer la mise à disposition",
  "dealer.accueil.prioriteEcartPrix": "Décision d'écart de prix",
  "dealer.accueil.ouvrirVehicule": "Ouvrir le véhicule",
  /*
   * P1-DLW-051 (the Accueil, iteration-2 frames `design/dealer-v2` `a2ede3b2`; the architect's orders of 2 November 2026): the day's work. The words of the frame (`proto/src/js/views1.js`); the dossier tasks' sentences and their buttons' labels are SERVED
   * (`dealer-dossier-words.ts`) and written nowhere here. « Ça bloque quelqu'un » and « Plus tard » are not drawn.
   */
  "dealer.accueil.choseAFaire": "chose à faire",
  "dealer.accueil.chosesAFaire": "choses à faire",
  "dealer.accueil.rienAFaire": "rien à faire",
  "dealer.accueil.ensuite": "Ensuite",
  "dealer.accueil.dossierAttend": "dossier attend votre action",
  "dealer.accueil.dossiersAttendent": "dossiers attendent votre action",
  "dealer.accueil.soldeExpertises": "pour les expertises",
  "dealer.accueil.proformaEmise": "Facture proforma émise",
  // `P1-DLW-052` (designer R, batch 1): the empty day is Hakili's card, a title and ONE true sentence; « aucune pièce à fournir » stays out until a door serves the pièce tasks.
  "dealer.hakili.repos": "Hakili, au repos",
  "dealer.hakili.signale": "Hakili, il signale",
  "dealer.hakili.termine": "Hakili, terminé",
  "dealer.pieces.hakiliSignale": "Hakili · il signale",
  "dealer.pieces.hakiliTermine": "Hakili · terminé",
  "dealer.accueil.rienAujourdhui": "Rien ne vous attend aujourd'hui",
  "dealer.accueil.rienAujourdhuiCorps": "Aucune visite à confirmer, aucun contrôle à préparer.",
  "dealer.accueil.voirMesVoitures": "Voir mes voitures",
  // `dealer.accueil.prioritesNote` is GONE: the founder's note of 7 September
  // says no footnote under the priorities box. What the box cannot yet raise is
  // recorded in `.agent/OPEN-DECISIONS.md` and in the round's report, which is
  // where a reader looks for it , not under a list a seller reads every morning.
  "dealer.accueil.enCours": "Vos véhicules en cours",
  // The drawing's second line on a car row: the buyer, or the stock check, or
  // the plain fact that nobody has asked. All three are derived.
  "dealer.accueil.controleStock": "Contrôle de stock",
  "dealer.accueil.aucunContact": "Aucun contact pour l'instant",
  "dealer.accueil.voirCatalogue": "Voir tout le catalogue",
  "dealer.accueil.aucunVehicule":
    "Aucun véhicule en cours. Mettez-en un en ligne depuis « Mes véhicules » : la mise en ligne est gratuite.",
  "dealer.accueil.sortie": "véhicule sorti du catalogue",
  "dealer.accueil.sorties": "véhicules sortis du catalogue",
  "dealer.accueil.sortiesSuite": "· ils restent lisibles dans « Mes véhicules »",
  "dealer.accueil.apporte": "Ce que Hakili vous apporte",
  // The drawing's own sentence, with its numbers DERIVED , zeros included. The
  // financed share is a `lending` fact no door carries, so that clause names
  // the module instead of a count. `R81`'s amendment: a dash where a value is
  // missing, and here the missing thing is a clause rather than a figure.
  "dealer.accueil.apporteDebut": "chez des acheteurs déjà qualifiés ce mois.",
  "dealer.accueil.apporteFinance":
    "La part financée par une banque partenaire sera comptée ici avec le module de crédit.",
  "dealer.accueil.vehiculeParti": "véhicule parti",
  "dealer.accueil.vehiculesPartis": "véhicules partis",
  "dealer.connexion.titre": "Connexion concession",
  "dealer.connexion.email": "Adresse e-mail",
  "dealer.connexion.motDePasse": "Mot de passe",
  "dealer.connexion.entrer": "Se connecter",
  // `P0-DLW-006`, `00-SPINE` 6.7: « Équipe » on dealer-web. The KEY segments are
  // English per the founder's decision of 5 September 2026; the VALUES are the
  // product's own `fr-ML`, which that decision leaves untouched (06 6.5.1).
  // `round F`, `F1`: **the mockup's own word.** The rail label is
  // « Tableau de bord » (`hakili-dealer.jsx:1047`), not « Accueil » , the
  // screen is a dashboard of what calls for a decision today, and the shorter
  // word was this app's when the landing was a welcome sentence. The KEY keeps
  // `accueil` because the SCREEN is still `accueil` and a route is not copy.
  /**
   * `F2`, the founder's reading of the pairs, 7 September 2026: **the rail's
   * labels fit on ONE line.**
   *
   * The drawing writes « Tableau de bord » and « Mes véhicules », and at the
   * rail's width both wrap onto two lines and one of them onto three. He read
   * his own drawing beside the build and chose the shorter words for the RAIL
   * , the screens keep their full titles, and « Mes véhicules » is still the
   * heading of the screen it leads to. Both texts are kept in the addendum so
   * that the change is a decision and not a drift.
   */
  "dealer.nav.accueil": "Accueil",
  // **`round F2`: « Déconnexion », one word, one line.** The founder's note of
  // 7 September, read on the pair: « Se déconnecter » wraps onto three lines in
  // an eighty-pixel rail. `dealer.session.deconnexion` keeps its longer form
  // for the Profil's « Sécurité » block, where there is room and where a
  // deliberate act deserves the fuller sentence.
  //
  // `dealer.nav.espace` is GONE with the word under the mark: the drawing
  // carries the mark alone, and the word was `NavRail`'s own invention.
  "dealer.nav.deconnexion": "Déconnexion",
  "dealer.nav.equipe": "Équipe",
  // `P0-PTA-001`, round I-A. « Profil » , where this dealership is. The PATH
  // stays French like « Équipe »'s, because a URL is product surface; the
  // screen behind it is `Profile`, in English, like every new name in this tree.
  "dealer.nav.profil": "Profil",
  "dealer.nav.vehicules": "Véhicules",
  // `round F`, `F1`: the three entries the rail was missing, bound to the
  // rights 06 §6.1.7 names , `gererVisites`, `voirSoldePrepaye`, and none for
  // the reliability note, which the matrix does not carry.
  "dealer.nav.rendezVous": "Rendez-vous",
  "dealer.nav.solde": "Argent",
  "dealer.nav.fiabilite": "Fiabilité",
  // `DEALER-PREQUAL` (round A-D1, D-1031-18): the rail entry until the Comptoir screen takes it in. « Estimer un financement » is 22 characters and the rail's ceiling is 11 (the founder's, 7 September: « Véhicules » for « Mes véhicules »), so the rail says « Estimer » and the screen says the question.
  "dealer.nav.estimation": "Estimer",
  "dealer.nav.estimationNom": "Estimer un financement",
  "dealer.nav.dossiers": "Dossiers",
  // `P1-DLW-054` (PROSPECTS, the architect's ruling of 2 November 2026): the rail's word, after « Véhicules » (R's batch 6 draws the rail in that order).
  "dealer.nav.prospects": "Prospects",
  // `round F`, `F1`: the three screens the rail now reaches, seated with their
  // FINAL titles and the mockup's own one-line summary, so `F5` replaces the
  // body and moves no furniture (`P1-UIK-025`). Every string below is the
  // mockup's, verbatim: `:2819-2820`, `:3576-3579`, `:3717-3721`.
  "dealer.rdv.titre": "Rendez-vous",
  "dealer.rdv.resume": "Visites d'acheteurs et passages d'experts",
  // `DEALER V2 B-D1`, ARGENT (frames `b3bafc2f`): the screen's own words. The frame's PROPOSITION words; the figures and the state are the doors'.
  "dealer.argent.titre": "Argent",
  "dealer.argent.resume": "Quand serai-je payé ? Chaque vente, ce que vous recevez et quand.",
  "dealer.argent.ventes": "Vos ventes en cours",
  "dealer.argent.payeurBanque": "Payé par la banque, directement sur votre compte",
  "dealer.argent.payeurAcheteur": "Payé par l'acheteur, directement sur votre compte",
  "dealer.argent.confirmer": "Confirmer la réception",
  "dealer.argent.question": "Avez-vous reçu votre part de la {voiture} ?",
  "dealer.argent.corps": "Votre part, telle que le serveur la sert : {montant}.",
  "dealer.argent.ouiConfirme": "Je confirme avoir reçu ma part",
  "dealer.argent.dateFuture": "La date de réception ne peut pas être dans le futur.",
  "dealer.solde.titre": "Solde prépayé",
  "dealer.solde.resume": "Les expertises sont prélevées sur ce solde. Rien à régler véhicule par véhicule.",
  "dealer.fiabilite.titre": "Votre note de fiabilité",
  "dealer.fiabilite.resume":
    "Calculée automatiquement sur l'ensemble de vos transactions, visible par les banques partenaires.",
  // ------------------------------------------------ `round F`, `F5`: Rendez-vous
  "dealer.rdv.acheteurs": "Visites d'acheteurs",
  "dealer.rdv.acheteursVide": "Aucune visite d'acheteur en cours.",
  // `round F5`, to the pair: the drawing's own word (`hakili-dealer.jsx:2860`).
  // « Passages d'experts » described the same thing from the dealership's
  // window; « Tournées d'expertise » is what the cabinet does , one round, all
  // the cars of one zone , which is why the section can ever hold more than one
  // line. The summary above keeps « passages d'experts », as the drawing does.
  "dealer.rdv.experts": "Tournées d'expertise",
  // `T-2c`, `M3 (6)`: WF-DLR-07 step 6's per-line confirmation built; T-2b's sentence retired.
  "dealer.rdv.dispositionConfirmee": "Mise à disposition confirmée",
  "dealer.rdv.expertsVide": "Aucune expertise programmée.",
  // `R81`: the drawing carries a day and an hour on every line. A slot is its
  // own fact and `catalog` holds none , `WF-DLR-04` and `WF-DLR-05` create
  // them, and neither has a route a concession can call. Sending a dealership
  // to its forecourt on the wrong morning is worse than sending it none.
  "dealer.rdv.enAttente":
    "Le jour et l'heure de chaque rendez-vous, et le geste pour confirmer un créneau, arriveront avec le module des visites. Cette page liste aujourd'hui les véhicules concernés, jamais une heure supposée.",
  // ----------------------------------------------------- `round F`, `F5`: Solde
  // **Vérifié contre 02 §2.4.13 et 06 `DECISION ADDED 06-25` avant d'être
  // recopié** , voir l'en-tête de `routes/balance.tsx` pour la lecture ligne à
  // ligne. C'est la copie la plus porteuse de cette surface : elle dit qu'aucune
  // facture de commission n'arrivera, que rien n'est prélevé sur ce qui est
  // versé, et que c'est la confirmation du concessionnaire qui libère le bon de
  // retrait de l'acheteur.
  "dealer.solde.commissionTitre": "Règlement en deux virements, commission de {taux} %",
  // `round T-1d`: the two figures are the dealer's OWN rate in T-1c's two layers
  // (his convention's term, else the published standard), read on the balance
  // and substituted by the screen , never a literal 10 % and 90 % again.
  "dealer.solde.commissionTexte":
    "Sur toute vente conclue via Hakili, comptant comme financement, le payeur vire directement : {part} % sur votre compte et {taux} % de commission à Hakili. La banque le fait au décaissement, l'acheteur le fait au règlement comptant. Votre argent ne transite jamais par nous, vous ne recevez aucune facture de commission et vous n'avez rien à prélever. En contrepartie, c'est vous qui confirmez la réception de votre virement : sans cette confirmation le bon de retrait n'est pas émis.",
  /* `A4`, D2 52-money (architect's note of 31 October 2026, `design/dealer-v2` `7440528f` `D2-copy.md`): the money line and the card
     « Comment le règlement se passe ». The rate is the SERVED one (T-1c's two layers), never a literal; « reverser » is banned. */
  "dealer.solde.commissionLigne":
    "Commission de Hakili : {taux} %. Hakili ne reçoit que sa commission : le payeur vous verse directement votre part, et la commission de Hakili par un virement séparé.",
  "dealer.solde.commentTitre": "Comment le règlement se passe",
  "dealer.solde.commentUn":
    "Hakili ne reçoit que sa commission : le payeur vous verse directement votre part, et la commission de Hakili par un virement séparé. Aucune déduction n'est faite sur votre part.",
  "dealer.solde.commentDeux":
    "Les deux virements n'arrivent pas à la même seconde. Ce n'est pas une anomalie.",
  "dealer.solde.commentTrois":
    "Le bon de retrait est émis par Hakili, jamais par vous. Il n'est émis qu'une fois les deux virements constatés : le vôtre, que vous confirmez ici, et celui de Hakili, que nous constatons sur notre compte. Le client récupère toujours son véhicule lui-même, sur votre parc.",
  /* `A4`, DEALER-READ-FAILURE-MONEY (architect's ruling of 30 October 2026): a failed read says what could not be read, then the door's
     own message, then « Réessayer »; a failed figure is never « 0 ». */
  "dealer.lecture.echec": "Impossible de lire {quoi} pour le moment.",
  "dealer.lecture.coordonnees": "vos coordonnées bancaires",
  "dealer.lecture.reglement": "le règlement",
  "dealer.lecture.ecart": "l'écart de prix",
  "dealer.lecture.controle": "le contrôle",
  "dealer.lecture.rapport": "le rapport",
  "dealer.lecture.alertes": "vos alertes",
  "dealer.lecture.chiffres": "vos chiffres",
  /* DEALER-READ-FAILURE-LISTS (architect's ruling of 31 October 2026): the two cards of the car's page that printed the door's raw refusal. */
  "dealer.lecture.pieces": "les pièces demandées",
  "dealer.lecture.papiers": "les papiers du véhicule",
  "dealer.lecture.estimation": "l'estimation",
  // `DEALER-PREQUAL` (round A-D1), the dealer's prequalification screen, to `design/dealer-v2` `e521637b`: « Combien le client peut-il financer ? ». Words approved by the architect (1 November 2026);
  // « creditsEnChiffres » and « hypotheses » were NEW and are APPROVED by the designer (1 November 2026).
  "dealer.estimation.titre": "Combien le client peut-il financer ?",
  "dealer.estimation.sousTitre":
    "Trois informations, une estimation. Rien n'est envoyé à une banque et aucun dossier n'est créé.",
  "dealer.estimation.formTitre": "Ce que le client vous dit",
  "dealer.estimation.salaire": "Salaire net par mois",
  "dealer.estimation.credits": "Crédits en cours, par mois",
  "dealer.estimation.creditsAide": "Laissez vide s'il n'en a aucun.",
  "dealer.estimation.unite": "FCFA",
  "dealer.estimation.duree": "Durée",
  "dealer.estimation.apport": "Apport",
  "dealer.estimation.a": "à",
  "dealer.estimation.mois": "{n} mois",
  "dealer.estimation.pourcent": "{n} %",
  "dealer.estimation.estimer": "Estimer",
  "dealer.estimation.aucunDocument": "Aucun document n'est demandé à ce stade.",
  "dealer.estimation.manqueSalaire": "Il manque : le salaire net.",
  // Approved by the designer: commitments written in words are not dropped in silence.
  "dealer.estimation.creditsEnChiffres": "Écrivez les crédits en cours en chiffres, par exemple 60 000.",
  "dealer.estimation.attenteTitre": "L'estimation apparaît ici",
  "dealer.estimation.attenteTexte": "Entrez le salaire net, choisissez la durée et l'apport.",
  "dealer.estimation.chip": "ESTIMATION",
  "dealer.estimation.heroTitre": "Il pourrait financer",
  "dealer.estimation.montantAlt": "de {min} à {max} francs CFA",
  // Designer R's change 2 (batch 9): the figure lives in the « Mensualité estimée » line only; the sentence says where it sits.
  "dealer.estimation.surSalaire": "Sur un salaire net de {salaire} : voici où se situe la mensualité.",
  "dealer.estimation.barreAlt":
    "Répartition du salaire : crédits en cours, mensualité estimée, marge jusqu'au maximum",
  "dealer.estimation.barreNote":
    "Le trait marque la part du salaire que les banques acceptent au plus pour cette estimation.",
  "dealer.estimation.legCredits": "Crédits en cours",
  "dealer.estimation.legMensualite": "Mensualité estimée",
  "dealer.estimation.legMarge": "Marge jusqu'au maximum",
  "dealer.estimation.kvMensualite": "Mensualité estimée",
  "dealer.estimation.kvDuree": "Durée",
  "dealer.estimation.kvApport": "Apport supposé",
  // `DEALER-PREQUAL` bind: the record's own name for the figure the door now serves per car (« resteAVivre », 02 §2.6.3); the instalment reuses « Mensualité estimée ».
  "dealer.estimation.resteAVivre": "Reste à vivre",
  // Approved by the designer: the door serves its assumption lines (rate, fees not financed...).
  "dealer.estimation.hypotheses": "Hypothèses de cette estimation",
  "dealer.estimation.voituresTitre": "Vos voitures dans cette fourchette",
  "dealer.estimation.voirFiche": "Voir la fiche : {titre}",
  "dealer.estimation.dossierAssiste": "Dossier assisté",
  "dealer.estimation.dossierAssisteRaison": "Pas encore disponible.",
  "dealer.estimation.parcVide": "Votre parc est vide",
  "dealer.estimation.parcVideTexte": "Publiez une voiture pour la voir ici.",
  "dealer.estimation.aucuneVoiture": "Aucune voiture de votre parc dans cette fourchette",
  "dealer.estimation.aucuneVoitureTexte":
    "Une voiture moins chère, un apport plus élevé ou une durée plus longue en ferait entrer.",
  "dealer.estimation.sansCapaciteTitre": "Pas de capacité d'emprunt pour le moment",
  "dealer.estimation.sansCapaciteTexte":
    "Selon ce que le client déclare, ses crédits en cours prennent déjà toute la part de son salaire que les banques acceptent. Aucune fourchette ne peut être donnée.",
  "dealer.estimation.changeraitTitre": "Ce qui changerait la réponse",
  "dealer.estimation.changerait1": "la fin d'un crédit en cours",
  "dealer.estimation.changerait2": "une voiture moins chère, avec un apport plus élevé",
  "dealer.estimation.echecPhrase": "Le service est indisponible. Rien n'a été modifié.",
  /* P1-DLW-048 (G-E1): the dossier list's failed read. */
  "dealer.lecture.dossiers": "vos dossiers",
  "dealer.lecture.dossier": "ce dossier",
  /*
   * P1-DLW-048 (G-E1, DEALER V2 B5-a): the dealership's dossier LIST. The words of D2's frame 19 (`design/dealer-v2` `0880ca2b`, `proto/src/js/views2.js`); the status, the waiting phrase, the next text and the action's label are SERVED
   * (`dealer-dossier-words.ts`) and written nowhere in the browser.
   */
  "dealer.dossiers.titre": "Dossiers",
  "dealer.dossiers.sousTitre": "{enCours} en cours",
  "dealer.dossiers.sousTitreClos": "{enCours} en cours · {clos} clos",
  "dealer.dossiers.tousLibelle": "Tous",
  "dealer.dossiers.tousSous": "y compris clos",
  "dealer.dossiers.chezVousLibelle": "Chez vous",
  "dealer.dossiers.chezVousSous": "ce qui vous attend",
  "dealer.dossiers.chezClientLibelle": "Chez le client",
  "dealer.dossiers.chezClientSous": "il doit répondre",
  "dealer.dossiers.chezBanqueLibelle": "Chez la banque",
  "dealer.dossiers.chezBanqueSous": "en étude",
  "dealer.dossiers.chezHakiliLibelle": "Chez Hakili",
  "dealer.dossiers.chezHakiliSous": "en vérification",
  "dealer.dossiers.rechercheEtiquette": "Chercher un dossier",
  "dealer.dossiers.recherche": "Chercher un client ou une voiture",
  "dealer.dossiers.canal": "Canal",
  "dealer.dossiers.canalClients": "Demandes des clients",
  "dealer.dossiers.canalConcession": "Déposés par la concession",
  "dealer.dossiers.affichage": "Affichage",
  "dealer.dossiers.vueListe": "Liste",
  "dealer.dossiers.vuePipeline": "Pipeline",
  "dealer.dossiers.etape": "Étape {n} sur {total} : {nom}",
  "dealer.dossiers.depuisAujourdhui": "depuis aujourd'hui",
  "dealer.dossiers.depuis": "depuis {duree}",
  "dealer.dossiers.jour": "jour",
  "dealer.dossiers.jours": "jours",
  "dealer.dossiers.vide": "Aucun dossier pour l'instant",
  "dealer.dossiers.videCorps": "Les demandes de vos clients sur la marketplace apparaîtront ici.",
  "dealer.dossiers.aucunResultat": "Aucun dossier ne correspond",
  "dealer.dossiers.aucunResultatCorps": "Changez le filtre ou la recherche.",
  "dealer.dossiers.colonneDepot": "Pièces et dépôt",
  "dealer.dossiers.colonneEtude": "À l'étude",
  "dealer.dossiers.colonneOffre": "Offre",
  "dealer.dossiers.colonneSignature": "Signature",
  "dealer.dossiers.colonneVersement": "Versement",
  "dealer.dossiers.colonneVide": "Aucun dossier",
  /**
   * `P1-DLW-054` (PROSPECTS, R's batch 6, frames 36 to 38 at `98e70c63`): the words of the screen. The titles are the drawing's; the two empty states and the whole-screen empty state are PROPOSED (the drawing's own says "pose une question, demande une visite", which this build does not serve: questions are after the pilot, visits and views wait for their doors).
   * A column with no door yet says what will fill it and draws no number: a count the server did not serve is never drawn, and never « 0 ».
   */
  "dealer.prospects.titre": "Prospects",
  "dealer.prospects.sousTitre":
    "Ce que font les clients sur vos voitures publiées, de l'enregistrement à la demande de financement.",
  "dealer.prospects.affichage": "Affichage",
  "dealer.prospects.vueListe": "Liste",
  "dealer.prospects.vueColonnes": "Colonnes",
  "dealer.prospects.voituresEnregistrees": "Voitures enregistrées",
  "dealer.prospects.colonneVisite": "Visite demandée",
  "dealer.prospects.colonneFinancement": "Demande de financement",
  "dealer.prospects.puceFinancement": "Demande de financement",
  "dealer.prospects.colonneVide": "Personne pour l'instant",
  "dealer.prospects.regardVide":
    "Les enregistrements de vos voitures publiées apparaîtront ici, voiture par voiture, sans aucun nom.",
  "dealer.prospects.enregistrement": "enregistrement",
  "dealer.prospects.enregistrements": "enregistrements",
  "dealer.prospects.consultationsNote": "Les consultations ne sont pas comptées.",
  "dealer.prospects.visiteVide": "Les demandes de visite de vos clients apparaîtront ici.",
  "dealer.prospects.vide": "Aucun prospect pour l'instant",
  "dealer.prospects.videCorps":
    "Quand un client demande un financement sur l'une de vos voitures, vous le voyez ici.",
  "dealer.prospects.anonymeNote": "des comptes seulement : aucun nom tant que le client n'a pas contacté",
  "dealer.prospects.appeler": "Appeler",
  "dealer.prospects.appelerNom": "Appeler {nom}",
  "dealer.prospects.personne": "personne",
  "dealer.prospects.personnes": "personnes",
  "dealer.dossiers.recuTitre": "À quelle date avez-vous reçu votre part ?",
  "dealer.dossiers.recuConfirmer": "Confirmer",
  /**
   * `P1-DLW-052` (B5-b): the dealer's dossier detail. The labels of the offer's four terms and of « Ce que vous recevrez » are SERVED (`offer.terms.labels`, `receipt.labels`) and written nowhere here; what stands here are the page's own words around them. « Quand » says the served status and the day the dealer stated.
   */
  "dealer.dossier.appeler": "Appeler",
  "dealer.dossier.whatsapp": "WhatsApp",
  "dealer.dossier.parcours": "Parcours du dossier",
  "dealer.dossier.offreTitre": "L'offre de la banque",
  "dealer.dossier.dureeMois": "{n} mois",
  "dealer.dossier.recuTitre": "Ce que vous recevrez",
  "dealer.dossier.payeurBanque": "la banque, directement",
  "dealer.dossier.payeurAcheteur": "l'acheteur, directement",
  "dealer.dossier.quandRecu": "reçu le {date}",
  "dealer.dossier.accesTitre": "Le journal",
  "dealer.dossier.accesExplication":
    "Les lignes avec un cadenas ne sont visibles que par l'administrateur de la concession et par Hakili, jamais par le client.",
  "dealer.dossier.accesVide": "Aucun accès enregistré pour ce dossier.",
  "dealer.solde.disponible": "Solde disponible",
  "dealer.solde.mouvements": "Mouvements",
  "dealer.solde.mouvementsAbsents":
    "Vos recharges et les avances de contrôle prélevées apparaîtront ici, chacune datée et rattachée à son véhicule.",
  // ------------------------------------------- `round T-1d`: the ledger's screen
  // `R81`: the advances to come are a figure no door carries until the mise à
  // disposition stands (`P2-PAY-009`) , the line says so, never a zero.
  "dealer.solde.avancesAVenir": "{montant} d'avances de contrôle à venir sur vos véhicules",
  // `T-2c`, `M2 (5)`: WF-CON-38 signal 7 on the dealer's own screen , « Demander une recharge ».
  "dealer.solde.bas": "Solde prépayé bas : sous le seuil de {seuil}. Demandez une recharge.",
  // `T-2b`, `M1 (d)`: the line is derived from the scheduled controls awaiting the dealer's mise à
  // disposition; none scheduled, a sentence and never a zero (R81).
  "dealer.solde.avancesAVenirAbsentes":
    "Aucune avance de contrôle à venir : aucun contrôle programmé n'attend votre mise à disposition.",
  "dealer.solde.dernierMouvement": "Dernier mouvement le {date}",
  "dealer.solde.recharger": "Recharger mon solde",
  "dealer.solde.rechargeMontant": "Montant de la recharge",
  "dealer.solde.rechargeAutreMontant": "Autre montant (francs)",
  "dealer.solde.rechargeManque": "Manque pour couvrir les avances à venir : {montant}",
  // WF-DLR-13 step 3 as amended by record (T-1d's addendum): the dealer DECLARES
  // the transfer he made to Hakili's account; Hakili confirms it on evidence.
  "dealer.solde.rechargeExplication":
    "Virez le montant sur le compte de Hakili, puis déclarez votre virement ici. Votre solde est crédité une fois le virement confirmé par Hakili sur preuve ; votre argent ne transite pas par la plateforme.",
  "dealer.solde.rechargeCompte": "Compte de Hakili",
  "dealer.solde.rechargeCompteAbsent":
    "Les coordonnées bancaires de Hakili ne sont pas encore publiées. Rapprochez-vous de votre chargé de relation avant de virer.",
  "dealer.solde.rechargeReference": "Référence de votre virement",
  "dealer.solde.rechargeReferenceAide":
    "Telle qu'elle figure sur votre avis de virement : Hakili la retrouve sur son relevé.",
  "dealer.solde.rechargeDeclarer": "Déclarer ma recharge de {montant}",
  "dealer.solde.rechargeDeclaree":
    "Recharge de {montant} déclarée. Hakili la confirme sur preuve de réception.",
  "dealer.solde.enAttenteTitre": "Recharges déclarées",
  "dealer.solde.enAttenteLigne": "Recharge de {montant}, virement {reference}",
  "dealer.solde.enAttenteDeclaree": "déclarée le {date}",
  "dealer.solde.enAttenteEtat": "en attente de confirmation par Hakili",
  // ------------------------------------------------- `round F`, `F5`: Fiabilité
  "dealer.fiabilite.note": "Votre note",
  "dealer.fiabilite.noteAbsente":
    "Votre note est tenue par Hakili sur l'ensemble de vos transactions et arrive avec le module partenaires. Aucune lettre n'est affichée tant qu'elle n'est pas lue : une note inventée ici contredirait celle que vos banques partenaires voient.",
  "dealer.fiabilite.controles": "Véhicules expertisés",
  "dealer.fiabilite.controlesSuite": "un rapport de contrôle existe",
  "dealer.fiabilite.nonConformes": "Contrôles non conformes",
  "dealer.fiabilite.nonConformesSuite": "vente bloquée sur la plateforme",
  "dealer.fiabilite.enAttente":
    "Partenaire depuis, dossiers financés, volume, délai moyen de mise à disposition, litiges et écart moyen entre vos prix et les valeurs expertisées sont tenus par Hakili et arrivent avec le module partenaires. L'écart de prix attend en plus la valeur portée par chaque rapport, que cette page ne lit pas encore.",
  // What a screen that is present but unbuilt says, once, for all three.
  "dealer.ecran.aVenir":
    "Cet écran arrive dans ce même tour. Il est déjà à sa place pour que rien ne bouge quand il se remplit.",
  "dealer.nav.idee": "Une idée ?",
  "dealer.nav.notifications": "Notifications",
  // `DEALER-NOTIFICATIONS` (batch 5, frame 100, approved by the architect on 3 November 2026): the panel when nothing is in it; the face's slot is lane F's (#915).
  "dealer.nav.notificationsVideTitre": "Rien de nouveau",
  "dealer.nav.notificationsVideTexte":
    "Les demandes de vos clients, les réponses des banques et les messages de Hakili apparaîtront ici.",
  // R's review of 3 November 2026 (review-934.md `44b23b76`): frame 100's two group titles (the served unread count fills {n}), the exit, and the one act a door serves.
  "dealer.nav.notificationsNouveau": "Nouveau · {n}",
  "dealer.nav.notificationsPlusTot": "Plus tôt",
  "dealer.nav.notificationsFermer": "Fermer",
  "dealer.nav.notificationsToutLu": "Tout marquer comme lu",
  // RATIFIED (A-H ruling of 3 November 2026; R asked for an accessible name for the unread state and drew none): said to a screen reader before the title of an unread row.
  "dealer.nav.notificationNonLue": "Non lue",
  /** `PRICE-GAP-DEALER`, M3: the dot's accessible name , something on this entry waits for the person's hand. */
  "dealer.nav.aTraiter": "Des actions vous attendent",
  "dealer.nav.notificationsRaison":
    "Les notifications arrivent avec leur module. Aucune pastille n'est affichée tant qu'aucune notification n'existe.",
  // `P0-CAT-023` / `P0-DLW-010`, round I-A. The inventory, its filters and the
  // add panel. The mockup's own words where it has them.
  "dealer.vehicules.titre": "Mes véhicules",
  "dealer.vehicules.intro":
    "La mise en ligne est gratuite. Aucune vente ne se conclut sans expertise indépendante.",
  "dealer.vehicules.ajouter": "Ajouter un véhicule",
  // **`round F`, `F3`: the card list of the 6 September addendum.**
  "dealer.vehicules.rechercher": "Rechercher un véhicule",
  // The placeholder names ONLY what this door can match. The drawing offers
  // marque, référence, immatriculation and acheteur; `me/vehicles` carries no
  // plate and no buyer name, and a box promising a plate that never matches one
  // teaches a seller that the search is broken.
  // The drawing's placeholder, verbatim (`hakili-dealer.jsx:1599`). It names
  // what is actually searched, which is why it may not be shortened: a box that
  // promises an immatriculation and never matches one teaches a seller that the
  // search is broken.
  /**
   * **The placeholder names what is MATCHED, and « acheteur » is not.**
   *
   * The drawing writes « Marque, référence, immatriculation, acheteur ». Three
   * of those four the list door carries and the box searches; the fourth is a
   * buyer's NAME, which no door puts on a car , the row carries only `hasBuyer`
   * (06 M3: a dealer is told THAT, not WHO). A box that promises a buyer and never matches
   * one teaches a seller that the search is broken, which is the exact defect
   * `R81` refuses. It rejoins the day a buyer's name exists on a car.
   */
  "dealer.vehicules.rechercherIndice": "Marque, référence, immatriculation",
  "dealer.vehicules.vider": "Effacer",
  "dealer.vehicules.aucuneCorrespondance": "Aucun véhicule ne correspond à cette recherche.",
  "dealer.vehicules.jour": "jour",
  // `F3`: one sentence per step of 06 §6.3.3's three calls. The founder read
  // « Failed to fetch » for a car that had already been created, and could not
  // tell from it which of the three had failed or what had become of the car.
  // `F3`, `W2`: the failed view, and the two ways out of it.
  "dealer.vehicules.photoEchouee": "Cette photo n'est pas passée :",
  "dealer.vehicules.reessayer": "Réessayer",
  "dealer.vehicules.abandonnerVue": "Continuer sans cette photo",
  "dealer.vehicules.demandeesTitre": "Pièces demandées",
  "dealer.vehicules.demandeesDossier": "Dossier",
  "dealer.vehicules.demandeesManquante": "Manquante",
  "dealer.vehicules.demandeesExpiree": "Expirée : une nouvelle est demandée",
  "dealer.vehicules.demandeesRefusee": "Refusée par Hakili",
  "dealer.vehicules.demandeesDansPapiers": "À déposer dans « Papiers du véhicule » ci-dessus",
  "dealer.vehicules.demandeesEmettre": "Émettre la facture proforma",
  "dealer.vehicules.demandeesEmission": "Émission…",
  "dealer.vehicules.demandeesDeposerSecours": "Ou déposer une facture déjà établie",
  "dealer.vehicules.emiseTitre": "Factures proforma émises",
  "dealer.vehicules.emiseLigne":
    "n° {numero}, version {version} · valable jusqu'au {date} · {prix} TTC (HT {ht}, TVA {tva})",
  "dealer.vehicules.emiseRemplacee": "Remplacée",
  "dealer.vehicules.emiseOuvrir": "Ouvrir",
  "dealer.vehicules.emiseNouvelleVersion": "Le prix a changé : émettre une nouvelle version",
  "dealer.vehicules.demandeesDeposer": "Déposer la facture proforma",
  "dealer.vehicules.papiersTitre": "Papiers du véhicule",
  "dealer.vehicules.papiersAide":
    "La carte grise et la visite technique appartiennent au véhicule : vous les déposez une seule fois, elles servent pour chaque dossier sur ce véhicule.",
  "dealer.vehicules.papiersNonImmatricule":
    "Ce véhicule est déclaré non immatriculé : aucune carte grise n'est demandée.",
  "dealer.vehicules.papierCarteGrise": "Carte grise",
  "dealer.vehicules.papierVisite": "Visite technique",
  "dealer.vehicules.papierManquant": "Manquante",
  "dealer.vehicules.papierEnRegle": "Reçue",
  "dealer.vehicules.papierExpire": "Expirée le",
  "dealer.vehicules.papierValableJusquau": "Valable jusqu'au",
  "dealer.vehicules.papierVoir": "Voir le fichier",
  "dealer.vehicules.papierAjouter": "Ajouter",
  "dealer.vehicules.papierRemplacer": "Remplacer",
  "dealer.vehicules.papierEnvoi": "Envoi…",
  "dealer.vehicules.papierDateFin": "Date de fin de validité de la visite technique",
  "dealer.vehicules.papiersDateRequise":
    "Indiquez d'abord la date de fin de validité de la visite technique.",
  "dealer.vehicules.papiersTicketEchoue":
    "La préparation de l'envoi de ce document a échoué. Réessayez : rien n'est perdu.",
  "dealer.vehicules.papiersEnvoiEchoue": "L'envoi de ce document a échoué",
  "dealer.vehicules.papiersConfirmEchoue":
    "Le document est parti mais n'a pas pu être enregistré. Réessayez.",
  "dealer.vehicules.chassisLabel": "Numéro de châssis",
  "dealer.vehicules.chassisEnregistrer": "Enregistrer",
  "dealer.vehicules.chassisEchoue": "Le numéro de châssis n'a pas pu être enregistré.",
  "dealer.vehicules.photoTicketEchoue":
    "Le véhicule est créé, mais la préparation de l'envoi de cette photo a échoué. Réessayez depuis sa page : rien n'est perdu.",
  "dealer.vehicules.photoStoreRefus":
    "Le véhicule est créé, mais votre navigateur n'a pas pu déposer cette photo. Réessayez depuis sa page ; si cela recommence, la console du navigateur en donne la raison exacte.",
  "dealer.vehicules.photoConfirmEchoue":
    "La photo est déposée mais n'a pas pu être rattachée au véhicule. Réessayez depuis sa page : le véhicule est créé et rien n'est perdu.",
  "dealer.vehicules.photoEnvoiEchoue":
    "L'envoi de cette photo a échoué. Réessayez : le véhicule n'est pas encore en ligne, rien n'est perdu.",
  // `F2.3`: the state the drawing never draws, because a car was born
  // published until this round. Flagged beside the mockup, the founder may
  // overrule.
  // `F2.3`: the gallery, in the drawing's own words (`hakili-dealer.jsx:1670`).
  "dealer.vehicules.sur": "sur",
  "dealer.vehicules.retirerPhoto": "Retirer",
  // PROPOSITION (R's last review of the car page's tab strip): « Retirer » on a gallery photograph asks first and says the consequence.
  // PROPOSITION (R's review of the tab strip): the question names the photograph, by the view caption the door served; a photograph stored without a view keeps the plain question. `{legende}` is replaced by the screen.
  "dealer.vehicules.retirerPhotoTitre": "Retirer la photo « {legende} » ?",
  "dealer.vehicules.retirerPhotoTitreSansNom": "Retirer cette photo ?",
  "dealer.vehicules.retirerPhotoNom": "Retirer la photo {legende}",
  "dealer.vehicules.retirerPhotoCorps": "Elle ne sera plus visible sur l'annonce.",
  "dealer.vehicules.galerieVide":
    "Aucune photo pour l'instant. Un acheteur d'une autre ville n'a que cela pour juger.",
  "dealer.vehicules.vuesManquantes": "Vues conseillées encore absentes :",
  "dealer.vehicules.vuesManquantesRaison":
    "Elles ne bloquent rien, mais un acheteur d'une autre ville juge sur ces photos.",
  // `F2.3`: the add panel's photographs, in the drawing's own words
  // (`hakili-dealer.jsx:3873-3910`).
  "dealer.vehicules.photosTitre": "Photos du véhicule",
  "dealer.vehicules.aucunePhoto": "aucune photo",
  // The drawing's own counter reads « 3 photos ajoutées » and « aucune photo »
  // (`hakili-dealer.jsx:1795`). The agreement is the noun's AND the participle's.
  "dealer.vehicules.photoChoisie": "photo ajoutée",
  "dealer.vehicules.photosChoisies": "photos ajoutées",
  "dealer.vehicules.retirerAutresPhotos": "Retirer les autres photos",
  "dealer.vehicules.photosRaison":
    "La première photo ajoutée fait la vignette de l'annonce. Une seule suffit pour publier ; les huit vues sont conseillées parce qu'un acheteur d'une autre ville n'a que cela pour juger.",
  "dealer.vehicules.autresPhotos": "Autres photos",
  // `F2.3` drew these two greyed, each with the reason `I19` demands. `round
  // F4` built their doors, so the buttons act and the two reasons are deleted
  // with them: a gesture that works needs no explanation of why it does not.
  "dealer.vehicules.modifierPrix": "Modifier le prix",
  "dealer.vehicules.retirerDuCatalogue": "Retirer du catalogue",
  // `round F4`, `WF-DLR-04`: the guard's own sentence, and the note that says
  // what a price decides. The forfait follows the grid, so it follows this.
  "dealer.vehicules.enregistrerPrix": "Enregistrer le prix",
  // G-D1 (`B3-PRICING-1`): the served opening price of the open dossier. The FIRST sentence is APPROVED (architect's ruling of 1 November 2026). The second, `prixPlafondMarge`, is BUILT and NOT SERVED before
  // `B3-PRICING-2`: until then the price is locked while a dossier is open, so it would promise a move the door refuses.
  "dealer.vehicules.prixPlafond": "Prix à l'ouverture du dossier {reference} : {montant}.",
  // G-D1 (`LOCK-STATE`, `P0-CAT-044`), PROPOSITION (the frames' own word, awaiting the architect's ruling): the chip drawn up front while a dossier stands on the car and the price cannot change.
  "dealer.vehicules.prixVerrouille": "Prix verrouillé",
  // `G-D4` (`P1-DLW-055`): the car page's tab strip, the frames' words (R's batches 4 and 7, `design/dealer-v2-r` `98e70c63`).
  "dealer.vehicules.ongletsLabel": "Sections de la voiture",
  "dealer.vehicules.ongletVente": "Vente",
  "dealer.vehicules.ongletDossiers": "Dossiers",
  "dealer.vehicules.ongletExpertise": "Expertise",
  "dealer.vehicules.ongletPhotos": "Photos",
  "dealer.vehicules.ongletPapiers": "Papiers",
  "dealer.vehicules.ongletJournal": "Journal",
  "dealer.vehicules.prixPlafondMarge": "Vous pouvez baisser votre prix, ou le remonter jusqu'à ce montant.",
  // Round `PAY-P`, `OD-29`: ONE forfait for every car; the price decides nothing.
  "dealer.vehicules.prixNoteForfait":
    "Le forfait de contrôle est un montant unique fixé par Hakili : il ne dépend pas de ce prix.",
  "dealer.vehicules.modifier": "Modifier",
  // The drawing's own band, and it carries the AMOUNT of the fall, not a word:
  // « moins cher » is a claim, « Prix baissé de 500 000 FCFA » is a fact.
  "dealer.vehicules.prixBaisse": "Prix baissé de",
  // `round F4`: the relist. The note is not decoration , it says that the car
  // goes back on sale at once, so nobody presses it expecting a draft.
  "dealer.vehicules.remettreEnLigne": "Remettre en ligne",
  "dealer.vehicules.remettreEnLigneNote":
    "Le véhicule revient à son étape précédente et redevient visible des acheteurs immédiatement.",
  // `round F4`: the lists the founder asked for, and their escape.
  "dealer.vehicules.autre": "Autre",
  "dealer.vehicules.autrePrecisez": "Précisez",
  "dealer.vehicules.marqueChoisir": "Choisir une marque",
  "dealer.vehicules.modeleChoisir": "Choisir un modèle",
  "dealer.vehicules.boiteChoisir": "Choisir une boîte",
  "dealer.vehicules.carburantChoisir": "Choisir un carburant",
  // The plate's own qualifier. The founder's decision of 7 September: in Mali
  // some dealerships sell cars registered and some do not.
  "dealer.vehicules.facultatif": "facultatif",
  "dealer.vehicules.referenceFigee":
    "Figée : le véhicule est publié et cette référence figure déjà sur l'annonce.",
  "dealer.vehicules.nonPublie": "Non publié",
  "dealer.vehicules.publier": "Mettre en ligne",
  // `C-D1` (iteration-2 frames, `design/dealer-v2` `bdd2722b`): the tiles, the two views and the photo chip, in the frames' words.
  "dealer.vehicules.tuileToutes": "Toutes",
  "dealer.vehicules.tuileToutesSous": "tout le stock",
  "dealer.vehicules.tuilePrivees": "Privées",
  "dealer.vehicules.tuilePriveesSous": "pas encore montrées",
  "dealer.vehicules.vueCartes": "Cartes",
  "dealer.vehicules.vueTableau": "Tableau",
  "dealer.vehicules.vueLabel": "Présentation de la liste",
  "dealer.vehicules.photoRequise": "Photo requise",
  "dealer.vehicules.compteUn": "véhicule",
  "dealer.vehicules.comptePlusieurs": "véhicules",
  "dealer.vehicules.nonPublieRaison": "Ce véhicule n'est pas encore visible des acheteurs. Une photo suffit.",
  "dealer.vehicules.sansRapport": "Sans rapport",
  "dealer.vehicules.avecRapport": "Rapport rendu",
  // `PRICE-GAP-DEALER`, M4 (V-005): what report stands on the car, as the grid reads it.
  "dealer.vehicules.rapportConforme": "Rapport conforme",
  "dealer.vehicules.rapportEcartPrix": "Écart de prix",
  "dealer.vehicules.rapportNonConforme": "Non conforme",
  /** DEALER V2 `A2` (D2 `proto` `ffd00b0d`, `views1.js` `viewCars`): the table's served columns, and the two states the drawing adds. */
  "dealer.vehicules.colVehicule": "Véhicule",
  "dealer.vehicules.colPrix": "Prix",
  "dealer.vehicules.colEtat": "État",
  "dealer.vehicules.colRapport": "Rapport",
  "dealer.vehicules.colAction": "Action",
  "dealer.vehicules.enStockDepuis": "en stock depuis",
  "dealer.vehicules.videTitre": "Aucun véhicule pour l'instant",
  "dealer.vehicules.videCorps": "Ajoutez votre première voiture : une photo suffit.",
  "dealer.vehicules.videCorps2":
    "Vous choisissez ensuite si elle est montrée aux clients ou gardée en inventaire privé.",
  "dealer.vehicules.lectureEchecTitre": "La liste de vos véhicules n'a pas pu être lue.",
  "dealer.vehicules.lectureEchecCorps":
    "Rien n'a été modifié. Vos véhicules sont toujours là ; les comptes sont masqués plutôt que faux.",
  "dealer.vehicules.voletVide": "Choisissez un véhicule dans la liste pour l'ouvrir ici.",
  // `R81`, once under the list. The addendum's card also carries a photo count,
  // les initiales de la personne qui suit la voiture, une bande de baisse de
  // prix et le geste du jour ; aucun de ces quatre faits n'existe encore.
  "dealer.vehicules.enAttente":
    "Les photos, la personne qui suit chaque véhicule, depuis combien de jours l'annonce est en ligne et les gestes du jour arriveront sur ces cartes avec leurs modules. Rien n'est affiché tant que rien n'est lu.",
  "dealer.vehicules.aucun": "Aucun véhicule en ligne pour l'instant.",
  "dealer.vehicules.filtreActifs": "Actifs",
  "dealer.vehicules.filtreVendus": "Vendus",
  "dealer.vehicules.filtreSortis": "Sortis du parcours",
  "dealer.vehicules.filtreTous": "Tous",
  // `F2.3`: the drawing's own four, in the drawing's own words
  // (`hakili-dealer.jsx:1282-1287`). « Prix » was one control doing one thing;
  // the drawing offers the price BOTH ways, and a dealer looking for what is
  // not selling wants the cheap end as often as the dear one.
  "dealer.vehicules.trierPar": "Trier",
  "dealer.vehicules.triRecent": "Plus récents",
  "dealer.vehicules.triPrixAsc": "Prix croissant",
  "dealer.vehicules.triPrixDesc": "Prix décroissant",
  "dealer.vehicules.triKilometrage": "Kilométrage",
  "dealer.vehicules.triEtape": "Étape",
  /**
   * `F2.3`: **two sentences, not one.**
   *
   * The card counts DAYS , « en ligne depuis 11 jours » (`hakili-dealer.jsx`
   * `:1410`) , and the fiche names a DATE , « en ligne depuis le 9 août »
   * (`:2534`). One key served both and put a « le » in front of a number, so
   * the pair read « en ligne depuis le 0 jour ». The drawing had it right in
   * both places; the front had copied one of them over the other.
   */
  "dealer.vehicules.enLigneDepuis": "en ligne depuis",
  /* `A2`, the designer's review: beside the « En ligne » chip the age is written alone. */
  "dealer.vehicules.depuis": "depuis",
  "dealer.vehicules.enLigneDepuisLe": "en ligne depuis le",
  "dealer.vehicules.aucunContact": "Aucun contact",
  "dealer.vehicules.acheteur": "Acheteur en cours",
  "dealer.vehicules.expertiseNonDemandee": "Non demandée",
  "dealer.vehicules.expertiseFaite": "Rapport disponible",
  // `round F6`, `OD-15`: the drawing's own block on the fiche
  // (`hakili-dealer.jsx:2577-2600`), and the card's pastille beside it.
  "dealer.vehicules.suiviPar": "Suivi par",
  // The drawing's own words for a car that follows nobody , every car listed
  // before the column existed. A fact, said plainly, and never a defect.
  "dealer.vehicules.suiviPersonne": "personne pour l'instant",
  "dealer.vehicules.suiviChanger": "Changer",
  // `OD-24` closed: the drawing's fifth control of the order-by row.
  "dealer.vehicules.suiviTous": "Toute la concession",
  "dealer.vehicules.suiviEquipeVide": "Personne d'autre n'est actif dans votre équipe.",
  "dealer.vehicules.progression": "Progression",
  // The founder's own label on the drawing (`hakili-dealer.jsx:1890`), with the
  // qualifier he wrote beside it. It is the whole rule in six words, and the
  // rule is what makes the field safe to put above the fields that DO block.
  "dealer.vehicules.equipements": "Équipements",
  "dealer.vehicules.equipementsFacultatif": "facultatif, ne bloque jamais la mise en ligne",
  "dealer.vehicules.journal": "Journal",
  // Round T-2 (WF-DLR-11): the settlement card with two rows, the dealer's own declaration, the handover.
  "dealer.reglement.titre": "Règlement de la vente",
  // Round T-2b (WF-DLR-07, P2-DLW-008): the dealer's step at the control , the checklist, the advance, the confirmation.
  "dealer.controle.titre": "Contrôle indépendant",
  "dealer.controle.sous": "Mise à disposition et avance",
  "dealer.controle.enAttenteProgrammation": "Contrôle en attente de programmation par Hakili",
  "dealer.controle.programmeLe": "Contrôle programmé le {jour}, {cabinet}",
  "dealer.controle.avanceDue": "Avance de contrôle à régler ({montant})",
  "dealer.controle.avanceDueReduite":
    "Avance de contrôle réduite, rapport valide sur ce véhicule ({montant})",
  "dealer.controle.avanceReglee": "Avance de contrôle réglée",
  // `D5`, 28 September 2026: a buyer's control is no longer advanced by the dealer , the buyer
  // pays the whole published fee up front, refunded in full on a failure and recovered from the
  // dealer only then. Round S4-7 (WF-DLR-07's balance guard retired for a buyer's control).
  "dealer.controle.avancePayeeParAcheteur":
    "Frais de contrôle réglés par l'acheteur ; à votre charge seulement si le contrôle échoue",
  "dealer.controle.avanceNonPubliee":
    "Tarif du contrôle non publié par Hakili : l'avance ne peut pas être calculée",
  "dealer.controle.miseADispositionAttendue": "Véhicule à mettre à disposition du cabinet",
  "dealer.controle.miseADispositionLe": "Mise à disposition confirmée le {date}",
  "dealer.controle.rapportAttendu": "Rapport du cabinet attendu",
  "dealer.controle.rapportRecu": "Rapport du cabinet reçu",
  // Lane E, M2: the report standing on the car, opened by its seller (the buyer is nowhere on it).
  "dealer.rapport.titre": "Rapport de ce véhicule",
  "dealer.rapport.cabinet": "Établi par le cabinet {cabinet}, version {version}",
  "dealer.rapport.valable": "Valable jusqu'au {date}",
  "dealer.rapport.papierOuvrir": "Ouvrir le document du cabinet",
  "dealer.rapport.lectureOuvrir": "Ouvrir la lecture Hakili",
  "dealer.rapport.papierAbsent": "Aucun document du cabinet n'est joint à ce rapport.",
  "dealer.rapport.lectureAbsente":
    "La lecture Hakili de ce rapport n'est pas encore disponible ; l'équipe Hakili peut la régénérer.",
  "dealer.controle.confirmer": "Confirmer la mise à disposition",
  "dealer.controle.recharger": "Recharger mon solde",
  "dealer.controle.attenteCabinet":
    "En attente du passage du cabinet et de son rapport. Le verdict sera calculé par la grille de conformité de Hakili, choisie ni par le cabinet ni par vous.",
  // `PRICE-GAP-DEALER`, M4 (V-005): the report is here , the panel stops saying it waits for it.
  "dealer.controle.verdictCalcule":
    "Le rapport du cabinet est reçu. Le verdict est calculé par la grille de conformité de Hakili, choisie ni par le cabinet ni par vous.",
  "dealer.controle.couvert":
    "Un rapport en cours de validité couvre déjà ce dossier : le cabinet n'a pas à repasser et aucune mise à disposition n'est à confirmer.",
  // `PRICE-GAP-DEALER`, M1 (WF-DLR-08; the founder's ruling of 16 October 2026): the seller answers a price gap on the platform.
  "dealer.ecart.titre": "Décision d'écart de prix",
  "dealer.ecart.sous": "Dossier {dossier}",
  "dealer.ecart.explication":
    "L'expert estime ce véhicule à {valeur}. Votre prix, {prix}, dépasse de plus de 10 % cette valeur : l'acheteur ne peut pas continuer tant que vous ne répondez pas. Tout prix jusqu'à {plafond} est conforme.",
  "dealer.ecart.valeur": "Valeur expertisée",
  "dealer.ecart.plafond": "Prix conforme jusqu'à",
  "dealer.ecart.prix": "Votre prix",
  /* `A3`, DEALER-CONFIRMS (architect's ruling of 30 October 2026): the four acts that cannot be taken back ask first, in these words. */
  "dealer.confirmer.retirerTitre": "Retirer ce véhicule du catalogue ?",
  "dealer.confirmer.retirerCorps":
    "{titre} ne sera plus visible des acheteurs. Les acheteurs qui attendent leur tour sont libérés. Si vous remettez le véhicule en ligne, leur place ne leur est pas rendue.",
  "dealer.confirmer.alignerTitre": "M'aligner sur la valeur expertisée ?",
  "dealer.confirmer.alignerCorps":
    "Votre prix passe de {ancien} à {nouveau}. Le dossier de l'acheteur repart.",
  "dealer.confirmer.proposerTitre": "Envoyer cette proposition ?",
  "dealer.confirmer.proposerCorps":
    "Votre prix passe de {ancien} à {nouveau}. L'acheteur doit l'accepter ou renoncer ; son dossier repart s'il accepte.",
  /* `A3`, D-1029-3 (architect's ruling of 31 October 2026): a plain price change asks too, with both prices and no percentage the server did not serve. */
  "dealer.confirmer.modifierPrixTitre": "Modifier le prix ?",
  "dealer.confirmer.modifierPrixCorps": "Votre prix passe de {ancien} à {nouveau}.",
  "dealer.confirmer.modifierPrixConfirmer": "Modifier le prix",
  "dealer.confirmer.maintenirTitre": "Maintenir votre prix ?",
  "dealer.confirmer.maintenirCorps":
    "Votre prix de {prix} ne change pas. L'acheteur peut renoncer et être remboursé. Vous pouvez encore changer d'avis.",
  "dealer.ecart.aligner": "M'aligner sur la valeur expertisée",
  "dealer.ecart.proposer": "Proposer un autre prix",
  "dealer.ecart.maintenir": "Maintenir mon prix",
  "dealer.ecart.montant": "Prix proposé (FCFA)",
  "dealer.ecart.envoyer": "Envoyer ma proposition",
  "dealer.ecart.annuler": "Annuler",
  "dealer.ecart.refusPlafond":
    "Ce prix dépasse la valeur expertisée de plus de 10 %. Le plus haut prix conforme est {plafond}.",
  "dealer.ecart.refusBaisse": "Le prix ne peut que baisser : proposez un montant inférieur à {prix}.",
  "dealer.ecart.refusMontant": "Entrez un montant entier en FCFA.",
  "dealer.ecart.reponseAligne": "Vous avez aligné votre prix sur {prix}. Le dossier de l'acheteur repart.",
  "dealer.ecart.reponsePropose":
    "Vous avez proposé {prix}. L'acheteur doit l'accepter ou renoncer ; son dossier repart s'il accepte.",
  "dealer.ecart.reponseMaintenu":
    "Vous maintenez votre prix de {prix}. L'acheteur peut renoncer et être remboursé. Vous pouvez encore changer d'avis.",
  "dealer.ecart.verrou":
    "Le prix d'un véhicule sur lequel un acheteur s'est engagé ne change pas par « Modifier le prix » : seule cette réponse peut le faire baisser.",
  "dealer.reglement.payeurBanque": "Payé par la banque, en deux virements",
  "dealer.reglement.payeurAcheteur": "Payé par l'acheteur, en deux virements",
  "dealer.reglement.votrePart": "Votre part",
  "dealer.reglement.votrePartMots": "{taux} % du prix, sur votre propre compte",
  "dealer.reglement.commissionHakili": "Commission Hakili",
  "dealer.reglement.commissionHakiliMots": "{taux} % du prix, sur le compte de Hakili",
  "dealer.reglement.confirmeeLe": "confirmée le {date}",
  "dealer.reglement.recueLe": "reçue le {date}",
  "dealer.reglement.enAttenteVous": "en attente de votre confirmation",
  "dealer.reglement.enAttente": "en attente",
  "dealer.reglement.confirmer": "Je confirme avoir reçu mes {taux} % ({montant})",
  "dealer.reglement.dateReception": "Date de réception",
  "dealer.reglement.rienRecu": "Je n'ai rien reçu",
  "dealer.reglement.rienRecuDeclare":
    "Vous avez déclaré n'avoir rien reçu le {date} ; votre chargé de dossier est prévenu.",
  "dealer.reglement.partiel": "Règlement partiel",
  "dealer.reglement.partielMots":
    "Les deux virements n'arrivent pas à la même seconde. Ce n'est pas une anomalie.",
  "dealer.reglement.complet": "Règlement complet",
  "dealer.reglement.completMots":
    "Les deux volets sont confirmés. Bon de retrait {reference} émis par Hakili.",
  "dealer.reglement.copie":
    "Le bon de retrait est émis par Hakili, jamais par vous. Il n'est émis qu'une fois les deux virements constatés : le vôtre, que vous confirmez ici, et celui de Hakili, que nous constatons sur notre compte. Vous attendez que {acheteur} le présente avec sa pièce d'identité. Le client récupère toujours son véhicule lui-même, sur votre parc.",
  "dealer.reglement.remise": "Le client a présenté son bon de retrait et pris son véhicule",
  "dealer.reglement.remiseReference": "Référence du bon présenté",
  "dealer.reglement.remiseMotifVotrePart":
    "Aucun bon de retrait émis : confirmez d'abord la réception de vos 90 %.",
  "dealer.reglement.remiseMotifHakili":
    "Bon de retrait non émis : la part de Hakili n'est pas encore parvenue.",
  "dealer.reglement.remiseMotifDeux": "Aucun bon de retrait émis : les deux virements sont attendus.",
  "dealer.reglement.remiseMotifReference": "Saisissez la référence du bon présenté par l'acheteur.",
  "dealer.reglement.remisLe": "Véhicule remis en main propre le {date}, bon {reference} présenté.",
  "dealer.reglement.confirmerMotif": "La date de réception est requise.",
  "dealer.profil.compteTitre": "Coordonnées bancaires de la concession",
  "dealer.profil.compteMots":
    "Le compte sur lequel la banque ou l'acheteur vous vire votre part. Saisi par vous, jamais par Hakili ; chaque saisie est une nouvelle version, l'ordre de versement lit la dernière.",
  "dealer.profil.compteAbsent":
    "Aucune coordonnée saisie : l'ordre de versement de la banque ne peut pas être émis.",
  "dealer.profil.compteVersion": "Version {version}, saisie le {date}",
  "dealer.profil.banque": "Banque",
  "dealer.profil.titulaire": "Titulaire du compte",
  "dealer.profil.numeroCompte": "Numéro de compte",
  "dealer.profil.compteEnregistrer": "Enregistrer les coordonnées",
  "dealer.profil.compteEnregistrerMotif": "La banque, le titulaire et le numéro de compte sont requis.",
  "dealer.profil.mentionsTitre": "Mentions de votre facture proforma",
  "dealer.profil.mentionsMots":
    "Ce que votre facture proforma imprime de vous : saisi par vous, imprimé tel quel, jamais rédigé par Hakili. Tant que l'une de ces quatre mentions manque, la facture proforma ne peut pas être émise.",
  "dealer.profil.mentionsAbsentes": "À compléter : {liste}.",
  "dealer.profil.mentionRccm": "RCCM",
  "dealer.profil.mentionNif": "NIF",
  "dealer.profil.mentionConditions": "Conditions de paiement",
  "dealer.profil.mentionGarantie": "Garantie",
  "dealer.profil.mentionsEnregistrer": "Enregistrer les mentions",
  "dealer.profil.mentionsModifier": "Modifier les mentions",
  "dealer.vehicules.mettreEnLigne": "Mettre en ligne",
  // `FX-2a`, `M1` (`F1`): the reason the publish control is greyed, which it
  // carried none of. It names the two fields `placeIsComplete` demands AND the
  // screen they are proposed from, because the founder found Profil by accident.
  // `FX-2a`, `M2` (`F2`): the SECONDS. `enCours` was set for the whole gesture ,
  // create, upload each view, publish , read once as a guard and rendered nowhere.
  // The label is what the person sees; the reason answers the second press,
  // because the wire says it is ignored and saying so is cheaper than a
  // dealership wondering whether it has two cars.
  // `FX-2a`, `M1`, second reading: the way to Profil, beside the reason. The
  // frame showed a greyed control with nothing next to it , `W5` put the reason
  // in the tooltip, and the founder did not hover; he hunted.
  "dealer.vehicules.mettreEnLigneProfil": "Compléter mon Profil",
  "dealer.vehicules.mettreEnLigneEnCours": "Mise en ligne en cours…",
  "dealer.vehicules.mettreEnLigneEnCoursMotif":
    "Votre véhicule est en cours de mise en ligne. Un second appui ne crée pas un second véhicule.",
  // `FX-2b`, `M2 (a)`: the FICHE's own sentence, and the frame is what found it owed.
  // The add panel's key ends « … ou dans votre Profil pour qu'ils soient proposés à chaque
  // véhicule », which is true there and FALSE here: the fiche's two fields are the car's own,
  // already saved, and nothing proposes them. One reason, two screens, two sentences.
  "dealer.vehicules.placeMotif": "La ville et le quartier sont requis.",
  "dealer.vehicules.mettreEnLigneMotif":
    "La ville et le quartier sont requis. Renseignez-les ici, ou dans votre Profil pour qu’ils soient proposés à chaque véhicule.",
  // The drawing's own line under the panel's title.
  "dealer.vehicules.ajouterSous": "Mise en ligne gratuite et immédiate",
  "dealer.vehicules.referenceIndice": "Proposée d'après votre parc, modifiable.",
  "dealer.vehicules.reference": "Référence de stock",
  "dealer.vehicules.marque": "Marque",
  "dealer.vehicules.modele": "Modèle",
  // `round F5`, to the pair: the drawing's own examples in the empty fields.
  // They say the SHAPE , four digits, a mileage without its unit, a plate as
  // Mali writes one , which is what a seller filling this in for the first time
  // is looking for. Placeholders, never values.
  "dealer.vehicules.anneeExemple": "2020",
  "dealer.vehicules.kilometrageExemple": "62 400",
  "dealer.vehicules.couleurExemple": "Gris argent",
  "dealer.vehicules.placesExemple": "5",
  "dealer.vehicules.prixExemple": "7 000 000",
  "dealer.vehicules.plaqueExemple": "BB 1042 MD",
  // `S4-3`: the declaration itself, asked once at listing. A question and not
  // a statement, because a select that merely NAMED the field would invite
  // the same blank-reads-as-an-answer bug the plate's own absence had.
  "dealer.vehicules.immatricule": "Véhicule immatriculé",
  "dealer.vehicules.immatriculeChoisir": "Immatriculé ou non ?",
  "dealer.vehicules.oui": "Oui",
  "dealer.vehicules.non": "Non",
  "dealer.vehicules.numeroImmatriculation": "Numéro d'immatriculation",
  "dealer.vehicules.numeroImmatriculationExemple": "AB 1234 ML",
  "dealer.vehicules.annee": "Année",
  "dealer.vehicules.kilometrage": "Kilométrage",
  "dealer.vehicules.prix": "Prix demandé (FCFA)",
  "dealer.vehicules.plaque": "Immatriculation",
  "dealer.vehicules.couleur": "Couleur",
  "dealer.vehicules.boite": "Boîte",
  "dealer.vehicules.carburant": "Carburant",
  "dealer.vehicules.places": "Places",
  "dealer.vehicules.ville": "Ville",
  "dealer.vehicules.quartier": "Quartier",
  "dealer.vehicules.quartierChoisir": "Choisir un quartier",
  // **The eleven stages of `catalog.VehicleStage`, ALL of them, `round F` `F2`.**
  //
  // The block that stood here said *"the eleven stages"* and carried eight, and
  // the count was the tell. `reserve` was a label for a value the wire cannot
  // send , the enum does not declare it and no api file writes it , and
  // `demande`, `visite`, `rapport` and `transaction` had no label at all, so a
  // car with a visit booked wore the raw word « visite » on its chip. The
  // fallback (render the stage as itself) was doing its job correctly and
  // showing a customer the wire's vocabulary.
  //
  // The words are the mockup's own `ETAPES_VEHICULE`
  // (`hakili-dealer.jsx:156-195`), full sentence and short form. « Fonds reçus »
  // and not « Payé »: Hakili never observes the dealer's leg of a settlement,
  // so the stage that matters to a dealership is the one where HE has his
  // money.
  // `T-2b`, `M0 (c)`: the lock's word before the stage's while a buyer holds the car firmly.
  "dealer.stage.reserveFerme": "Réservé",
  // `S4-10`: a soft lock still live , a buyer has declared, payment not yet confirmed.
  "dealer.stage.reserveSoft": "Réservation en cours",
  "dealer.stage.enligne": "En ligne",
  "dealer.stage.enligneCourt": "En ligne",
  "dealer.stage.demande": "Visite demandée",
  "dealer.stage.demandeCourt": "Visite demandée",
  "dealer.stage.visite": "Visite programmée",
  "dealer.stage.visiteCourt": "Visite",
  "dealer.stage.expertise": "Expertise programmée",
  "dealer.stage.expertiseCourt": "Expertise",
  "dealer.stage.rapport": "Rapport rendu",
  "dealer.stage.rapportCourt": "Rapport",
  "dealer.stage.transaction": "Transaction en cours",
  "dealer.stage.transactionCourt": "Transaction",
  "dealer.stage.paye": "Fonds reçus",
  "dealer.stage.payeCourt": "Fonds reçus",
  "dealer.stage.retire": "Véhicule retiré par l'acheteur",
  "dealer.stage.retireCourt": "Retiré",
  "dealer.stage.retireDuCatalogue": "Retiré du catalogue",
  "dealer.stage.retireDuCatalogueCourt": "Retiré",
  "dealer.stage.nonConforme": "Vente bloquée, contrôle non conforme",
  "dealer.stage.nonConformeCourt": "Non conforme",
  "dealer.stage.vendueHorsPlateforme": "Vendu hors plateforme",
  "dealer.stage.vendueHorsPlateformeCourt": "Hors plateforme",
  "dealer.profil.intro":
    "Les coordonnées de votre concession. Elles pré-remplissent la ville et le quartier de chaque véhicule que vous mettez en ligne.",
  // In WORDS, never an empty line: the four concessions Hakili a ouvertes n'ont
  // pas d'adresse, et une ligne vide se lit comme un écran qui n'a pas chargé.
  "dealer.profil.adresseACompleter": "Adresse à compléter.",
  "dealer.profil.modifier": "Modifier",
  // ---------------------------------------------- `round F`, `F6`: le Profil
  "dealer.profil.titre": "Profil",
  "dealer.profil.coordonnees": "Coordonnées",
  "dealer.profil.adresse": "Adresse",
  "dealer.profil.telephoneLigne": "Téléphone",
  "dealer.profil.nonRenseigne": "Non renseigné",
  "dealer.profil.securite": "Sécurité",
  // La derniere connexion que la maquette montre ici n'est portee par aucune
  // porte ; ce que ce bloc ajoute est ce que « Se deconnecter » FAIT, qui est la
  // moitie qu'une personne a besoin de lire avant d'appuyer.
  "dealer.profil.securiteIntro":
    "Vous pouvez quitter cet espace à tout moment. Votre session est fermée des deux côtés, pas seulement sur cet appareil.",
  "dealer.profil.idee": "Une idée pour améliorer Hakili ?",
  // `OD-18`, `round F5` : la porte existe, la raison de l'inertie est levée, et
  // les deux phrases qui la portaient sont remplacées par ce qu'on demande a
  // une personne de faire.
  // The drawing's own two lines (`hakili-dealer.jsx`, « Profil »), now that the
  // door makes them true.
  // `round F5`: the reason on the employee's greyed « Modifier ». It names WHO
  // may, because « vous n'avez pas le droit » tells a person nothing they can
  // act on , they ask their manager, and now they know to.
  "dealer.profil.modifierRefus": "Seul le responsable de la concession peut modifier ces coordonnées.",
  "dealer.profil.ideeIntro": "Vos demandes sont analysées et priorisées par la direction produit",
  "dealer.profil.ideeBouton": "Demander une fonctionnalité",
  // `R81` : ce que la maquette montre et qu'aucune porte ne porte.
  "dealer.profil.enAttente":
    "Le contact principal, la date d'ouverture de votre concession par Hakili, le cabinet d'expertise de votre zone et votre dernière connexion arriveront ici avec leurs modules.",
  "dealer.profil.ville": "Ville",
  "dealer.profil.quartier": "Quartier",
  "dealer.profil.telephone": "Téléphone",
  "dealer.team.title": "Équipe",
  // `round F`, `F4`: the drawing's own sentence (`hakili-dealer.jsx:3278-3281`).
  // It says who opened the dealership and who invites whom, which is `I16` in
  // words a manager reads on his first afternoon.
  "dealer.team.introDrawn":
    "Qui travaille sur quoi, et qui attend un geste. Hakili a ouvert la concession et vous a nommé responsable ; les employés, c'est vous qui les invitez.",
  "dealer.team.vous": "vous",
  "dealer.team.personnes": "Les personnes",
  // `round F6`, `OD-15`: the per-person load, derived from the park , the
  // drawing's own `chargeDe` (`hakili-dealer.jsx:920-932`).
  "dealer.team.chargeAucune": "Aucun véhicule",
  // `round F6`, cured on the pair: the NOUN alone, so `pl` agrees it with its
  // own number , « 1 véhicule en cours » and « 16 véhicules en cours ».
  "dealer.team.chargeVehicule": "véhicule",
  "dealer.team.chargeEnCours": "en cours",
  "dealer.team.chargeRien": "Rien à son nom pour l'instant.",
  "dealer.team.chargeANom": "à son nom, dont",
  "dealer.team.chargeDontEnCours": "en cours",
  "dealer.team.tuilePersonnes": "Personnes actives",
  "dealer.team.tuilePersonnesSuite": "sur cette concession",
  "dealer.team.tuileInvitations": "Invitations en attente",
  "dealer.team.tuileInvitationsSuite": "lien envoyé, pas encore accepté",
  "dealer.team.tuileVehicules": "Véhicules suivis",
  "dealer.team.tuileVehiculesSuite": "en cours sur la concession",
  "dealer.team.tuileVisites": "Visites en cours",
  "dealer.team.tuileVisitesSuite": "à confirmer ou programmées",
  "dealer.team.invitationEnvoyee": "Invitation envoyée à",
  // `round F5`: the drawing's own bullet , « Tant qu'elle n'a pas accepté,
  // vous pouvez renvoyer l'invitation depuis la liste » , made a control.
  "dealer.team.resend": "Renvoyer",
  "dealer.team.resent": "Renvoyée",
  // The two dates `EquipeScreen` puts under every person.
  "dealer.team.depuis": "Dans l'équipe depuis le",
  "dealer.team.derniereConnexion": "dernière connexion le",
  // `R81`, once under the list. The drawing gives each person a load , ses
  // véhicules, ses visites, ses gestes attendus , and a « Voir ses véhicules »
  // that unfolds them. `catalog.vehicles` carries no `responsable` column:
  // `OD-15` settled the RULE (the lister follows the car, the manager may hand
  // any car to anyone) and `I-D` builds it.
  "dealer.team.intro": "Les personnes de votre concession, et ce à quoi chacune accède.",
  // **`round F`, `F4`: three words for one role, settled.**
  //
  // `P0-IDN-067` created `employeConcession` and named its labels in the same
  // breath , « Employé de concession » / « Employée de concession ». This
  // catalogue said « Collaborateur », which no item and no decision ever chose;
  // the mockup's chip says « Employé » (`hakili-dealer.jsx:306-307`).
  //
  // The RECORD wins on vocabulary: the label is the api's own, and a screen
  // that renamed a role would be describing a machine it has not read. The
  // drawing's shorter forms are kept as the CHIP words, where the row already
  // says which dealership this is , that is a length, not a different name.
  "dealer.team.roleManager": "Responsable de concession",
  "dealer.team.roleMember": "Employé de concession",
  "dealer.team.roleManagerCourt": "Responsable",
  "dealer.team.roleMemberCourt": "Employé",
  "dealer.team.statusActive": "Actif",
  "dealer.team.statusInvited": "Invitation envoyée",
  "dealer.team.statusRemoved": "Compte désactivé le",
  "dealer.team.invite": "Inviter un employé",
  // `round F5`, to the pair: the panel's own labels
  // (`hakili-dealer.jsx:3041-3070`). « Nom complet » and « Adresse
  // professionnelle » were this build's words; the drawing asks for a first
  // name and a surname, and for an address, without calling it professional ,
  // an employee of a dealership in Bamako signs in with the address they have.
  "dealer.team.inviteName": "Prénom et nom",
  "dealer.team.inviteEmail": "Adresse électronique",
  "dealer.team.inviteNamePlaceholder": "Aïssata Bocoum",
  "dealer.team.inviteEmailPlaceholder": "aissata.bocoum@autoplus.ml",
  "dealer.team.inviteEmailHint":
    "C'est à cette adresse que part son lien, et c'est avec elle qu'elle se connectera.",
  "dealer.team.inviteRole": "Rôle",
  // The one role `may_invite_roles` allows, and what it means in the terms a
  // manager cares about: the same work, none of the authority.
  "dealer.team.inviteRoleSentence":
    "Cette personne gère l'inventaire et les rendez-vous comme vous, sans toucher à l'équipe : vous seul invitez et retirez, et ce droit ne se transmet pas depuis cet écran.",
  "dealer.team.inviteNext": "Ce qui se passe ensuite",
  "dealer.team.inviteNext1":
    "Un lien personnel part à son adresse et ouvre l'espace concessionnaire, pas un autre.",
  "dealer.team.inviteNext2": "Son compte n'existe qu'une fois le lien accepté et son mot de passe choisi.",
  "dealer.team.inviteNext3":
    "Tant qu'elle n'a pas accepté, vous pouvez renvoyer l'invitation depuis la liste.",
  "dealer.team.inviteNext4":
    "Le lien est une clé : nous ne l'affichons jamais ici, et personne ne peut le recopier depuis cet écran.",
  // The three reasons the panel refuses, in the drawing's own words. They are
  // shown only once something has been typed , a form that makes the lesson
  // before it has been touched is a form that scolds an empty page.
  "dealer.team.inviteReasonName": "indiquez le prénom et le nom",
  "dealer.team.inviteReasonEmail": "indiquez une adresse électronique valide",
  "dealer.team.inviteReasonAlready": "cette adresse est déjà celle d'un membre de votre équipe",
  "dealer.team.inviteSend": "Envoyer l'invitation",
  "dealer.team.inviteFieldsRequired": "Un nom complet et une adresse e-mail valide sont requis.",
  "dealer.team.remove": "Réattribuer et retirer",
  // `round F5`, to the pair: the dialog carries the GESTURE's own name, which
  // is the founder's, of one piece. « Retirer de l'équipe » named the half a
  // manager fears and not the half that protects the parc.
  "dealer.team.removeTitle": "Réattribuer et retirer",
  "dealer.team.removeHolds": "Ce qui est à son nom aujourd'hui",
  // `FX-2a`, `M3` (`F7`): the line's own gesture on Équipe, and who it goes to.
  // The drawing's own two words over this list.
  "dealer.team.reassignOne": "Réattribuer",
  "dealer.team.reassignTo": "Passer à",
  "dealer.team.removeTargetDefault": "(par défaut)",
  // The verb agrees with the number too , « 1 véhicule PASSE », « 2 véhicules
  // PASSENT ». Read on the pair of 8 September, on this very line.
  "dealer.team.removeMoves": "passe à",
  "dealer.team.removeMovesPlural": "passent à",
  "dealer.team.removeMovesTrace": ", et chaque fiche en garde la trace datée.",
  "dealer.team.removeWhat": "Ce que ce geste fait, exactement",
  // Both are true of the door and neither was said. `users.controller.ts`
  // revokes every session in the same transaction, and `login.ts` refuses a
  // closed account in the SAME words as an unknown address , enumeration is
  // still enumeration when the account is closed.
  "dealer.team.removeSessions": "Ses sessions ouvertes se ferment aussitôt, sur tous ses appareils.",
  "dealer.team.removeSilent":
    "Une tentative de connexion est refusée, sans jamais dire que ce compte a existé.",
  "dealer.team.removeConfirm": "Réattribuer et retirer",
  // The three sentences 6.7 requires a manager to have read before pressing.
  "dealer.team.removeHoldsNothing": "Cette personne ne détient aucun dossier en cours.",
  "dealer.team.removeNeverDeleted": "Le compte est désactivé, jamais supprimé.",
  "dealer.team.removeRowStays": "La ligne reste dans la liste, grisée et datée.",
  // `round F6`, to the pair: the drawing's own label (`hakili-dealer.jsx:3187`).
  "dealer.team.removeTarget": "À qui cela revient",
  "dealer.team.removeTargetManager": "Le responsable de la concession",
  "dealer.team.removeBlocked": "Dossiers ouverts à transférer d'abord :",
  "dealer.team.removeUnknown": "Le nombre de dossiers ouverts ne peut pas être établi pour cette personne.",
  "dealer.team.removeOwed":
    "La réattribution groupée n'est pas encore disponible : le transfert se fait dossier par dossier en attendant.",
  "bank.pipeline.titre": "Mon pipeline",
  "bank.pipeline.vide": "Aucun dossier ne vous est affecté pour le moment.",
  "bank.pipeline.reference": "Référence",
  "bank.pipeline.etape": "Étape",
  "bank.pipeline.depuis": "Depuis",
  "bank.pipeline.montant": "Montant financé",
  "bank.pipeline.quotite": "Quotité",
  "bank.pipeline.comite": "Comité requis",
  "bank.dossiers.titre": "Dossiers",
  "bank.dossiers.actifs": "En cours",
  "bank.dossiers.archives": "Archives",
  "bank.dossiers.vide": "Aucun dossier dans cette liste.",
  "bank.dossiers.porteePersonnel": "Vos dossiers",
  "bank.dossiers.porteeEquipe": "Les dossiers de l'équipe",
  // `P1-BKW-004` and `P1-BKW-005`, round 190: WF-BNK-04's workspace and
  // WF-BNK-05's sixteen points in their three sub-steps.
  "bank.revue.titre": "Revue du dossier",
  // `VISITE-SANS-OBJET` (17 October 2026): a pièce the chargé marked not applicable before the file came to the bank.
  "bank.revue.prendre": "Prendre en revue",
  "bank.revue.agent": "Analyse assistée (à venir)",
  "bank.revue.compteur": "points traités",
  "bank.revue.valider": "Valider la revue",
  "bank.revue.demander": "Demander les informations",
  "bank.revue.signaler": "Signaler",
  "bank.revue.cocher": "Valider",
  "bank.revue.motif": "Motif du signalement",
  "bank.revue.groupePieces": "Pièces",
  "bank.revue.groupeCoherence": "Cohérence",
  "bank.revue.groupeConformite": "Conformité",
  /**
   * Round `L-0`, the bank's home: the five screens of August to their drawing
   * (`hakili-bank.jsx`). The rail, the login's card, the pipeline's greeting,
   * tiles and tabs, the dossiers' tiles, search and filter, the workspace's
   * header, stepper and review flow. A control whose screen is not built is
   * greyed with its round in its title; a value no door gives is a dash.
   */
  "bank.nav.uneIdee": "Une idée ?",
  "bank.nav.compte": "Compte",
  "bank.nav.notificationsUneNonLue": "1 non lue",
  "bank.nav.notificationsNonLues": "{n} non lues",
  "bank.nav.notifications": "Notifications",
  // `U-4`, `M3` (`U-3`'s booking 7): the bell, live , its panel's empty sentence.
  "bank.nav.notificationsAucune": "Aucune notification.",
  // `NOTIFS-D2` (D-1028-1; D2 frames 147 to 149): the empty panel's second line, and the unread row's chip.
  "bank.nav.notificationsAucuneAide":
    "Les nouveaux dossiers, les réponses du client et les réaffectations apparaîtront ici.",
  "bank.nav.notificationNonLue": "Non lue",
  "bank.nav.notificationsToutLire": "Tout marquer comme lu",
  "bank.nav.bank": "bank",
  "bank.login.sousTitre": "Espace banque · Direction du crédit",
  // Round `FX-3`, `M3` (`S2-2`): the second factor, on the bank's own login. Optional for every role but
  // `administration` (`DECISION ADDED 06-02`) , and a bank seat that HAS enrolled needs somewhere to type it.
  "bank.login.code": "Code à six chiffres",
  "bank.login.codeAide":
    "Votre compte est protégé par une application d'authentification : saisissez le code affiché.",
  "bank.login.pied": "Accès réservé aux équipes autorisées · connexion chiffrée",
  "bank.pipeline.bonjour": "Bonjour, {prenom}",
  "bank.pipeline.ligneJourUn": "{n} nouveau dossier complet reçu de Hakili, à prendre en revue",
  "bank.pipeline.ligneJourPlusieurs": "{n} nouveaux dossiers complets reçus de Hakili, à prendre en revue",
  "bank.pipeline.ligneJourAucun": "Aucun nouveau dossier reçu de Hakili en attente de revue",
  "bank.pipeline.tuileATraiter": "Dossiers à traiter",
  "bank.pipeline.tuileATraiterSous": "tout ce qui appelle une action",
  "bank.pipeline.tuileNouveaux": "Nouveaux",
  "bank.pipeline.tuileNouveauxSous": "reçus de Hakili, complets et vérifiés",
  "bank.pipeline.tuileOffres": "Offres en attente client",
  "bank.pipeline.tuileOffresSous": "réponse attendue du client",
  "bank.pipeline.tuileTemps": "Temps moyen par dossier",
  "bank.pipeline.tuileTempsSous": "aucune mesure ne l'établit encore",
  "bank.pipeline.titreAnalyste": "Pipeline",
  "bank.pipeline.titreChef": "Pipeline de l'équipe",
  "bank.pipeline.sousChef": "Les dossiers de vos analystes, en cours.",
  "bank.pipeline.nouveauUn": "{n} nouveau dossier",
  "bank.pipeline.nouveauxN": "{n} nouveaux dossiers",
  "bank.pipeline.luUn": " lu par Hakili, il attend votre revue.",
  "bank.pipeline.lusN": " lus par Hakili, ils attendent votre revue.",
  "bank.pipeline.aucunNouveau": "Aucun nouveau dossier.",
  "bank.pipeline.offreUne": "Une offre attend la réponse du client.",
  "bank.pipeline.offresN": "{n} offres attendent la réponse du client.",
  "bank.pipeline.veilleurUn": "Hakili a lu {n} dossier.",
  "bank.pipeline.veilleurN": "Hakili a lu {n} dossiers.",
  "bank.pipeline.veilleurAttendUn": "{n} attend votre revue.",
  "bank.pipeline.veilleurAttendN": "{n} attendent votre revue.",
  "bank.pipeline.ongletTous": "Tous",
  "bank.pipeline.ongletEnCours": "En cours",
  "bank.pipeline.listeLabel": "Filtrer le pipeline",
  "bank.pipeline.colGeste": "Prochain geste",
  "bank.pipeline.colAnalyste": "Analyste",
  /** `P1-BKW-029`, D2 frame 143: a file the chef holds himself, in the « Analyste » column. */
  "bank.pipeline.holderVous": "Vous",
  "bank.pipeline.holderChef": "chef du crédit",
  "bank.pipeline.gesteNouveau": "Prendre en revue",
  "bank.pipeline.gesteRevue": "Terminer la revue",
  "bank.pipeline.gesteValidation": "Attente du chef",
  "bank.pipeline.gesteOffre": "Réponse du client",
  "bank.pipeline.gesteComite": "Comité en cours",
  "bank.pipeline.gesteSignature": "Faire signer",
  "bank.pipeline.gesteConditions": "Lever les conditions",
  "bank.pipeline.gesteRetour": "Chez Hakili",
  "bank.pipeline.videTitre": "Rien à traiter pour le moment",
  "bank.pipeline.videPhrase":
    "Quand Hakili vous transmettra un nouveau dossier, il apparaîtra ici avec sa note et son avis.",
  "bank.pipeline.vueVideTitre": "Aucun dossier dans cette vue",
  "bank.pipeline.vueVidePhrase": "Choisissez une autre vue, ou revenez à « Tous ».",
  "bank.pipeline.erreurTitre": "Le service ne répond pas",
  "bank.pipeline.erreurPhrase":
    "Vos dossiers n'ont pas pu être chargés. Rien n'a été modifié. Réessayez dans un instant ; si cela dure, dites-le à votre chef du crédit.",
  "bank.pipeline.filtreDe": "Dossiers de",
  "bank.pipeline.filtreSeulement": "seulement.",
  "bank.pipeline.filtreToute": "Voir toute l'équipe",
  "bank.dossiers.geste": "Fonds versés, deux virements",
  "bank.pipeline.colRecu": "Reçu",
  "bank.pipeline.recuAujourdhui": "aujourd'hui",
  "bank.pipeline.recuHier": "hier",
  "bank.pipeline.recuJours": "il y a {n} j",
  "bank.pipeline.veilleurAttendUnSans": "{n} attend votre revue, aucun constat bloquant.",
  "bank.pipeline.veilleurAttendNSans": "{n} attendent votre revue, aucun constat bloquant.",
  "bank.dossiers.perduNonRetenue": "Offre non retenue le {date} : le client en a choisi une autre",
  "bank.dossiers.perduSansSuite": "Sans suite le {date} : le dossier du client a été clos",
  "bank.dossiers.perduClientDeclined": "Offre encore ouverte à la clôture du dossier du client, le {date}",
  "bank.dossiers.perduExpiree": "Offre expirée le {date} : le client n'a pas répondu",
  "bank.dossiers.refuseMotif": "Refusé : {motif}",
  "bank.pipeline.ongletATraiter": "À traiter",
  "bank.pipeline.ongletNouveaux": "Nouveaux",
  "bank.pipeline.ongletRevue": "En revue",
  "bank.pipeline.ongletOffres": "Offres",
  "bank.pipeline.ongletComite": "Comité",
  "bank.pipeline.ongletFinalisation": "En finalisation",
  "bank.pipeline.ongletHakili": "Chez Hakili",
  "bank.pipeline.soliditeMotif":
    "Le tri par solidité lit la note d'intelligence, qui n'est pas encore disponible.",
  // `L-5`, `M2`: the pipeline filtered on one analyst from « Équipe » (WF-BNK-24's analyst filter).
  "bank.pipeline.filtreAnalyste": "Dossiers de {nom}",
  "bank.pipeline.filtreEffacer": "Toute l'équipe",
  "bank.pipeline.creditAuto": "Crédit auto",
  "bank.pipeline.fcfa": "FCFA",
  "bank.pipeline.soliditeTitre": "Solidité : la note d'intelligence, phase 3",
  "bank.pipeline.pied": "Les dossiers décaissés, refusés ou déclinés quittent le pipeline.",
  "bank.pipeline.clotures": "{n} dossier clôturé récemment, voir dans Dossiers",
  "bank.pipeline.cloturesPluriel": "{n} dossiers clôturés récemment, voir dans Dossiers",
  "bank.pipeline.consulterTous": "Consulter l'ensemble des dossiers",
  "bank.pipeline.vueEquipe": "Le pipeline de l'équipe",
  "bank.phrase.nouveau": "Reçu le {date} de Hakili, complet et vérifié",
  "bank.phrase.revue": "En revue depuis le {date}",
  "bank.phrase.validation": "Offre indicative en validation chez le chef du crédit",
  "bank.phrase.offre": "Offre envoyée au client · réponse attendue",
  "bank.phrase.comite": "Offre acceptée · dossier de comité en préparation",
  "bank.phrase.signature": "Approuvé · en attente de signature",
  "bank.phrase.conditions": "Conditions préalables en cours de vérification",
  "bank.phrase.decaisse": "Décaissé · domiciliation active",
  "bank.phrase.retour": "Retourné à Hakili · informations demandées au client",
  "bank.phrase.refuse": "Refusé · motif transmis à Hakili",
  "bank.phrase.perdu": "Offre déclinée par le client · dossier clos",
  "bank.dossiers.sousTitre":
    "Tous vos dossiers, en cours et clos. Les dossiers clos restent consultables et nourrissent le calibrage des notes.",
  "bank.dossiers.tuileTotal": "Dossiers",
  "bank.dossiers.tuileTotalSous": "en cours et clos",
  "bank.dossiers.tuileFinances": "Décaissés",
  "bank.dossiers.tuileFinancesSous": "financés",
  "bank.dossiers.tuileApprobation": "Taux d'approbation",
  "bank.dossiers.tuileApprobationSous": "sur {n} dossiers décidés",
  "bank.dossiers.tuileApprobationSousAucun": "aucun dossier décidé",
  "bank.dossiers.tuileApprobationSousUn": "sur {n} dossier décidé",
  "bank.dossiers.tuileEncours": "Encours originé",
  "bank.dossiers.tuileEncoursSous": "FCFA",
  "bank.dossiers.rechercher": "Rechercher par client, référence ou véhicule",
  "bank.dossiers.filtreStatut": "Filtrer par statut",
  "bank.dossiers.filtreTous": "Tous",
  "bank.dossiers.filtreActifs": "En cours",
  "bank.dossiers.filtreDecaisses": "Décaissés",
  "bank.dossiers.filtreRefuses": "Refusés",
  "bank.dossiers.filtreDeclines": "Clos",
  "bank.dossiers.exporter": "Exporter",
  "bank.dossiers.colClient": "Client et référence",
  "bank.dossiers.colObjet": "Objet",
  "bank.dossiers.colMontant": "Montant",
  "bank.dossiers.colNote": "Note",
  "bank.dossiers.colStatut": "Statut",
  "bank.dossiers.colActivite": "Activité",
  "bank.dossiers.aucunResultat": "Aucun dossier ne correspond à cette recherche.",
  "bank.dossiers.pied":
    "{n} dossier affiché sur {total}. Les dossiers clôturés restent consultables et alimentent le calibrage des notes.",
  "bank.dossiers.piedPluriel":
    "{n} dossiers affichés sur {total}. Les dossiers clôturés restent consultables et alimentent le calibrage des notes.",
  "bank.dossier.filAriane": "{reference}",
  "bank.dossier.historique": "Historique",
  // `U-4`, `M2` (WF-BNK-18): the panel's own words , the thread, the doors, the refusal.
  "bank.dossier.questionFermer": "Fermer le panneau",
  "bank.dossier.questionPortes": "Lu dans :",
  "bank.dossier.questionEchec":
    "En échec : Hakili n'a pas pu répondre. La question est gardée, la réponse est à redemander.",
  "bank.dossier.questionDecide": "Une proposition de mots. Vous décidez.",
  "bank.dossier.questionEnvoyer": "Envoyer",
  // `ASK-WIDE` (D-1028-4, D2 `6a9746bf` frames 154 to 160): the wide conversation panel. The words are D2's.
  "bank.bay.questionChamp": "Interroger Hakili sur ce dossier",
  "bank.conversation.interroger": "Interroger Hakili",
  "bank.conversation.echangeUn": "1 échange dans la conversation",
  "bank.conversation.echangesN": "{n} échanges dans la conversation",
  "bank.conversation.retour": "Retour à la conversation",
  "bank.conversation.pieceOrigine":
    "Ouverte depuis la conversation avec Hakili. Vos échanges et votre question sont gardés.",
  "bank.conversation.consultationSeule": "Consultation seule.",
  "bank.conversation.reduire": "Réduire",
  "bank.conversation.titre": "Conversation avec Hakili",
  "bank.conversation.vous": "Vous",
  "bank.conversation.hakili": "Hakili",
  "bank.conversation.puce2": "Pourquoi cette note ?",
  "bank.conversation.puce3": "Quels engagements en cours ?",
  "bank.conversation.aide": "Entrée envoie · Échap réduit la conversation",
  "bank.conversation.champ": "Votre question à Hakili",
  "bank.conversation.videTitre": "Posez votre première question sur ce dossier.",
  "bank.conversation.videTexte":
    "Hakili répond d'après les pièces lues et dit dans quelle pièce il a lu. Vous décidez toujours.",
  "bank.conversation.attente": "Hakili lit le dossier…",
  "bank.conversation.echec":
    "Hakili ne répond pas pour le moment. Votre question est gardée : réessayez dans un instant.",
  // D2 `58472093` frames 153 and 160 (ASK-FIGURE-RELATION R2): the answer that failed the figure check.
  "bank.conversation.nonVerifiee":
    "La réponse n'a pas pu être vérifiée contre le dossier. Reformulez la question.",
  "bank.conversation.reessayer": "Réessayer",
  "bank.conversation.copier": "Copier",
  "bank.conversation.copiee": "Réponse copiée.",
  "bank.conversation.pieces": "Pièces du dossier",
  // Proposed (designer round e1ecf6db, R3): said in the lock chip when « Envoyer » is pressed on an empty field; the door's own refusal reads the same.
  "bank.conversation.questionVide": "Écrivez votre question.",
  // Proposed by the founder (designer round 53acaca2, R6): said in the lock chip when a press comes while Hakili is still reading.
  "bank.conversation.questionEnCours": "Hakili lit encore le dossier.",
  "bank.conversation.rapport": "Rapport d'expertise",
  "bank.conversation.pieceIdentite": "Pièce d'identité",
  "bank.conversation.pieceBulletins": "Bulletins de salaire",
  "bank.conversation.pieceReleves": "Relevés bancaires",
  "bank.conversation.pieceAttestation": "Attestation de travail",
  "bank.conversation.pieceProforma": "Facture proforma",
  // WF-BNK-18's own three suggestions, in the drawing's words.
  "bank.dossier.questionSuggestion1": "Résumer le profil client",
  "bank.dossier.etape": "Étape du dossier",
  "bank.dossier.banniereNouveau":
    "Dossier reçu de Hakili, complet et vérifié. Prenez-le en revue pour commencer l'analyse.",
  "bank.revue.traites": "{n} sur {total} points traités",
  // `OD-37` round 5: a point the founder retired, on a file opened before; the date is `YYYY-MM-DD`, as the door gives it.
  "bank.revue.pointRetire": "Point retiré le\u00A0{date}.",
  "bank.revue.toutConfirmer": "Tout confirmer",
  "bank.revue.conforme": "Conforme",
  "bank.revue.ouvrirPiece": "Ouvrir la pièce et les vérifications",
  "bank.revue.criblageTitre": "Criblage effectué",
  "bank.revue.criblageOui": "Oui",
  "bank.revue.criblageNon": "Non",
  "bank.revue.criblageSource": "Outil ou source du criblage",
  "bank.revue.criblagePlaceholder": "Outil de la banque",
  "bank.revue.criblageMotif": "Indiquez si le criblage a été effectué, et l'outil ou la source.",
  "bank.revue.noteAide": "Ce qui pose problème et ce qu'il faut obtenir",
  "bank.revue.noteEnregistrer": "Enregistrer",
  "bank.revue.noteAnnuler": "Annuler",
  "bank.revue.votreNote": "Votre note : {note}",
  "bank.revue.piedEnCours": "Traitez chaque point pour terminer la revue.",
  "bank.revue.piedSignale": "{n} point signalé : la demande d'informations reprendra vos notes.",
  "bank.revue.piedSignalePluriel": "{n} points signalés : la demande d'informations reprendra vos notes.",
  "bank.revue.piedConforme": "Tous les points sont conformes. Vous pouvez préparer l'offre indicative.",
  "bank.revue.refuser": "Refuser",
  // Round `L-3b`, `M1` (WF-BNK-08, `hakili-bank.jsx` `RefuseModal`): the drawing's modal, its
  // sentence, the select opening on « Choisir un motif » (M0 (b), a drawing in words, F8).
  "bank.refus.titre": "Refuser le dossier",
  "bank.refus.phrase": "Le motif est transmis à Hakili et alimente le calibrage des notes de solidité.",
  "bank.refus.motif": "Motif du refus",
  "bank.refus.choisir": "Choisir un motif",
  "bank.refus.confirmer": "Confirmer le refus",
  // The `refuse` state on the workspace: one full-width callout reading the row; no « Rouvrir » (D3, D-1019-2).
  "bank.refus.banniereTitre": "Dossier refusé.",
  "bank.refus.banniere": "Motif transmis à Hakili : {motif}. Il alimente le calibrage des notes.",
  // Round `L-3`, `M1` (WF-BNK-07, `hakili-bank.jsx`'s `InfoRequestModal`): the analyst asks.
  "bank.demande.titre": "Demander des informations",
  "bank.demande.phrase":
    "Le dossier retournera à Hakili, qui contactera le client et le resoumettra complété.",
  "bank.demande.aide": "Ex. : préciser l'origine des fonds de l'apport personnel",
  "bank.demande.envoyer": "Envoyer la demande à Hakili",
  "bank.retour.banniereClient":
    "Retourné à Hakili. La demande d'informations a été transmise au client via la plateforme.",
  "bank.retour.banniereInterne":
    "Retourné à Hakili. La demande d'informations est traitée par Hakili en interne.",
  "bank.retour.demandeLe": "Demande envoyée le {date}",
  "bank.retour.informationsRecues": "Informations reçues",
  "bank.revue.terminer": "Terminer la revue et préparer l'offre",
  "bank.composer.montant": "Montant financé",
  "bank.composer.duree": "Durée",
  "bank.composer.mois": "{n} mois",
  "bank.composer.taux": "Taux nominal",
  "bank.composer.seuil": "Seuil de délégation du chef du crédit :",
  "bank.composer.politiqueLien": "Voir la politique de la banque",
  "bank.composer.accessoiresTitre": "Financements accessoires",
  "bank.composer.fraisBanque": "Frais de dossier banque",
  "bank.composer.fraisHakili": "Frais de dossier Hakili",
  "bank.composer.fraisLigne": "{montant} · hors gage et hors LTV",
  "bank.composer.fraisBanquePct": "Frais de dossier banque ({taux}\u202f%)",
  "bank.composer.fraisHakiliPct": "Frais de dossier Hakili ({taux}\u202f%)",
  "bank.composer.grilleSous": "{points} point sous la grille {note} ({taux}\u202f%)",
  "bank.composer.grilleSur": "Au taux de la grille {note} ({taux}\u202f%)",
  "bank.composer.grilleAu": "{points} point au-dessus de la grille {note} ({taux}\u202f%)",
  "bank.composer.capaciteDepassee": "La mensualité dépasse la capacité disponible.",
  "bank.composer.financer": "Financer",
  "bank.composer.aLaSignature": "À la signature",
  "bank.composer.titreTete": "Offre indicative \u00b7 {nom}",
  "bank.composer.grillePartielleTitre": "La grille n'a pas de taux pour la note {note}.",
  "bank.composer.grillePartielleCorps":
    "Hakili ne propose ni durée ni taux et ne calcule aucun écart de taux pour cette note. L'offre ne part qu'une fois le TAEG calculé et le plafond d'usure contrôlé sur le taux que vous saisissez.",
  "bank.composer.noteProvisoireTitre": "Note provisoire : pas de proposition de Hakili.",
  "bank.composer.noteProvisoireCorps": "Une lecture n'a pas été faite : Hakili ne propose ni durée ni taux.",
  "bank.composer.ecartPolitiqueTitre":
    "Grille tarifaire : aucun taux pour la note {note} \u00b7 écart de politique",
  "bank.composer.ecartPolitiqueAvant":
    "La grille n'a pas de taux pour cette note : l'absence est enregistrée comme un écart à la politique, au niveau",
  "bank.composer.ecartPolitiqueNiveau": "chef du crédit",
  "bank.composer.ecartPolitiqueApres":
    "L'offre ne part qu'après sa décision, jamais directement vers le client.",
  "bank.composer.noteIncompleteTitre": "Note incomplète : pas d'offre.",
  "bank.composer.revenirRevue": "Revenir à la revue",
  "bank.composer.saisirTaux": "Saisissez le taux nominal pour lire le récapitulatif.",
  "bank.composer.offreModifiee": "Offre modifiée : financements accessoires ajustés.",
  "bank.composer.grilleNonComparable":
    "Note de solidité non établie : la grille tarifaire ne peut pas être comparée sur ce dossier.",
  "bank.composer.totalFinance": "Total financé",
  "bank.composer.mensualite": "Mensualité (assurance incluse)",
  "bank.composer.capacite": "Capacité disponible",
  "bank.composer.capaciteUtilisee": "{pct}\u202f%",
  "bank.composer.fraisBanqueLigne": "Frais de dossier banque",
  "bank.composer.fraisHakiliLigne": "Frais de dossier Hakili",
  "bank.composer.fraisAide":
    "Refuser d'en financer un ne refuse jamais le dossier : les frais restent dus, celui de la banque à la signature, celui de Hakili avant le rendez-vous de signature.",
  "bank.composer.fraisDemandeRegler":
    "Le client a demandé à régler les deux frais lui-même : celui de la banque à la signature, celui de Hakili avant le rendez-vous de signature.",
  "bank.composer.fraisDemandeFinancer": "Le client a demandé à financer les deux frais dans le prêt.",
  "bank.composer.fraisSignatureNote":
    "Frais non financés par défaut, finançables via le mandat : celui de la banque est exigible à la signature, celui de Hakili est réglé avant le rendez-vous de signature.",
  "bank.composer.taeg": "TAEG estimé",
  // R-I (round `L-3`, `M0 (d)`): the agency the buyer's card names, the bank's own text.
  "bank.composer.taegSous": "{taeg}\u202f% · sous le plafond de {plafond}\u202f%",
  "bank.composer.taegAuDessus": "{taeg}\u202f% · dépasse le plafond de {plafond}\u202f%",
  "bank.composer.formule":
    "Taux nominal {taux}\u202f% + assurance 0,9\u202f% + frais de dossier (banque et Hakili) {frais}\u202f% amortis sur {duree} mois",
  "bank.composer.blocageTitre": "{regle} · règle éliminatoire",
  "bank.composer.blocageRegle":
    "Une règle éliminatoire ne peut pas faire l'objet d'une dérogation. Ajustez les conditions pour poursuivre.",
  "bank.composer.derogationTitre": "{regle} · dérogation requise",
  "bank.composer.validationNecessaire": "Validation nécessaire : {level}",
  "bank.composer.niveauChef": "Chef du crédit",
  "bank.composer.niveauComite": "Comité de crédit",
  "bank.composer.justification": "Justification de la dérogation",
  "bank.composer.conditionsTitre": "Les conditions",
  "bank.composer.recapTitre": "Récapitulatif en direct",
  "bank.composer.tauxBaisser": "Baisser le taux de 0,1",
  "bank.composer.tauxMonter": "Monter le taux de 0,1",
  "bank.composer.tauxValeur": "{n}\u202f%",
  "bank.composer.fraisAvantRdv": "Avant le rendez-vous",
  "bank.composer.mensualiteRecap": "Mensualité, assurance incluse",
  "bank.composer.dontAssurance": "dont assurance",
  "bank.composer.fraisBanqueRecap": "Frais banque",
  "bank.composer.fraisHakiliRecap": "Frais Hakili",
  "bank.composer.fraisValeurFinances": "{montant} · financés",
  "bank.composer.fraisValeurSignature": "{montant} · à la signature",
  "bank.composer.fraisValeurAvantRdv": "{montant} · réglés avant le rendez-vous de signature",
  "bank.composer.ecartDemandeSeul":
    "Écart à la demande du client\u202f: {signe} {montant} au capital financé.",
  "bank.composer.justificationLigne": "Pourquoi ce taux ? Une ligne",
  "bank.composer.obligatoire": "obligatoire",
  "bank.composer.recapAucun": "Le récapitulatif apparaît dès qu'un taux est saisi.",
  "bank.composer.envoyerValidation": "Envoyer pour validation",
  "bank.composer.envoyerChef": "Envoyer au chef du crédit",
  "bank.composer.envoyerComite": "Envoyer au comité de crédit",
  "bank.composer.transmise": "Offre transmise pour validation",
  "bank.composer.transmiseNote":
    "Le chef du crédit sera notifié. Une fois validée, l'offre indicative sera envoyée au client via Hakili.",
  // Round `L-5`, `M2` (WF-BNK-25, `P3-BKW-003`'s phase-1 half): « Équipe », the drawing's own words.
  "bank.equipe.titre": "Équipe",
  "bank.equipe.roleAnalyste": "Analyste crédit",
  "bank.equipe.disponible": "Disponible",
  "bank.equipe.indisponibleJusquau": "Indisponible jusqu'au {date}",
  "bank.equipe.sonRetour": "son retour",
  "bank.equipe.compteRetireLe": "Compte retiré le {date}",
  "bank.equipe.invitationEnAttente": "Invitation envoyée",
  "bank.equipe.chargeTitre": "Charge et disponibilité",
  "bank.equipe.chargeEchelle":
    "Charge pondérée, pas un nombre de dossiers : une revue pèse {revue} points, un passage en comité {comite}, un dossier nouveau {nouveau}, une validation {validation}, une offre {offre}, une signature {signature}, des conditions préalables {conditions}, parce qu'on y attend un tiers. Un dossier décaissé ne pèse plus rien.",
  "bank.equipe.inviter": "Inviter un analyste",
  "bank.equipe.voirSesDossiers": "Voir ses dossiers ({n})",
  "bank.equipe.declarerAbsence": "Déclarer une absence",
  "bank.equipe.rendreDisponible": "Rendre disponible",
  "bank.equipe.retirer": "Retirer de l'équipe",
  "bank.equipe.retourLe": "Retour le",
  "bank.equipe.confirmerAbsence": "Confirmer l'absence",
  "bank.equipe.confirmerAbsenceMotif":
    "La date de retour est obligatoire : la porte refuse une absence sans date.",
  "bank.equipe.annuler": "Annuler",
  "bank.equipe.inviterTitre": "Inviter un analyste",
  "bank.equipe.inviterSousTitre": "Direction du crédit.",
  "bank.equipe.adresseProfessionnelle": "Adresse professionnelle",
  "bank.equipe.adresseExemple": "prenom.nom@banque.ml",
  "bank.equipe.inviterPhrase": "La personne reçoit un lien d'invitation et définit son mot de passe.",
  "bank.equipe.inviterEntre": "{nom} ne reçoit aucun dossier avant d'avoir accepté l'invitation.",
  "bank.equipe.envoyerInvitation": "Envoyer l'invitation",
  // Round `L-5`, `M1` (WF-BNK-26, `P3-BKW-005`): « Politique », the drawing's own words.
  // `D-1020-4`, `BANK-ROUTES`: an address that is not hers, named.
  "bank.adresse.reserveChefTitre": "Cette page est réservée au chef du crédit",
  "bank.adresse.reserveChefTexte":
    "Votre rôle d'analyste crédit ne donne pas accès à cet écran : les décisions et l'équipe sont tenues par le chef du crédit. Le serveur refuserait de toute façon de vous les lire.",
  "bank.adresse.reserveAnalysteTitre": "Cette page est réservée à l'analyste crédit",
  "bank.adresse.reserveAnalysteTexte":
    "Le chef du crédit suit les dossiers par le pipeline de l'équipe et par ses décisions ; la liste « Dossiers » est celle de l'analyste.",
  "bank.adresse.introuvableTitre": "Cette page n'existe pas",
  "bank.adresse.introuvableTexte": "L'adresse ouverte ne correspond à aucun écran de l'espace banque.",
  "bank.adresse.dossierIntrouvableTitre": "Ce dossier est introuvable",
  "bank.adresse.dossierIntrouvableTexte":
    "Il n'existe pas, ou il n'est pas dans votre portefeuille : le serveur ne vous envoie que les dossiers qui sont les vôtres.",
  "bank.adresse.retourPipeline": "Revenir au pipeline",
  "bank.adresse.retourDecisions": "Revenir aux décisions",
  "bank.politique.titre": "Politique de crédit",
  "bank.politique.sousTitre": "Crédit auto · paramètres appliqués à tous les dossiers de la banque.",
  "bank.politique.lectureSeuleTitre": "Lecture seule.",
  "bank.politique.lectureSeuleSuite":
    "La politique se définit par le chef du crédit, sur cet écran et nulle part ailleurs.",
  "bank.politique.enregistrer": "Enregistrer les modifications",
  "bank.politique.absente": "Aucune politique n'est encore définie pour cette banque.",
  /** `POLICY-FIRST` Screen B (D2 `b284f616`, frames 166, 168, 169; D-1028-6 point 3): while the bank has no credit policy. */
  "bank.politique.manquanteChefTitre": "La politique de crédit de votre banque n'est pas définie.",
  "bank.politique.manquanteChefCorps":
    "Tant qu'elle ne l'est pas, aucun dossier n'arrive dans votre banque : Hakili n'en envoie aucun. Hakili n'applique aucune valeur à votre place.",
  "bank.politique.manquanteChefBouton": "Définir la politique",
  "bank.politique.manquanteAnalysteTitre": "Votre banque n'a pas encore de politique de crédit.",
  "bank.politique.manquanteAnalysteCorps":
    "Aucun nouveau dossier n'arrive tant que le chef du crédit ne l'a pas définie.",
  "bank.politique.manquanteAnalysteBouton": "Voir la politique",
  "bank.politique.manquanteGarde": "La politique de la banque n'est pas définie.",
  "bank.politique.absenteLecture": "Tant qu'elle ne l'est pas, aucun dossier n'arrive dans la banque.",
  "bank.politique.banniere":
    "Ces paramètres pilotent aussi le filtre de Hakili en amont. Chaque règle porte son niveau :",
  "bank.politique.motEliminatoire": "éliminatoire",
  "bank.politique.banniereEliminatoire":
    ", Hakili bloque le dossier avant soumission, il ne vous parvient jamais et le client est invité à corriger ;",
  "bank.politique.motDerogeable": "dérogeable",
  "bank.politique.banniereDerogeable":
    ", le dossier vous parvient, explicitement signalé comme hors norme, et il ne peut être validé qu'au niveau indiqué.",
  "bank.politique.niveauEliminatoire": "Éliminatoire",
  "bank.politique.niveauChef": "Dérogeable · chef du crédit",
  "bank.politique.niveauComite": "Dérogeable · comité",
  "bank.politique.niveauConstantMotif":
    "Le niveau de cette règle est une constante du composeur (05 §2.6.7) ; une colonne par règle est une migration que l'architecte ordonne.",
  "bank.politique.niveauDe": "Niveau de la règle : {regle}",
  "bank.politique.tauxNote": "Taux nominal de la note {note}",
  "bank.politique.grilleTitre": "Grille tarifaire",
  "bank.politique.grilleSousTitre": "taux nominal par note",
  "bank.politique.grilleNote": "Taux nominal de référence par note de solidité.",
  "bank.politique.tolerance": "Tolérance de la banque : {points} point sous la grille avant une dérogation.",
  "bank.politique.tolerancePluriel":
    "Tolérance de la banque : {points} points sous la grille avant une dérogation.",
  "bank.politique.noteLigne": "Note {note}",
  "bank.politique.filOffre": "Offre indicative",
  "bank.politique.filCourant": "Politique",
  "bank.politique.ltvSous": "Fixé par Hakili pour votre banque",
  "bank.politique.dureeMax": "Durée maximale",
  "bank.politique.dureeMaxSous": "Fixée pour toutes les banques",
  "bank.politique.mois": "mois",
  "bank.politique.exigencesTitre": "Exigences non négociables",
  "bank.politique.plafondUemoa":
    "Plafond réglementaire UEMOA de {plafond}\u202f% de TAEG, assurance et frais inclus.",
  "bank.politique.delegationTitre": "Délégation de pouvoir",
  "bank.politique.delegationSousTitre": "qui décide, selon le montant",
  "bank.politique.analyste": "Analyste",
  "bank.politique.analysteAucunEngagement": "aucune décision d'engagement",
  "bank.politique.chefJusqua": "Chef du crédit, jusqu'à",
  /* `P1-BKW-055` (2b), D2 frames 181 to 184 and 197, `design/bank-v2` `f2d25b57` `D2-copy.md` (approved by the architect, 30 October): the switch under
     « Chef du crédit, jusqu'à ». « Activé » is the INTERIM sentence (frame 197), retired at Round 2: that round replaces the value of
     `chefValideTexteOn` and nothing else (done by `P1-BKW-056`). U+0020 before « : » and « ; ». */
  "bank.politique.chefValideTitre": "Le chef du crédit peut valider les dossiers qu'il instruit",
  "bank.politique.chefValideDesactive": "Désactivé",
  "bank.politique.chefValideActive": "Activé",
  "bank.politique.chefValideTexteOff":
    "Désactivé : le chef du crédit ne valide pas ses propres dossiers ; il les réaffecte à un analyste, qui les envoie en validation.",
  /* `P1-BKW-056`: the FINAL « Activé » (D-1029-2 complete server-side, #852 on main; the architect's orders of 31 October 2026), verbatim. The INTERIM
     sentence of D2 frame 197 is KEPT IN THE RECORD here and no longer served: « Activé : le chef du crédit valide et envoie l'offre d'un dossier qu'il
     a instruit ; au-delà de sa délégation, le comité de crédit décide après l'acceptation du client ; pour une dérogation, il réaffecte le dossier à
     un analyste. » It ships back to back with lane C's Round 2 bind (`sendToCommittee`): neither is deployed alone. */
  "bank.politique.chefValideTexteOn":
    "Activé : le chef du crédit valide et envoie l'offre d'un dossier qu'il a instruit ; au-delà de sa délégation, le comité de crédit décide après l'acceptation du client ; une dérogation part au comité avant l'offre.",
  "bank.politique.comite": "Comité de crédit",
  "bank.politique.comiteSous":
    "au-delà de {millions} M FCFA, ou pour toute règle dérogeable au niveau comité, après acceptation de l'offre par le client",
  "bank.politique.vehiculeTitre": "Règles sur le véhicule",
  "bank.politique.vehiculeSousTitre": "conditions d'éligibilité du collatéral",
  "bank.politique.ageOctroi": "Âge maximal à l'octroi",
  "bank.politique.ageEcheance": "Âge maximal à l'échéance",
  "bank.politique.ltv": "LTV maximal sur valeur expertisée",
  "bank.politique.ltvMotif":
    "Le plafond de LTV est la configuration de la banque par Hakili (05 §2.6.1), posée par la porte du responsable des relations bancaires ; il se lit ici et ne s'y modifie pas.",
  "bank.politique.ans": "ans",
  "bank.politique.expertiseObligatoire":
    "Expertise indépendante obligatoire, mandatée par Hakili, valable 90 jours.",
  "bank.politique.emprunteurTitre": "Règles sur l'emprunteur",
  "bank.politique.emprunteurSousTitre": "conditions d'éligibilité du dossier",
  "bank.politique.quotite": "Quotité cessible",
  "bank.politique.anciennete": "Ancienneté minimale dans l'emploi",
  "bank.politique.ancienneteMotif":
    "Aucune colonne ni aucune règle ne porte l'ancienneté aujourd'hui : le chiffre reste un tiret.",
  "bank.politique.exigenceDomiciliation": "Domiciliation irrévocable du salaire",
  "bank.politique.exigenceAssurance": "Assurance emprunteur",
  "bank.politique.exigenceBulletins": "Trois bulletins de salaire et trois relevés",
  "bank.politique.pied":
    "Toute modification est horodatée, attribuée et versée au registre de conformité. Rendre une règle dérogeable augmente le volume de dossiers reçus et le nombre de décisions à arbitrer.",
  // `L-1b`, `M4` (c): the chef's instructions on a returned offer (WF-BNK-10 step 4).
  "bank.revue.consignesTitre": "Consignes du chef du crédit",
  "bank.revue.consignesAide":
    "L'offre a été renvoyée. Traitez chaque consigne, puis recomposez l'offre et renvoyez-la au chef du crédit.",
  "bank.revue.consigneTraitee": "Marquer comme traitée",
  // `L-1b`, `M4` (b): the chef's queue (WF-BNK-20).
  "bank.decisions.enAttente": "{n} décision en attente",
  "bank.decisions.enAttentePlural": "{n} décisions en attente",
  "bank.decisions.toutesConformes": "Toutes sont conformes à la politique.",
  "bank.decisions.unEcart": "{n} porte un écart de politique.",
  "bank.decisions.plusieursEcarts": "{n} portent un écart de politique.",
  "bank.decisions.rienAttend": "Rien n'attend votre décision.",
  "bank.decisions.indicAttente": "Décisions en attente",
  "bank.decisions.indicAttenteSous": "offres, dérogations, inscriptions",
  "bank.decisions.indicMontant": "Montant en jeu",
  "bank.decisions.indicMontantSous": "FCFA à arbitrer",
  "bank.decisions.millions": "{n} M",
  "bank.decisions.indicDelai": "Délai moyen de décision",
  "bank.decisions.indicDelaiSous": "objectif interne 24 h",
  "bank.decisions.heures": "{n} h",
  "bank.decisions.indicConformes": "Conformes à la politique",
  "bank.decisions.indicConformesSous": "sans écart à examiner",
  "bank.decisions.surN": "{n} sur {total}",
  "bank.decisions.filtreToutes": "Toutes",
  "bank.decisions.filtreConformes": "Conformes",
  "bank.decisions.filtreEcarts": "Avec écart",
  "bank.decisions.filtreRetard": "En retard",
  "bank.decisions.ecartIndividuel": "Un écart de politique impose un examen individuel",
  "bank.decisions.typeOffre": "Offre indicative",
  "bank.decisions.typeDerogation": "Dérogation",
  "bank.decisions.resumeConforme": "{mois} mois à {taux}\u202f%, conforme à la politique",
  "bank.decisions.resumeEcart": "{mois} mois à {taux}\u202f%, {n} écart à la politique",
  "bank.decisions.recueInstant": "reçue de votre analyste à l'instant",
  "bank.decisions.aLInstant": "à l'instant",
  "bank.decisions.attenteHeures": "il y a {n} h",
  "bank.decisions.attenteJours": "il y a {n} j",
  "bank.decisions.aucune": "Aucune décision dans ce filtre.",
  "bank.decisions.valideeConditions": "Offre validée aux conditions proposées",
  "bank.decisions.valideeTauxAjuste": "Offre validée au taux ajusté de {taux}\u202f%",
  "bank.decisions.renvoyee": "Renvoyé à l'analyste",
  "bank.decisions.refusee": "Offre refusée",
  "bank.decisions.chipEmise": "Offre émise",
  "bank.decisions.chipRetour": "Retour analyste",
  "bank.decisions.chipRefuse": "Dossier refusé",
  // `U-4`, `M1` (`P1-ITL-033`, WF-BNK-22 step 9): the séance's counsel on the committee card.
  // `L-1b`, `M4` (b): the decision screen (WF-BNK-21).
  "bank.decision.fileRetour": "File de décision",
  "bank.decision.titre": "{type} · {client}",
  "bank.decision.note": "Note {note}",
  "bank.decision.soumisePar": "soumise par {analyste}",
  "bank.decision.sansEcart": "Sans écart",
  "bank.decision.tuileMois": "mois",
  "bank.decision.tuileGrille": "grille {taux}",
  "bank.decision.tuileSansGrille": "aucun taux de grille pour {note}",
  "bank.decision.tuileALaGrille": "{signe}\u00a0{n} à la grille",
  "bank.decision.mensualiteTuile": "Mensualité",
  "bank.decision.tuileInterets": "Produit d'intérêts",
  "bank.decision.assuranceIncluse": "assurance incluse",
  "bank.decision.signee": "signée",
  "bank.decision.metaJustification":
    "{analyste} · analyste crédit · {date} · frais de dossier : banque {banque} · Hakili {hakili} (deux lignes)",
  "bank.decision.barreIndice":
    "Valider autorise l'envoi immédiat de l'offre au client, via Hakili. Votre décision est horodatée et jointe au journal d'audit.",
  "bank.decision.demande": "Ce qui vous est demandé",
  "bank.decision.ecarts": "{n} écart à la politique",
  "bank.decision.ecartsPlural": "{n} écarts à la politique",
  "bank.decision.montant": "Montant",
  "bank.decision.duree": "Durée",
  "bank.decision.mensualite": "Mensualité (assur. incl.)",
  "bank.decision.tauxPropose": "Taux proposé",
  "bank.decision.pct": "{n}\u202f%",
  "bank.decision.justificationTitre": "Justification de l'analyste",
  "bank.decision.justificationAbsente": "Aucune justification : l'offre ne porte pas d'écart à la politique.",
  "bank.decision.produitInteretsCourt": "Produit d'intérêts",
  // `U-4`, `M2`: the reason is now the RECORD's own, not a round number , `P1-ITL-034`'s
  // advisory (« trois dérogations accordées sans incident à ce jour ») needs an OUTCOME the
  // platform keeps nowhere: the arc ends at le décaissement, and ce qui arrive ensuite au prêt
  // est le système de la banque. Le compte seul serait une affirmation sur un risque que
  // personne n'a mesuré (R81).
  // `U-2`, `M3`: Hakili's analysis on the bank's side , the proposals, the grade, the avis, the trail.
  "bank.hakili.proposition": "Proposition de Hakili",
  "bank.hakili.verdictConforme": "Conforme",
  "bank.hakili.verdictNonConforme": "Non conforme",
  "bank.hakili.verdictIncertain": "Incertain",
  "bank.hakili.modeArithmetique": "calcul sur les chiffres du dossier",
  "bank.hakili.modeLecture": "lecture des pièces",
  "bank.hakili.preuvePiece": "Lu sur la pièce {piece}, page {page}",
  "bank.hakili.preuvePieceSansNom": "cette pièce",
  "bank.hakili.preuveZone": "zone à {x}\u202f% / {y}\u202f% de la page",
  "bank.hakili.preuveChiffres": "Chiffres comparés",
  "bank.hakili.confirmer": "Confirmer",
  "bank.hakili.acteConfirme": "Proposition confirmée par {nom} le {quand}",
  "bank.hakili.acteCorrige": "Proposition corrigée par {nom} le {quand}",
  "bank.hakili.acteAncienne": "{acte}, sous l'ancienne politique, à reconfirmer",
  "bank.hakili.noteTitre": "Note de solidité",
  "bank.hakili.noteAbsente": "Aucune note : l'analyse de Hakili n'a pas abouti sur ce dossier.",
  "bank.hakili.noteRederivee":
    "Note retenue après vos corrections : {note}. La grille se compare sur cette note ; la proposition de Hakili reste ci-dessous.",
  "bank.hakili.noteProvisoire": "provisoire",
  "bank.hakili.noteProvisoireTitre": "Note provisoire.",
  "bank.hakili.noteProvisoireUne":
    "1 lecture manquante. Hakili ne propose pas d'offre sur une note provisoire.",
  /** `BAY-NOTE-BOX-WORDS` (D2 `bay.js:42`, `.provnote`): D2's sentence for the one unread reading, after the bold « Note provisoire. »; the figures are the served ones. */
  "bank.hakili.noteProvisoireLectureNonFaite":
    "{n} lecture non faite : « {composante} » n'a pas pu être lue ; elle compte {score} et la note porte le mot « provisoire ». Hakili ne propose pas d'offre sur une note provisoire.",
  "bank.hakili.noteProvisoireDes":
    "{n} lectures manquantes. Hakili ne propose pas d'offre sur une note provisoire.",
  // `ARC2-BAY` (D2 `.cbr.inc`): under a component a provisional note could not read.
  "bank.bay.compNonLu": "non lu · compte {score}",
  // `CAPACITE-NON-CALCULEE`: a capacity that cannot be computed is not a reading not done. No score, no mean, no letter: the state is « note incomplète »,
  // the cause is Hakili's own words (the same on the console card, `console.analyse.*`). `BANK-AGENT`'s two keys for the grid case are replaced by these.
  "bank.hakili.noteIncomplete": "note incomplète",
  // `ARC2-BAY` (D2 `.incring`): the ring of an incomplete note, capitalised as D2 draws it.
  "bank.hakili.noteIncompleteRond": "Note incomplète",
  "bank.hakili.noteIncompleteExplication":
    "Note incomplète : la capacité n'a pas pu être calculée, Hakili ne donne ni moyenne ni lettre et ne propose pas d'offre.",
  "bank.hakili.capaciteNonCalculee": "Capacité non calculée",
  "bank.hakili.capaciteNonCalculeeMot": "non calculée",
  // `ARC2-BAY` (D2 `ui.js` INC, bound to the typed `incomplete.cause` by the AA-AD ruling): the short form and the sentence of each known cause.
  // `tauxMandat` and `inconnue` (D2 frames 137, 138) take the short form of D2; their sentence is the server's own words when it gave one.
  "bank.hakili.causeGrilleCourt": "pas de grille de taux",
  "bank.hakili.causeGrillePhrase":
    "La grille de taux de votre banque n'est pas saisie : sans elle, la mensualité de référence n'existe pas.",
  "bank.hakili.causeQuotiteCessibleCourt": "pas de quotité cessible",
  "bank.hakili.causeQuotiteCessiblePhrase":
    "La quotité cessible n'est pas connue pour ce dossier : la capacité disponible ne peut pas être calculée.",
  "bank.hakili.causeRevenuNetCourt": "pas de salaire net retenu",
  "bank.hakili.causeRevenuNetPhrase":
    "Aucun salaire net n'est retenu pour ce dossier : la capacité disponible ne peut pas être calculée.",
  "bank.hakili.causeEngagementsCourt": "aucun engagement connu",
  "bank.hakili.causeEngagementsPhrase":
    "Les engagements en cours de l'emprunteur ne sont pas connus : la capacité disponible ne peut pas être calculée.",
  "bank.hakili.causeTauxMandatCourt": "pas de taux de mandat",
  "bank.hakili.causeTauxMandatPhrase":
    "Le taux du mandat n'est pas connu pour ce dossier : sans lui, la mensualité de référence ne peut pas être calculée.",
  "bank.hakili.causeInconnueCourt": "cause non précisée",
  "bank.hakili.causeInconnuePhrase":
    "Le calcul de la capacité n'a pas abouti et le serveur n'en a pas donné la raison : rien n'est estimé à sa place.",
  "bank.hakili.causeSuite": "Hakili ne calcule ni note, ni offre.",
  "bank.hakili.causeCarteTitre": "Capacité non calculée : {court}.",
  "bank.bay.propositionIncomplete":
    "Note incomplète : {court}. Hakili ne calcule ni offre, ni TAEG, ni contrôle d'usure, ni écart de taux.",
  /** D2 `bay.js:78`: what follows the served reason when the grid has no row for the letter (the door serves the reason alone). */
  "bank.bay.propositionSansTaux":
    "Hakili ne propose ni durée ni taux et ne calcule aucun écart de taux pour cette note. Le TAEG est calculé sur le taux que l'analyste saisit.",
  /** `BAY-PROVISIONAL-WORDS` (D2 `bay.js:79`, frame 33): the provisional note's own sentence, fixed; the door's « note provisoire, N lectures manquantes » is not printed here. */
  "bank.bay.propositionProvisoire":
    "Note provisoire : une lecture n'a pas été faite. Hakili ne propose ni durée ni taux.",
  "bank.bay.propositionChef": "Votre chef saisit la grille sous «\u202fPolitique\u202f».",
  "bank.bay.compNonCalculee": "non calculée · {court}",
  // `QUOTITE-OPERANDS`: the grade's own figure, never called « quotité » (the quotité is the ratio of the net; this is its share of the cessible quotité).
  "bank.hakili.quotiteUtilisee": "part de la quotité cessible utilisée",
  "console.analyse.quotiteUtilisee": "part de la quotité cessible utilisée",
  "console.analyse.noteIncomplete": "note incomplète",
  "console.analyse.noteIncompleteExplication":
    "Note incomplète : la capacité n'a pas pu être calculée, Hakili ne donne ni moyenne ni lettre et ne propose pas d'offre.",
  "console.analyse.capaciteNonCalculee": "Capacité non calculée",
  "bank.hakili.provisoireComposante": "La composante « {composante} » n'a pas pu être lue.",
  "bank.hakili.provisoireLecture": "La lecture « {lecture} » manque.",
  "bank.hakili.lectureIncidents": "incidents",
  "bank.hakili.lectureMoisNegatifs": "mois à solde négatif ou nul",
  "bank.hakili.lectureRegularite": "régularité du salaire",
  "bank.hakili.lectureApportEvidencie": "apport évidencié",
  "bank.hakili.lectureBulletins": "bulletins de salaire",
  "bank.hakili.lectureReleves": "relevés bancaires",
  "bank.hakili.lectureAttestation": "attestation de travail",
  "bank.hakili.lectureSalaire": "salaire déclaré contre observé",
  "bank.hakili.lectureEmploi": "qualité de l'emploi",
  "bank.hakili.composanteRevenus": "Stabilité des revenus",
  "bank.hakili.composanteGaranties": "Garanties",
  "bank.hakili.composanteHistorique": "Historique bancaire",
  "bank.hakili.composanteApport": "Apport",
  "bank.hakili.composanteCapacite": "Capacité",
  "bank.hakili.avisTitre": "Avis",
  "bank.hakili.avisFavorable": "Favorable",
  "bank.hakili.avisReserve": "Réservé",
  "bank.hakili.avisDefavorable": "Défavorable",
  "bank.hakili.avisAbsent": "Aucun avis : l'analyse de Hakili n'a pas abouti sur ce dossier.",
  "bank.hakili.avisConfirmer": "Confirmer l'avis",
  "bank.hakili.avisEnregistrer": "Enregistrer mon avis",
  "bank.hakili.avisGarde": "L'avis de Hakili est conservé à côté du vôtre.",
  "bank.hakili.avisVide": "Rédigez l'avis avant de l'enregistrer.",
  // `F-2` (BANK-V2): the underwriter's bay. The face's five states are said in words as well as drawn; nothing here names a model, a prompt, a rule or a run.
  "bank.bay.titre": "Hakili, le souscripteur",
  "bank.bay.role": "Souscripteur",
  "bank.bay.nom": "Hakili",
  "bank.bay.mascotte": "Hakili, le veilleur",
  "bank.bay.etatRepos": "Au repos",
  "bank.bay.etatLit": "Il lit, il calcule",
  // D2 says « Terminé »; the catalogue may hold no value equal to a stage label (`i18n.spec.ts`, « holds NO stage label »), and « Terminé » is one. Kept, and listed to the architect.
  "bank.bay.etatTermine": "Lecture terminée",
  "bank.bay.etatSignale": "Il signale",
  "bank.bay.etatEchec": "N'a pas pu finir",
  "bank.bay.charge": "Hakili lit le dossier.",
  "bank.bay.erreurTitre": "Le service ne répond pas.",
  "bank.bay.erreurCorps": "La lecture de Hakili n'est pas arrivée pour ce dossier. Rien n'a été modifié.",
  "bank.bay.echecTitre": "L'analyse de Hakili n'a pas abouti.",
  "bank.bay.echecCorps":
    "La revue se fait à la main : chaque point attend votre verdict. Aucune proposition n'a été inventée.",
  "bank.bay.echecImmobile": "Le dossier n'a pas bougé : l'analyse ne fait jamais avancer une étape.",
  "bank.bay.calme":
    "Hakili n'intervient pas à cette étape. Il garde l'analyse du dossier et répond à vos questions.",
  "bank.bay.datesTitre": "Dates à surveiller",
  "bank.bay.dateRapport": "Rapport d'expertise",
  "bank.bay.dateProforma": "Facture proforma",
  "bank.bay.valableJusquau": "valable jusqu'au {date}",
  "bank.bay.dateAbsente": "la date de validité n'est pas au dossier",
  "bank.bay.datesNote": "Au-delà du rapport, une nouvelle expertise est à mandater et le LTV à recalculer.",
  "bank.bay.gravBloquant": "Bloquant",
  // `ARC2-BAY` (D2 `bay.js` registerBlock): the fold's summary counts the findings and says the blocking ones, in D2's words.
  "bank.bay.registreUn": "1 constat signalé, aucun bloquant",
  "bank.bay.registreAucunBloquant": "{n} constats signalés, aucun bloquant",
  "bank.bay.registreUnBloquant": "{n} constats signalés, dont 1 bloquant",
  "bank.bay.registreDesBloquants": "{n} constats signalés, dont {k} bloquants",
  "bank.bay.registreUnBloquantSeul": "1 constat signalé, bloquant",
  // `ARC2-BAY` (D2 `bay.js` traceList): the four lines of the reading. The count is the number of points the run proposed.
  "bank.bay.traceLecture": "Lecture des pièces",
  "bank.bay.tracePoints": "{n} points proposés",
  "bank.bay.tracePointsUn": "1 point proposé",
  "bank.bay.tracePointsAttente": "Points proposés",
  "bank.bay.traceNote": "Note calculée",
  "bank.bay.traceNoteIncomplete": "Note incomplète",
  "bank.bay.traceAvis": "Avis rédigé",
  "bank.bay.pasDeProposition": "Pas de proposition.",
  // `ARC2-BAY` (D2 `usepropo`): the bay's button under Hakili's proposal.
  "bank.bay.reprendre": "Reprendre la proposition",
  // `BANK-IDS-2`: the bank's grid has no rate for these letters of the ladder (A+, A, B+, B); the letters are the server's typed list, the sentence is ours.
  // `ARC2-BAY` (D2 `ratenote`, frame 18): a bold lead, the rate the Capacité was computed at (the quotité point's own operands), the missing notes.
  "bank.bay.grilleIncompleteTitre": "Grille de taux incomplète.",
  "bank.bay.grilleIncompleteTaux":
    "Capacité calculée au taux le plus haut présent dans la grille ({note}, {taux}\u202f%), sans l'embellir.",
  "bank.bay.grilleIncompleteUne": "La grille n'a pas de taux pour la note {notes}.",
  "bank.bay.grilleIncompleteDes": "La grille n'a pas de taux pour les notes {notes}.",
  // `TERM-FIT` (the designer's three lines): the term and the offer's rate, then the basis in the server's own words, then the figures.
  "bank.bay.proposeDuree": "Hakili propose {n} mois à {taux}\u202f%",
  "bank.bay.proposeChiffres": "Mensualité {mensualite} · TAEG {taeg}\u202f%",
  "bank.bay.porteDossier": "le dossier",
  "bank.bay.porteAnalyse": "l'analyse de Hakili",
  "bank.bay.porteOffre": "l'offre composée",
  "bank.hakili.voirAnalyse": "Voir l'analyse de Hakili",
  // `BANK-AGENT`: what Hakili did, in the banker's French (the old step titles named the artefact, not the act).
  "bank.hakili.faitPoints": "Lecture des pièces par Hakili",
  "bank.hakili.faitNote": "Calcul de la note de solidité par Hakili",
  "bank.hakili.faitOffre": "Calcul de l'offre selon la grille par Hakili",
  "bank.hakili.faitAvis": "Rédaction de l'avis par Hakili",
  "bank.hakili.carte_titre": "Les lectures derrière la note",
  "bank.hakili.carte_capaciteDossier": "Capacité à la soumission",
  "bank.hakili.carte_capaciteObservee": "capacité observée",
  "bank.hakili.carte_charges": "charges mensuelles moyennes",
  "bank.hakili.carte_resteAVivre": "reste à vivre",
  "bank.hakili.aucuneAnalyse": "Aucune analyse de Hakili sur ce dossier : la revue se fait à la main.",
  "bank.hakili.regardQuestion": "Êtes-vous d'accord avec cette note ?",
  "bank.hakili.regardOui": "D'accord",
  "bank.hakili.regardNon": "Pas d'accord",
  "bank.hakili.regardAide":
    "Votre réponse est gardée à côté de la note, pour l'étalonner ; elle ne change ni la note ni votre décision.",
  "bank.hakili.regardAucune": "Aucune seconde lecture enregistrée.",
  // `ARC2-BAY` (D2 `bay.js` gradeBlock): the last line of the fold, the mean and the letter, and the grid's version.
  "bank.hakili.calculPied":
    "Moyenne des cinq composantes : {moyenne} → {note}{provisoire}. Grille v{version}, calculée par le code, jamais par le modèle.",
  "bank.hakili.calculPiedIncomplete":
    "Quatre composantes sur cinq lues. Ni moyenne ni lettre tant que la capacité n'est pas calculée. Grille v{version}, calculée par le code, jamais par le modèle.",
  "bank.hakili.calculPiedProvisoire": " (provisoire)",
  "bank.hakili.regardDesaccord": "En désaccord sur cette composante",
  "bank.hakili.regardMotif": "Motif",
  "bank.hakili.regardMotifChoisir": "Choisir un motif",
  "bank.hakili.regardPhrase": "En quelques mots (facultatif)",
  "bank.hakili.regardEnregistrer": "Enregistrer ma réponse",
  "bank.hakili.regardMotifSous": "Composante sous-estimée",
  "bank.hakili.regardMotifSur": "Composante surestimée",
  "bank.hakili.regardMotifConstat": "Un constat n'est pas reflété",
  "bank.hakili.regardMotifStrict": "Seuil trop strict",
  "bank.hakili.regardMotifSouple": "Seuil trop souple",
  "bank.hakili.regardMotifAutre": "Autre",
  "bank.hakili.regardEnCours": "Enregistrement en cours.",
  "bank.hakili.regardMotifRepondre": "Répondez d'abord : d'accord, ou pas d'accord.",
  "bank.hakili.regardMotifCocher": "Cochez au moins une composante en désaccord.",
  "bank.hakili.regardMotifManque": "Choisissez un motif pour chaque composante cochée.",
  "bank.hakili.regardFaitOui": "Seconde lecture de {nom} le {quand} : d'accord avec la note.",
  "bank.hakili.regardFaitNon": "Seconde lecture de {nom} le {quand} : pas d'accord avec la note.",
  "bank.hakili.regardAncienne": "{regard} Sous l'ancienne politique, à reconfirmer.",
  "bank.hakili.regardFaitNombre": "Réponses enregistrées sur ce dossier : {n} ; la dernière est affichée.",
  "bank.hakili.acteCriblageOui": "Criblage effectué : oui, par {source}",
  "bank.hakili.acteCriblageNon": "Criblage effectué : non",
  "bank.hakili.enCours": "Hakili analyse le dossier ; ses propositions arrivent.",
  "bank.hakili.offreProposee":
    "{duree} mois à {taux}\u202f%, mensualité {mensualite}, TAEG {taeg}\u202f% (note {note})",
  "bank.hakili.offreComite":
    "Montant au-dessus du seuil de délégation : le comité décidera après l'accord du client.",
  "bank.hakili.offreActe": "Vos conditions : {acte}",
  "bank.decision.avisAnalyste": "Avis retenu par l'analyste",
  "bank.decision.avisCorrigePar": "Corrigé par {nom}, le {quand}",
  "bank.decision.dejaPrise": "La décision est prise sur cette offre.",
  "bank.decision.refuser": "Refuser",
  "bank.decision.renvoyer": "Renvoyer",
  "bank.decision.ajusterTaux": "Ajuster le taux",
  "bank.decision.validerEnvoyer": "Valider et envoyer au client",
  "bank.decision.consignesLabel": "Consignes pour l'analyste, une par ligne",
  "bank.decision.consignesAide": "Ce que l'analyste doit traiter avant de renvoyer l'offre.",
  "bank.decision.renvoyerConsignes": "Renvoyer avec ces consignes",
  "bank.decision.motifCode": "Motif de refus",
  "bank.decision.motifCapacite": "Capacité de remboursement insuffisante",
  "bank.decision.motifPolitique": "Hors politique de crédit",
  "bank.decision.motifDocumentation": "Documentation non conforme",
  "bank.decision.motifAutre": "Autre motif",
  "bank.decision.motifLabel": "Motif détaillé, conservé côté banque",
  "bank.decision.motifNote": "Le motif reste à la banque : il n'est jamais transmis au client.",
  "bank.decision.refuserConfirmer": "Refuser le dossier",
  "bank.decision.tauxRetenu": "Taux retenu",
  "bank.decision.tauxMoins": "Baisser le taux d'un dixième de point",
  "bank.decision.tauxPlus": "Monter le taux d'un dixième de point",
  // `COMITE-RECORD-2`, row 64: D2's own sentence (`D2-copy.md:123`), the one the drawing writes under « Ajuster le taux ».
  "bank.decision.arbitrageAppartient":
    "Le calcul est celui du composeur : une règle éliminatoire est refusée, un nouvel écart demande votre propre justification.",
  "bank.decision.validerA": "Valider à {taux}\u202f%",
  "bank.decision.apercuMensualite": "Mensualité",
  "bank.decision.taeg": "TAEG",
  "bank.decision.ecartTitre": "{regle} · écart",
  "bank.decision.regleEliminatoire": "{regle} · règle éliminatoire",
  "bank.decision.blocageRaison": "Une règle éliminatoire ne peut pas faire l'objet d'une dérogation.",
  "bank.decision.justification": "Votre justification",
  "bank.decision.ecartDemandeJustification": "Un écart demande votre justification.",
  "bank.decision.calculEnCours": "Calcul de la mensualité en cours.",
  "bank.decision.envoiEnCours": "Envoi en cours.",
  // `L-1b`, `M4` (c): the analyst's validation panel (WF-BNK-10).
  "bank.validation.enAttente": "En attente de validation du chef du crédit",
  "bank.validation.conditionsSoumises": "Conditions soumises",
  "bank.validation.sansEcart": "Sans écart à la politique",
  "bank.validation.grilleBanque": "Grille de la banque",
  "bank.validation.taegPlafond": "{taeg}\u202f% · plafond {plafond}\u202f%",
  "bank.validation.barreGrille": "Grille",
  "bank.validation.barreProposee": "Proposé",
  "bank.validation.circuit": "Circuit de validation",
  "bank.validation.roleAnalyste": "Analyste crédit",
  "bank.validation.roleChef": "Chef du crédit",
  "bank.validation.roleComite": "Décision finale",
  "bank.validation.chefDuCredit": "Chef du crédit",
  "bank.validation.comite": "Comité de crédit",
  "bank.validation.soumise": "Revue terminée et offre soumise",
  "bank.validation.soumiseA": "Revue terminée et offre soumise le {quand}",
  "bank.validation.delegationJusqua": "Délégation jusqu'à {seuil}",
  "bank.validation.comiteApres": "Après acceptation du client, conformément au circuit",
  "bank.validation.comiteNonRequis": "Non requis : montant dans la délégation du chef du crédit",
  "bank.validation.tracabilite": "Traçabilité",
  "bank.validation.delegation": "Délégation",
  "bank.validation.delegationComite":
    "{montant} dépasse le seuil de délégation du chef du crédit, fixé à {seuil}. Le comité se prononcera après acceptation du client.",
  "bank.validation.delegationDirecte":
    "{montant} relève de la délégation du chef du crédit, dont le seuil est fixé à {seuil}. Aucun passage en comité n'est requis.",
  "bank.validation.delegationInconnue":
    "Le seuil de délégation n'est pas encore défini par le chef du crédit.",
  "bank.validation.decisionBandeau": "Décision du chef du crédit : {decision}, le {quand}",
  "bank.validation.decisionValidee": "Validée",
  "bank.validation.decisionRefusee": "Refusée",
  "bank.validation.decisionRenvoyee": "Renvoyée",
  "bank.validation.motif": "Motif : {motif}",
  // `L-1b`, `M5` (d): the offer-at-client panel (WF-BNK-11).
  "bank.offre.statutLigne": "Envoyée le {sentOn} · valable jusqu'au {validUntil}",
  // Round `L-2`, `M3` (F5): the status line's two states once the client answered.
  "bank.offre.retenueLe": "Offre retenue par le client le {date}",
  "bank.offre.nonRetenueLe": "Offre non retenue par le client, le {date}",
  // Round `L-4`, `M1` (F7, a drawing in words): the clock's end on the status line, and the chip.
  "bank.offre.expireeLe": "Offre expirée sans réponse du client, le {date}",
  // `L-4`, `M2` (F7): declined by the closure of the buyer's file , the row's own reason, not the losing offer's.
  "bank.offre.declineeLe": "Offre déclinée par le client, le {date}",
  "bank.offre.expiree": "Expirée",
  // F7: the `perdu` banner's four copies, by the reason on the row (05 §2.6.15 `lost_reason`).
  // Round T-2 (WF-CON-18 steps 5 and 6, P2-PAY-025): the two-leg board and the file's règlement card.
  "console.reglements.titre": "Règlements de vente",
  "console.reglements.sousTitre":
    "Un règlement par vente, deux volets : la part du concessionnaire, déclarée par lui, et celle de Hakili, constatée sur notre compte. Un seul volet confirmé est un état nommé, jamais une erreur.",
  "console.reglements.vide": "Aucun règlement de vente en cours.",
  // `S2-FINDINGS-2`, `M5`: a refusal or a failed read is not an empty board.
  "console.reglements.erreur":
    "Les règlements de vente n'ont pas pu être lus (accès refusé ou connexion perdue). Rechargez la page ; si cela continue, prévenez l'administration.",
  "console.reglements.voletConcessionnaire": "Volet concessionnaire",
  "console.reglements.voletHakili": "Volet Hakili",
  "console.reglements.confirmeLe": "confirmé le {date}",
  "console.reglements.recuLe": "reçu le {date}",
  "console.reglements.enAttente": "en attente",
  "console.reglements.nonRecuDeclare": "non-réception déclarée le {date}",
  "console.reglements.depuis": "jour dans cet état",
  "console.reglements.depuisPlusieurs": "jours dans cet état",
  "console.reglements.complet": "Règlement complet",
  "console.reglements.partiel": "Règlement partiel",
  "console.reglements.attente": "En attente des deux virements",
  "console.reglements.bon": "Bon de retrait {reference}",
  "console.reglements.bonPresente": "Bon {reference} présenté le {date}",
  "console.reglements.confirmerPreuve": "Confirmer sur preuve",
  // `T-2b`, `M2 (e)`: a cash sale on the board, the buyer the payer.
  "console.reglements.payeurAcheteur": "Vente comptant · payée par l'acheteur, en deux virements",
  "console.reglements.confirmerPreuveMots":
    "La preuve de réception de la part de Hakili (avis de crédit, ligne de relevé).",
  "console.reglements.confirmerPreuveMotif":
    "La part de Hakili se constate par l'administration, sur preuve.",
  "console.reglements.referenceVirement": "Référence du virement (facultatif)",
  "console.reglements.annuler": "Annuler",
  "console.reglements.preuveRequise": "Choisissez la preuve à déposer.",
  "console.dossier.reglementTitre": "Règlement en deux volets",
  "console.dossier.ordreTransmisMotif":
    "Ordre de versement transmis, règlement {reference} ouvert par la banque.",
  "console.dossier.bonRetraitAucunOrdre":
    "Bon de retrait bloqué : aucun ordre de versement transmis par la banque.",
  "console.dossier.bonBloqueHakili": "Bon de retrait bloqué : virement de 10 % à Hakili non reçu.",
  "console.dossier.bonBloqueConcessionnaire":
    "Bon de retrait bloqué : le concessionnaire n'a pas confirmé la réception de ses 90 %.",
  "console.dossier.bonBloqueDeux": "Bon de retrait bloqué : les deux virements de la banque sont attendus.",
  "console.dossier.reglementAucun": "Aucun ordre de versement transmis par la banque.",
  "console.dossier.bonBloque": "Bon de retrait bloqué : {motif}",
  "console.dossier.bonEmis": "Bon de retrait {reference} émis par Hakili le {date}",
  "console.dossier.bonPresente": "Bon de retrait {reference} présenté le {date} : véhicule remis.",
  "bank.perdu.declinee":
    "Offre déclinée par le client. Dossier clos, données conservées pour le calibrage des offres.",
  "bank.perdu.nonRetenue":
    "Offre non retenue par le client. Dossier clos, données conservées pour le calibrage des offres.",
  "bank.perdu.expiree":
    "Offre expirée sans réponse du client. Dossier clos, données conservées pour le calibrage des offres.",
  // Round `L-5`, `M3` (F7 extended, the ended file's words): the header's chip at `perdu`, by the row's reason.
  "bank.perdu.chipExpiree": "Offre expirée",
  "bank.perdu.chipDeclinee": "Offre déclinée",
  "bank.perdu.chipNonRetenue": "Offre non retenue",
  "bank.perdu.chipRetiree": "Dossier retiré",
  // DESIGN SPRINT D-1026-2 (Lane E), D2 frames 50 and 51: the ended file's three cards, D2's words.
  "bank.clos.titre": "Dossier clos.",
  "bank.clos.aucuneAction": "Aucune action n'est possible sur ce dossier. Il reste consultable.",
  "bank.clos.chemin": "Le chemin parcouru",
  "bank.clos.recuRevue": "Reçu de Hakili, revue terminée",
  "bank.clos.derniereOffre": "Dernière offre transmise",
  "bank.clos.montant": "Montant",
  "bank.clos.duree": "Durée",
  "bank.clos.taux": "Taux",
  "bank.clos.valable": "Valable jusqu'au",
  "bank.clos.mois": "{n} mois",
  "bank.clos.expiree": "Offre expirée le {jour} : le client n'a pas répondu",
  "bank.clos.expireeSansJour": "Offre expirée : le client n'a pas répondu",
  "bank.clos.declinee": "Offre déclinée par le client",
  "bank.clos.nonRetenue": "Offre non retenue par le client",
  "bank.clos.retiree": "Étude retirée : le dossier est clos sans suite",
  "bank.clos.sansMotif": "Dossier clos sans suite",
  "bank.clos.revueEnCours": "Revue en cours",
  "bank.clos.pointUn": "{n} point traité",
  "bank.clos.pointsN": "{n} points traités",
  "bank.clos.comite": "Comité de crédit",
  "bank.clos.luHakili": "Ce que Hakili avait lu",
  "bank.clos.noteSolidite": "Note de solidité",
  "bank.clos.noteIncomplete": "Incomplète · capacité non calculée",
  "bank.clos.partQuotite": "Part de la quotité cessible utilisée à {duree}",
  "bank.clos.partQuotiteSans": "Part de la quotité cessible utilisée",
  "bank.clos.ltv": "LTV",
  "bank.perdu.retiree":
    "Dossier retiré par Hakili : le client n'a pas donné suite. Données conservées pour le calibrage.",
  // The drawing's « Le client décline » and « Rouvrir » on a lost file are simulation devices (R81).
  // Round `T-1`, `M1` (WF-BNK-15; `hakili-bank.jsx`, `SignaturePanel`, `signatureDocs`): the
  // signature's words, the drawing's own , the parties and notes per document, the appointment's
  // card, « Ce qui reste après la signature », the terms with the two fees on two lines (I2), the
  // footer's three sentences. The pack's five labels are the rows' (05 §2.6.21), not the screen's.
  "bank.signature.approuveComite": "Approuvé par le comité",
  "bank.signature.approuveDelegation": "Approuvé par délégation du chef du crédit",
  "bank.signature.signeUn": "{n} document signé sur {total}",
  "bank.signature.signes": "{n} documents signés sur {total}",
  "bank.signature.dossier": "Dossier de signature",
  "bank.signature.titreContrat": "Contrat de prêt",
  "bank.signature.titreGage": "Acte de gage du véhicule",
  "bank.signature.titreDomiciliation": "Demande irrévocable de domiciliation",
  "bank.signature.titreAssurance": "Bulletin d'adhésion à l'assurance emprunteur",
  "bank.signature.titrePrelevement": "Autorisation de prélèvement",
  "bank.signature.envoyer": "Envoyer au client",
  "bank.signature.aucunDocument":
    "Les cinq documents du dossier de signature sont générés à l'envoi au client.",
  "bank.signature.unePage": "1 page",
  "bank.signature.pages": "{n} pages",
  "bank.signature.partiesContrat": "Emprunteur et banque",
  "bank.signature.partiesGage": "Emprunteur et banque",
  "bank.signature.partiesDomiciliation": "Emprunteur et employeur",
  "bank.signature.partiesAssurance": "Emprunteur et assureur",
  "bank.signature.partiesPrelevement": "Emprunteur",
  "bank.signature.noteContrat": "Conditions définitives approuvées",
  "bank.signature.noteGage": "À inscrire au registre après signature",
  "bank.signature.noteDomiciliation": "Accusé de réception de l'employeur requis",
  "bank.signature.noteAssurance": "Prime déjà intégrée à la mensualité",
  "bank.signature.notePrelevement": "Première échéance après le décaissement",
  "bank.signature.etatPret": "Prêt à envoyer",
  "bank.signature.etatSigne": "Signé",
  "bank.signature.marquerSigne": "Marquer signé",
  "bank.signature.rdv": "Rendez-vous de signature",
  "bank.signature.rdvAucun": "Aucun rendez-vous fixé à ce jour.",
  "bank.signature.rdvLigne": "{when} · {agence}",
  "bank.signature.rdvFixer": "Fixer le rendez-vous",
  "bank.signature.rdvReporter": "Reporter",
  "bank.signature.rdvAgence": "Agence",
  "bank.signature.rdvJour": "Jour",
  "bank.signature.rdvJourExemple": "22 octobre",
  "bank.signature.rdvAgenceExemple": "Agence Hamdallaye",
  "bank.signature.rdvAnnuler": "Annuler",
  "bank.signature.rdvHeure": "Heure",
  "bank.signature.rdvConfirmer": "Confirmer le rendez-vous",
  "bank.signature.rdvMotifAgence": "Écrivez l'agence.",
  "bank.signature.rdvMotifJour": "Écrivez le jour et le mois, par exemple 22 octobre.",
  "bank.signature.rdvMotifHeure": "Écrivez l'heure, par exemple 10:00.",
  "bank.signature.reste": "Ce qui reste après la signature",
  "bank.signature.resteIntro":
    "Le client dépose son apport au guichet lors du rendez-vous et apporte l'attestation de son assurance tous risques. Ensuite, rien ne dépend plus de lui :",
  "bank.signature.montant": "Montant",
  "bank.signature.resteDomiciliation": "le transfert effectif de la domiciliation du salaire (l'employeur)",
  "bank.signature.resteAssurance": "le certificat de l'assurance emprunteur (l'assureur)",
  "bank.signature.resteGage": "l'inscription du gage sur le véhicule (le notaire)",
  "bank.signature.conditionsDefinitives": "Conditions définitives",
  "bank.signature.duree": "Durée",
  "bank.signature.mensualite": "Mensualité",
  "bank.signature.fraisBanque": "Frais de dossier banque (2\u202f%)",
  "bank.signature.fraisHakili": "Frais de dossier Hakili (2\u202f%)",
  "bank.signature.piedEnvoyer": "Envoyez le dossier de signature au client pour démarrer.",
  "bank.signature.piedAttente": "En attente des signatures manquantes.",
  "bank.signature.passerConditions": "Passer aux conditions préalables",
  // Round `T-1b`, `M1` (WF-BNK-16; `hakili-bank.jsx`, `ConditionsPanel`): the conditions' words, the
  // record's over the drawing's where they differ , the order as TWO transfers (step 5), the
  // footer's two sentences, the party's words keyed by the definition's code.
  "bank.conditions.titre": "Conditions préalables au décaissement",
  "bank.conditions.contratSigneLe": "Contrat signé le {date}",
  "bank.conditions.contratNonSigne": "Contrat non signé",
  "bank.conditions.leveeUne": "{n} condition levée sur {total}",
  "bank.conditions.levees": "{n} conditions levées sur {total}",
  "bank.conditions.decaissementBloque": "Décaissement bloqué",
  "bank.conditions.pretADecaisser": "Prêt à décaisser",
  "bank.conditions.suspensives": "Conditions suspensives",
  "bank.conditions.toutesRequises": "toutes requises avant le versement des fonds",
  "bank.conditions.partieEmployeur": "Employeur",
  "bank.conditions.partieAssureur": "Assureur du contrat groupe de la banque",
  "bank.conditions.partieClient": "Client",
  "bank.conditions.partieNotaire": "Notaire de la banque",
  "bank.conditions.partieHakili": "Hakili",
  "bank.conditions.apportDe": "Dépôt de l'apport de {montant}",
  "bank.conditions.attenteUnJour": "en attente depuis 1 jour",
  "bank.conditions.attenteJours": "en attente depuis {n} jours",
  "bank.conditions.pieceRecueLe": "Pièce reçue le {date}",
  "bank.conditions.etatAttente": "En attente",
  "bank.conditions.etatLevee": "Levée",
  "bank.conditions.relancer": "Relancer",
  "bank.conditions.pieceRecue": "Pièce reçue",
  "bank.conditions.echeances": "Échéances à surveiller",
  // Lane E, M2: the report of the submitted file, opened by the bank (paper, sheet, provenance, validity).
  "bank.rapport.titre": "Rapport d'expertise du dossier",
  "bank.rapport.cabinet": "Établi par le cabinet {cabinet}, version {version}, le {date}",
  "bank.rapport.visa": "Visa de {nom}",
  "bank.rapport.valable": "Valable jusqu'au {date}",
  "bank.rapport.papierOuvrir": "Ouvrir le document du cabinet",
  "bank.rapport.lectureOuvrir": "Ouvrir la lecture Hakili",
  "bank.rapport.papierAbsent": "Aucun document du cabinet n'est joint à ce rapport.",
  "bank.rapport.lectureAbsente": "La lecture Hakili de ce rapport n'est pas encore disponible.",
  "bank.rapport.aucun": "Aucun rapport d'expertise ne tient encore sur ce dossier.",
  "bank.rapport.empreinte": "Empreinte SHA-256 : {empreinte}",
  "bank.rapport.integrite": "Intégrité vérifiée",
  "bank.conditions.echeanceRapport": "Validité du rapport d'expertise",
  "bank.conditions.echeanceRapportNote": "Au-delà, nouvelle expertise à mandater et LTV à recalculer",
  "bank.conditions.echeanceProforma": "Facture proforma du vendeur",
  "bank.conditions.echeanceProformaNote": "Au-delà, prix à reconfirmer auprès du concessionnaire",
  "bank.conditions.echeanceProformaMotif":
    "Le dossier ne porte pas la date de validité de la facture proforma : question au dossier, jamais une date devinée.",
  "bank.conditions.echeancesNote":
    "Hakili surveille ces dates et alerte avant expiration, pour éviter de reprendre l'instruction.",
  "bank.conditions.obligations": "Obligations postérieures au décaissement",
  "bank.conditions.sousJours": "sous {n} jours",
  "bank.conditions.renouvellementAnnuel": "renouvellement annuel",
  "bank.conditions.obligationsNote": "Suivies après le versement, sans bloquer le décaissement.",
  "bank.conditions.instruction": "Instruction de décaissement",
  "bank.conditions.apportClient": "Apport du client",
  "bank.conditions.depose": "déposé",
  "bank.conditions.attendu": "attendu",
  "bank.conditions.pretBanque": "Prêt de la banque (véhicule)",
  "bank.conditions.prixVehicule": "Prix du véhicule",
  "bank.conditions.fraisBanqueFinances": "Frais de dossier banque, financés dans le prêt",
  "bank.conditions.fraisBanqueSignature": "Frais de dossier banque, réglés à la signature",
  "bank.conditions.fraisHakiliFinances": "Frais de dossier Hakili, financés dans le prêt",
  "bank.conditions.fraisHakiliSignature": "Frais de dossier Hakili, réglés avant le rendez-vous de signature",
  "bank.conditions.virement": "Virement",
  "bank.conditions.beneficiaire": "Bénéficiaire",
  "bank.conditions.montant": "Montant",
  "bank.conditions.legConcessionnaire":
    "Le concessionnaire, sur son propre compte · {taux} du prix, contre facture proforma",
  "bank.conditions.legHakili": "Hakili, sur son propre compte · {taux} du prix, contre facture de commission",
  // `T-1c` (I31 amended): the rate's source beside the two legs.
  "bank.conditions.tauxNegocie": "Taux négocié sur la convention de ce concessionnaire.",
  "bank.conditions.tauxStandard": "Taux standard publié par Hakili.",
  "bank.conditions.beneficiaires":
    "Bénéficiaires : le concessionnaire pour {dealerRate} du prix, contre facture proforma, et Hakili pour sa commission de {hakiliRate}, contre facture. Aucun versement direct au client. Aucun versement de la part vendeur à Hakili.",
  "bank.conditions.compteHakili": "Compte de Hakili",
  "bank.conditions.compteHakiliMotif":
    "Les coordonnées bancaires de Hakili se renseignent par l'administration, dans les paramètres de paiement ; aucune n'est publiée.",
  "bank.conditions.compteConcessionnaire": "Compte du concessionnaire",
  "bank.conditions.compteConcessionnaireMotif":
    "Le dossier de la plateforme ne porte les coordonnées bancaires d'aucun concessionnaire : question au dossier, jamais une saisie devinée.",
  "bank.conditions.suiviHakili": "Suivi assuré par Hakili",
  "bank.conditions.d2Domiciliation": "Transfert effectif de la domiciliation du salaire",
  "bank.conditions.d2DomiciliationLigne": "L'employeur · accusé de réception",
  "bank.conditions.d2AssuranceEmprunteur": "Assurance emprunteur",
  "bank.conditions.d2AssuranceEmprunteurLigne":
    "L'assureur du contrat groupe de la banque · certificat d'adhésion",
  "bank.conditions.d2AssuranceAuto": "Assurance tous risques du véhicule",
  "bank.conditions.d2AssuranceAutoLigne":
    "Le client, auprès de son propre assureur · attestation, banque bénéficiaire",
  "bank.conditions.d2Gage": "Inscription du gage au registre",
  "bank.conditions.d2GageLigne": "Le notaire de la banque · récépissé d'inscription",
  "bank.conditions.d2Apport": "Dépôt de l'apport",
  "bank.conditions.d2ApportLigne": "Le client, à l'agence · avis de crédit",
  "bank.conditions.d2FraisHakili": "Frais de dossier Hakili réglés",
  "bank.conditions.d2FraisHakiliLigne": "Hakili · confirmé sur preuve par Hakili",
  "bank.conditions.conditionComite": "Condition du comité : {texte}",
  "bank.conditions.fixeeParComite": "fixée par le comité le {date} · enregistrée par {chef}",
  "bank.conditions.attenteSimple": "en attente",
  "bank.conditions.leveeParHakili": "Hakili la lève sur preuve",
  "bank.conditions.avancement": "Avancement",
  "bank.conditions.leveesMot": "levées",
  "bank.conditions.avancementTexte":
    "Contrat signé le {date}. Le versement reste bloqué tant qu'une condition n'est pas levée.",
  "bank.conditions.passerOrdre": "Passer à l'ordre de paiement",
  "bank.conditions.restanteUne": "1 condition à lever : le versement reste bloqué.",
  "bank.conditions.restantes": "{n} conditions à lever : le versement reste bloqué.",
  "bank.conditions.retourConditions": "Revenir aux conditions",
  "bank.conditions.apres": "Après le décaissement",
  "bank.conditions.apresAide": "Suivi après le versement, sans jamais le bloquer.",
  "bank.conditions.renouvellementChaque": "à chaque renouvellement annuel",
  "bank.conditions.premiereEcheance": "Première échéance",
  "bank.conditions.prelevement": "Prélèvement",
  "bank.conditions.prelevementMots": "automatique, sur salaire domicilié",
  "bank.conditions.retrait": "Retrait du véhicule",
  "bank.conditions.retraitMots": "par le client, à la réception des fonds",
  "bank.conditions.piedBloque": "Le versement reste bloqué tant qu'une condition suspensive n'est pas levée.",
  "bank.conditions.piedLevees":
    "Toutes les conditions suspensives sont levées. Le versement peut être ordonné.",
  // `T-2c`, `M1 (1)`: the lifted sentence followed by the door's own reason while the ordre is greyed.
  "bank.conditions.piedLeveesMais": "Toutes les conditions suspensives sont levées.",
  "bank.conditions.ordonner": "Ordonner les deux virements",
  // Round T-2: the ordre in two acts on the bank's screen, the décaissé state.
  "bank.ordre.aucunVersement": "Aucun versement direct au client, ni de la part vendeur à Hakili.",
  "bank.ordre.aucunVersementSuite":
    "Le concessionnaire reçoit {dealer} du prix contre la facture proforma ; Hakili reçoit ses {hakili} contre sa facture de commission, et rien d'autre.",
  "bank.ordre.virementN": "Virement {n} · {taux} du prix",
  "bank.ordre.surSonCompte": "{nom}, sur son propre compte",
  "bank.ordre.leConcessionnaire": "Le concessionnaire",
  "bank.ordre.hakili": "Hakili",
  "bank.ordre.compte": "Compte",
  "bank.ordre.contre": "Contre",
  "bank.ordre.reference": "Référence",
  "bank.ordre.factureProforma": "facture proforma",
  "bank.ordre.factureCommission": "facture de commission",
  "bank.ordre.totalAvant": "Les deux virements totalisent",
  "bank.ordre.totalApres": ", le prix de vente.",
  "bank.ordre.totalFrais":
    "Les frais de dossier du client, {banque} (banque) et {hakili} (Hakili), restent sur leurs propres lignes : ils n'entrent dans aucun des deux virements.",
  "bank.ordre.transmisD2":
    "Ordre de versement {reference} transmis le {date} : deux virements, deux bénéficiaires. Saisissez les deux références de virement exécutées.",
  "bank.ordre.lectureMotif": "L'ordre n'a pas encore été lu.",
  "bank.ordre.enCours": "Ordre en cours de transmission.",
  "bank.ordre.transmis":
    "Ordre de versement {reference} transmis le {date} : deux virements, deux bénéficiaires.",
  "bank.ordre.beneficiaireConcessionnaire":
    "Le concessionnaire, sur son propre compte, contre facture proforma",
  "bank.ordre.beneficiaireHakili": "Hakili, sur son propre compte, contre facture de commission",
  "bank.ordre.referenceVirementConcessionnaire": "Référence du virement au concessionnaire",
  "bank.ordre.referenceVirementHakili": "Référence du virement à Hakili",
  "bank.ordre.fondsVerses": "Fonds versés",
  "bank.ordre.fondsMotif": "Les deux références de virement sont requises.",
  "bank.ordre.fondsEnregistrement": "Enregistrement…",
  "bank.ordre.fondsEnregistres": "Fonds versés enregistrés.",
  "bank.ordre.fondsEnregistresSuite": "Le dossier passe à « Décaissé » dans un instant.",
  "bank.ordre.fondsEnRetard": "Le dossier n'a pas encore changé d'étape. Rechargez la page dans un instant.",
  "bank.ordre.recharger": "Recharger",
  "bank.ordre.fondsVersesLe":
    "Fonds versés le {date} : les deux virements sont exécutés, le dossier est décaissé.",
  "bank.parcours.titre": "Le parcours",
  "bank.parcours.recu": "Reçu de Hakili",
  "bank.parcours.offre": "Offre validée et envoyée",
  "bank.parcours.signe": "Contrat signé",
  "bank.parcours.conditions": "Conditions levées",
  "bank.parcours.fonds": "Fonds versés",
  "bank.decaisse.executes": "Les deux virements sont exécutés.",
  "bank.decaisse.suite":
    "Le dossier a quitté le pipeline actif ; il reste consultable et nourrit le calibrage de la note de solidité.",
  "bank.decaisse.contre": "Contre",
  "bank.decaisse.prixVente": "Prix de vente · aucun versement direct au client",
  "bank.decaisse.paiement": "Paiement : deux virements exécutés",
  "bank.decaisse.detailArrive": "Le détail des virements arrive dans un instant.",
  "bank.decaisse.sousReference": "Sous-référence",
  "bank.decaisse.dateValeur": "Date de valeur",
  "bank.decaisse.part": "Part",
  "bank.decaisse.obligations": "Obligations après le décaissement",
  "bank.decaisse.obligationCarteGrise": "Carte grise transférée avec la mention du gage",
  "bank.decaisse.obligationRenouvellement": "à chaque renouvellement",
  "bank.offre.passageComite": "Passage en comité requis",
  "bank.offre.passageDirect": "Passage direct à la signature",
  "bank.offre.circuitSansSeuil": "Aucun seuil de délégation en vigueur : le circuit se lit dans Politique.",
  "bank.offre.expireLe": "Expire le {date}",
  "bank.offre.relancer": "Relancer via Hakili",
  "bank.offre.transmise": "Offre transmise",
  "bank.offre.nonContractuelle": "Non contractuelle, sous réserve d'approbation finale",
  "bank.offre.montantFinance": "Montant financé",
  "bank.offre.apportClient": "Apport du client",
  "bank.offre.tauxNominal": "Taux nominal",
  "bank.offre.taeg": "TAEG",
  "bank.offre.garantieDomiciliation": "Domiciliation irrévocable du salaire",
  "bank.offre.garantieAssurance": "Assurance emprunteur",
  "bank.offre.garantieGage": "Gage sur le véhicule",
  "bank.offre.contexte": "Contexte concurrentiel",
  "bank.offre.contexteLigne":
    "{n} établissements saisis par Hakili. Le nombre d'offres transmises au client n'est pas communiqué.",
  "bank.offre.contexteLigneUn":
    "1 établissement saisi par Hakili. Le nombre d'offres transmises au client n'est pas communiqué.",
  "bank.offre.contexteInconnu": "Le nombre d'établissements saisis n'est pas lisible.",
  "bank.offre.contexteNote": "Le détail des offres concurrentes n'est jamais communiqué.",
  // Round `L-3`, `M0 (b)`: WF-BNK-11 step 2's caption in its three states, the offer's own flags.
  "bank.offre.fraisFinancesTous":
    "Frais de dossier banque et Hakili financés dans le prêt, compris dans le montant financé.",
  "bank.offre.fraisFinancesBanque":
    "Frais de dossier banque financés dans le prêt ; frais de dossier Hakili exigibles à la signature.",
  "bank.offre.fraisFinancesHakili":
    "Frais de dossier Hakili financés dans le prêt ; frais de dossier banque exigibles à la signature.",
  "bank.offre.echeancier": "Échéancier",
  "bank.offre.expiration": "Expiration de l'offre",
  "bank.offre.echeancierNote": "Sans réponse à l'expiration, le dossier bascule en offre perdue.",
  "bank.offre.circuitApres": "Circuit après acceptation",
  "bank.offre.circuitComite": "Montant supérieur au seuil : passage en comité requis avant la signature.",
  "bank.offre.circuitDirect":
    "Montant dans la délégation du chef du crédit : passage direct à la signature, sans comité.",
  // `P1-ITL-041`, round 199d: the piece panel. Every sentence below is bound
  // from the recorded mockup , `hakili-bank.jsx:1019` for the hint, `:1618` for
  // the two gestures , except the undo, which no mockup draws and which the
  // ratified row requires.
  "bank.pieces.titre": "Conformité des pièces",
  "bank.pieces.aide":
    "Hakili a contrôlé chaque pièce. Ouvrez un élément pour voir la pièce et ses vérifications.",
  "bank.pieces.ouvrir": "Ouvrir la pièce et les vérifications",
  "bank.pieces.conforme": "Conforme",
  "bank.pieces.signaler": "Signaler",
  "bank.pieces.note": "Votre note",
  "bank.pieces.annuler": "Annuler ma décision",
  "bank.pieces.enLecture": "Lecture en cours",
  "bank.pieces.aucune": "Aucune lecture pour ce dossier.",
  "bank.pieces.noteRequise": "Un signalement doit porter une note.",
  // `P1-ITL-043`, round 199g. The alt text is not decoration: it is what a
  // screen reader reads instead of the page, and a scan with no alt text is a
  // finding an analyst who cannot see it can never check.
  // `P1-ITL-044`, round 199h. `bank:1119` and `:1120` verbatim , the section's
  // title and its hint are the mockup's own words, not a paraphrase.
  "bank.recoupements.titre": "Recoupements et vérifications de fond",
  "bank.recoupements.aide":
    "Chaque donnée a été recoupée entre plusieurs pièces. Ouvrez un point pour voir le détail du recoupement.",
  // Said while the crossing is still running. An empty section and "nothing to
  // report" look identical on a screen, and only one of them is true.
  "bank.recoupements.enCours": "Recoupement des pièces en cours",
  "bank.pieces.page": "Scan de la pièce, tel que Hakili l a lu",
  "bank.pieces.pageIndisponible": "Le scan de cette pièce n a pas pu être affiché.",
  // `P1-ITL-040`, round 199e: illisible finally has somewhere to live, so it
  // finally has something to say. It names the piece and hands the work back
  // to a person, like every other refusal in this slice.
  "bank.pieces.illisible": "Hakili n'a pas pu lire cette pièce. Elle doit être lue par une personne.",
  "bank.pieces.lectureAnterieure": "Lecture précédente",
  "client.nav.recherche": "Rechercher",
  "client.nav.dossier": "Mon dossier",
  "client.nav.suivi": "Suivi",
  "client.nav.profil": "Profil",
  // `P1-CLI-001` and `P1-CLI-002`: WF-CLI-01 and WF-CLI-02, screen by screen.
  // Every sentence a person reads on the way into this product is here, because
  // a string typed into a component is a string outside every check that reads
  // this file , and this is the journey a room watches on 15 November.
  "client.bienvenue.titre": "Acheter votre voiture, sereinement",
  "client.bienvenue.argument1": "Voyez ce que vous pouvez payer chaque mois avant de choisir.",
  "client.bienvenue.argument2": "Un véhicule vérifié par un expert indépendant.",
  "client.bienvenue.argument3": "Plusieurs banques consultées pour vous, en une fois.",
  "client.bienvenue.creer": "Créer mon compte",
  "client.bienvenue.jaiUnCompte": "J'ai déjà un compte",
  "client.numero.titre": "Votre numéro",
  "client.numero.champ": "Numéro de téléphone",
  // `P0-IDN-052`: the country code is a control now, so it needs a name a
  // screen reader can announce. Visible label stays on the number itself.
  "client.numero.indicatif": "Indicatif du pays",
  "client.numero.identifiant": "Votre numéro est votre identifiant. Nos relances passent par WhatsApp.",
  "client.numero.consentement":
    "Nous utilisons vos données pour étudier votre dossier, et pour rien d'autre.",
  "client.numero.ceQueNousNeDemandonsPas":
    "Aucun document, aucun bulletin de salaire et aucun frais pour regarder.",
  "client.numero.recevoir": "Recevoir mon code",
  "client.code.titre": "Votre code",
  "client.code.champ": "Code à six chiffres",
  "client.code.renvoyer": "Renvoyer le code",
  "client.code.parSms": "Recevoir le code par SMS",
  "client.code.confirmer": "Confirmer",
  "client.profil.titre": "Vous",
  "client.profil.nom": "Nom complet",
  "client.profil.ville": "Ville",
  // `REG-PROFILE-001`, Arc B: Step 0 of `docs/claude/underwriter-workflow.md` supersedes WF-CLI-01 step 4's
  // « le salaire et l'employeur viennent plus tard » , the declared profile is entered at registration. The half
  // of the sentence that is still true stays: documents are asked only if a financing is pursued.
  "client.profil.plusTard": "Vos documents ne sont demandés que si vous demandez un financement.",
  "client.profil.continuer": "Continuer",
  // `REG-PROFILE-001`, Arc B, `M1`: the declared profile, on the registration screen and on the profile screen.
  // French copy PROPOSED by the build and not yet ratified by the architect or the founder.
  "client.declaration.titre": "Votre profil déclaré",
  "client.declaration.retour": "Retour à mon compte",
  "client.declaration.nom": "Nom complet",
  "client.declaration.ville": "Ville",
  "client.declaration.telephone": "Téléphone",
  "client.declaration.naissance": "Date de naissance",
  "client.declaration.adresse": "Adresse",
  "client.declaration.erreurNaissance": "La date de naissance doit être une date réelle.",
  "client.declaration.emplois": "Vos emplois",
  "client.declaration.emploi": "Emploi {n}",
  "client.declaration.formel":
    "Un emploi n'est retenu que s'il est formel : son revenu doit apparaître sur votre compte bancaire. Sinon, Hakili ne le prendra pas en compte.",
  "client.declaration.employeur": "Employeur",
  "client.declaration.poste": "Poste",
  "client.declaration.contrat": "Type de contrat",
  "client.declaration.choisir": "Choisir",
  "client.declaration.contratFonctionnaireTitulaire": "Fonctionnaire titulaire",
  "client.declaration.contratCdi": "CDI",
  "client.declaration.contratCdd": "CDD",
  "client.declaration.contratContractuelEtat": "Contractuel de l'État",
  "client.declaration.contratAutre": "Autre",
  "client.declaration.embauche": "Date d'embauche",
  "client.declaration.anciennete": "Ancienneté déclarée, en mois",
  "client.declaration.ancienneteOu": "L'un des deux suffit : la date d'embauche ou l'ancienneté déclarée.",
  "client.declaration.salaire": "Salaire net mensuel (FCFA)",
  "client.declaration.ajouterEmploi": "Ajouter un autre emploi",
  "client.declaration.retirer": "Retirer",
  "client.declaration.erreurEmploi":
    "Complétez cet emploi : employeur, poste, type de contrat, salaire net mensuel, et la date d'embauche ou l'ancienneté.",
  "client.declaration.engagements": "Vos engagements en cours",
  "client.declaration.engagementsQuestion": "Remboursez-vous déjà un ou plusieurs crédits ?",
  "client.declaration.engagementsOui": "J'ai des engagements en cours",
  "client.declaration.engagementsAucun": "Aucun engagement en cours",
  "client.declaration.engagementsObligatoire":
    "Indiquez si vous avez des engagements en cours, ou si vous n'en avez aucun. Cette réponse est nécessaire pour continuer.",
  "client.declaration.engagementsAjoutez":
    "Ajoutez au moins un engagement, ou choisissez « Aucun engagement en cours ».",
  "client.declaration.engagement": "Engagement {n}",
  "client.declaration.preteur": "Établissement prêteur",
  "client.declaration.typeCredit": "Type de crédit",
  "client.declaration.creditPretBancaire": "Prêt bancaire",
  "client.declaration.creditMicrofinance": "Microfinance",
  "client.declaration.creditAvanceEmployeur": "Avance de l'employeur",
  "client.declaration.solde": "Capital restant dû (FCFA)",
  "client.declaration.dateSolde": "Date de ce solde",
  "client.declaration.mensualite": "Mensualité (FCFA)",
  "client.declaration.debutPret": "Début du prêt (facultatif)",
  "client.declaration.finPrevue": "Fin prévue du prêt (facultatif)",
  "client.declaration.ajouterEngagement": "Ajouter un autre engagement",
  "client.declaration.erreurEngagement":
    "Complétez cet engagement : établissement prêteur, type de crédit, capital restant dû, date du solde et mensualité.",
  "client.declaration.erreurDatesPret": "La fin prévue du prêt ne peut pas précéder son début.",
  "client.declaration.finalite":
    "Ces informations servent à chaque vérification d'éligibilité. Les tenir à jour est de votre responsabilité. Une information inexacte ne vous bloque pas : les pièces de votre dossier la vérifieront plus tard.",
  "client.declaration.enregistrer": "Enregistrer mon profil",
  "client.declaration.enregistrement": "Enregistrement en cours",
  // `S5-1 (a)`: a known number reached through « Créer mon compte »; continuing replaces its PIN.
  "client.compteExistant.titre": "Ce numéro a déjà un compte",
  "client.compteExistant.corps":
    "Un compte Hakili existe déjà pour le {numero}. Si vous continuez, vous choisirez un nouveau code : l'ancien code sera remplacé et ne fonctionnera plus.",
  "client.compteExistant.continuer": "Continuer et remplacer mon code",
  "client.compteExistant.annuler": "Annuler, je me connecte avec mon code",
  "client.choixCode.titre": "Choisissez votre code",
  "client.choixCode.champ": "Code à quatre chiffres",
  "client.choixCode.surCetAppareil":
    "Ce code ne fonctionne que sur cet appareil. Un changement de téléphone demande une nouvelle vérification par SMS.",
  "client.choixCode.creer": "Créer mon compte",
  "client.connexion.titre": "Connexion",
  "client.connexion.champ": "Votre code à quatre chiffres",
  "client.connexion.entrer": "Se connecter",
  "client.connexion.oublie": "J'ai oublié mon code",
  "client.connexion.pasVous": "Ce n'est pas vous ?",
  "client.dossier.titre": "Mon dossier",
  "client.dossier.conditions": "Apport {apport} · {duree}",
  "client.dossier.apportPct": "{apport} %",
  // `OD-37`, round 7 (workflow v3 Step 0, section 3.6; the founder's apport rulings of 8 October): where the
  // buyer's deposit is, declared at the step where the file is opened. The sentence is the founder's, word for word.
  "client.apport.titre": "Votre apport",
  "client.apport.question": "D'où vient votre apport ?",
  "client.apport.origineEpargneConstituee": "Épargne constituée",
  "client.apport.origineVirementOuDonTiers": "Virement ou don d'une autre personne",
  "client.apport.origineRevenuActivite": "Revenu d'une activité",
  "client.apport.origineVenteBien": "Vente d'un bien",
  "client.apport.origineAutre": "Autre",
  "client.apport.precisionLibelle": "Précisez",
  "client.apport.consigneVirementOuDonTiers":
    "Indiquez le nom de la personne, votre lien avec elle et le motif de son aide.",
  "client.apport.consigneRevenuActivite": "Indiquez laquelle de vos activités vous a procuré cet apport.",
  "client.apport.consigneVenteBien": "Indiquez quel bien vous avez vendu.",
  "client.apport.consigneAutre": "Précisez d'où vient cet apport.",
  "client.apport.refusCompteFormel": "L'apport doit se trouver sur un compte bancaire formel à votre nom",
  "client.apport.compteSalaireQuestion":
    "Votre apport se trouve-t-il sur le compte où votre salaire est versé ?",
  "client.apport.oui": "Oui",
  "client.apport.non": "Non",
  "client.apport.releveADeposer":
    "Vous aurez à déposer le relevé du compte où se trouve votre apport, avec vos autres pièces.",
  "client.dossier.pieceRetiree": "pièce retirée le {date}",
  "client.dossier.dureeMois": "{duree} mois",
  // `round CW`, `CW.1`: « Suivi » is the drawing's fourth tab , where the
  // buyer's demand stands, as against « Mon dossier », which is his pieces.
  // `CW.5` builds the screen; the tab and its stub arrive here so the bar is
  // the drawing's from the first pair.
  "client.suivi.titre": "Où en est ma demande",
  "client.suivi.resume":
    "L'avancement de votre demande, étape par étape, et ce qui attend un geste de votre part.",
  "client.suivi.enCours": "· en cours",
  "client.suivi.completer": "Compléter mon dossier",
  /**
   * `round CW`, `CW.5`: the buyer's sentence for each of 2.4.1's THIRTEEN
   * stages, and they are NEW COPY.
   *
   * The drawing has sixteen states in its own vocabulary and the record has
   * thirteen stages in 2.4.1's, so the two do not map one to one. These carry
   * the drawing's meaning onto the record's stages with **every figure
   * removed** , no count of banks, no delay in hours, no date of a committee ,
   * because no door answers one, and a screen that printed them would be
   * making a promise on the product's behalf. The addendum records them for
   * ratification.
   *
   * The chip above them is NOT here: « À vous de jouer » and « Terminé » are
   * `domain-types`' vocabulary and this catalogue holds no stage label.
   */
  /**
   * `round C3-A`, `C3-A.4`: **the card said two things at once, and the pair is
   * what showed it.**
   *
   * It read « Rien ne vous est demandé pour l'instant » under a chip saying
   * « À vous de jouer » and above a control saying « Régler les frais ». Both
   * halves were written when `intake` was Hakili's own work; the architect's
   * ruling of 10 September made it the BUYER's , it is « paiement » seen from
   * his side , and the chip and the control moved with the ruling while this
   * sentence did not. A card that contradicts itself is worse than either half
   * alone, because a buyer cannot tell which one to believe.
   */
  "client.suivi.etapeIntake":
    "Votre dossier est ouvert. Les frais de vérification sont à régler pour que nous mandations l'expert.",
  /**
   * `round C3-H`: the fee received, the launch his , the drawing's own words
   * (`hakili-client.jsx`, « Vous décidez du départ »).
   */
  // Round `PAY-P`, `M3` (the founder, 13 September: « I agree with your recommendation »):
  // the buyer reads the DATE of his control, never a delay , the 48 hours leave.
  "client.suivi.etapeIntakeLance":
    "Vos frais sont reçus. Nous mandatons l'expert et vérifions la visite technique dès que vous lancez.",
  "client.suivi.controleLe": "Contrôle le {day} ; le rapport suit le passage.",
  "client.suivi.controleSansJeudi":
    "Aucun jeudi de contrôle n'est encore fixé pour {city} ; nous vous prévenons dès qu'une date est ouverte.",
  "client.suivi.etapeCollecte":
    "Il nous manque des pièces pour aller plus loin. Vous les déposez depuis « Mon dossier », et une photo nette suffit.",
  "client.suivi.etapeExpertise":
    "Un expert agréé examine le véhicule. Nous vous prévenons dès que son rapport nous parvient.",
  "client.suivi.etapeAnalyse":
    "Nous vérifions votre dossier et le comparons aux critères des banques partenaires.",
  // `C7.2`, WF-CLI-19: « <chargé> contrôle vos pièces » , the name is the door's.
  "client.suivi.controleVosPieces": "contrôle vos pièces. Nous vous prévenons dès que c'est fait.",
  "client.suivi.etapePret":
    "Votre dossier est complet et vérifié. Nous le soumettons aux banques partenaires.",
  // `L-1b`, `M5` (c): the drawing's « offres » card at `soumis`, the count from the door.
  // Round `L-3`, `M2` (WF-CLI-20, `hakili-client.jsx`'s `banques` and `complement`): the study in aggregate.
  "client.suivi.banquesTitre": "Envoyé aux banques",
  "client.suivi.banquesDetail": "Une banque étudie votre dossier.",
  "client.suivi.banquesDetailPluriel": "{n} banques étudient votre dossier.",
  "client.suivi.banquesDelai": "Réponse attendue sous {n} jours ouvrés.",
  "client.suivi.complementTitre": "Une pièce complémentaire est demandée",
  "client.suivi.complementDetail":
    "L'une des banques souhaite un document de plus avant de se prononcer. Ce n'est pas un refus : c'est une étape normale de l'étude, et les autres banques poursuivent leur analyse pendant ce temps.",
  "client.suivi.complementFournir": "Fournir la pièce demandée",
  "client.suivi.etudeTitre": "Où en est l'étude",
  "client.suivi.etudeBanques": "Banques ayant reçu votre dossier",
  "client.suivi.etudeEnCours": "Études en cours",
  "client.suivi.etudeComplement": "Pièce complémentaire demandée",
  "client.suivi.etudeOffres": "Offres reçues",
  "client.suivi.etudeOffresAucune": "0 pour l'instant",
  "client.suivi.etudePied":
    "Chaque banque décide de son côté. Vos offres apparaîtront ici dès qu'elles arrivent, et nous ne vous solliciterons que si un document vous est demandé.",
  "client.dossier.complementLabel": "Demandé par l'une des banques",
  "client.dossier.complementPasRefus":
    "Ce n'est pas un refus : la banque complète simplement son analyse, et les autres poursuivent la leur.",
  "client.dossier.complementEnvoyer": "Envoyer la pièce",
  "client.dossier.complementPied":
    "{charge} la transmet à la banque dès réception, sans nouvelle démarche de votre part.",
  "client.dossier.complementPiedSansNom":
    "Votre chargé de dossier la transmet à la banque dès réception, sans nouvelle démarche de votre part.",
  "client.suivi.offresRecues": "{n} offre reçue",
  "client.suivi.offresRecuesPlural": "{n} offres reçues",
  "client.suivi.offresComparez": "Comparez les propositions et choisissez celle qui vous convient.",
  "client.suivi.voirOffres": "Voir mes offres",
  // Round `L-2`, `M4` (A8, WF-CLI-24 step 1): the card after the buyer's choice.
  "client.suivi.offreAccepteeTitre": "Offre acceptée, accord définitif en cours",
  "client.suivi.circuitComite": "La banque retenue présente votre dossier à son comité.",
  "client.suivi.circuitSignature": "{bank} prépare votre accord définitif et votre rendez-vous de signature.",
  "client.suivi.circuitInconnu": "{bank} vous précisera le circuit de son accord définitif.",
  "client.suivi.revoirOffres": "Revoir les offres et mon choix",
  // Round `T-1`, `M3` (WF-CLI-24 steps 2 to 5, `hakili-client.jsx`, `etatsDossier.signature` and the
  // `Suivi` signature block; A12, the insurance parked by record , the detail and the notice amended,
  // both texts kept in 03): the buyer's signature card.
  "client.suivi.signatureTitre": "Prêt accordé, deux choses à faire",
  "client.suivi.signatureDetail":
    "Apportez votre attestation d'assurance tous risques au nom de la banque, puis un seul passage en agence où vous signez, déposez votre apport, et tout ce qui vous concerne est réglé.",
  "client.suivi.signatureNoticeTitre": "Une chose à préparer avant d'y aller",
  "client.suivi.signatureNotice":
    "Pensez à faire ajouter la banque comme bénéficiaire, et apportez l'attestation au rendez-vous.",
  "client.suivi.signatureRdvTitre": "Votre rendez-vous de signature",
  "client.suivi.signatureRdvLigne": "{agence}, {when}.",
  "client.suivi.signatureRdvAFixer": "La banque vous précisera le lieu, la date et l'heure.",
  "client.suivi.signatureRdvCharge": "Présence de votre chargé de dossier : {presence}.",
  "client.suivi.signatureVoirDocuments": "Voir les documents à signer",
  "client.suivi.apporterTitre": "Ce qu'il faut apporter",
  "client.suivi.apporterSousTitre":
    "Tout se règle pendant ce rendez-vous. Après, vous n'aurez plus rien à faire.",
  "client.suivi.apporterIdentite": "Votre pièce d'identité",
  "client.suivi.apporterIdentiteDetail": "L'originale, celle déposée dans votre dossier",
  "client.suivi.apporterApport": "Votre apport de {amount}",
  "client.suivi.apporterApportDetail":
    "En espèces au guichet, ou par virement effectué avant le rendez-vous avec le reçu à présenter",
  "client.suivi.apporterAssurance": "Votre attestation d'assurance tous risques, banque bénéficiaire",
  "client.suivi.apporterAssuranceDetail":
    "L'attestation de votre assureur, avec la banque désignée bénéficiaire.",
  "client.suivi.apporterFraisBanque": "Les frais de dossier de {bank} : {amount}, réglés à {bank}",
  "client.suivi.apporterFraisHakili": "Les frais de dossier Hakili : {amount}, réglés à Hakili",
  "client.suivi.apporterFraisDetail":
    "Réglés le jour de la signature, au guichet de la banque, sans intérêts dessus : ils ne sont pas financés.",
  // Round `T-1c`, `M2` (WF-CLI-24 step 5 amended, the founder's decision of 18 September, A14 to the designer):
  // Hakili's fee leaves « Ce qu'il faut apporter » , it is paid to Hakili, by the published means, before the
  // appointment; the block « Frais de dossier Hakili » on the signature card, the buyer's own declaration.
  "client.suivi.fraisTitre": "Frais de dossier Hakili",
  "client.suivi.fraisMontant": "{amount}, réglés à Hakili avant le rendez-vous",
  "client.suivi.fraisFinances": "{amount}, financés par la banque : rien à régler avant le rendez-vous.",
  "client.suivi.fraisMoyensTitre": "Par l'un des moyens publiés",
  "client.suivi.fraisVirementCompte": "{bank} · {holder} · {number}",
  "client.suivi.fraisEtatARegler": "À régler",
  "client.suivi.fraisEtatDeclare": "Déclarés réglés",
  "client.suivi.fraisEtatEncaisse": "Encaissés par Hakili",
  "client.suivi.fraisEtatFinances": "Financés par la banque",
  "client.suivi.fraisDeclarer": "J'ai réglé les frais de dossier",
  "client.suivi.fraisDeclarationTitre": "Comment avez-vous réglé ?",
  "client.suivi.fraisDeclarationMoyen": "Moyen utilisé",
  "client.suivi.fraisDeclarationReference": "Référence du paiement",
  "client.suivi.fraisDeclarationReferenceAide": "Le numéro de transaction ou la référence de votre virement.",
  "client.suivi.fraisDeclarationEnvoyer": "Déclarer le règlement",
  "client.suivi.fraisDeclarationAnnuler": "Annuler",
  "client.suivi.fraisDeclare": "Déclaré le {date} ({means}, réf. {reference}). Hakili confirme à réception.",
  "client.suivi.fraisEncaisse": "Encaissés le {date}. Cette formalité est réglée.",
  "client.suivi.fraisPied":
    "Ces frais se règlent à Hakili et à Hakili seulement, jamais au guichet de la banque. Hakili les confirme sur la preuve du règlement.",
  "client.suivi.apporterGarantie": "Votre garantie mécanique de {amount}",
  "client.suivi.apporterGarantieDetail":
    "Réglée comptant le même jour : la banque ne la finance pas, vous avez choisi de la garder",
  // Round `T-1b`, `M3` (WF-CLI-26; `hakili-client.jsx`, the `Suivi` conditions block and « Comment l'argent
  // circule »): the buyer's conditions card , WF-CLI-26's detail, the five rows' words, the two transfers.
  "client.suivi.conditionsTitre": "Dernières formalités",
  "client.suivi.conditionsDetail":
    "Vous avez tout réglé en agence, assurance comprise. Il reste des formalités entre la banque, votre employeur, votre assureur et le notaire.",
  "client.suivi.condTitre": "Ce qui reste avant le versement",
  "client.suivi.condEntete": "Le vendeur n'est payé qu'une fois tout ceci levé.",
  "client.suivi.condUnPointVous": "Un point vous revient encore, signalé ci-dessous.",
  "client.suivi.condRienVous": "Rien ne dépend plus de vous.",
  "client.suivi.condApport": "Apport déposé sur votre compte",
  // `T-1c`, `M2`: WF-CLI-26's first row split by record , the deposit (the bank's fifth condition) and the
  // fee (Hakili's fact, absent when the bank finances it); the detail conditional on the fee's fact.
  "client.suivi.condFrais": "Frais de dossier Hakili réglés",
  "client.suivi.condFraisQui": "Réglés à Hakili avant le rendez-vous, confirmés sur preuve",
  "client.suivi.condFraisQuoi": "Une fois encaissés par Hakili, plus rien ne dépend de vous.",
  "client.suivi.condFraisDeclareQuoi":
    "Vous les avez déclarés réglés ; Hakili confirme à réception de la preuve.",
  "client.suivi.attenteDepuis": "Votre dossier attend une action de votre part depuis {n} jours.",
  "client.suivi.conditionsDetailReste":
    "Il vous reste les frais de dossier Hakili à régler. Le reste des formalités se joue entre la banque, votre employeur, votre assureur et le notaire.",
  "client.suivi.condApportQui": "Fait en agence, le jour de la signature",
  "client.suivi.condApportQuoi": "Votre apport reste sur votre compte jusqu'au paiement du vendeur.",
  "client.suivi.condDomiciliation": "Transfert de votre domiciliation de salaire",
  "client.suivi.condDomiciliationQui": "Votre employeur",
  "client.suivi.condDomiciliationQuoi":
    "Votre salaire sera désormais versé sur votre compte à la banque, qui y prélèvera chaque échéance.",
  "client.suivi.condAssuranceEmprunteur": "Assurance emprunteur",
  "client.suivi.condAssuranceEmprunteurQui": "L'assureur de la banque, sur le bulletin signé en agence",
  "client.suivi.condAssuranceEmprunteurQuoi":
    "Elle assure le prêt, pas la voiture : si vous décédez ou devenez invalide, elle rembourse ce qu'il reste à payer, et votre famille n'hérite pas de la dette. Sa prime est déjà comprise dans votre mensualité.",
  "client.suivi.condAssuranceAuto": "Assurance du véhicule, tous risques",
  "client.suivi.condAssuranceAutoQui": "Votre assurance actuelle, banque ajoutée comme bénéficiaire",
  "client.suivi.condAssuranceAutoQuoi":
    "Elle assure la voiture, pas le prêt. Obligatoire pour circuler et exigée par la banque tant que le prêt court, puisque le véhicule la garantit.",
  "client.suivi.condGage": "Inscription du gage sur le véhicule",
  "client.suivi.condGageQui": "La banque, par son notaire, au registre du commerce",
  "client.suivi.condGageQuoi":
    "Le gage inscrit la banque comme créancier sur le véhicule et se mentionne sur la carte grise. Vous en restez propriétaire, mais vous ne pouvez pas le revendre tant que le prêt n'est pas soldé. Hakili suit la démarche sans la faire.",
  "client.suivi.argentTitre": "Comment l'argent circule",
  "client.suivi.argentApport": "Votre apport",
  "client.suivi.argentPret": "Montant financé pour le véhicule",
  "client.suivi.argentGarantieFinancee": "Dont garantie mécanique financée",
  "client.suivi.argentGarantieComptant": "Garantie mécanique réglée comptant, par vous",
  "client.suivi.argentVendeur": "{rate} % du prix, directement au vendeur",
  "client.suivi.argentHakili": "{rate} % du prix, directement à Hakili",
  "client.suivi.argentFraisBanqueBanque": "Frais de dossier banque, versés par la banque",
  "client.suivi.argentFraisBanqueVous": "Frais de dossier banque, réglés par vous",
  "client.suivi.argentFraisHakiliBanque": "Frais de dossier Hakili, versés par la banque",
  "client.suivi.argentFraisHakiliVous": "Frais de dossier Hakili, réglés par vous à Hakili",
  "client.suivi.argentFraisTotal": "Total des frais à régler",
  "client.suivi.argentPhrase":
    "La banque paie chaque bénéficiaire directement. Le total versé reste le prix du véhicule, il n'y a rien de plus à votre charge.",
  "client.suivi.argentPiedFinances":
    "Vous n'avez rien à sortir pour ces frais : la banque règle directement Hakili et sa propre part au moment du versement, et tout est compris dans votre prêt.",
  "client.suivi.argentPiedVous":
    "Vous réglez chaque frais à son destinataire : celui de la banque au guichet le jour de la signature, celui de Hakili à Hakili avant le rendez-vous. Ils ne sont pas financés, donc vous ne payez pas d'intérêts dessus.",
  "client.suivi.apporterPied":
    "Votre apport est déposé sur votre propre compte à la banque, il ne nous est pas versé. La banque ne le débite qu'au moment de payer le vendeur. Si une formalité échouait ensuite, cet argent resterait le vôtre.",
  // `L-5`, `M3`: the same link on the expired state, without a choice that was never made.
  "client.suivi.revoirOffresExpirees": "Revoir les offres expirées",
  // Round `L-2`, `M5` (WF-CLI-23): the warranty the bank did not finance, the drawing's card.
  "client.suivi.garantieTitre": "La banque ne finance pas la garantie mécanique",
  "client.suivi.garantieDetail":
    "{bank} a accordé votre prêt, mais n'a pas retenu le financement de la garantie mécanique de {amount}. Ce n'est pas un refus de la garantie : vous pouvez la garder en la réglant comptant, hors prêt, ou la retirer. Votre mensualité ne change pas, la garantie n'y est pas comprise.",
  "client.suivi.garantieComptant": "La régler comptant",
  "client.suivi.garantieRetirer": "Retirer la garantie",
  "client.suivi.etapeSoumis":
    "Les banques partenaires étudient votre dossier. Chacune décide de son côté, et nous ne vous solliciterons que si un document vous est demandé.",
  "client.suivi.etapeOffre":
    "Vous avez retenu une offre. Nous poursuivons avec la banque que vous avez choisie.",
  "client.suivi.etapeAssurance":
    "L'assurance de votre prêt est en cours de placement. Vous n'avez rien à avancer.",
  "client.suivi.etapeConditions":
    "Il reste des formalités avant le versement au vendeur. Nous les suivons pour vous et vous prévenons dès qu'une signature vous revient.",
  "client.suivi.etapeDecaisse":
    "Le vendeur a été payé. Vous pouvez récupérer votre véhicule chez le concessionnaire.",
  // 2.4.1's firm rule holds in all three: never the name of a bank, and never
  // which one refused. A refus nominatif crée un doute sur lequel le client ne
  // peut pas agir.
  "client.suivi.etapeRefuse":
    "Votre dossier ne remplit pas les critères des banques partenaires. Aucune d'elles ne l'a reçu.",
  // Round `L-3b`, `M3` (`P1-CLW-022`, WF-CLI-21, `DECISION ADDED 7`): variant B's sentence is the record's own.
  "client.suivi.etapeRefuseBanque": "Aucune des banques saisies n'a retenu votre dossier.",
  // Round `L-4`, `M1` (A11): every offer received has expired, none retained , the buyer's card never « refusé ».
  "client.suivi.offresExpireesTitre": "Vos offres ont expiré",
  "client.suivi.offresExpireesDetail": "Les offres reçues ont expiré. {charge} revient vers vous.",
  // The refused state's card (the drawing's `dossierRefuse`), the chip, the two variants' cards (M0 (c), A10).
  "client.suivi.refuseChip": "Dossier refusé",
  "client.suivi.refuseTitre": "Votre dossier n'est pas éligible au financement",
  "client.suivi.refuseRoseTitreA": "Ce dossier ne peut pas être transmis",
  "client.suivi.refuseRoseDetailA":
    "La vérification de vos documents a montré un écart avec ce qui était nécessaire pour ce financement. Cette décision ne dépend pas d'une banque en particulier : aucune banque ne recevra ce dossier, et il n'y a rien à corriger ici.",
  "client.suivi.refuseRoseTitreB": "Ce dossier n'a pas été retenu",
  "client.suivi.refuseRoseDetailB":
    "Chaque banque saisie a étudié votre dossier et aucune ne l'a retenu. Cette décision ne dépend pas d'une banque en particulier ; il n'y a rien à corriger ici.",
  "client.suivi.refuseFrais":
    "Les frais de vérification du véhicule restent acquis : le contrôle du véhicule lui-même a bien eu lieu et n'est pas remis en cause. Vous gardez le rapport et pouvez toujours acheter ce véhicule comptant si vous le souhaitez.",
  "client.suivi.refuseVoirVehicules": "Voir des véhicules dans votre capacité",
  "client.suivi.refuseComptant": "Acheter comptant, sans crédit",
  "client.suivi.refuseComptantMotif":
    "Le paiement comptant après un refus bancaire n'est pas encore ouvert ; les frais de vérification ne seront pas re-facturés.",
  "client.suivi.etapeSansSuite": "Ce dossier est clos sans suite.",
  // `round K-2` (resumed), `M4` (WF-CLI-16's neighbour, `DECISION ADDED 08-17`): the seller's breach
  // in the record's own words; the refund's two states.
  // Round T-2 (WF-CLI-27): the bon de retrait, or the block and its reason; the money's circuit.
  "client.retrait.bloqueTitre": "Retrait bloqué",
  "client.retrait.aucun": "Aucun règlement n'est encore ordonné par la banque.",
  "client.retrait.pretTitre": "Vous pouvez récupérer votre véhicule",
  "client.retrait.pretMots":
    "Le vendeur a confirmé avoir été payé. Présentez-vous au concessionnaire avec les pièces ci-dessous.",
  "client.retrait.bon": "Votre bon de retrait",
  "client.retrait.bonReference": "Référence {reference}",
  "client.retrait.bonVehicule": "Véhicule",
  "client.retrait.bonVendeur": "Vendeur",
  // `T-2b`, `M0 (a)`: the shares are the settlement row's frozen rate, never 90 and 10 as constants.
  "client.retrait.voletVendeur": "Vendeur réglé par {payeur}, {part} %, confirmé le {date}",
  "client.retrait.voletHakili": "Commission Hakili, {taux} %, reçue le {date}",
  "client.retrait.payeurBanque": "la banque",
  "client.retrait.payeurVous": "vous",
  "client.retrait.bonAtteste":
    "Le vendeur a confirmé avoir été réglé par la banque ; ce bon atteste que le véhicule peut vous être remis.",
  // `T-2b`, found at the park's shooting (client row 107): on the cash path the seller was paid by
  // the buyer himself, never by a bank , the bon's sentence names the right payer.
  "client.retrait.bonAttesteComptant":
    "Le vendeur a confirmé avoir reçu votre virement ; ce bon atteste que le véhicule peut vous être remis.",
  "client.retrait.aPresenter": "À présenter au concessionnaire",
  "client.retrait.pieceIdentite": "Votre pièce d'identité",
  "client.retrait.bonDeRetrait": "Ce bon de retrait ({reference})",
  "client.retrait.attestation": "Votre attestation d'assurance",
  "client.retrait.aVerifier": "À vérifier avant de partir",
  "client.retrait.verif1": "Le numéro de châssis correspond au rapport.",
  "client.retrait.verif2": "Le kilométrage n'a pas bougé anormalement.",
  "client.retrait.verif3": "L'état général correspond aux photos de l'expert.",
  "client.retrait.verif4": "Vous repartez avec la carte grise ou son récépissé de mutation.",
  "client.retrait.ecart":
    "Un écart avec le rapport ? Ne signez pas la remise et appelez votre chargé de dossier. C'est exactement ce à quoi sert l'expertise que vous avez payée.",
  "client.retrait.pdf": "Télécharger le bon (PDF)",
  "client.retrait.pdfMotif": "Le PDF du bon n'est pas encore disponible ; la référence affichée fait foi.",
  "client.retrait.remisLe": "Véhicule remis le {date}. Bonne route.",
  "client.suivi.circuitMots":
    "La banque vire directement au vendeur et à Hakili, en deux virements. Aucun fonds ne transite par Hakili.",
  "client.suivi.circuitVendeur": "Part du vendeur",
  "client.suivi.circuitHakili": "Part de Hakili",
  "client.suivi.argentConfirme": "confirmée le {date}",
  "client.suivi.argentRecue": "reçue le {date}",
  "client.suivi.argentAttente": "en attente",
  "client.suivi.argentFin": "Les deux virements sont constatés : votre bon de retrait est émis.",
  "client.suivi.voirRetrait": "Afficher mon bon de retrait",
  "client.suivi.closManquement":
    "Votre dossier est clos : le véhicule a été retiré par le vendeur pendant votre réservation. Hakili vous rembourse les frais que vous avez versés : {amount}.",
  "client.suivi.closManquementSansFrais":
    "Votre dossier est clos : le véhicule a été retiré par le vendeur pendant votre réservation.",
  "client.suivi.remboursementDu": "Remboursement en cours, par Hakili, sur le moyen que vous aviez utilisé.",
  // `T-2b` (`P2-PAY-037`): the file ended without the bank's funds; Hakili's file fee goes back.
  "client.suivi.closFraisRembourses":
    "Votre dossier est clos sans que la banque ait versé les fonds. Hakili vous rembourse les frais de dossier que vous aviez réglés : {amount}.",
  "client.suivi.rembourseLe": "Remboursé le {date}.",
  /**
   * `round W-13`, `M1`: the card follows the VERDICT , WF-CLI-12, 14 and 15's
   * states in the drawing's own words (`hakili-client.jsx`, `resultat`,
   * `resultatPrix`, `resultatKo`, `rapportsExpert`, the decision block).
   */
  /** `round W-13`, `M2`: the hold, read everywhere , the card's line, the fiche's, the reason on « Ouvrir mon dossier ». */
  // `round C5-2`, `M2`, decision D: the control that could not take place, on the buyer's card.
  "client.suivi.controleEmpeche":
    "Le contrôle prévu n'a pas pu avoir lieu : {reason}. Hakili reprogramme le passage et vous prévient de la nouvelle date.",
  "client.suivi.empecheAbsent": "véhicule absent du parc",
  "client.suivi.empecheCles": "clés indisponibles",
  "client.suivi.empecheBatterie": "batterie à plat, démarrage impossible",
  "client.suivi.empecheInaccessible": "véhicule inaccessible",
  "client.suivi.empecheFerme": "parc fermé à l'heure convenue",
  "client.suivi.empecheAutre": "autre raison",
  "client.vehicule.engage": "Un acheteur est engagé sur ce véhicule",
  "client.suivi.verdictConformeTitre": "Le véhicule est conforme",
  "client.suivi.verdictConformeFrais":
    "Le contrôle a été fait et le véhicule est sain, les frais de vérification restent donc acquis même si vous renoncez. Ils ont servi à payer l'expert et à vous éviter d'acheter à l'aveugle.",
  "client.suivi.verdictPrixTitre": "Véhicule sain, mais prix trop élevé",
  "client.suivi.verdictPrixDetail":
    "Mécaniquement le véhicule est bon. Le prix demandé dépasse de plus de 10 % la valeur estimée par l'expert.",
  "client.suivi.verdictKoTitre": "Le véhicule n'est pas conforme",
  "client.suivi.verdictKoDetail":
    "L'expert a relevé des défauts qui rendent ce véhicule impropre au financement.",
  "client.suivi.rapportTitre": "Rapport de l'expert",
  "client.suivi.rapportChipConforme": "Conforme",
  "client.suivi.rapportChipPrix": "Écart de prix",
  "client.suivi.rapportChipKo": "Non conforme",
  "client.suivi.rapportValeur": "Valeur estimée par l'expert",
  "client.suivi.rapportPrix": "Prix demandé",
  "client.suivi.rapportEcart": "Écart",
  /**
   * Round `C6`, `M4` (`OD-31`, `OD-33`), WF-CLI-14 step 2 amended: « Voir le
   * rapport » opens the cabinet's own document and Hakili's derived sheet,
   * each with its fingerprint and the report's version; neither carries the
   * asked price or the verdict , the gap stays on the card, above them. A
   * missing sheet says why (A47).
   */
  "client.suivi.voirRapport": "Voir le rapport",
  "client.suivi.replierRapport": "Replier le rapport",
  "client.suivi.rapportDuCabinet": "Rapport du cabinet {cabinet}",
  "client.suivi.rapportOuvrir": "Ouvrir",
  /**
   * `LANE-E`, `M1`: the two documents, each named for what it is. The cabinet's paper is a right and not an obligation
   * of its own door; Hakili's sheet is always there, so a report with neither is a defect, said as one.
   */
  "client.suivi.papierOuvrir": "Ouvrir le document du cabinet",
  "client.suivi.lectureOuvrir": "Ouvrir la lecture Hakili",
  "client.suivi.papierAbsent":
    "Le cabinet {cabinet} n'a pas joint son propre document : la lecture Hakili de ses constats tient lieu de rapport.",
  "client.suivi.rapportRien":
    "Ce rapport n'est pas encore consultable en document ; l'équipe Hakili en est prévenue et le rétablit.",
  "client.suivi.rapportVersionEmpreinte": "version {n} · empreinte SHA-256 {sha}",
  "client.suivi.lectureHakili": "Lecture Hakili",
  "client.suivi.lectureAbsente":
    "Hakili n'a pas pu conserver sa lecture de ce rapport ; le rapport du cabinet fait foi.",
  "client.suivi.rapportAbsent": "Le rapport n'est pas encore consultable.",
  "client.suivi.negociationRapport": "Le rapport en main : voir le rapport",
  /** `{montant}` , the buyer's half, as the door answered it. */
  "client.suivi.remboursementPromis":
    "Vos {amount} vous sont rendus quelle que soit votre décision, sur le numéro utilisé pour le paiement. Le contrôle est à la charge du vendeur :",
  "client.suivi.remboursementPrixMotif": "c'est son prix qui n'est pas conforme à la valeur expertisée.",
  "client.suivi.remboursementKoMotif": "c'est son véhicule qui n'est pas conforme.",
  /** The execution is a transfer by Hakili's hand , said on the card, never implied. */
  "client.suivi.remboursementExecution":
    "Le remboursement est exécuté par virement par Hakili ; nous vous prévenons dès qu'il part.",
  /** `{montant}`, `{date}` , once `payments` ordered it. */
  "client.suivi.remboursementOrdonne":
    "Remboursement de {amount} ordonné le {date}, exécuté par virement par Hakili.",
  "client.suivi.decisionTitre": "Que faites-vous ?",
  "client.suivi.decisionPrix":
    "Le rapport est un argument : peu de vendeurs maintiennent un prix qu'une expertise indépendante contredit.",
  "client.suivi.decisionKo": "Nous vous proposerons d'autres véhicules comparables dans votre capacité.",
  /** `{value}` , the expertised value, never rounded. */
  "client.suivi.negocier": "Négocier à {value} avec le vendeur",
  /** `{seller}`, `{value}`. */
  "client.suivi.negociationExplication":
    "La négociation se fait directement avec {seller}, rapport en main : la valeur d'expertise est votre argument. Si le prix descend à {ceiling} ou moins (la valeur expertisée majorée de 10 %), votre dossier repart tel quel.",
  /** `{seller}`, `{number}` , the seller's business number, or a dash when no door answered it. */
  // `PRICE-GAP-DEALER`, M2 (WF-CLI-14; the founder's ruling of 16 October 2026): the seller answers ON THE PLATFORM,
  // and the card shows his answer. `{prix}` , the price that stands now; `{valeur}` , the expertised value.
  "client.suivi.vendeurAttente":
    "Le vendeur a été prévenu de l'écart sur la plateforme. Il peut aligner son prix, en proposer un autre ou le maintenir : vous verrez sa réponse ici.",
  "client.suivi.vendeurMaintient":
    "Le vendeur maintient son prix de {prix}. Vous pouvez renoncer et être remboursé de vos frais de vérification.",
  "client.suivi.vendeurPropose":
    "Le vendeur vous propose un nouveau prix : {prix}. Il dépasse la valeur expertisée ({valeur}) sans la dépasser de plus de 10 % : si vous l'acceptez, votre dossier repart ; sinon, vous pouvez renoncer et être remboursé.",
  "client.suivi.acceptProposedPrice": "J'accepte ce prix",
  "client.suivi.verdictPropositionTitre": "Le vendeur propose un nouveau prix",
  "client.suivi.verdictPropositionDetail":
    "Mécaniquement le véhicule est bon. Le vendeur a baissé son prix : votre accord est nécessaire pour continuer.",
  "client.suivi.appelerVendeur": "Appeler {seller} au {number}",
  "client.suivi.renoncerRembourse": "Renoncer et être remboursé",
  // `FX-1`, `M3` (`F8`): the buyer's way out, on every OPEN stage. `02`:430 allows a
  // closure from any non-terminal stage with a mandatory motive, and the door records
  // `clientChangeAvis` , so the screen NAMES that motive rather than offering a choice
  // the door would discard. The label is the enum's own, not a second wording.
  "client.suivi.renoncement": "Renoncer à ce dossier",
  "client.suivi.renoncementMotif":
    "Le dossier sera clos sans suite, avec le motif « Client a changé d'avis ». Vous pourrez repartir sur un autre véhicule.",
  "client.suivi.renoncementConfirmer": "Confirmer le renoncement",
  // `FX-1`, `M2`: the founder's rule of 24 September asks Suivi to keep the closed
  // file's MOTIVE beside its final stage. The code is `02` §2.4.1's own; the sentence
  // names it in the buyer's words rather than printing an enum at him.
  "client.suivi.closMotif": "Motif : {motif}",
  "client.suivi.closLe": "Clos le {date}",
  "client.suivi.closMotifClientChangeAvis": "vous avez changé d'avis",
  "client.suivi.closMotifClientInjoignable": "vous étiez injoignable",
  "client.suivi.closMotifVehiculeAcheteAilleurs": "le véhicule a été acheté ailleurs",
  "client.suivi.closMotifFinancementObtenuAilleurs": "le financement a été obtenu ailleurs",
  "client.suivi.closMotifAutre": "autre motif",
  "client.suivi.voirAutresVehicules": "Voir d'autres véhicules",
  "client.dossier.chemin": "Où en est votre dossier",
  "client.dossier.pieces": "Vos pièces",
  "client.dossier.aucun": "Vous n'avez pas encore de dossier.",
  // `FX-1`, `M2`: the founder's rule of 24 September , when no dossier is live,
  // « Mon dossier » says so and offers the start. « Suivi » keeps the last closed
  // file readable beside it, so this is not the same sentence as `aucun`, which
  // means the buyer has never had one.
  "client.dossier.aucunEnCours": "Aucun dossier en cours.",
  "client.dossier.aucunEnCoursDetail":
    "Votre dernier dossier est clos ; vous pouvez le relire dans Suivi. Pour repartir, choisissez un véhicule.",
  // The COUNT is composed by `pl` at the call site, which is 6.5.5's rule:
  // agreement is computed, never parenthesised. The plural starts at two in
  // French, so "1 pièce manquante" and "2 pièces manquantes" both read right.
  "client.dossier.manquante": "pièce manquante",
  "client.dossier.manquantes": "pièces manquantes",
  // `round CW`, `CW.5`: the buyer has no pieces door of his own , the console
  // reads them through `GET /dossiers/{id}/pieces` and `/me/dossiers/{id}`
  // answers none. The sentence says so rather than letting an empty list read
  // as an empty file.
  "client.dossier.piecesSansPorte":
    "Bientôt : la liste de vos pièces n'est pas encore consultable depuis l'application. Votre chargé de dossier vous dit ce qui manque.",
  "client.dossier.deconnexion": "Se déconnecter",
  // `round C7`, `C7.1`: WF-CLI-18's screen to the drawing, its copy the
  // record's. The pieces door exists now: `piecesSansPorte` above is kept for
  // the file whose door answered nothing, and never printed beside a list.
  "client.dossier.piecesRecues": "Pièces reçues",
  "client.dossier.sur": "sur",
  "client.dossier.copieComptant":
    "Pour un achat comptant, seule votre pièce d'identité nous est nécessaire. Vous décidez au vu du rapport d'expertise, rien ne part à une banque.",
  "client.dossier.copieTransmis": "Dossier transmis. Il n'y a plus rien à envoyer ici.",
  // R-A (the architect's ruling on `L-3`): the FIFTH copy, while a bank's request stands open above.
  "client.dossier.copieComplement":
    "Dossier transmis aux banques. Une pièce complémentaire vous est demandée ci-dessus.",
  "client.dossier.copieComplet": "Tout est là. Vous pouvez nous transmettre votre dossier.",
  "client.dossier.copieAttente": "Dès que tout est reçu, nous vérifions et transmettons à la banque.",
  "client.dossier.envoyerDossier": "Envoyer mon dossier à la vérification",
  // `AA-3d2` (D-1018-AA2): what the chargée, having sent the file back to the collecte, asks HIM to complete. The
  // sentence under the list is the ratified text, verbatim; his send sits directly beneath it.
  "client.dossier.aCompleterTitre": "Ce qu'il faut compléter",
  "client.dossier.aCompleterReprise": "Votre dossier reprendra son analyse dès réception de ces éléments",
  "client.dossier.aCompleterNote": "Le message de votre chargé de dossier",
  "client.dossier.aCompleterFait": "Reçu",
  "client.dossier.aCompleterAFaire": "À faire",
  // `G3-3`, the founder's decision of 17 October 2026 (WAIT IN COLLECTE): sent, and the seller's pièces still to come.
  "client.dossier.attenteVendeur":
    "Votre dossier est envoyé, en attente des pièces du vendeur. Il partira à la vérification dès qu'elles seront là.",
  "client.dossier.copieAttenteVendeur":
    "Votre part est complète : nous sommes en attente des pièces du vendeur.",
  "client.dossier.chargeRole": "Votre chargé de dossier",
  "client.dossier.chargeAbsent": "Un chargé de dossier vous sera attribué.",
  "client.dossier.piecesVehicule": "Pièces du véhicule",
  "client.dossier.piecesVehiculeResume": "Vous n'avez pas à les réclamer, nous nous en chargeons",
  "client.dossier.vehiculeCarteGrise": "Carte grise du véhicule",
  "client.dossier.vehiculeFactureProforma": "Facture proforma",
  "client.dossier.vehiculeVisiteTechnique": "Visite technique en cours de validité",
  "client.dossier.vehiculeExpertise": "Expertise indépendante",
  "client.dossier.vehiculeParLeVendeur": "Le vendeur",
  "client.dossier.vehiculeParLExpert": "Expert mandaté par Hakili",
  "client.dossier.vehiculeAttendu": "attendu sous 48 h",
  // `VISITE-SANS-OBJET` (17 October 2026): the chargé marked the visite technique not applicable.
  "client.dossier.vehiculeSansObjet": "sans objet",
  "client.dossier.frais": "Ce que vous payez à Hakili",
  "client.dossier.fraisVerification": "Frais de vérification",
  "client.dossier.fraisVerificationDetail":
    "Expertise indépendante et visite technique. Vous les payez même sans crédit : c'est ce qui vous évite d'acheter un véhicule défectueux. Si le véhicule échoue au contrôle, la somme vous est rendue et le vendeur en supporte le coût.",
  "client.dossier.aRegler": "À régler",
  "client.dossier.regle": "Réglé",
  "client.dossier.fraisDossier": "Frais de dossier Hakili",
  "client.dossier.fraisDossierDetail":
    "2 % du montant financé, acceptés au mandat et exigibles à la signature, avant le versement au vendeur. Si aucune banque ne vous accorde le prêt, vous ne payez rien. La banque a ses propres frais de dossier, indiqués sur chaque offre.",
  "client.dossier.auDeblocage": "Au déblocage",
  "client.dossier.aLaSignature": "À la signature",
  "client.dossier.fraisTaeg":
    "Les frais de dossier entrent dans le TAEG de votre crédit, celui que vous voyez sur chaque offre. Rien ne s'y ajoute en dehors de ce tableau.",
  "client.dossier.demandeePar": "Demandée par votre chargé de dossier",
  "client.dossier.commentFaire": "Comment faire ?",
  "client.dossier.masquer": "Masquer",
  "client.dossier.envoyer": "Envoyer",
  // `FX-2c`, `M1` (`F4`): the founder's option A , the buyer puts a pièce right while the
  // dossier is his, and reads why he no longer can once it has gone.
  "client.dossier.remplacer": "Remplacer",
  "client.dossier.remplacerFerme":
    "Votre dossier est parti à la vérification : cette pièce ne peut plus être remplacée ici.",
  "client.dossier.envoiEnCours": "Envoi en cours…",
  "client.dossier.recu": "Reçu",
  "client.dossier.depotRefuse": "Le dépôt a été refusé par le stockage",
  // A deposit that fails is never silent: the buyer reads which crossing failed, in words.
  "client.dossier.depotInjoignable":
    "Le fichier n'a pas pu être envoyé : la connexion au stockage a échoué. Vérifiez votre réseau et réessayez.",
  "client.dossier.depotSansMotif":
    "Le dépôt n'a pas abouti et le service n'a donné aucune raison. Patientez un instant puis réessayez.",
  "client.dossier.conseilPhoto":
    "Posez le document à plat, sur fond sombre, en pleine lumière. Une photo nette évite un aller-retour et fait gagner deux jours.",
  // `S4-5-MULTI-FILE`: the binding finding's own fix , `bulletinsSalaire` and `relevesBancaires`
  // alone (`piece.multiFile`), a client sent several photos together or one at a time instead of
  // merging them into one document himself.
  "client.dossier.plusieursFichiers":
    "Envoyez les trois documents ensemble, ou un par un : chaque envoi s'ajoute aux précédents.",
  "client.dossier.ajouterFichier": "Ajouter un autre fichier",
  // `PIECES-COUNT-ENFORCE` (words approved by the A-H architect, 29 October 2026): a file of a pièce that asks for several months says how many it holds (the door's `declaredMonths`);
  // the pièce is complete when the months add up to the count it asks for. The count line stays « x sur N reçus ».
  "client.dossier.moisDuFichier": "Combien de mois ce fichier contient-il ?",
  "client.dossier.moisUn": "1 mois",
  "client.dossier.moisDeux": "2 mois",
  "client.dossier.moisTrois": "3 mois",
  /** `PIECES-COUNT`: why « Envoyer » is greyed while a file has no answer (the designer's verdict, approved words). */
  "client.dossier.moisConsigne": "Dites combien de mois contient chaque fichier.",
  "client.dossier.rejetee": "Photo refusée :",
  // `C7.4`: the civility, learned by the chargé off the identity document ,
  // said after, and nothing before.
  "client.dossier.bonjourMadame": "Bonjour Madame",
  "client.dossier.bonjourMonsieur": "Bonjour Monsieur",
  // `P0-UIK-016` / round 203: the client shell's eleven screens, each present
  // and reachable. A screen that is not built yet still owns its title here, so
  // the words do not move when the function lands behind them.
  "client.accueil.titre": "Votre parcours",
  "client.accueil.resume": "Le véhicule que vous suivez, votre dossier, et ce qu'il attend de vous.",
  // `round CW`, `CW.2`: the DRAWING's own title for this screen, replacing the
  // stub's « Rechercher un véhicule ». The mockups ARE the copy.
  "client.recherche.titre": "Trouvez votre voiture",
  "client.recherche.resume":
    "Les véhicules des concessionnaires partenaires, filtrés par votre ville et votre budget.",
  "client.vehicule.titre": "Le véhicule",
  "client.vehicule.resume": "La fiche complète, son concessionnaire, et la mensualité simulée à votre nom.",
  // `round CW`, `CW.4`: the stub said « Votre capacité »; the DRAWING's own
  // control and screen say « Vérifier mon éligibilité », and the mockups are
  // the copy. Both texts noted, the later one kept.
  //
  // `round C2`, `C2.9`: the 9 September drawing separates the two , the FICHE's
  // control still reads « Vérifier mon éligibilité en 2 minutes »
  // (`client.vehicule.verifier`), and the SCREEN it opens is headed « Votre
  // éligibilité ». A screen titled with the button that opened it tells a
  // person where he has been, not where he is.
  "client.eligibilite.titre": "Votre éligibilité",
  "client.eligibilite.resume":
    "Ce que votre salaire permet, engagements en cours déduits, avant qu'une banque ne l'étudie.",
  // `round N+3`, `S2-14`: Profil's own door, re-declaring with no vehicle in mind.
  "client.eligibilite.titreMiseAJour": "Votre situation financière",
  "client.eligibilite.sousMiseAJour":
    "Mettez à jour votre salaire, votre situation et votre crédit en cours.",
  "client.pieces.titre": "Vos pièces",
  "client.pieces.resume":
    "Les pièces attendues, celles déjà reçues, et l'envoi des manquantes depuis votre téléphone.",
  "client.offres.titre": "Les offres",
  "client.offres.resume": "Les propositions des banques qui ont étudié votre dossier, comparées côte à côte.",
  // Round `L-2`, `M2`: the offers screen (WF-CLI-22, A7, A8, `M0 (i)`).
  "client.offres.vosOffres": "Vos offres",
  "client.offres.verrouilleSous":
    "Voici ce que les banques vous avaient proposé et l'offre que vous avez retenue.",
  "client.offres.uneBanqueARepondu":
    "Une banque a répondu. Comparez, puis choisissez celle qui vous convient.",
  "client.offres.banquesOntRepondu":
    "{n} banques ont répondu. Comparez, puis choisissez celle qui vous convient.",
  "client.offres.aucune":
    "Chaque banque décide de son côté. Vos offres apparaîtront ici dès qu'elles arriveront.",
  "client.offres.offreFerme": "Offre ferme",
  // Round `L-5`, `M3` (the ended file's words, A11): every card expired , the intro asks no choice.
  "client.offres.toutesExpirees": "Les offres reçues ont expiré. Aucune ne peut plus être choisie.",
  "client.offres.chipRefusee": "Refusée",
  // Round `L-4`, `M1` (A11, a drawing in words): the card past its validity, the chip beside the bank's name.
  "client.offres.chipExpiree": "Offre expirée",
  "client.offres.parMois": "par mois",
  "client.offres.mois": "mois",
  "client.offres.duree": "Durée",
  "client.offres.taux": "Taux",
  "client.offres.taeg": "TAEG",
  "client.offres.memeMensualite": "Même mensualité que votre estimation.",
  "client.offres.moinsQueEstimation": "{amount} de moins par mois que votre estimation",
  "client.offres.plusQueEstimation": "{amount} de plus par mois que votre estimation",
  "client.offres.causeTaux": "taux {bank} au lieu de {indicative} indicatif",
  "client.offres.causeFraisBanque": "frais de dossier banque {rate} % ajoutés",
  "client.offres.causeGarantie": "garantie mécanique de {amount} financée",
  "client.offres.modifieeTitre": "Offre modifiée par la banque",
  "client.offres.ajustementGarantie": "le financement de la garantie mécanique n'a pas été retenu",
  "client.offres.ajustementFrais":
    "les frais de dossier seront à régler à la signature, ils ne sont pas financés",
  "client.offres.etVirgule": ", et ",
  "client.offres.modifieePasUnRefus":
    "Ce n'est pas un refus de votre dossier : le prêt vous est bien accordé, et la mensualité affichée correspond à ce que cette banque finance réellement.",
  "client.offres.garantieApresAcceptation":
    "Après votre acceptation, vous choisirez de régler la garantie comptant ou de la retirer, sans toucher au prêt.",
  "client.offres.nonDisponibleTitre": "Non disponible à cette mensualité",
  // `L-3b`, M0 (h): an offer past its validity, the date the bank set.
  "client.offres.expireeTitre": "Cette offre a expiré le {date} ; elle ne peut plus être acceptée.",
  "client.offres.nonDisponibleDetailCharge":
    "Avec la garantie et les frais choisis, cette mensualité représente {effort} % de votre salaire net moyen, crédits en cours compris, au-delà des 50 % que les banques acceptent. Choisissez une offre avec une durée plus longue ou un apport plus élevé, ou demandez à votre chargé de dossier de revoir la garantie et le mode de règlement des frais.",
  "client.offres.nonDisponibleDetailChargee":
    "Avec la garantie et les frais choisis, cette mensualité représente {effort} % de votre salaire net moyen, crédits en cours compris, au-delà des 50 % que les banques acceptent. Choisissez une offre avec une durée plus longue ou un apport plus élevé, ou demandez à votre chargée de dossier de revoir la garantie et le mode de règlement des frais.",
  "client.offres.badgeMensualite": "Mensualité la plus basse",
  "client.offres.badgeDuree": "La moins chère sur la durée",
  "client.offres.badgeApport": "Apport le plus faible",
  "client.offres.apportDemande": "Apport demandé {amount}",
  "client.offres.apportDePlus": ", soit {amount} de plus que la moins exigeante",
  "client.offres.apportCourt": "apport {amount}",
  "client.offres.valableJusquAu": "· valable jusqu'au {date}",
  "client.offres.voirDetail": "Voir le détail des coûts",
  "client.offres.masquerDetail": "Masquer le détail",
  "client.offres.lignePrixFinance": "Montant financé pour le véhicule",
  "client.offres.ligneFraisBanqueInclus": "Frais de dossier banque, inclus ({rate} %)",
  "client.offres.ligneFraisHakiliInclus": "Frais de dossier Hakili, inclus ({rate} %)",
  "client.offres.ligneGarantieIncluse": "Garantie mécanique incluse",
  "client.offres.ligneGarantieComptant": "Garantie mécanique réglée comptant, hors prêt",
  "client.offres.ligneGarantieNonFinancee": "Garantie mécanique, non financée par cette banque",
  "client.offres.ligneMontantEmprunte": "Montant emprunté",
  "client.offres.ligneTotalRembourse": "Total remboursé sur {n} mois",
  "client.offres.ligneInterets": "Dont intérêts et assurance",
  "client.offres.ligneFraisBanqueSignature": "Frais de dossier banque, à régler à la signature",
  "client.offres.ligneFraisHakiliSignature": "Frais de dossier Hakili, à régler à la signature",
  "client.offres.prixDuCredit":
    "C'est le prix du crédit, comparable au loyer d'une somme que vous n'avez pas à sortir aujourd'hui.",
  "client.offres.pedagogie":
    "Deux choses font baisser une mensualité : une durée plus longue, qui coûte plus cher au total, et un apport plus élevé, qui demande plus d'argent tout de suite. Comparez toujours la mensualité avec l'apport exigé. Les mentions ci-dessus sont des constats chiffrés, pas une recommandation.",
  "client.offres.continuer": "Continuer avec cette offre",
  "client.offres.confirmerTitre": "Confirmer votre choix",
  "client.offres.representeTitre": "Ce que cela représente pour vous",
  "client.offres.representeDetail":
    "Le montant définitif, garantie et frais compris, comparé à votre salaire net moyen. Ce n'est plus une estimation : c'est ce que vous vous engagez à payer chaque mois.",
  "client.offres.representeSansSalaire":
    "Votre capacité sera vérifiée avec vos pièces. Déclarez votre salaire à l'étape d'éligibilité pour comparer cette mensualité à vos revenus.",
  "client.offres.avertissementRefus":
    "En confirmant, vous acceptez ces conditions et les autres offres sont refusées.",
  "client.offres.avertissementComite":
    "Votre dossier passe ensuite en comité pour l'accord définitif de {bank}.",
  "client.offres.avertissementDirect": "{bank} prépare ensuite votre accord définitif.",
  "client.offres.avertissementSansSeuil": "{bank} vous précisera le circuit de son accord définitif.",
  "client.offres.codePhrase":
    "Pour cette étape, nous vérifions votre identité par {channel}. Entrez le code envoyé au {phone}.",
  "client.offres.canalSms": "SMS",
  "client.offres.canalWhatsapp": "WhatsApp",
  "client.offres.chiffre": "Chiffre {n}",
  "client.offres.renvoyer": "Renvoyer le code",
  "client.offres.renvoyerDans": "Renvoyer le code dans {s} s",
  "client.offres.accepter": "Accepter cette offre",
  "client.offres.preuve": "Votre acceptation est horodatée et conservée comme preuve de votre accord.",
  "client.assurance.titre": "Votre assureur",
  "client.assurance.resume": "Le choix de l'assureur et la souscription, une fois l'offre retenue.",
  // Round T-2b (WF-CLI-13, WF-CLI-28): the cash buyer's three options and the conclusion's screen.
  "client.suivi.optionFinancement": "Poursuivre avec un financement",
  "client.suivi.optionComptant": "Acheter comptant, sans crédit",
  "client.suivi.optionRenoncer": "Je renonce à ce véhicule",
  "client.suivi.voirComptant": "Voir mes deux virements",
  // `T-2c`, `M2 (4)`: WF-CLI-28 step 4's end on the suivi , the sale concluded, the dossier released.
  "client.suivi.comptantConclu": "Vente conclue : véhicule remis le {date} contre votre bon de retrait.",
  "client.suivi.chercherAutre": "Chercher un autre véhicule",
  "client.comptant.titre": "Votre achat comptant",
  "client.comptant.mots":
    "Vous réglez le véhicule vous-même, en deux virements à deux bénéficiaires, pour le prix affiché de {prix} et pas un franc de plus. Rien ne transite par Hakili.",
  "client.comptant.aucun": "Aucun achat comptant n'est engagé sur votre dossier.",
  "client.comptant.auVendeur": "Virement au vendeur",
  "client.comptant.aHakili": "Virement à Hakili",
  "client.comptant.banque": "Banque",
  "client.comptant.titulaire": "Titulaire",
  "client.comptant.numero": "Numéro de compte",
  "client.comptant.compteAbsent": "Coordonnées bancaires non communiquées.",
  "client.comptant.total": "Total des deux virements : {prix}",
  "client.comptant.declarationMots":
    "Une fois vos deux virements effectués, dites-le-nous. Chaque virement est ensuite constaté de son côté : le vendeur confirme le sien, Hakili constate le sien sur son compte. Votre déclaration ne remplace ni l'un ni l'autre.",
  "client.comptant.declarer": "J'ai effectué les deux virements",
  "client.comptant.declareLe": "Virements déclarés le {date}",
  "client.comptant.attenteVendeur": "Virement au vendeur : en attente de confirmation du vendeur",
  "client.comptant.vendeurConfirme": "Virement au vendeur : confirmé par le vendeur",
  "client.comptant.attenteHakili": "Virement à Hakili : en attente de réception sur notre compte",
  "client.comptant.hakiliRecu": "Virement à Hakili : reçu sur notre compte",
  "client.retrait.titre": "Le retrait",
  "client.retrait.resume": "Le décaissement et la récupération du véhicule chez le concessionnaire.",
  // `round CW`, `CW.1`: Profil to the drawing. « Mon compte » is the drawing's
  // own title (`hakili-client.jsx`, `Profil`), and it replaces the stub's
  // « Votre profil » , the mockups ARE the copy.
  // `round N+3`, `S2-14`: the new row, the declared salary beside the dossier
  // one it sits above. `{salaire}`, `{pret}` and `{date}` are replaced by the
  // caller, the same convention as `client.moncompte.appareilAjoute` below.
  "client.moncompte.situationTitre": "Ma situation financière",
  "client.moncompte.situationAucune": "Non renseigné",
  "client.moncompte.situationResumeSalaire": "{salaire} net",
  "client.moncompte.situationResumePret": "{pret} de prêt en cours",
  "client.moncompte.situationResumeDeclaree": "Déclaré le {date}",
  "client.moncompte.favoris": "Mes favoris",
  "client.moncompte.dossier": "Mon dossier",
  "client.moncompte.dossierComptant": "Mon achat comptant",
  "client.moncompte.dossierFinancement": "Mon dossier de financement",
  "client.moncompte.dossierAucun": "aucun dossier en cours",
  "client.moncompte.documents": "Mes documents",
  "client.moncompte.notifications": "Notifications",
  "client.moncompte.notificationsSous": "WhatsApp, canal principal",
  "client.moncompte.aide": "Aide",
  "client.moncompte.aideSous": "Questions fréquentes et contact",
  "client.moncompte.idee": "Demander une fonctionnalité",
  "client.moncompte.ideeSous": "Dites-nous ce qui vous manque",
  "client.moncompte.deconnexion": "Se déconnecter",
  // The context line the feature-request modal carries, so a reader of the
  // requests knows which screen a demand came from. 6.5.1: French lives here.
  "client.moncompte.ideeContexte": "Client · écran Mon compte",
  // `R81` as amended: a control whose door does not exist keeps the drawing's
  // look, greyed, and says WHY in its own title.
  "client.moncompte.sansPorte": "Bientôt : cette page n'a pas encore de porte.",
  // Round `PB-2`, `M4` (`P0-IDN-057`, `P1-IDN-058`): the handsets the PIN is bound to, and what this
  // handset was told. The notices' sentences are the api's own; only the labels live here.
  "client.moncompte.appareils": "Mes appareils",
  "client.moncompte.appareilCourant": "Cet appareil",
  "client.moncompte.appareilAutre": "Autre appareil",
  "client.moncompte.appareilAjoute": "Ajouté le {date}",
  "client.moncompte.appareilRetirer": "Retirer",
  "client.moncompte.avisLu": "J'ai compris",
  // The DRAWING's title for this screen, and it replaces « Votre profil » ,
  // the mockups ARE the copy (`hakili-client.jsx`, `Profil`). The sign-up
  // step keeps its own `client.profil.titre`; they are two screens.
  // `round CW`, `CW.2`: « Trouvez votre voiture », the drawing's own words.
  "client.recherche.sousListe": "Chaque véhicule est expertisé avant la vente, aux frais du vendeur",
  "client.recherche.sousDecouvrir": "Un véhicule à la fois, à vous de trancher",
  "client.recherche.placeholder": "Marque, modèle, référence",
  "client.recherche.filtres": "Filtres",
  "client.recherche.vousEtesA": "Vous êtes à",
  "client.recherche.marque": "Marque",
  "client.recherche.toutesMarques": "Toutes les marques",
  "client.recherche.rayonVille": "Ma ville",
  "client.recherche.rayon300": "Jusqu'à 300 km",
  "client.recherche.rayonMali": "Tout le Mali",
  "client.recherche.mensualiteMax": "Mensualité maximale",
  // `round C2`, ruling 5: the slider's top notch hides nothing, and it says so
  // in words rather than in a number, because no number is honest , a ceiling
  // of ten million is still a ceiling.
  "client.recherche.sansLimite": "Sans limite",
  "client.recherche.expliqueMensualite":
    "Cherchez par ce que vous pouvez payer chaque mois, pas seulement par le prix. Au-dessus de votre mensualité, un véhicule reste visible mais s'affiche en retrait.",
  "client.recherche.modeListe": "Liste",
  "client.recherche.modeDecouvrir": "Découvrir",
  "client.recherche.segTous": "Tous",
  "client.recherche.segFavoris": "Mes favoris",
  "client.recherche.segPasses": "Passés",
  "client.recherche.trier": "Trier",
  // `round C3-B`, `C3-B.1`: the legend at the head of the list, one sentence.
  "client.recherche.legendeEstimation":
    "Mensualités estimées, frais de dossier Hakili inclus, assurance en plus. Chaque banque fixe son taux.",
  "client.recherche.triMensualite": "Mensualité",
  "client.recherche.triPrix": "Prix croissant",
  "client.recherche.triProches": "Les plus proches",
  "client.recherche.triKm": "Kilométrage",
  "client.recherche.revoirPasses": "Remettre ces véhicules dans Découvrir",
  "client.recherche.plusLoinAvant": "Voir",
  "client.recherche.plusLoinApres1": "véhicule plus loin dans le pays",
  "client.recherche.plusLoinApresN": "véhicules plus loin dans le pays",
  "client.recherche.videFavoris":
    "Aucun favori ici. Mettez un véhicule de côté avec le coeur, ou dites J'aime dans Découvrir.",
  "client.recherche.videPasses":
    "Vous n'avez passé aucun véhicule. Un véhicule passé se retrouve toujours ici, il n'est jamais supprimé.",
  "client.recherche.videRecherche": "Aucun véhicule ne correspond à cette recherche.",
  "client.recherche.videRayon": "Aucun véhicule dans ce rayon. Élargissez votre recherche.",
  // `round C2`, `C2.3`: the drawing's own way into the fiche from Découvrir,
  // and the toggle's word when the car is already a favourite , never a greyed
  // control with no reason (`R81` as amended).
  "client.recherche.voirFiche": "Voir la fiche complète",
  "client.recherche.jaimeDeja": "Retirer des favoris",
  "client.recherche.passer": "Passer",
  "client.recherche.jaime": "J'aime",
  "client.recherche.decouvrirFini": "Vous avez tout vu dans ce rayon",
  "client.recherche.decouvrirFiniPasses":
    "Vos véhicules passés restent visibles dans la liste, ils ne sont pas supprimés.",
  "client.recherche.decouvrirFiniRien":
    "De nouveaux véhicules arrivent chaque semaine, nous vous prévenons sur WhatsApp.",
  "client.recherche.decouvrirRappel": "Un véhicule passé reste dans la liste, il n'est jamais supprimé.",
  // `AVAILABILITY-QUEUE`, `M2`, point 3: « Véhicules disponibles pour vous ».
  "client.queueFront.titre": "Véhicules disponibles pour vous",
  "client.queueFront.resume":
    "Ces véhicules vous attendent : personne d'autre ne peut les engager pour l'instant.",
  "client.queueFront.expireLe": "Expire le",
  "client.queueFront.continuer": "Continuer",
  "client.queueFront.vide": "Vous n'avez aucune place en file d'attente pour le moment.",
  "common.action.effacer": "Effacer",
  // `round CW`, `CW.4`: l'éligibilité, `WF-CLI-05` et `06`.
  // `round C2`, `C2.9`: **« Deux questions » was false the moment the situation
  // was asked**, and the pair is where it was read. The drawing's own count and
  // its own promise, which it moved to the foot of the form beside the button:
  // *« Trois questions, réponse immédiate »*.
  "client.eligibilite.sous": "Trois questions, réponse immédiate",
  "client.eligibilite.salaire": "Votre salaire net mensuel (FCFA)",
  // The drawing's own question, and it is a better one: a person answers
  // « remboursez-vous déjà un crédit » without having to work out what a
  // « mensualité d'un prêt en cours » is.
  "client.eligibilite.pret": "Remboursez-vous déjà un crédit chaque mois ? (FCFA)",
  "client.eligibilite.pretZero": "Indiquez 0 si vous n'avez aucun crédit en cours",
  // The drawing's reassurance, kept VERBATIM: it says why a complete answer is
  // in the buyer's own interest, which is not the same sentence as the bank
  // question's.
  "client.eligibilite.releves":
    "Vos relevés bancaires permettent de vérifier ces informations. Une réponse complète évite un refus tardif une fois le dossier chez la banque.",
  "client.eligibilite.gratuit": "Gratuit et sans engagement. Aucun document n'est demandé à ce stade.",
  // `round C2`, `C2.1`: `WF-CLI-05` step 1's own questions. The three
  // situations themselves are NOT here , they are the door's wire values and
  // live in `eligibilite.tsx` beside the control that sends them, so an accent
  // cannot drift between a label and a value.
  "client.eligibilite.situation": "Votre situation",
  // `C2.9`: the drawing's wording for both, read off the pair. The question is
  // « Auprès de quelle banque ? » and the explanation is the domiciliation
  // TRANSFER , which is the thing that actually happens , where the build's own
  // sentence explained it twice and promised something about eligibility that
  // is not this screen's to promise.
  "client.eligibilite.banque": "Auprès de quelle banque ?",
  "client.eligibilite.banquePourquoi":
    "Cette information sert à organiser le transfert de votre domiciliation si votre nouveau dossier est financé ailleurs.",
  "client.eligibilite.verifier": "Voir mon résultat",
  // `round N+3`, `S2-14`: Profil's own door submits the same form under this
  // label instead , there is no result card to promise in that mode.
  "client.eligibilite.enregistrer": "Enregistrer",
  // `round N+3`, `S2-14`: the confirm-or-update prompt, at or beyond
  // `PREQUALIFICATION_FRESHNESS_DAYS`. `{date}` is replaced by the caller,
  // the catalogue's own interpolation convention (`client.moncompte.appareilAjoute`).
  "client.eligibilite.toujoursExact": "Toujours exact ?",
  "client.eligibilite.toujoursExactSous":
    "Vous avez déclaré ces informations le {date}. Confirmez qu'elles sont toujours exactes, ou modifiez-les ci-dessus.",
  "client.eligibilite.toujoursExactConfirmer": "Oui, toujours exact",
  // `REG-PROFILE-001`, Arc B: the form step reads the declared profile and asks for nothing. Copy PROPOSED, not
  // ratified. `{jours}` and `{preteurs}` are replaced by the caller; the threshold is the API's, never this file's.
  "client.eligibilite.chargement": "Lecture de votre profil en cours",
  "client.eligibilite.sousProfil": "Nous lisons votre profil déclaré. Vous n'avez rien à ressaisir.",
  "client.eligibilite.source":
    "Ces chiffres viennent de votre profil. Si quelque chose a changé, mettez votre profil à jour avant de continuer.",
  "client.eligibilite.resumeSalaire": "Revenu net mensuel retenu",
  "client.eligibilite.resumeEmplois": "Vos emplois",
  "client.eligibilite.resumeEngagements": "Vos engagements en cours",
  "client.eligibilite.resumeAucun": "Vous avez déclaré n'avoir aucun engagement en cours.",
  "client.eligibilite.resumeParMois": "par mois",
  "client.eligibilite.modifierProfil": "Modifier mon profil",
  "client.eligibilite.completer": "Compléter mon profil",
  "client.eligibilite.profilIncomplet":
    "Votre profil n'a pas encore d'emploi déclaré. Sans lui, nous ne pouvons pas vérifier votre éligibilité.",
  "client.eligibilite.engagementsADeclarer":
    "Indiquez dans votre profil si vous avez des engagements en cours, ou si vous n'en avez aucun.",
  "client.eligibilite.soldeAncien":
    "La date du solde de certains de vos engagements remonte à plus de {jours} jours. Mettez-la à jour dans votre profil : {preteurs}.",
  "client.eligibilite.banquesNote":
    "Vos engagements auprès de {preteurs} : si votre financement est accordé par une autre banque, le transfert de votre domiciliation sera à organiser.",
  "client.eligibilite.favorable": "Vous pouvez emprunter",
  // `round C3`, `C3.0(f)`: l'issue favorable et l'issue défavorable, aux mots du
  // dessin du 9 septembre. La date du contrôle n'y est PAS , la porte qui la
  // dérive est celle du round `C3-B`.
  "client.eligibilite.profilEligible": "Votre profil est éligible",
  "client.eligibilite.viser": "D'après ce que vous avez déclaré, vous pouvez viser un financement d'environ",
  "client.eligibilite.sur": "sur",
  "client.eligibilite.mensualiteEstimee": "Mensualité estimée",
  "client.eligibilite.estimation":
    "Ceci est une estimation fondée sur vos déclarations, pas un accord de la banque. L'étape suivante vérifie vos documents, et c'est la banque qui décide.",
  // `round C3-B`, `C3-B.1`: the one estimate, named on the result.
  "client.eligibilite.estimationFrais":
    "Même estimation que sur la fiche : frais de dossier Hakili inclus, assurance en plus.",
  "client.eligibilite.laSuite": "La suite",
  "client.eligibilite.suiteFrais": "Vous réglez la vérification du véhicule",
  "client.eligibilite.suiteExpert": "Un expert agréé le contrôle",
  "client.eligibilite.suiteDecider": "Vous décidez au vu de son rapport",
  "client.eligibilite.suitePieces": "Vous envoyez vos pièces, guidé pas à pas",
  "client.eligibilite.suiteBanques": "Les banques répondent, vous choisissez",
  "client.eligibilite.resteApres": "Après cette mensualité, il vous resterait",
  "client.eligibilite.parMois": "par mois",
  "client.eligibilite.legendePret": "Prêt en cours",
  "client.eligibilite.legendeMensualite": "Cette mensualité",
  "client.eligibilite.legendeReste": "Reste à vivre",
  "client.eligibilite.marqueCinquante":
    "Le trait marque 50 %, la limite au-delà de laquelle les banques ne prêtent pas.",
  /**
   * `round C3-A`, `C3-A.2`: **the drawing's two figures replace one.**
   *
   * `effortPhrase` , « Vos charges représentent » , printed the effort with the
   * running loan folded in, so a buyer carrying one could not tell what this
   * car costs him from what he already owes. The drawing says both, and the
   * second clause only when a loan was declared.
   */
  "client.eligibilite.effortMensualite": "Cette mensualité représente",
  "client.eligibilite.effortDuSalaire": "% de votre salaire",
  "client.eligibilite.effortEt": ", et",
  "client.eligibilite.effortAvecPret": "% avec votre prêt en cours.",
  "client.eligibilite.effortSeul": "% de votre salaire.",
  /**
   * `round C3-A`, `C3-A.2`: `WF-CLI-10`'s payment screen.
   *
   * **The mandate's five clauses are NOT here.** They are served by
   * `GET /me/dossiers/{id}/mandate` and rendered from that answer, because a
   * tie between a catalogue and a domain constant holds in CI and not in a
   * buyer's hand. What lives here is the chrome AROUND the text , headings, the
   * labels on the two options, the sentence beside the checkbox , which a
   * designer moves and no regulator reads as a promise.
   */
  "client.suivi.reglerFrais": "Régler les frais",
  /** `WF-CLI-10` §9's own control, on the card once the money has landed. */
  "client.suivi.lancerVerifications": "Lancer les vérifications",
  "client.paiement.titre": "Frais de vérification",
  "client.paiement.retour": "Revenir au suivi",
  "client.paiement.objet": "Expertise indépendante et vérification de la visite technique, pour la",
  "client.paiement.de": "de",
  "client.paiement.apresReception":
    "L'expert n'est mandaté qu'après réception, et aucun frais de transfert n'est à votre charge.",
  "client.paiement.suitVotreDossier": "suit votre dossier.",
  /** `R81`: no chargé is seated until `P1-DOS-010`, so the line says so. */
  "client.paiement.chargeInconnu": "Votre chargé de dossier vous sera présenté sous peu.",
  "client.paiement.remboursementFort": "Si le véhicule échoue au contrôle, vous êtes remboursé en totalité",
  "client.paiement.remboursementSuite":
    "sur le même numéro, et c'est le vendeur qui supporte le coût. Vous ne payez que pour un véhicule qui s'avère sain.",
  "client.paiement.mandatTitre": "Mandat de recherche de financement",
  "client.paiement.mandatResume":
    "Vous nous confiez la recherche d'un financement pour ce véhicule. Nos frais de dossier s'élèvent à",
  "client.paiement.mandatDeuxPourCent":
    ", soit 2 % du montant financé, et ne sont dus que si une banque vous accorde le prêt. Ils se règlent avant le versement des fonds au vendeur.",
  // `round C3-B`, `C3-B.1`: the mandate's line, the fourth reading of the one estimate.
  "client.paiement.mandatMensualite":
    "Mensualité estimée avec ces frais dans le prêt : {amount} par mois, assurance en plus.",
  "client.paiement.lireMandat": "Lire le mandat en entier",
  "client.paiement.masquerMandat": "Masquer le détail",
  "client.paiement.commentRegler": "Comment les régler",
  "client.paiement.inclusTitre": "Les inclure dans le financement",
  "client.paiement.inclusDetail":
    "Rien à sortir. La banque nous règle directement au moment du versement, et vous remboursez ces frais avec le prêt.",
  "client.paiement.signatureTitre": "Les régler à la signature",
  "client.paiement.signatureDetail":
    "Vous payez ces frais le jour de la signature, avant le versement au vendeur.",
  "client.paiement.plus": "+",
  "client.paiement.interetsSurLaDuree": "d'intérêts sur la durée",
  "client.paiement.aucunInteret": "Aucun intérêt sur ces frais",
  "client.paiement.jAccepte": "J'accepte le mandat et les frais de dossier de",
  "client.paiement.dusUniquement": ", dus uniquement en cas d'accord d'une banque.",
  "client.paiement.comptantTitre": "Ce paiement ne concerne que le véhicule",
  "client.paiement.comptantDetail":
    "Vous avez choisi de payer comptant. Ce montant couvre uniquement l'expertise indépendante et la visite technique. Il n'y a ni mandat de recherche de financement, ni frais de dossier : nous ne cherchons pas de prêt pour vous.",
  "client.paiement.commentPayer": "Comment souhaitez-vous payer ?",
  "client.paiement.moyenOrange": "Orange Money",
  "client.paiement.moyenWave": "Wave",
  "client.paiement.moyenMoov": "Moov Money",
  "client.paiement.moyenSama": "Sama Money",
  "client.paiement.moyenVirement": "Virement bancaire",
  "client.paiement.moyenDetailOrange": "Paiement vers le numéro Orange Money de Hakili",
  "client.paiement.moyenDetailWave": "Paiement vers le numéro Wave de Hakili",
  "client.paiement.moyenDetailMoov": "Paiement vers le numéro Moov Money de Hakili",
  "client.paiement.moyenDetailSama": "Paiement vers le numéro Sama Money de Hakili",
  "client.paiement.moyenDetailVirement": "Confirmation sous 24 heures ouvrées",
  /** `R81`: a means that is not offered says so, rather than vanishing. */
  "client.paiement.moyenIndisponible": "Indisponible pour le moment",
  "client.paiement.numeroLabel": "Numéro de téléphone",
  "client.paiement.numeroMoyen": "Numéro {moyen}",
  "client.paiement.indicatifLabel": "Indicatif du pays",
  "client.paiement.indicatifRequis": "Indiquez l'indicatif du pays.",
  "client.paiement.numeroAide":
    "Indiquez le numéro depuis lequel vous payez. Si votre numéro n'est pas malien, changez l'indicatif.",
  "client.paiement.afficherCoordonnees": "Afficher les coordonnées de paiement",
  "client.paiement.recevoirCoordonnees": "Recevoir les coordonnées bancaires",
  "client.paiement.declareTitre": "Paiement déclaré",
  "client.paiement.payezTitre": "Effectuez votre paiement",
  "client.paiement.jaiPaye": "J'ai effectué le paiement",
  "client.paiement.annulerDeclaration": "Annuler cette déclaration",
  "client.paiement.referenceRecuFacultative": "Référence de votre reçu (facultatif)",
  "client.paiement.referenceRecu": "Référence de votre reçu",
  "client.paiement.heuresOuvreesDepuisDeclaration": "heures ouvrées à compter de votre déclaration.",
  "client.paiement.virementTitre": "Virement en attente",
  "client.paiement.paiementDeclare": "Paiement déclaré, en attente de confirmation par Hakili",
  "client.paiement.payezSurCompte":
    "Payez ce montant sur le compte ci-dessous en citant la référence. Hakili confirme dès réception.",
  "client.paiement.coordonneesAbsentes":
    "Hakili vous enverra les coordonnées du compte à utiliser. Citez la référence ci-dessous lors de votre paiement.",
  "client.paiement.virementAttendu": "Nous attendons votre virement",
  "client.paiement.confirmationSous": "Confirmation sous",
  "client.paiement.heuresOuvrees": "heures ouvrées.",
  "client.paiement.coordonneesTitre": "Coordonnées bancaires Hakili",
  "client.paiement.coordonneesMobileTitre": "Payez vers le numéro {moyen} de Hakili",
  "client.paiement.montantAPayer": "Montant à payer",
  "client.paiement.banque": "Banque",
  "client.paiement.titulaire": "Titulaire",
  "client.paiement.numero": "Numéro",
  "client.paiement.referenceAciter": "Référence à citer",
  "client.paiement.changerMoyen": "Changer de moyen de paiement",
  "client.paiement.confirmeTitre": "Paiement confirmé",
  "client.paiement.recus": "reçus",
  "client.paiement.conserveeJusquAu":
    "La somme est conservée jusqu'au résultat du contrôle. Si le véhicule échoue, elle vous est rendue en totalité, sur le même numéro.",
  "client.paiement.vousDecidez": "Vous décidez du départ",
  "client.paiement.nousMandatons":
    "Nous mandatons l'expert et vérifions la visite technique dès que vous lancez.",
  /**
   * `round C3-H`, the founder's finding on 65: the drawing's second sentence,
   * copied as such. `C3-B` decides whether it derives its date from the next
   * open control day or names no figure (the amendment of 12 September §4).
   */
  "client.paiement.comptez48h": "Comptez 48 heures pour recevoir le rapport.",
  "client.paiement.lancerVerifications": "Lancer les vérifications",
  "client.paiement.accepterPourContinuer": "Acceptez le mandat pour continuer.",
  /**
   * `R81` as amended: a control ahead of its doors says what it is waiting for.
   * The means and the `attente` step are `C3-A.3`'s, which builds the settings
   * that decide which means are OFFERED , a screen listing three today would
   * promise a transfer nobody can receive.
   */
  "client.paiement.moyensAVenir":
    "Le choix du moyen de paiement arrive très bientôt. Votre mandat est enregistré.",
  /**
   * `R81`: what a screen prints where it knows nothing.
   *
   * A plain hyphen, which is what `carte.tsx` already prints for a distance it
   * has no centroid for. **Not an em dash**: `no-forbidden-dash` bans `U+2014`
   * by `hakili-conventions.md` §3, and the rule caught this line , « ponctuation
   * simple » is a house convention and a placeholder is not an exception to it.
   */
  "common.valeur.tiret": "-",
  /**
   * `round C5.0`: the cabinet's page behind the tour sheet's link , the
   * drawing's words (`hakili-expert.jsx`, `FeuilleTournee`, the link surface)
   * and the door's for what the door refuses.
   */
  "expert.feuille.titre": "Feuille de tournée",
  "expert.feuille.versionEnvoyee": "Feuille version {version}, envoyée par WhatsApp le {date}.",
  "expert.feuille.receptionEnCours": "Réception en cours de confirmation à Hakili.",
  "expert.feuille.receptionConfirmee": "Réception confirmée à l'ouverture de ce lien, le {date}.",
  "expert.feuille.sansConfirmation":
    "Sans cette confirmation, personne ne pouvait garantir que le jeudi tiendrait.",
  "expert.feuille.vehicules": "Véhicules",
  "expert.feuille.version": "Version",
  "expert.feuille.arret": "{n} arrêt",
  "expert.feuille.arrets": "{n} arrêts",
  "expert.feuille.arretTitre": "Arrêt {n} · {seller}",
  "expert.feuille.nVehicule": "{n} véhicule",
  "expert.feuille.nVehicules": "{n} véhicules",
  "expert.feuille.surPlace": "Sur place :",
  "expert.feuille.controleStock": "Contrôle de stock",
  // `round C5.1`: the sheet's facts, to the drawing; the fee a dash.
  "expert.feuille.aControler": "À contrôler",
  "expert.feuille.nEnvoye": "{n} envoyé",
  "expert.feuille.nEnvoyes": "{n} envoyés",
  "expert.feuille.honoraires": "Honoraires",
  "expert.feuille.honorairesTournee": "FCFA pour la tournée",
  "expert.feuille.versionDu": "Version {version}, envoyée le {date}.",
  "expert.feuille.faitsSur": "{n} sur {m}",
  "expert.feuille.appeler": "Appeler le",
  "expert.feuille.appelerAbsent": "Numéro du responsable absent à l'envoi de cette feuille.",
  "expert.feuille.chipAFaire": "À contrôler",
  "expert.feuille.chipEnvoye": "Rapport envoyé",
  // `round C5-2`, `M2`: the drawing's third tile off the desk (`OD-32`), the failed pass, the vehicle's control.
  "expert.feuille.chipEmpeche": "Empêché",
  "expert.feuille.empeches": "Empêchés",
  "expert.feuille.empechesSignales": "signalés à Hakili",
  "expert.feuille.empechesAucun": "aucun",
  "expert.feuille.ouvrir": "Ouvrir",
  "expert.feuille.km": "{n} km",
  "expert.feuille.note":
    "Ce lien vous a été envoyé par WhatsApp et ne demande aucun compte. Il ne porte que cette tournée : ni vos contrôles passés, ni vos honoraires du mois n'y sont accessibles. La même page s'ouvre aussi bien sur le téléphone de l'expert que sur l'ordinateur du bureau.",
  "expert.feuille.refusAide":
    "Demandez à Hakili de vous renvoyer la feuille de tournée : le lien en vigueur est celui du dernier envoi.",
  "expert.feuille.lienInconnu": "Ce lien ne correspond à aucune feuille de tournée.",
  /**
   * `round C5-2`, `M1`: the cabinet's space, stage 2 , its login, its session,
   * its shell by role and « Mon cabinet » (`hakili-expert.jsx`, `MonCabinet`,
   * `RailBureau`, `ONGLETS_AGENT`). The words are the drawing's.
   */
  "expert.connexion.titre": "Espace cabinet",
  "expert.connexion.sousTitre": "Connectez-vous avec l'adresse de votre invitation.",
  "expert.connexion.email": "Adresse e-mail",
  "expert.connexion.motDePasse": "Mot de passe",
  "expert.connexion.entrer": "Se connecter",
  "expert.session.chargement": "Chargement…",
  "expert.session.inattendue": "Réponse inattendue du serveur.",
  "expert.session.deconnexion": "Se déconnecter",
  "common.surface.refusCabinet":
    "Cet espace est réservé aux cabinets d'expertise. Connectez-vous sur l'application de votre organisation.",
  "expert.rail.tournee": "Tournée du jeudi",
  "expert.rail.rapports": "Rapports",
  "expert.rail.honoraires": "Honoraires",
  "expert.rail.cabinet": "Mon cabinet",
  "expert.rail.bientot": "Bientôt disponible",
  "expert.onglet.tournee": "Ma tournée",
  "expert.tournee.aucune": "Aucune feuille de tournée n'est arrêtée pour votre cabinet.",
  "expert.tournee.nouvelleVersion":
    "Une nouvelle version de la feuille est arrivée (version {version}) ; elle remplace la version {previous} que vous aviez lue.",
  "expert.tournee.noteTerrain":
    "Votre tournée arrive ici en notification, répartie par le bureau. Les honoraires du cabinet et son historique restent au bureau : ce téléphone porte le travail, pas les comptes.",
  "expert.tournee.noteBureau":
    "Votre tournée arrive ici en notification. Vous n'avez plus de lien à ouvrir, et votre historique reste dans l'espace.",
  "expert.tournee.saisieBientot": "Bientôt disponible",
  // `M2`, decision D: « Contrôle empêché », the drawing's form (`ControleEmpecheFormulaire`).
  "expert.empeche.titre": "Contrôle empêché",
  "expert.empeche.retour": "Retour à la tournée",
  "expert.empeche.pasUnVerdict":
    "Ce n'est pas un verdict : un véhicule qu'on n'a pas pu examiner n'est ni conforme ni non conforme. Votre signalement remonte à Hakili, qui reprogramme le passage et prévient le vendeur.",
  "expert.empeche.question": "Qu'est-ce qui a empêché le contrôle ?",
  "expert.empeche.absent": "Véhicule absent du parc",
  "expert.empeche.absentAide": "Le véhicule n'était pas sur place au passage.",
  "expert.empeche.cles": "Clés indisponibles",
  "expert.empeche.clesAide": "Personne sur place ne pouvait ouvrir ni démarrer le véhicule.",
  "expert.empeche.batterie": "Batterie à plat, démarrage impossible",
  "expert.empeche.batterieAide": "L'essai routier et les contrôles moteur n'ont pas pu être faits.",
  "expert.empeche.inaccessible": "Véhicule inaccessible",
  "expert.empeche.inaccessibleAide": "Encombré, bloqué par d'autres véhicules, ou hors du parc.",
  "expert.empeche.ferme": "Parc fermé à l'heure convenue",
  "expert.empeche.fermeAide": "Personne pour ouvrir à l'heure de la tournée.",
  "expert.empeche.autre": "Autre raison",
  "expert.empeche.autreAide": "À préciser en une phrase, elle sera lue par Hakili.",
  "expert.empeche.aSavoir": "Ce que Hakili doit savoir",
  "expert.empeche.aSavoirFacultatif": "Ce que Hakili doit savoir (facultatif)",
  "expert.empeche.aSavoirAide":
    "Une phrase suffit : ce que vous avez trouvé sur place, et avec qui vous avez parlé.",
  "expert.empeche.aTrancher":
    "Qui supporte un passage empêché reste à trancher entre Hakili et les cabinets : aucun montant n'est décompté ici, et vos honoraires du jour ne bougent pas tant que la règle n'est pas écrite.",
  "expert.empeche.signaler": "Signaler à Hakili",
  "expert.empeche.manqueRaison": "choisissez une raison",
  "expert.empeche.manquePhrase": "précisez la raison en une phrase",
  "expert.empeche.signale": "Contrôle empêché signalé à Hakili pour {reference}.",
  "expert.empeche.sansDemande": "Ce véhicule ne porte pas de demande sur cette version de la feuille.",
  // `M3`: the entry (`Controle`), the drawing's words; the verdict after the send (`RapportEnvoye`); the desk (`BureauRapports`).
  "expert.saisie.oui": "Oui",
  "expert.saisie.non": "Non",
  // `ROUND S4-2-REFERENCE-DOCUMENT`, M1 (3): one line, earlier in the flow ,
  // before the inspection fields, not after , so the paper asked for on the
  // desk's own visa screen is never a surprise. Kept to one sentence.
  "expert.saisie.rapportAvis":
    "Un rapport du cabinet pourra être joint à l'envoi, ou ajouté plus tard : ce n'est jamais une obligation pour transmettre cette saisie.",
  "expert.saisie.vehicule": "Le véhicule",
  "expert.saisie.immatriculation": "Immatriculation",
  "expert.saisie.nonImmatricule": "non immatriculé",
  "expert.saisie.kmDeclare": "Kilométrage déclaré",
  "expert.saisie.couleur": "Couleur",
  "expert.saisie.demande": "Demande",
  "expert.saisie.controleStock": "contrôle de stock, sans acheteur",
  "expert.saisie.enArrivant": "Ce que vous vérifiez en arrivant",
  "expert.saisie.chassisQuestion": "Le numéro de châssis correspond à la carte grise ?",
  // `LANE E`, the expert checklist: « sans objet » is said by the system when
  // there is no carte grise to compare with , never an answer the expert gives.
  "expert.saisie.sansCarteGrise": "Sans objet : pas de carte grise",
  "expert.saisie.chassisAide": "Caractère par caractère, sur le véhicule et sur le document.",
  "expert.saisie.chassisLu": "Numéro frappé sur le véhicule",
  "expert.saisie.chassisNonConforme":
    "Cochez alors le constat éliminatoire « Numéro de châssis non conforme à la carte grise » ci-dessous : c'est lui qui porte le fait au dossier.",
  "expert.saisie.plaqueQuestion": "La plaque correspond à la carte grise",
  "expert.saisie.plaqueAide":
    "Au Mali, certaines concessions vendent des véhicules non immatriculés : l'absence de plaque n'est pas un défaut.",
  "expert.saisie.plaqueLue": "Plaque lue sur le véhicule",
  // `LANE E`: un véhicule déclaré sans plaque ne se voit pas demander une plaque
  // à lire : on lui demande s'il n'en porte aucune, comme déclaré. « Oui » est la
  // réponse entière ; « Non » demande la plaque lue et enregistre l'écart.
  "expert.saisie.aucunePlaqueQuestion": "Le véhicule ne porte aucune plaque, comme déclaré ?",
  "expert.saisie.aucunePlaqueEcart":
    "Notez la plaque lue : l'écart avec la déclaration est enregistré, il ne bloque pas la transmission.",
  // `LANE E`: la carte grise est la PREMIÈRE question, posée sur tout véhicule ; le
  // châssis et l'année se rapprochent d'elle, ou sont « sans objet » sans elle.
  "expert.saisie.carteGriseQuestion": "Avez-vous trouvé une carte grise sur ce véhicule ?",
  "expert.saisie.carteGriseAide":
    "Sans carte grise, le châssis et l'année n'ont rien à rapprocher : l'absence est enregistrée une fois. Un écart avec la déclaration du concessionnaire est un fait enregistré, jamais un blocage.",
  // `ROUND OD-43-VEHICLE-YEAR-VERIFICATION`, M1: l'année déclarée par le concessionnaire,
  // vérifiée sur la carte grise comme le châssis et la plaque ; un écart est le constat
  // éliminatoire « Année », que l'expert coche lui-même.
  "expert.saisie.anneeQuestion": "L'année correspond à la carte grise ?",
  "expert.saisie.anneeAide":
    "Le vendeur a déclaré {n}. Relevez l'année que vous lisez sur la carte grise, même si elle est différente.",
  "expert.saisie.anneeAideSansDeclaration":
    "Le vendeur n'a pas déclaré d'année. Relevez l'année que vous lisez sur la carte grise.",
  "expert.saisie.anneeLue": "Année lue sur la carte grise",
  "expert.saisie.anneeNonConforme":
    "Cochez alors le constat éliminatoire « Année » ci-dessous : c'est lui qui porte le fait au dossier.",
  "expert.saisie.compteur": "Compteur relevé sur le véhicule",
  "expert.saisie.compteurAide":
    "Le vendeur a déclaré {n} km. Relevez ce que vous lisez, même si c'est différent.",
  "expert.saisie.ecartAuDessus": "Écart de {n} km avec le kilométrage déclaré, au-dessus.",
  "expert.saisie.ecartEnDessous": "Écart de {n} km avec le kilométrage déclaré, en dessous.",
  "expert.saisie.ecartFait":
    "Le fait est porté au rapport tel quel. S'il ne s'explique ni par le carnet d'entretien ni par les factures, c'est le constat éliminatoire « kilométrage incohérent » qui s'applique, et c'est à vous de le cocher.",
  "expert.saisie.nConstat": "{n} constat",
  "expert.saisie.nConstats": "{n} constats",
  "expert.saisie.rienASignaler": "rien à signaler",
  "expert.saisie.aideConstat": "Ce qu'on vous demande de constater",
  "expert.saisie.masquer": "Masquer",
  "expert.saisie.valorisation": "Valorisation",
  "expert.saisie.valorisationAide":
    "Valeur de marché estimée et état général. Ce n'est pas une case à cocher : c'est un chiffre. Le prix demandé par le vendeur ne vous est pas montré, pour que votre estimation reste la vôtre.",
  "expert.saisie.valeur": "Valeur de marché estimée",
  "expert.saisie.observations": "Observations et état général",
  "expert.saisie.observationsAide":
    "Ce que vous avez vu, ce qui explique votre valeur, et le détail de vos réserves.",
  "expert.saisie.photos": "Photographies",
  "expert.vue.frontRight": "Avant droit",
  "expert.vue.sideRight": "Profil droit",
  "expert.vue.rear": "Arrière",
  "expert.vue.sideLeft": "Profil gauche",
  "expert.vue.chassisNumber": "Numéro de châssis",
  "expert.vue.odometer": "Compteur",
  "expert.vue.engine": "Moteur",
  "expert.vue.underbody": "Soubassement",
  "expert.saisie.photosAide":
    "Vos vues sont une pièce à conviction : elles portent ce que vous attestez, et leur provenance est enregistrée comme celle d'une pièce du client.",
  "expert.saisie.photosNote":
    "Les vues conseillées ne bloquent pas l'envoi : c'est votre rapport qui fait foi, pas le nombre de photos.",
  "expert.saisie.aucunConstat": "{family} : aucun constat.",
  "expert.saisie.attestation":
    "Je certifie avoir examiné ce véhicule moi-même, et que les constats ci-dessus sont les miens.",
  "expert.saisie.grilleDecide":
    "Vous constatez, la grille décide. Le verdict est calculé par la grille de conformité de Hakili à partir de vos constats et de votre valeur : il ne vous est pas demandé, et il ne s'affiche pas pendant votre saisie pour ne pas peser sur votre estimation.",
  "expert.saisie.transmettre": "Transmettre au bureau",
  "expert.saisie.garder": "Garder sans transmettre",
  // `LANE E`: the button never blocks mute , it counts what is missing and names it.
  "expert.saisie.pointSansReponse": "{n} point sans réponse : {liste}",
  "expert.saisie.pointsSansReponse": "{n} points sans réponse : {liste}",
  "expert.saisie.manqueCarteGrise": "carte grise trouvée ou non",
  "expert.saisie.manqueChassisLu": "numéro de châssis lu",
  "expert.saisie.manqueChassis": "châssis conforme ou non",
  "expert.saisie.manqueAnneeLue": "année lue sur la carte grise",
  "expert.saisie.manqueAnnee": "année conforme ou non",
  "expert.saisie.manquePlaque": "plaque",
  "expert.saisie.manquePlaqueLue": "plaque lue sur le véhicule",
  "expert.saisie.manqueCompteur": "compteur relevé",
  "expert.saisie.manqueValeur": "valeur de marché estimée",
  "expert.saisie.manqueAttestation": "attestation",
  "expert.saisie.gardeeSurAppareil":
    "Votre saisie est enregistrée à chaque envoi ; le bureau la vise avant qu'elle parte à Hakili.",
  // `LANE E`, follow-up: the confirmation after « Transmettre au bureau », a green banner with the time.
  "expert.saisie.bandeauTransmise": "Saisie transmise au bureau : en attente du visa du responsable",
  "expert.saisie.bandeauHeure": "Transmise le {heure}",
  "expert.saisie.gardee": "Saisie gardée.",
  "expert.saisie.renvoyee": "Le bureau vous a renvoyé cette saisie : « {note} »",
  "expert.saisie.aViserTerrain": "Votre saisie est transmise : le bureau la vise avant l'envoi à Hakili.",
  "expert.saisie.aViserBureau": "Saisie attestée par l'expert : à viser.",
  "expert.rapport.verdictCalcule": "Verdict calculé",
  "expert.rapport.conforme": "conforme",
  "expert.rapport.ecartPrix": "écart de prix",
  "expert.rapport.nonConforme": "non conforme",
  "expert.rapport.bareme": "Barème",
  "expert.rapport.valeur": "Valeur estimée",
  "expert.rapport.envoyeLe": "Envoyé à Hakili le",
  "expert.rapport.saisiPar": "Saisi par",
  "expert.rapport.apresEnvoi":
    "Le verdict est celui de la grille de Hakili, calculé après l'envoi à partir de vos constats et de votre valeur. La correction d'un rapport envoyé est l'acte du cabinet, version suivante.",
  "expert.bureau.titre": "Rapports",
  "expert.bureau.sousTitre": "Les saisies de vos experts, à viser avant l'envoi à Hakili.",
  "expert.bureau.aucune": "Aucune saisie en cours dans le cabinet.",
  "expert.bureau.aViser": "À viser",
  "expert.bureau.enCours": "En cours de saisie",
  "expert.bureau.renvoyee": "Renvoyée à l'expert",
  "expert.bureau.visee": "Visée et envoyée",
  /** Round `C6`, `M4`: the entry opened again by the cabinet's own correction. */
  "expert.bureau.correction": "Correction en cours",
  "expert.bureau.retour": "Retour aux rapports",
  "expert.bureau.saisiPar": "Saisi par {name}, le {date}.",
  "expert.bureau.renvoyerTitre": "Renvoyer à l'expert",
  "expert.bureau.renvoyerAide": "Un mot pour l'expert : ce qu'il faut relire ou préciser.",
  "expert.bureau.renvoyer": "Renvoyer à l'expert",
  "expert.bureau.renvoyeeMot": "Saisie {reference} renvoyée à l'expert.",
  "expert.bureau.visaTitre": "Viser et envoyer à Hakili",
  // `ROUND S4-2-REFERENCE-DOCUMENT`, M1 (1) et (2): le rapport du cabinet est
  // une PIÈCE jointe au dossier, jamais « le » document de référence , c'est
  // le dossier Hakili (les constats, l'estimation) que la banque consulte.
  // Hakili ne le modifie jamais, mais il ne le remplace pas non plus. Le
  // joindre n'est plus une obligation pour viser (M1 (2)).
  "expert.bureau.visaAide":
    "Le rapport de votre cabinet, sur votre propre papier, est une pièce jointe au dossier : Hakili ne le modifie jamais, mais ce n'est pas le document de référence ; c'est le dossier Hakili, avec les constats et l'estimation, que la banque consulte. Vous pouvez attester et envoyer sans le joindre ; il pourra être ajouté ou corrigé plus tard, depuis « Mes rapports ».",
  "expert.bureau.joindrePapier": "Joindre le rapport du cabinet (PDF ou photo), facultatif",
  "expert.bureau.papierJoint": "Rapport joint : {name}",
  "expert.bureau.attestation": "Les constats saisis sont ceux de mon rapport.",
  "expert.bureau.viser": "Viser et envoyer",
  "expert.bureau.gardeAttestation": "attestez que les constats saisis sont ceux de votre rapport",
  "expert.bureau.viseeMot": "Rapport {reference} visé et envoyé à Hakili.",
  "expert.cabinet.titre": "Mon cabinet",
  "expert.cabinet.agrement": "Agrément",
  "expert.cabinet.zone": "Zone couverte",
  "expert.cabinet.depuis": "Partenaire depuis",
  "expert.cabinet.telephone": "Téléphone",
  "expert.cabinet.personnes": "Les personnes",
  "expert.cabinet.personneDepuis": "Dans le cabinet depuis {date}",
  "expert.cabinet.invitationEnAttente": "Invitation en attente",
  "expert.cabinet.inviter": "Inviter un expert",
  "expert.cabinet.inviterNote":
    "Hakili a ouvert le cabinet et nommé son responsable ; les experts, c'est le responsable qui les invite. Un compte se désactive et ne se supprime jamais, et ce qu'une personne suit est réattribué avant qu'elle ne parte.",
  "expert.cabinet.inviterNom": "Nom complet",
  "expert.cabinet.inviterEmail": "Adresse e-mail",
  "expert.cabinet.inviterTelephone": "Téléphone",
  "expert.cabinet.inviterEnvoyer": "Envoyer l'invitation",
  "expert.cabinet.inviterAnnuler": "Annuler",
  "expert.cabinet.inviterParti": "Invitation envoyée à {email}.",
  "expert.cabinet.inviterNonParti":
    "Le compte est créé mais l'invitation n'est pas partie : le canal n'est pas encore relié. Renvoyez-la depuis la liste.",
  "expert.cabinet.aucunePersonne": "Aucune personne dans ce cabinet.",
  "expert.cabinet.loi":
    "Vous constatez, la grille décide. Aucun écran de cet espace ne vous demande de signer un refus : un « non conforme » est la décision commerciale de Hakili, tirée de vos constats, et elle ne se met pas à votre nom.",
  // `round C5-2`, `M4`: the office screens , « Honoraires » (OD-29, decision D
  // for the prevented), « Mes rapports » (the validity follows the vehicle),
  // « Ma capacité du jeudi » (OD-30).
  "expert.honoraires.titre": "Honoraires",
  "expert.honoraires.sousTitre": "Par véhicule contrôlé, au montant convenu avec Hakili.",
  "expert.honoraires.parVehicule": "Par véhicule contrôlé",
  "expert.honoraires.figureNote": "FCFA, le montant convenu",
  "expert.honoraires.periode": "Sur la période",
  "expert.honoraires.nControle": "FCFA pour {n} contrôle rendu",
  "expert.honoraires.nControles": "FCFA pour {n} contrôles rendus",
  "expert.honoraires.aucun": "Aucun rapport envoyé sur la période : rien n'est acquis pour l'instant.",
  "expert.honoraires.acquis":
    "Un honoraire est acquis quand le rapport est envoyé. Pour un passage empêché, la règle n'est pas écrite : rien n'est décompté ni promis.",
  "expert.honoraires.empecheATrancher":
    "{n} passage empêché sur la période : à trancher avec Hakili, rien n'est décompté ni promis.",
  "expert.honoraires.empechesATrancher":
    "{n} passages empêchés sur la période : à trancher avec Hakili, rien n'est décompté ni promis.",
  "expert.historique.titre": "Mes rapports",
  "expert.historique.sousTitre": "L'historique des rapports envoyés, avec leur validité.",
  "expert.historique.aucun": "Aucun rapport envoyé par ce cabinet.",
  "expert.historique.valableJusquau": "Valable jusqu'au {date}",
  "expert.historique.visePar": "visé par {name}",
  "expert.historique.signalerErreur": "Signaler une erreur",
  "expert.historique.erreurAide": "Ce qui est faux dans ce rapport, en quelques mots.",
  "expert.historique.erreurEnvoyer": "Envoyer à Hakili",
  "expert.historique.erreurSignalee":
    "Erreur signalée à Hakili. Le rapport reste tel quel jusqu'à sa correction.",
  /**
   * Round `C6`, `M4` (`OD-31`, `OD-33`): « Mes rapports » reads the document
   * of record and Hakili's sheet, the versions kept, Hakili's request for a
   * correction; « Corriger le rapport » is the cabinet's own act, in place
   * of « Signaler une erreur ». Two keys for the agreement, never a
   * parenthesis. A missing sheet says why (A47).
   */
  "expert.historique.version": "Version {n} · empreinte SHA-256 {sha}",
  "expert.historique.versionsConservees": "{n} version précédente conservée",
  "expert.historique.versionsConserveesPluriel": "{n} versions précédentes conservées",
  "expert.historique.ouvrirRapport": "Ouvrir le rapport",
  "expert.historique.lectureHakili": "Lecture Hakili",
  "expert.historique.lectureAbsente":
    "Hakili n'a pas pu conserver sa lecture de ce rapport ; le rapport du cabinet fait foi.",
  "expert.historique.papierAbsent":
    "Ce rapport a été enregistré avant que le papier du cabinet ne soit exigé : aucun document joint.",
  "expert.historique.corrigerRapport": "Corriger le rapport",
  "expert.historique.correctionOuverte":
    "Correction en cours : reprenez la saisie sur le bureau, puis visez à nouveau ; la nouvelle version remplacera celle-ci, qui reste consultable.",
  "expert.historique.correctionDemandee": "Correction demandée par Hakili le {date} : {reason}",
  "expert.historique.refuserAide": "Pourquoi le rapport tient tel quel, en quelques mots.",
  "expert.historique.refuserEnvoyer": "Refuser la correction",
  "expert.historique.refusEnvoye": "Le refus a été transmis à Hakili ; le rapport reste tel quel.",
  "expert.historique.correctionOuverteMot":
    "La correction est ouverte : la saisie est à reprendre sur le bureau, puis à viser.",
  "expert.capacite.titre": "Ma capacité du jeudi",
  "expert.capacite.aide":
    "Le nombre de véhicules que le cabinet peut contrôler ce jeudi-là. Un chiffre par jeudi, zéro compris ; sans déclaration, le minimum du contrat s'applique.",
  "expert.capacite.aucunJeudi": "Aucun jeudi de contrôle à venir.",
  "expert.capacite.minimumParDefaut": "Minimum du contrat : {n}",
  "expert.capacite.declareeLe": "Déclarée : {n}",
  "expert.capacite.confirme": "{n} confirmé par Hakili",
  "expert.capacite.confirmes": "{n} confirmés par Hakili",
  "expert.capacite.sousMinimum": "Sous le minimum du contrat ({n}) : Hakili est prévenu.",
  "expert.capacite.autre": "Autre",
  "expert.capacite.declarer": "Déclarer",
  "expert.capacite.declaree": "Capacité déclarée : {n}.",
  "expert.role.responsableCabinet": "Responsable du cabinet",
  "expert.role.expertTerrain": "Expert",
  "client.eligibilite.resultat": "Résultat",
  "client.eligibilite.retourFormulaire": "Revenir au formulaire",
  "client.eligibilite.avecUnSalaire": "Avec un salaire de",
  "client.eligibilite.etUnPret": " et un prêt en cours de",
  "client.eligibilite.capaciteEstDe": ", l'estimation de votre capacité mensuelle est d'environ",
  "client.eligibilite.ilManque": ". Il manque",
  "client.eligibilite.pourCeVehicule": "pour ce véhicule.",
  "client.eligibilite.defavorable": "Pas pour ce véhicule, pas aujourd'hui",
  "client.eligibilite.capacite": "Votre capacité mensuelle",
  // `round C3`, `C3.0(f)`: the drawing's own three, which say what the band
  // MEANS rather than naming it , « confortable » alone reads as an adjective
  // with no subject.
  "client.eligibilite.bandeConfortable": "Charge confortable",
  "client.eligibilite.bandeAcceptable": "Charge acceptable, peu de marge",
  "client.eligibilite.bandeAuDela": "Au-delà de ce que les banques acceptent",
  "client.eligibilite.ouvrir": "Ouvrir mon dossier",
  "client.eligibilite.manque": "Il manque",
  "client.eligibilite.changerait": "Ce qui changerait la situation",
  "client.eligibilite.changeVehicule": "Un véhicule moins cher.",
  "client.eligibilite.changeApport": "Un apport plus élevé.",
  "client.eligibilite.changeFinDuPret": "La fin de votre prêt en cours.",
  "client.eligibilite.veille": "Nous vous prévenons dans",
  "client.eligibilite.dansMaCapacite": "Voir les véhicules dans ma capacité",
  "client.eligibilite.comptant": "Acheter comptant, avec expertise",
  // `WF-CLI-04`'s acceptance criterion, said to the buyer rather than enforced
  // in silence: this screen is about ONE véhicule's mensualité.
  "client.eligibilite.sansSimulation":
    "Choisissez d'abord un véhicule : cette vérification porte sur une mensualité précise.",
  // `S5-4`: the button beside that sentence, and the way out of the vehicle sheet's wait.
  "client.eligibilite.choisirVehicule": "Choisir un véhicule",
  "client.vehicule.retourRecherche": "Retour à la recherche",
  // `round CW`, `CW.3`: « Voir le véhicule », `WF-CLI-07`.
  "client.visite.titre": "Voir le véhicule",
  "client.visite.demander": "Demander une visite",
  "client.visite.appeler": "Appeler le",
  "client.visite.appelerSansPorte": "Bientôt : le numéro du vendeur n'est pas encore enregistré chez Hakili.",
  "client.visite.acheter": "Je veux acheter ce véhicule",
  "client.visite.raccourci": "Vous ne savez pas encore ce que vous pouvez emprunter ? Vérifier en 2 minutes",
  "client.visite.commentPayer": "Comment comptez-vous payer ?",
  "client.visite.financer": "Je vais financer",
  "client.visite.comptant": "Je paie comptant",
  "client.visite.dossierEnCours": "Votre dossier sur ce véhicule est en cours",
  // `round CW`, `CW.3`: the fiche, `WF-CLI-04`, in the drawing's own words.
  "client.vehicule.sansPhoto": "Aucune photo pour ce véhicule",
  "client.vehicule.photo": "Photographie du véhicule",
  "client.vehicule.sur": "sur",
  "client.vehicule.places": "places",
  // `round C3-B`, the consolidated amendment: the fiche to the 9 September
  // drawing, head to foot, in its own words (`hakili-client.jsx`, `Vehicule`).
  "client.vehicule.aKmDeVous": "à {km} km de vous",
  "client.vehicule.fiabilite": "Fiabilité",
  "client.vehicule.fiabiliteAbsente": "La note de fiabilité du vendeur n'est pas encore calculée.",
  "client.vehicule.loinTitre": "Ce véhicule est à {km} km de vous",
  "client.vehicule.loinDepuis": "{trajet} depuis {ville}. Ne faites pas le trajet pour rien.",
  "client.vehicule.loinExpertiseTitre": "Expertise avant votre déplacement",
  "client.vehicule.loinExpertiseDetail":
    "Nous mandatons un expert indépendant. Vous recevez son rapport et ses photos avant de décider de faire la route.",
  "client.vehicule.loinVideoTitre": "Visite en vidéo avec votre chargé de dossier",
  "client.vehicule.loinVideoDetail":
    "Il se rend chez le vendeur et vous montre le véhicule en direct, vos questions comprises.",
  "client.vehicule.simulateur": "Combien cela coûte par mois",
  "client.vehicule.apport": "Apport",
  "client.vehicule.duree": "Durée",
  "client.vehicule.mois": "mois",
  // `round C3-B`, the founder's decision of 11 September: ONE estimate, the
  // frais de dossier Hakili inside the loan; 03 WF-CLI-04 step 3 amended by
  // record. The box carries its label, the figure and ONE sentence , the
  // insurance and the bank-rate lines are the designer's stroke, owed.
  "client.vehicule.mensualiteEstimee": "Mensualité estimée, frais de dossier inclus",
  "client.vehicule.estimationIndicative": "Estimation indicative. Le taux définitif est fixé par la banque.",
  "client.vehicule.horsCouvertureTitre": "Véhicule à {city}, hors de nos zones de contrôle",
  "client.vehicule.horsCouvertureTexte":
    "Nos cabinets agréés passent à Bamako. Nous ne pouvons donc ni faire expertiser ce véhicule, ni le financer, et nous ne prendrons aucun frais pour lui. Vous pouvez le garder en favori : nous vous prévenons dès que la couverture arrive.",
  "client.vehicule.comparaison": "Comptant ou à crédit",
  "client.vehicule.comptant": "Vous payez comptant",
  "client.vehicule.comptantSuite": "Il ne vous reste rien de cette somme pour un imprévu",
  "client.vehicule.credit": "Vous financez",
  "client.vehicule.parMois": "par mois",
  "client.vehicule.creditSuite": "Vous gardez {amount}\u00a0FCFA disponibles",
  "client.vehicule.comparaisonPied":
    "Les deux options sont possibles sur Hakili. Le crédit coûte plus cher au total, mais laisse votre trésorerie intacte.",
  "client.vehicule.verification": "Ce qui est vérifié avant que vous achetiez",
  "client.vehicule.check1": "Visite technique à jour",
  "client.vehicule.check1Detail": "Contrôle officiel valide, vérifié avant la vente",
  "client.vehicule.check2": "Expertise indépendante",
  "client.vehicule.check2Detail": "Un expert agréé examine le véhicule et estime sa valeur réelle",
  "client.vehicule.check3": "Carte grise et propriété",
  "client.vehicule.check3Detail": "Le vendeur est bien propriétaire, le véhicule n'est pas gagé",
  "client.vehicule.check4": "Kilométrage vérifié",
  "client.vehicule.check4Detail": "Compteur recoupé avec le carnet d'entretien",
  "client.vehicule.jamaisSans":
    "N'achetez jamais un véhicule sans visite technique ni expertise indépendante. Sur Hakili, aucune vente ne se conclut sans les deux.",
  "client.vehicule.frais": "Frais de vérification",
  "client.vehicule.fraisRembourses":
    "Si le véhicule échoue au contrôle, ces frais vous sont intégralement remboursés et c'est le vendeur qui supporte le coût.",
  "client.vehicule.rapportDisponible": "Rapport d'expertise déjà disponible",
  "client.vehicule.rapportDisponibleDetail":
    "Ce véhicule a déjà été expertisé et son rapport reste valable. Vous ne payez qu'un forfait réduit pour y accéder.",
  "client.vehicule.ordre": "Dans quel ordre cela se passe",
  "client.vehicule.ordre1": "Vous réglez les frais de vérification",
  "client.vehicule.ordre1Detail": "{fee}, rien n'est engagé avant",
  "client.vehicule.ordre2": "Nous faisons contrôler le véhicule",
  "client.vehicule.ordre2Date": "Expert agréé et visite technique, le {date}",
  "client.vehicule.ordre3": "Vous recevez le rapport",
  "client.vehicule.ordre3Detail": "État réel, photos et valeur estimée",
  "client.vehicule.ordre4": "Vous décidez",
  "client.vehicule.ordre4Detail": "Financer, payer comptant, ou renoncer",
  "client.vehicule.pied":
    "Aucune banque n'est contactée à ce stade, et aucun document personnel ne vous est demandé.",
  "client.vehicule.verifier": "Vérifier mon éligibilité en 2 minutes",
  "client.vehicule.voir": "Voir le véhicule",
  "client.vehicule.gratuit": "Gratuit. Vous contactez le vendeur directement, sans engagement de notre part.",
  "common.action.retour": "Retour",
  // `round CW`, `CW.2`: the card the drawing draws, word for word.
  "client.carte.baisse": "Prix baissé de",
  // `round C2`, `C2.7`: the drawing's own sentence for a car with no picture ,
  // « Aucune photo pour ce véhicule » , so the frame says why it is empty
  // instead of reading as a broken image.
  "client.carte.sansPhoto": "Aucune photo pour ce véhicule",
  "client.carte.dejaPasse": "Déjà passé",
  "client.carte.favori": "Mettre de côté",
  "client.carte.km": "km",
  "client.carte.a": "à",
  "client.carte.fiabilite": "Fiabilité",
  "client.carte.aPartirDe": "à partir de",
  "client.carte.parMois": "par mois",
  "client.carte.partSalaire": "% de votre salaire",
  "common.monnaie.fcfa": "FCFA",
  "client.moncompte.titre": "Mon compte",
  "client.moncompte.resume": "Vos informations, l'appareil de confiance de ce téléphone, et la déconnexion.",
  "client.depot.titre": "Déposer une pièce",
  "client.depot.resume":
    "Photographiez la pièce demandée. Ce lien vous identifie : aucune connexion n'est nécessaire.",
  // `round C7`, `C7.3`: the public page behind the deposit link , WF-CON-06
  // step 3 and WF-CLI-18. No drawing exists for it; the copy is the record's.
  "client.depot.pieceDemandee": "Pièce demandée",
  "client.depot.photographier": "Prendre une photo",
  "client.depot.choisirFichier": "Choisir un fichier",
  "client.depot.envoiEnCours": "Envoi en cours…",
  "client.depot.merci": "Merci, votre pièce est bien reçue.",
  "client.depot.merciDetail": "Votre chargé de dossier la vérifie. Vous pouvez fermer cette page.",
  // `S4-6`: the same page, opened by the dealer for its own pièce , never "votre" dossier.
  "client.depot.merciDetailVendeur": "Le chargé du dossier la vérifie. Vous pouvez fermer cette page.",
  "client.depot.dejaUtilise": "Ce lien a déjà servi : la pièce a été reçue.",
  "client.depot.expire": "Ce lien n'est plus valable. Demandez-en un nouveau à votre chargé de dossier.",
  "client.depot.inconnu": "Ce lien n'existe pas ou n'est plus valable.",
  "client.depot.chargement": "Lecture du lien…",
  "client.session.chargement": "Chargement…",
  "client.session.inattendue": "Réponse inattendue du serveur.",
  // ── bank · lane A ──
  "bank.liste.ouvrirDossier": "Ouvrir le dossier de {client}",
  "bank.liste.colDossier": "Dossier",
  "bank.liste.colEtape": "Étape",
  "bank.liste.colDernierFait": "Dernier fait",
  "bank.revue.confirmerConformes": "Confirmer les propositions conformes",
  "bank.revue.etatSignale": "Signalé",
  "bank.revue.etatOuvert": "À traiter",
  "bank.revue.barreActions": "Actions de la revue",
  "bank.composer.formuleTitre": "Comment le TAEG est calculé",
  "bank.composer.barreActions": "Actions du composeur",
  "bank.composer.taegSousReglementaire":
    "{taeg}\u202f% · sous le plafond de {plafond}\u202f% : plafond réglementaire, la banque n'a pas de politique saisie",
  "bank.composer.envoyerSaisirTaux":
    "Saisissez un taux : le TAEG et le contrôle d'usure doivent être calculés avant l'envoi.",
  "bank.feuille.titre": "Voir le calcul de la note",
  // `ARC2-SHEET` (D2 `analyst.js:91-110`): the sheet's own words.
  "bank.feuille.feuille": "Feuille de calcul",
  "bank.feuille.fcfaMois": "FCFA par mois",
  "bank.feuille.dureeMois": "{n} mois ({base})",
  "bank.feuille.dureeDemandee": "durée demandée",
  "bank.feuille.dureeMaximale": "durée maximale",
  "bank.feuille.tete": "{duree} : {pct} de la quotité cessible utilisée",
  "bank.feuille.teteDepasse": ", la mensualité dépasse",
  "bank.feuille.barre": "{n} mois · {base}",
  "bank.feuille.legende": "Trait pointillé : la part cessible, {montant}.",
  "bank.feuille.revenu": "Salaire net retenu",
  "bank.feuille.cessible": "× quotité cessible {pct}",
  "bank.feuille.cessibleVide": "× quotité cessible",
  "bank.feuille.engagements": "engagements en cours",
  "bank.feuille.mensualiteA": "+ mensualité à {duree}",
  "bank.feuille.mensualite": "+ mensualité",
  "bank.feuille.utiliseeFormule":
    "part de la quotité cessible utilisée = (engagements + mensualité) ÷ ({pct} du net)",
  "bank.feuille.quotiteTest": "Quotité, le test de ce point (enregistrée, sans durée) ; plafond {pct}",
  "bank.feuille.tauxLigneHaute":
    "Mensualité au taux de la ligne la plus haute de la grille ({note}, {taux}\u202f%).",
  "bank.feuille.tauxMandat": "Mensualité au taux du mandat ({taux}\u202f%).",
  "bank.feuille.carteLit": "Ce point lit la quotité : {quotite} (plafond {pct}), sans durée.",
  "bank.feuille.carteCapacite": "La Capacité de la note se lit à {duree}",
  "bank.feuille.carteCapaciteDepasse": " : la mensualité dépasse.",
  "bank.feuille.carteTitreNon": "Quotité non calculée : {court}.",
  "bank.feuille.quotite": "quotité",
  "bank.feuille.plafond": "quotité cessible",
  "bank.feuille.vide": "\u2212",
  "bank.composer.choisirDuree": "Choisissez une durée.",
  "bank.composer.capaciteRestante": "Part de la capacité restante",
  "bank.composer.choisirDureeRecap": "Choisissez une durée pour lire le récapitulatif.",
  "bank.signature.barreActions": "Actions de la signature",
  "bank.signature.rdvHeureExemple": "10:00",
  "bank.conditions.barreActions": "Actions des conditions",
  "bank.conditions.progression": "{n} conditions levées sur {total}",
  "bank.ordre.repartition":
    "Répartition du prix : {dealer}\u202f% au concessionnaire, {hakili}\u202f% à Hakili",
  "bank.conditions.attenteAujourdhui": "en attente depuis aujourd'hui",
  "bank.conditions.piedOrdonne": "Ordre transmis. En attente des fonds.",
  "bank.conditions.deuxVirements": "Les deux virements",
  "bank.conditions.constateParHakili": "Constaté par Hakili à la réception des frais.",
  "bank.conditions.fraisRecusLe": "Frais reçus le {date}",
  "console.dossier.constateParHakili": "constaté par Hakili",
  "console.dossier.transmettreOrdre": "Transmettre l'ordre de versement",
  "bank.briefing.cleLtv": "LTV sur le véhicule",
  "bank.briefing.clePlafondLtv": "plafond de LTV",
  "bank.briefing.dureeMois": "{n} mois ({base})",
  "bank.briefing.precisionDuree": "à {duree}",
  "bank.briefing.dureeDemandee": "durée demandée",
  "bank.briefing.dureeMaximale": "durée maximale",
  "bank.briefing.tauxLigneHaute": "au taux de la ligne la plus haute de la grille ({note}, {taux}\u202f%)",
  "bank.briefing.tuileQuotite": "Part de la quotité cessible utilisée",
  "bank.briefing.tuileLtv": "LTV",
  "bank.briefing.tuileApport": "Apport",
  "bank.briefing.tuileNonCalculee": "non calculée",
  "bank.briefing.plafondBanque": "plafond de la banque {plafond}",
  "bank.briefing.attentionTitre": "Ce qui demande votre attention",
  "bank.briefing.capacite": "Capacité",
  "bank.briefing.capaciteLigne": "{duree}\u202f: {pct}\u202f% de la quotité cessible utilisée.",
  "bank.briefing.pct": "{n}\u202f%",
  "bank.briefing.explicationUn":
    "Hakili a proposé 1 point\u202f: vous le confirmez ou le corrigez à l'étape suivante. Il ne fait avancer aucune étape.",
  "bank.briefing.piecesLues": "{n} pièces lues",
  "bank.briefing.pieceLueUne": "1 pièce lue",
  "bank.briefing.piecesLuesInconnu": "nombre de pièces lues non conservé",
  "bank.briefing.tuileIncidents": "Incidents bancaires",
  "bank.briefing.incidentsReleves": "sur les {n} relevés",
  "bank.briefing.incidentsReleveUn": "sur 1 relevé",
  "bank.briefing.rienATitre": "Rien à signaler.",
  // The designer's words (review dc825d13): « non lu par Hakili » accepted; the sub-reasons without « apport »; « nombre de pièces lues non conservé ». « non conservé pour ce dossier » stays PROPOSITION (the other causes).
  "bank.briefing.causeNonLu": "non lu par Hakili",
  "bank.briefing.causeInconnue": "non conservé pour ce dossier",
  "bank.briefing.apportNonVisible": "non visible sur le compte",
  "bank.briefing.origineNonCoherente": "origine non cohérente",
  "bank.briefing.apportEmprunte": "emprunté",
  "bank.briefing.origineACribler": "origine à cribler",
  "bank.synthese.afficher": "Afficher la synthèse du dossier",
  "bank.synthese.masquer": "Masquer la synthèse du dossier",
  "bank.synthese.client": "Le client",
  "bank.synthese.demande": "La demande",
  "bank.synthese.capacite": "Capacité et garanties",
  "bank.synthese.financement": "Financement\u202f: ce qui entre dans le prêt",
  "bank.synthese.employeur": "Employeur",
  "bank.synthese.anciennete": "Ancienneté",
  "bank.synthese.contrat": "Contrat",
  "bank.synthese.salaire": "Salaire net",
  "bank.synthese.engagements": "Engagements",
  "bank.synthese.vehicule": "Véhicule",
  "bank.synthese.prix": "Prix",
  "bank.synthese.apport": "Apport",
  "bank.synthese.apportFrancs": "Apport en francs",
  "bank.synthese.finance": "Financé",
  "bank.synthese.dureeDemandee": "Durée demandée",
  "bank.synthese.vendeur": "Vendeur",
  "bank.synthese.cessible": "Quotité cessible {pct}\u202f%",
  "bank.synthese.cessibleSansPart": "Quotité cessible",
  "bank.synthese.disponible": "Capacité disponible",
  "bank.synthese.quotite": "Quotité",
  "bank.synthese.quotiteA": "Quotité à {duree}",
  "bank.synthese.partUtiliseeA": "Part de la quotité cessible utilisée à {duree}",
  "bank.synthese.valeurExpertisee": "Valeur expertisée",
  "bank.synthese.ltvPlafond": "{ltv} (plafond {plafond})",
  "bank.synthese.garantie": "Garantie mécanique",
  "bank.synthese.cleGarantie": "garantie financée",
  "bank.synthese.capital": "Capital financé",
  "bank.synthese.montantNote": "{montant}, {note}",
  "bank.synthese.fraisBanqueMoment": "à la signature",
  "bank.synthese.fraisHakiliMoment": "avant le rendez-vous de signature",
  "bank.synthese.aucun": "aucun",
  "bank.synthese.parMois": "{montant}\u00a0/\u00a0mois",
  "bank.synthese.fraisFinances": "financés dans le prêt à la demande du client",
  "bank.synthese.offreErreurCorps":
    "Les éléments de l'offre ne sont pas arrivés pour ce dossier. Rien n'a été modifié.",
  // `S1-a` COMPORTEMENT DU COMPTE (D2 `D2-behaviour.md`, frames 201 to 211, the architect's ruling of 31 October): the SENTENCE FRAMES only; every month, year, count and figure is served.
  "bank.comportement.onglet": "Comportement du compte",
  "bank.comportement.titre": "Comportement du compte",
  "bank.comportement.dossierOnglet": "Dossier",
  "bank.comportement.onglets": "Dossier et comportement du compte",
  "bank.comportement.fenetre": "Les {lus} relevés bancaires, de {periode} ({lus} mois)",
  "bank.comportement.fenetrePartielle": "{lus} relevés lus sur {total}, {mois}",
  "bank.comportement.faits":
    "Faits lus sur les lignes des relevés. Aucun jugement sur la personne, aucune note.",
  // Frame 208 (`D2-behaviour.md:17`, the architect's ruling of 31 October): `{demois}` is « de juillet » or « d'août » (the elision is the view's), `{premier}` the first month unread.
  // The third sentence is TRUE only when the months unread open the window; otherwise the short pair says only « Aucun mois n'est estimé. ».
  "bank.comportement.partielNonLu":
    "Le relevé {demois} n'a pas pu être lu. Le comportement est lu sur {lus} mois seulement. Ce qui précède {premier} n'est pas connu ; aucun mois n'est estimé.",
  "bank.comportement.partielNonLuCourt":
    "Le relevé {demois} n'a pas pu être lu. Le comportement est lu sur {lus} mois seulement. Aucun mois n'est estimé.",
  "bank.comportement.partielNonLuPluriel":
    "Les relevés {demois} n'ont pas pu être lus. Le comportement est lu sur {lus} mois seulement. Ce qui précède {premier} n'est pas connu ; aucun mois n'est estimé.",
  "bank.comportement.partielNonLuPlurielCourt":
    "Les relevés {demois} n'ont pas pu être lus. Le comportement est lu sur {lus} mois seulement. Aucun mois n'est estimé.",
  // PROPOSITION words for the designer (`D2-copy.md`: sections and traceability): the record's own sections and links.
  "bank.comportement.sectionEntrees": "Ce qui entre",
  "bank.comportement.sectionPret": "Paiements récurrents qui ressemblent à un prêt",
  "bank.comportement.voirLigne": "Voir la ligne du relevé",
  "bank.comportement.retourLigne": "Retour au comportement du compte",
  "bank.comportement.montant": "Montant",
  "bank.comportement.solde": "Solde",
  "bank.comportement.colMois": "Mois",
  "bank.comportement.colTotal": "Total crédité",
  "bank.comportement.colSalaire": "Salaire",
  "bank.comportement.colJour": "Jour",
  "bank.comportement.jourDuMois": "le {jour}",
  "bank.comportement.moisVus": "{n} mois sur {total}",
  // `S1-a`: the months as the behaviour page names them (served as `YYYY-MM`; the names are the catalogue's, section 06 6.5.1).
  "bank.comportement.mois01": "janvier",
  "bank.comportement.mois02": "février",
  "bank.comportement.mois03": "mars",
  "bank.comportement.mois04": "avril",
  "bank.comportement.mois05": "mai",
  "bank.comportement.mois06": "juin",
  "bank.comportement.mois07": "juillet",
  "bank.comportement.mois08": "août",
  "bank.comportement.mois09": "septembre",
  "bank.comportement.mois10": "octobre",
  "bank.comportement.mois11": "novembre",
  "bank.comportement.mois12": "décembre",
  "bank.comportement.heroBas": "Il reste au moins {montant} sur le compte la veille du salaire.",
  "bank.comportement.heroSurtitre": "À retenir · la veille du salaire",
  "bank.comportement.partDuSalaire": "{percent}\u202f% du salaire",
  "bank.comportement.autresMois": "{montant} en {mois}",
  "bank.comportement.nonLuTitre": "Les relevés n'ont pas encore été lus.",
  "bank.comportement.nonLuCorps":
    "Le comportement du compte apparaît dès que Hakili a lu les trois relevés du dossier.",
  "bank.comportement.indisponibleTitre": "Le comportement du compte n'est pas disponible pour le moment.",
  "bank.comportement.indisponibleCorps":
    "Les relevés sont au dossier ; Hakili n'a pas pu en tirer le comportement. Rien n'a été inventé.",
  "bank.comportement.chargement": "Lecture du comportement du compte…",
  "bank.comportement.boutonChef": "Comportement du compte",
  "bank.comportement.boutonChefSousTitre": "lu sur les relevés bancaires du dossier",
  "bank.synthese.contratFonctionnaireTitulaire": "Fonctionnaire titulaire",
  "bank.synthese.contratCdi": "CDI",
  "bank.synthese.contratCdd": "CDD",
  "bank.synthese.contratContractuelEtat": "Contractuel de l'État",
  "bank.synthese.contratAutre": "Autre",
  "bank.synthese.ancienneteMois": "{n} mois",
  "bank.synthese.embaucheLe": "embauché le {date}",
  "bank.synthese.nonDeclaree": "non déclarée",
  // The designer's words (review 22ba2035): « réglée comptant » and « retirée » approved; « non déclarée » plain, no dash, no stop.
  "bank.synthese.garantieReglement": "Garantie non financée",
  "bank.synthese.garantieComptant": "réglée comptant",
  "bank.synthese.garantieRetiree": "retirée",
  // ARC-1 (D2 frame 26, the pièce viewer). « Ouverture de la pièce… » and « Cette pièce n'est pas disponible pour le moment. » are
  // PROPOSITIONS (D2 draws a stylised page and neither state); the rest is D2's text verbatim.
  "bank.visionneuse.fermer": "Fermer",
  "bank.visionneuse.apercu": "Aperçu de la pièce",
  "bank.visionneuse.extrait": "Extrait relevé par Hakili",
  "bank.visionneuse.page": "page {n} sur {total}",
  "bank.visionneuse.conclusion": "Conclusion proposée\u202f:",
  "bank.visionneuse.verifications": "Vérifications de Hakili",
  "bank.visionneuse.constatBloquant": "Constat bloquant",
  "bank.visionneuse.constatALever": "Constat à lever",
  "bank.visionneuse.constatSignale": "Constat signalé",
  "bank.visionneuse.marquerConforme": "Marquer conforme",
  "bank.visionneuse.consultation": "Consultation. La validation des pièces se fait à l'étape de revue.",
  "bank.visionneuse.ouverture": "Ouverture de la pièce…",
  "bank.visionneuse.indisponible": "Cette pièce n'est pas disponible pour le moment.",
  // ARC-1 (D2 frames 20 to 25, the revue as a table): D2's words verbatim.
  "bank.revue.compteurProgres": "Points traités",
  // D2 frames 11, 12 and 24: the work column's analysis callouts, D2's words verbatim.
  "bank.hakili.enCoursTitre": "Hakili analyse le dossier.",
  "bank.hakili.enCoursSuite": "Ses propositions arrivent.",
  "bank.hakili.echecTitre": "L'analyse de Hakili n'a pas abouti.",
  "bank.hakili.echecSuite": "La revue se fait à la main\u202f: vous traiterez les {n} points un par un.",
  "bank.revue.etapes": "Les trois étapes de la revue",
  "bank.revue.listePoints": "Points de la revue",
  "bank.revue.colNumero": "#",
  "bank.revue.colPoint": "Point",
  "bank.revue.colPropose": "Hakili propose",
  "bank.revue.colPreuve": "Preuve",
  "bank.revue.colActe": "Votre acte",
  "bank.revue.preuvePage": "p. {n}",
  "bank.revue.preuveCalcul": "calcul",
  "bank.revue.preuvePieces": "pièces",
  "bank.revue.sansProposition": "Pas de proposition",
  "bank.revue.pasDeProposition": "Pas de proposition de Hakili\u202f: votre verdict.",
  "bank.revue.voirPieceDuPoint": "Voir la pièce du point {n}",
  "bank.revue.examiner": "Examiner",
  "bank.revue.examinerLePoint": "Examiner le point {n}",
  "bank.revue.reprendre": "Reprendre",
  "bank.revue.corrige": "Corrigé",
  "bank.revue.confirme": "Confirmé",
  "bank.revue.voirPiece": "Voir la pièce",
  "bank.revue.confirmerProposition": "Confirmer la proposition",
  "bank.revue.fermer": "Fermer",
  "bank.visionneuse.source": "page {n}",
  "bank.synthese.garantieNonSouscrite": "non souscrite",
  "bank.synthese.garantieNonSouscriteMontant": "non souscrite · {montant}",
  "bank.visionneuse.depose": "déposé le {date}",
  "bank.demande.votreDemande": "Votre demande",
  "bank.demande.aideSousLaBoite":
    "Reprend automatiquement les points que vous avez signalés en revue. Modifiable avant envoi.",
  "bank.demande.quiTraite": "Qui traite la demande",
  "bank.demande.parClient": "Le client, via Hakili",
  "bank.demande.parHakili": "Vérification interne par Hakili",
  "bank.refus.choisirMotif": "Choisissez un motif.",
  "bank.dossier.moisDemandes": "{n} mois demandés",
  "bank.dossier.noteDeSolidite": "Note de solidité",
  "bank.dossier.noteProvisoire": "Note provisoire",
  "bank.dossier.noteIncomplete": "Note incomplète",
  "bank.dossier.capaciteNonCalculee": "Capacité non calculée",
  "bank.briefing.recuDeHakili": "Reçu {quand} de Hakili · dossier complet",
  "bank.briefing.recuDeHakiliPieces": "Reçu {quand} de Hakili · dossier complet, {pieces}",
  "bank.briefing.apportVisibleCompte": "visible sur le compte",
  "bank.briefing.explication":
    "Hakili a proposé {points}{etat}\u202f: vous les confirmez ou les corrigez à l'étape suivante. Il ne fait avancer aucune étape.",
  "bank.briefing.lesPoints": "les {n} points",
  "bank.briefing.etatProvisoire": "\u202f; la note est provisoire, une lecture n'a pas été faite",
  "bank.briefing.etatIncomplete": "\u202f; la note est incomplète, aucune lettre",
  "bank.briefing.rienACorps": "Les {n} points sont proposés conformes.",
  "bank.briefing.grilleIncomplete": "Grille de taux incomplète.",
  "bank.briefing.grilleSansOffre":
    "La capacité est calculée au taux le plus haut présent dans la grille ({note}, {taux}\u202f%), sans l'embellir. La grille n'a pas de taux pour {notes} {liste}\u202f: aucune offre n'est proposée pour la note {lettre} et aucun écart de taux n'est calculé\u202f; le TAEG est calculé sur le taux que vous saisissez.",
  "bank.briefing.grilleAvecOffre":
    "La capacité est calculée au taux le plus haut présent dans la grille ({note}, {taux}\u202f%), sans l'embellir. La grille n'a pas de taux pour {notes} {liste}.",
  "bank.briefing.lesNotes": "les notes",
  "bank.briefing.laNote": "la note",
  "bank.hakili.echecSuiteSansNombre":
    "La revue se fait à la main\u202f: vous traiterez les points un par un.",
  "bank.briefing.causeGrilleExplique":
    "La grille de taux de votre banque n'est pas saisie\u202f: sans elle, la mensualité de référence n'existe pas.",
  "bank.briefing.causeQuotiteExplique":
    "La quotité cessible n'est pas connue pour ce dossier\u202f: la capacité disponible ne peut pas être calculée.",
  "bank.briefing.causeSalaireExplique":
    "Aucun salaire net n'est retenu pour ce dossier\u202f: la capacité disponible ne peut pas être calculée.",
  "bank.briefing.causeEngagementsExplique":
    "Les engagements en cours de l'emprunteur ne sont pas connus\u202f: la capacité disponible ne peut pas être calculée.",
  "bank.briefing.pasDeNotePasDOffre": "Pas de note, pas d'offre.",
  "bank.briefing.chefSaisitLaGrille": "Votre chef du crédit saisit la grille sous « Politique ».",
  "bank.briefing.etatToutesPreuves": ", tous avec leur preuve",
  "bank.dossier.chezHakili": "Chez Hakili",
  "bank.briefing.causeGrille": "pas de grille de taux",
  "bank.briefing.causeQuotite": "pas de quotité cessible",
  "bank.briefing.causeSalaire": "pas de salaire net retenu",
  "bank.briefing.causeEngagements": "aucun engagement connu",
  "bank.briefing.causeMandat": "pas de taux de mandat",
  "bank.briefing.causeInconnueCapacite": "cause non précisée",
  "bank.briefing.capaciteNonCalculeeCause": "Capacité non calculée\u202f: {cause}.",
  "bank.briefing.proposeDuree": "Hakili propose {n} mois ({base}).",
  "bank.briefing.proposeDureeSeule": "Hakili propose {n} mois.",
  "bank.briefing.pasDOffre": "Pas d'offre\u202f: {raison}.",
  // ── end bank · lane A ──
  // ── bank · lane C ──
  "bank.login.titreConnexion": "Connexion",
  "bank.login.expiree": "Votre session a expiré. Veuillez vous reconnecter.",
  "bank.login.saisirIdentifiants": "Saisissez votre adresse e-mail et votre mot de passe.",
  "bank.login.panneauPhrase1": "Un dossier lu avant que vous l'ouvriez.",
  "bank.login.panneauPhrase2": "Une décision qui reste la vôtre.",
  // ── bank · RESET-PASSWORD R2 (frames 100 to 109, D-1024-4, `design/bank-v2` at 10c95f31) ──
  "bank.login.oublie": "Mot de passe oublié ?",
  "bank.login.reinitFaite": "Votre mot de passe est enregistré.",
  "bank.login.reinitFaiteSuite":
    "Toutes vos sessions ont été fermées, sur tous vos appareils. Connectez-vous avec le nouveau mot de passe.",
  "bank.oubli.titre": "Mot de passe oublié",
  "bank.oubli.consigne":
    "Indiquez l'adresse e-mail de votre compte : nous vous envoyons un lien pour choisir un nouveau mot de passe.",
  "bank.oubli.envoyer": "Envoyer le lien",
  "bank.oubli.saisirAdresse": "Saisissez votre adresse e-mail.",
  "bank.oubli.retour": "Retour à la connexion",
  "bank.oubli.tropDemandes": "Trop de demandes pour cette adresse.",
  "bank.oubli.reessayerHeure": "Réessayez dans une heure.",
  "bank.oubli.verifiezTitre": "Vérifiez votre boîte mail",
  "bank.oubli.verifiezAvant": "Si un compte correspond à ",
  "bank.oubli.verifiezApres":
    ", un e-mail vient de partir avec un lien pour choisir un nouveau mot de passe.",
  "bank.oubli.lienValable":
    "Le lien est valable 60 minutes et ne sert qu'une fois. Pensez à regarder dans vos courriers indésirables.",
  "bank.oubli.nouveauLien": "Recevoir un nouveau lien",
  "bank.reinit.titre": "Nouveau mot de passe",
  "bank.reinit.consigneAvant": "Choisissez le mot de passe de ",
  "bank.reinit.consigneVotreCompte": "votre compte",
  "bank.reinit.nouveau": "Nouveau mot de passe",
  "bank.reinit.confirmer": "Confirmez le mot de passe",
  "bank.reinit.aide": "Au moins 12 caractères.",
  "bank.reinit.differents": "Les deux saisies ne sont pas identiques.",
  "bank.reinit.enregistrer": "Enregistrer le mot de passe",
  "bank.reinit.garde12": "Choisissez un mot de passe d'au moins 12 caractères.",
  "bank.reinit.gardeIdentiques": "Les deux saisies doivent être identiques.",
  "bank.reinit.tropCourant": "Ce mot de passe est trop courant.",
  "bank.reinit.tropCourantSuite": "Choisissez une phrase plus longue ou plus personnelle.",
  "bank.reinit.expireTitre": "Ce lien a expiré",
  "bank.reinit.expire": "Le lien de réinitialisation n'est plus valable.",
  "bank.reinit.expireSecurite": "Pour votre sécurité, il n'est actif que 60 minutes.",
  // The door's own sentence for a spent link (`410`), which the screen matches to tell « déjà servi » from « expiré ».
  "bank.reinit.porteServi": "Ce lien a déjà été utilisé.",
  "bank.reinit.serviTitre": "Ce lien a déjà servi",
  "bank.reinit.servi": "Un lien de réinitialisation ne sert qu'une fois.",
  "bank.reinit.serviSuite":
    "Votre mot de passe a déjà été changé avec ce lien. Si ce n'était pas vous, demandez un nouveau lien et prévenez la Direction du crédit.",
  // ── end bank · RESET-PASSWORD R2 ──
  "bank.politique.tauxAbsentIndice": "Aucun taux saisi",
  "bank.politique.annuler": "Annuler",
  // `POLITIQUE-STEPPERS` (D2 `screens2.js:99-111`): the steppers' names and their words, and the optional C row's two acts.
  "bank.politique.baisser": "Baisser {nom}",
  "bank.politique.monter": "Monter {nom}",
  "bank.politique.nomTaux": "le taux de la note {note}",
  "bank.politique.nomSeuil": "le seuil de délégation",
  "bank.politique.nomAgeOctroi": "l'âge maximal à l'octroi",
  "bank.politique.nomAgeEcheance": "l'âge maximal à l'échéance",
  "bank.politique.nomQuotite": "la quotité cessible",
  "bank.politique.pasDeTaux": "Pas de taux",
  "bank.politique.ajouterC": "Ajouter une ligne C",
  "bank.politique.retirerC": "Retirer la ligne C",
  "bank.politique.piedCourt":
    "Toute modification est horodatée, attribuée et versée au registre de conformité.",
  "bank.politique.barre": "Enregistrement de la politique",
  "bank.politique.saisirUnTaux": "Saisissez au moins un taux de la grille.",
  "bank.politique.grilleVideTitre": "La grille de taux de votre banque n'est pas saisie.",
  "bank.politique.grilleVideSaisir":
    "Saisissez les taux : sans aucun taux, Hakili ne peut pas calculer la capacité de remboursement.",
  "bank.politique.grilleVideChef":
    "Le chef du crédit saisit les taux : sans aucun taux, Hakili ne peut pas calculer la capacité de remboursement.",
  "bank.politique.grillePartielleUne": "La grille n'a pas de taux pour la note {notes}.",
  "bank.politique.grillePartiellePlusieurs": "La grille n'a pas de taux pour les notes {notes}.",
  "bank.politique.grillePartielleSaisir": "Saisissez les taux manquants.",
  "bank.politique.grillePartielleChef": "Le chef du crédit saisit les taux manquants.",
  // `POLITIQUE-STEPPERS` R2 (D2 `screens2.js:104`, `partBox`): what a partial grid changes, four lines, then the reassurance.
  "bank.politique.grillePartielleCeQueTitre": "Ce que cela change",
  "bank.politique.grillePartielleCapaciteTitre": "La capacité est calculée.",
  "bank.politique.grillePartielleCapacite":
    " Elle l'est au taux le plus haut présent dans la grille, sans l'embellir : la note est calculée et sa lettre est imprimée.",
  "bank.politique.grillePartielleOffreTitre": "L'offre.",
  "bank.politique.grillePartielleOffreAvant": " Pour un dossier dont la note est ",
  "bank.politique.grillePartielleOffreApres":
    ", l'offre dit « la grille n'a pas de taux pour la note » ; Hakili ne propose ni durée ni taux.",
  "bank.politique.grillePartielleEcartTitre": "L'écart de taux.",
  "bank.politique.grillePartielleEcart": " Il n'est pas calculé pour cette lettre.",
  "bank.politique.grillePartielleTaegTitre": "Le TAEG et le plafond d'usure.",
  "bank.politique.grillePartielleTaeg":
    " Ils sont calculés sur le taux que l'analyste saisit, et l'offre ne part qu'une fois les deux calculés.",
  "bank.politique.grillePartielleNormal": "Les autres notes se comportent normalement.",
  "bank.politique.ou": "ou",
  "bank.politique.et": "et",
  "bank.politique.ancienneteAucuneRegle": "aucune règle aujourd'hui",
  "bank.politique.enregistrement": "Enregistrement en cours…",
  "bank.politique.niveauxVerrouilles": "Le niveau de chaque règle est verrouillé.",
  "bank.politique.grilleVideCeQueTitre": "Ce que Hakili ne peut pas calculer sans cette grille",
  "bank.politique.grilleVideCapaciteTitre": "La capacité de remboursement.",
  "bank.politique.grilleVideCapacite":
    " La mensualité de la note se calcule au taux de la ligne la plus haute de la grille ; sans ligne, la composante Capacité est ",
  "bank.politique.grilleVideNonCalculee": "« non calculée »",
  "bank.politique.grilleVideCapaciteMilieu": ". Le dossier lit alors ",
  "bank.politique.grilleVideNoteIncomplete": "« note incomplète »",
  "bank.politique.grilleVideCapaciteDeux": " : ",
  "bank.politique.grilleVideAucune": "aucune lettre, aucune moyenne, aucun score",
  "bank.politique.grilleVideOffreTitre": "L'offre.",
  "bank.politique.grilleVideOffre":
    " Hakili ne calcule aucune offre sur une note incomplète ; le composeur reste fermé.",
  "bank.politique.grilleVideTaegTitre": "Le TAEG et l'usure.",
  "bank.politique.grilleVideTaeg":
    " Aucun TAEG n'est calculé ni comparé au plafond tant que la capacité n'est pas calculée.",
  "bank.politique.grilleVideEcartTitre": "L'écart de taux.",
  "bank.politique.grilleVideEcart":
    " La grille tarifaire ne peut pas être comparée : aucune dérogation de taux n'est détectée.",
  "bank.politique.grilleVideContinue":
    "Hakili continue de lire les pièces, de proposer les quinze points et de rédiger l'avis ; vos gestes restent ouverts.",
  "bank.politique.tauxDecimale": "Un taux s'écrit avec une seule décimale, par exemple 10,8.",
  "bank.politique.tauxBornes":
    "Un taux est compris entre 0,1 et {plafond}\u202f% : le plafond de TAEG de la banque.",
  "bank.politique.corrigerTaux": "Corrigez les taux signalés avant d'enregistrer.",
  // GRADE-C (D-1024-3): the grid's optional fifth row, the words of frames 97, 98, 122 to 124 (design/bank-v2 25abd5b5).
  "bank.politique.noteCSous": "ligne facultative",
  "bank.politique.noteCInfo":
    "Avec {taux}\u202f%, la ligne C est la plus haute de la grille : la capacité de remboursement de tous les dossiers se calcule à ce taux.",
  "bank.politique.noteCInfoRecalcul":
    "À l'enregistrement, vos dossiers ouverts sont recalculés : leur mensualité de la note monte et leur Capacité baisse.",
  "bank.politique.noteCAideTitre": "Note C, facultative.",
  "bank.politique.noteCAide":
    "Hakili calcule la capacité de remboursement au taux de la ligne la plus haute de la grille ; une ligne C plus haute que B le devient. Sans ligne C, la grille reste complète.",
  "bank.politique.enregistree": "Politique enregistrée : horodatée et versée au registre de conformité.",
  // `KIT-TOAST-D2` 3b and 3c (D2 `5365fd20`, screens2.js:119 and 224): the toast only confirms; the consequence is a callout that stays.
  "bank.politique.enregistreeC": "Politique enregistrée.",
  "bank.politique.noteEnregistreeTitre": "Politique enregistrée.",
  "bank.politique.noteEnregistreeSuite":
    "Avec {taux}, la ligne C est la plus haute de la grille : vos dossiers ouverts sont recalculés à ce taux, leur Capacité baisse.",
  "bank.politique.compris": "Compris",
  // `POLICY-FIRST` Screen A (D-1028-6; D2 `b284f616`, `screens2.js:299`): the first-policy form, the chef's eight values typed by him.
  "bank.politique.premiereTitre": "Définissez la politique de crédit de votre banque",
  "bank.politique.premiereSousTitre": "Crédit auto · première définition, avant tout dossier.",
  "bank.politique.premiereAucuneValeur": "Aucune valeur par défaut.",
  "bank.politique.premiereAucuneValeurSuite":
    "Hakili n'applique aucun chiffre à votre place : chaque valeur ci-dessous est la vôtre, et la politique vaut ensuite pour tous les dossiers de la banque. Tapez chaque valeur ; le stepper sert ensuite à l'ajuster.",
  "bank.politique.premiereCompteur": "{n} sur 8",
  "bank.politique.premiereCompteurSuite": "valeurs renseignées",
  "bank.politique.premiereCompteurNom": "Valeurs renseignées",
  "bank.politique.premiereGrilleTitre": "Grille tarifaire",
  "bank.politique.premiereGrilleSous": "les quatre notes, taux nominal",
  "bank.politique.premiereGrilleAide":
    "Taux nominal de référence par note de solidité. La note C, facultative, s'ajoute ensuite depuis cet écran.",
  "bank.politique.premierePlafondsTitre": "Tolérance et plafonds",
  "bank.politique.premierePlafondsSous": "ce qu'une offre ne peut pas dépasser",
  "bank.politique.premiereDelegationTitre": "Délégation de pouvoir",
  "bank.politique.premiereDelegationSous": "qui décide, selon le montant",
  "bank.politique.premiereVehiculeTitre": "Règles sur le véhicule",
  "bank.politique.premiereVehiculeSous": "conditions d'éligibilité du collatéral",
  "bank.politique.premiereEmprunteurTitre": "Règles sur l'emprunteur",
  "bank.politique.premiereEmprunteurSous": "conditions d'éligibilité du dossier",
  "bank.politique.premiereTolerance": "Tolérance de la banque",
  "bank.politique.premiereToleranceSous": "point sous la grille avant une dérogation",
  "bank.politique.premiereDuree": "Durée maximale",
  "bank.politique.premiereDureeSous": "maximum 60 mois",
  "bank.politique.premiereTaeg": "Plafond de TAEG",
  "bank.politique.premiereTaegSous": "plafond réglementaire UEMOA : 14\u202f% au plus",
  "bank.politique.premiereSeuil": "Chef du crédit, jusqu'à",
  "bank.politique.premiereSeuilSous": "au-delà, le comité de crédit se prononce",
  "bank.politique.premiereAgeOctroi": "Âge maximal à l'octroi",
  "bank.politique.premiereAgeEcheance": "Âge maximal à l'échéance",
  "bank.politique.premiereQuotite": "Quotité cessible",
  "bank.politique.premiereUnitePoint": "point",
  "bank.politique.premiereSaisir": "Saisir",
  "bank.politique.premiereNombre": "Saisissez un nombre.",
  "bank.politique.premiereAuPlus": "Au plus {max}.",
  "bank.politique.premierePied":
    "Ces valeurs sont versées au registre de conformité avec votre nom et la date. Vous pourrez les modifier ensuite, une à une.",
  "bank.politique.premiereBouton": "Définir la politique",
  "bank.politique.premiereHorsLimite": "Corrigez la valeur hors limite.",
  "bank.politique.premiereRenseignez": "Renseignez {valeurs}.",
  "bank.politique.premiereNValeurs": "{n} valeurs : ",
  "bank.politique.premiereEtAutres": " et d'autres",
  "bank.politique.premiereNomGrille": "la grille de taux",
  "bank.politique.premiereNomTolerance": "la tolérance",
  "bank.politique.premiereNomDuree": "la durée maximale",
  "bank.politique.premiereNomTaeg": "le plafond de TAEG",
  "bank.politique.premiereNomSeuil": "le seuil de délégation",
  "bank.politique.premiereNomAgeOctroi": "l'âge à l'octroi",
  "bank.politique.premiereNomAgeEcheance": "l'âge à l'échéance",
  "bank.politique.premiereNomQuotite": "la quotité cessible",
  "bank.politique.premiereEnregistree":
    "La politique de crédit est définie. Les dossiers peuvent être validés.",
  "bank.equipe.tuileDisponibles": "Analystes disponibles",
  "bank.equipe.tuileOuverts": "Dossiers ouverts",
  "bank.equipe.tuileCharge": "Charge pondérée",
  "bank.equipe.tuileIndisponibles": "Indisponibles",
  "bank.equipe.aucunDossierOuvert": "Aucun dossier ouvert",
  "bank.equipe.aucunDossierMotif": "Aucun dossier ouvert.",
  "bank.equipe.envoyerMotif": "Saisissez l'adresse professionnelle de la personne : son nom s'en déduit.",
  "bank.equipe.chargePiedC2":
    "Une absence ne déplace aucun dossier : elle sort l'analyste des cibles proposées. Un analyste sorti de l'équipe reste dans la liste, grisé et daté, pour que la trace de qui portait quoi ne disparaisse jamais.",
  "bank.equipe.lectureImpossible": "La lecture de l'équipe a échoué : réponse inattendue du serveur.",
  // `EQUIPE-GESTURES`, round 2 (WF-BNK-25 steps 3 to 5 and 7; frames 80, 85, 86, 88, 89, 117, 118): the chef's three gestures, in the
  // frames' own words. The tags « WF-BNK-25 étape N » of the drawing are the drawing's, not the screen's, and are not printed (`PASTILLES-OUT` holds it).
  "bank.equipe.deplier": "Déplier le portefeuille",
  "bank.equipe.replier": "Replier le portefeuille",
  "bank.equipe.reaffecter": "Réaffecter",
  "bank.equipe.reaffecterTitre": "Réaffecter {nom}",
  "bank.equipe.reaffecterSous": "{reference} · actuellement chez {chez}.",
  "bank.equipe.propositionTitre": "Proposition de Hakili : {nom}.",
  "bank.equipe.ecartes": "Écartés : {liste}.",
  "bank.equipe.ecarteAbsent": "{nom} (absent·e jusqu'au {date})",
  "bank.equipe.ecarteInvite": "{nom} (invitation pas encore acceptée)",
  "bank.equipe.reaffecterA": "Réaffecter à",
  "bank.equipe.cibleOption": "{nom} · {points}",
  "bank.equipe.reaffecterAide":
    "Une réaffectation est tracée avec son auteur, son motif et la règle qui l'a proposée.",
  "bank.equipe.aucunAnalyste": "Aucun analyste disponible.",
  // The causes of « Aucun analyste disponible. » (founder's ruling E3, under D-1025-13): the sentence is picked by the causes PRESENT among
  // those set aside, never « tous … absents » when one is not. The fourth is for nobody set aside at all: no cause is named.
  "bank.equipe.aucunAbsents":
    "Tous les autres analystes sont absents. Rendez l'un d'eux disponible, ou invitez un analyste, puis revenez réaffecter ce dossier.",
  "bank.equipe.aucunInvites":
    "Les autres analystes n'ont pas encore accepté leur invitation. Attendez qu'une invitation soit acceptée, puis revenez réaffecter ce dossier.",
  "bank.equipe.aucunLesDeux":
    "Les autres analystes sont absents ou n'ont pas encore accepté leur invitation. Rendez l'un d'eux disponible, ou attendez qu'une invitation soit acceptée, puis revenez réaffecter ce dossier.",
  "bank.equipe.aucunSansCause": "Invitez un analyste, puis revenez réaffecter ce dossier.",
  "bank.equipe.transfertAucunAbsents":
    "Tous les autres analystes sont absents : aucun dossier de {nom} ne peut être transféré. Rendez l'un d'eux disponible, ou invitez un analyste, puis revenez.",
  "bank.equipe.transfertAucunInvites":
    "Les autres analystes n'ont pas encore accepté leur invitation : aucun dossier de {nom} ne peut être transféré. Attendez qu'une invitation soit acceptée, puis revenez.",
  "bank.equipe.transfertAucunLesDeux":
    "Les autres analystes sont absents ou n'ont pas encore accepté leur invitation : aucun dossier de {nom} ne peut être transféré. Rendez l'un d'eux disponible, ou attendez qu'une invitation soit acceptée, puis revenez.",
  "bank.equipe.transfertAucunSansCause":
    "Aucun dossier de {nom} ne peut être transféré. Invitez un analyste, puis revenez.",
  // `EQUIPE-GESTURES` R4 (D2's frames 85 to 88 and 117 to 130): the drawing's workflow tag under the heading of each gesture window.
  // Frame 125 (founder's E2): the analyst's Pipeline shows the notice under the counting sentence, with the quiet action.
  "bank.pipeline.ouvrirDossier": "Ouvrir le dossier",
  // D2's reasons beside the guarded act (designer's review 45abddea, row 1): reassign, transfer with no target at all, and with none for at least one file.
  "bank.equipe.aucuneCible": "Aucune cible disponible.",
  "bank.equipe.transfertSansCible": "Aucun analyste disponible : le transfert n'a pas de cible.",
  "bank.equipe.transfertSansCibleUn": "Aucune cible disponible pour au moins un dossier.",
  // D2's words for a stage inside a transfer line (« · comité », « · conditions »), designer's review 45abddea row 5; not the product's labels.
  "bank.equipe.ligneEtapeNouveau": "nouveau",
  "bank.equipe.ligneEtapeRevue": "revue",
  "bank.equipe.ligneEtapeValidation": "validation",
  "bank.equipe.ligneEtapeOffre": "offre",
  "bank.equipe.ligneEtapeComite": "comité",
  "bank.equipe.ligneEtapeSignature": "signature",
  "bank.equipe.ligneEtapeConditions": "conditions",
  "bank.equipe.ligneEtapeDecaisse": "décaissé",
  "bank.equipe.ligneEtapeRetour": "chez Hakili",
  "bank.equipe.ligneEtapeRefuse": "refusé",
  "bank.equipe.ligneEtapePerdu": "clos",
  // `EQUIPE-MODALS` (D2 frames 82 and 84): the removal and the absence get their dialogs, in D2's words.
  // Frame 82's date (the architect's ruling, words ratified): a text field, read as it is written.
  "bank.equipe.retourExemple": "28 octobre",
  "bank.equipe.retourIllisible": "Écrivez le jour et le mois, par exemple 28 octobre.",
  // `DESIGN SPRINT` (D-1026-2), Équipe page to D2 (frame 80): the table's heads, the row's actions, the band under it.
  "bank.equipe.colAnalyste": "Analyste",
  "bank.equipe.colDisponibilite": "Disponibilité",
  "bank.equipe.colEtapes": "Dossiers par étape",
  "bank.equipe.colCharge": "Charge",
  "bank.equipe.voirSesN": "Voir ses {n} dossiers dans le pipeline",
  "bank.equipe.voirSonUn": "Voir son dossier dans le pipeline",
  "bank.equipe.voirZero": "Voir ses dossiers (0)",
  "bank.equipe.niveau": "Niveau",
  "bank.equipe.niveauJunior": "Junior",
  "bank.equipe.niveauStandard": "Standard",
  "bank.equipe.niveauSenior": "Senior",
  "bank.equipe.niveauAide":
    "Le niveau départage les égalités de charge : à charge égale, l'analyste le plus expérimenté reçoit les étapes sensibles.",
  "bank.equipe.ptsN": "{n} pts",
  "bank.equipe.chargeAria": "Charge {n} points",
  "bank.equipe.chargeAriaUn": "Charge {n} point",
  "bank.equipe.ptUn": "{n} pt",
  "bank.equipe.pts": "pts",
  "bank.equipe.jours": "{n} j",
  "bank.equipe.personne": "personne",
  /** `CHEF-HELD-FILES` (D-1027-1; `P1-BKW-030`, `-031`, `-032`; D2 frames 144 to 146): the chef's own line on « Équipe ». */
  "bank.equipe.chefVous": "{nom} (vous)",
  "bank.equipe.roleChef": "Chef du crédit",
  "bank.equipe.detientDossiers": "Détient des dossiers",
  "bank.equipe.deplierMes": "Déplier mes dossiers",
  "bank.equipe.replierMes": "Replier mes dossiers",
  "bank.equipe.voirMesUn": "Voir mes 1 dossier dans le pipeline",
  "bank.equipe.voirMesN": "Voir mes {n} dossiers dans le pipeline",
  "bank.equipe.bandeChef":
    "Vos propres dossiers comptent dans la charge de l'équipe, pas dans les analystes disponibles : vous pouvez les réaffecter à un analyste.",
  "bank.equipe.chargeBande":
    "Charge pondérée : {liste}. La revue et le comité sont du vrai travail d'analyse ; les étapes d'attente d'un tiers pèsent peu. Une absence ne déplace aucun dossier par elle-même : elle sort l'analyste des cibles proposées.",
  "bank.equipe.retirerTitre": "Retirer {nom} de l'équipe",
  "bank.equipe.retirerPhraseSans":
    "Le compte est désactivé, jamais effacé : il reste grisé et daté dans la liste, pour la trace.",
  "bank.equipe.retirerPhraseAvec":
    "Le compte est désactivé, jamais effacé ; il reste grisé et daté dans la liste, pour la trace.",
  "bank.equipe.sortieUn": "Sortie impossible : 1 dossier ouvert est encore à son nom.",
  "bank.equipe.sortiePlusieurs": "Sortie impossible : {n} dossiers ouverts sont encore à son nom.",
  "bank.equipe.absencePhraseD2":
    "{nom} ne recevra plus de dossier jusqu'à son retour. Ses dossiers en cours ne bougent pas : vous décidez, avec le transfert de portefeuille.",
  "bank.equipe.transfererTout": "Transférer tout son portefeuille",
  "bank.equipe.transfertTitre": "Transférer le portefeuille de {nom}",
  "bank.equipe.transfertSous":
    "Hakili propose une cible motivée dossier par dossier, la charge simulée au fil des affectations. Vous confirmez en un geste.",
  "bank.equipe.transfertLigne": "{client} · {etape}",
  "bank.equipe.transfertBouton": "Transférer les {n} dossiers",
  "bank.equipe.transfertBoutonUn": "Transférer le dossier",
  "bank.equipe.transfertRaccourci": "Transférer son portefeuille",
  "bank.equipe.journalTitre": "Journal des réaffectations",
  "bank.equipe.journalSous": "chaque déplacement, quelle qu'en soit l'origine",
  "bank.equipe.journalVide": "Aucune réaffectation n'a encore été enregistrée.",
  "bank.equipe.journalTransfert": "transfert de portefeuille",
  "bank.equipe.journalPar": "par {nom}",
  "bank.equipe.journalLigne": "de {de} vers {vers}",
  "bank.equipe.toastReaffecte": "{client} est réaffecté à {vers}.",
  "bank.equipe.toastTransfere": "Le portefeuille est transféré, dossier par dossier.",
  "bank.equipe.fichierLigne": "{montant} · {etape} · {points}",
  "bank.equipe.sousTitreC2": "Charge, disponibilité et dossiers de vos analystes.",
  "bank.equipe.tuileDisponiblesSous": "reçoivent des dossiers",
  "bank.equipe.tuileOuvertsSous": "dans le pipeline de l'équipe",
  "bank.equipe.tuileChargeSous": "points, pas un nombre de dossiers",
  "bank.equipe.tuileIndisponiblesSous": "ne reçoivent plus de dossier",
  "bank.equipe.legendeNom": "Légende des étapes",
  "bank.decisions.filtresNom": "Filtrer la file des décisions",
  "bank.decision.puceEcart": "{n} écart",
  /** `PARSER-GAPCOUNT` (A47): the decision door's answer broke its contract; named, never papered over. */
  "bank.decision.contratRompu":
    "Cette décision ne peut pas être affichée : la réponse reçue est incomplète. Réessayez, ou signalez-le à Hakili.",
  "bank.decision.politiqueTitre": "Le taux face à la politique",
  "bank.decision.colPropose": "Proposé",
  "bank.decision.grilleTarifaire": "Grille tarifaire",
  "bank.decision.ecartAucunTaux": "Écart : aucun taux dans la grille",
  "bank.decision.colPolitique": "Politique",
  "bank.decision.zoneTolerance": "zone de tolérance",
  "bank.decision.politiqueTaux": "grille {note} {grille}\u202f% − tolérance {tolerance}",
  "bank.decision.dansLaGrille": "Dans la grille",
  "bank.decision.ecartPoint": "Écart {n} point",
  "bank.decision.politiqueDuree": "maximum {n} mois",
  "bank.decision.dansLaPolitique": "Dans la politique",
  "bank.decision.auDessus": "Au-dessus",
  "bank.decision.politiqueTaeg": "plafond UEMOA {n}\u202f%",
  "bank.decision.sousLePlafond": "Sous le plafond",
  "bank.decision.depasse": "Dépasse",
  "bank.decision.partCapacite": "Part de la capacité restante",
  "bank.decision.politiqueCapacite": "{n}\u202f% de la capacité restante",
  "bank.decision.dansLaQuotite": "Dans la quotité",
  "bank.decision.ltv": "LTV",
  "bank.decision.politiqueLtv": "plafond de la banque {n}\u202f%",
  "bank.decision.politiqueDelegation": "délégation du chef : {montant}",
  "bank.decision.comiteApresAccord": "Comité après l'accord du client",
  "bank.decision.comiteApresAccordMotif": "Comité après l'accord du client : {motif}",
  "bank.decision.etMot": " et ",
  "bank.decision.ecartNiveauComite": "Écart : niveau comité",
  "bank.decision.ecartNiveauChef": "Écart : niveau chef du crédit",
  "bank.decision.dansVotreDelegation": "Dans votre délégation",
  "bank.decision.colControle": "Contrôle",
  "bank.decision.colEtat": "État",
  "bank.decision.etatEcart": "Écart",
  "bank.decision.tauxNominal": "Taux nominal",
  "bank.decision.tauxSaisi": "Taux saisi {taux}\u202f%",
  "bank.decision.barre": "La décision du chef du crédit",
  "bank.decision.consigneMotif": "Écrivez au moins une consigne : une par ligne.",
  // C-4 (designer's review e048333d).
  "bank.modal.annuler": "Annuler",
  // `COMITE-RECORD-2` (D-1024-7, frames 110 to 113; D2-copy « Chef, file », « Chef, écran », « Pourquoi le comité »).
  "bank.decisions.comitesTitre": "Comités à enregistrer",
  "bank.decisions.comitesSous":
    "au-delà de votre délégation : le comité se prononce, vous enregistrez l'issue",
  "bank.decisions.comitesPhrase": "{n} comité à enregistrer",
  "bank.decisions.comitesPhrasePluriel": "{n} comités à enregistrer",
  "bank.decisions.comitesPhraseFin": ": le client a accepté, le comité se prononce.",
  "bank.decisions.comitesColDossier": "Dossier",
  "bank.decisions.comitesColMontant": "Montant",
  "bank.decisions.comitesColPourquoi": "Pourquoi le comité",
  "bank.decisions.comitesColSeance": "Séance",
  "bank.comite.raisonMontant": "Montant au-delà du seuil",
  "bank.comite.raisonRegle": "Règle de niveau comité : {regle}",
  "bank.comite.passe": "Passé en comité ?",
  "bank.comite.ajourneLe": "Ajourné le {date}",
  "bank.comite.avantOffre": "Offre soumise au comité · non encore envoyée au client",
  "bank.comite.filCourant": "Passage en comité",
  "bank.comite.titre": "Passage en comité · {client}",
  "bank.comite.sousTitre": "{reference} · {montant} · {duree} mois",
  "bank.comite.pourquoi": "Pourquoi ce dossier passe en comité.",
  "bank.comite.pourquoiFin": "Le client a accepté l'offre : le comité se prononce.",
  "bank.comite.phraseMontant": "{montant} dépasse le seuil de délégation du chef du crédit, fixé à {seuil}.",
  "bank.comite.phraseRegle": "La règle « {regle} » est dérogeable au niveau comité : {detail}.",
  "bank.comite.dejaAjourne": "Ce dossier a déjà été ajourné.",
  "bank.comite.seanceDu": "Séance du {date} : {resume}",
  "bank.comite.carte": "Passé en comité",
  "bank.comite.carteSous": "vous enregistrez l'issue de la séance",
  "bank.comite.date": "Date de la séance",
  "bank.comite.datePlaceholder": "jj/mm/aaaa",
  "bank.comite.issue": "Issue du comité",
  "bank.comite.accorde": "Accordé",
  "bank.comite.accordeAide": "le dossier passe à la signature",
  "bank.comite.accordeSousConditions": "Accordé sous conditions",
  "bank.comite.accordeSousConditionsAide": "le dossier passe à la signature, les conditions sont jointes",
  // eslint-disable-next-line hakili/no-literal-label -- the committee OUTCOME word (D2-copy, « Issue du comité »), not the stage label that spells alike
  "bank.comite.refuse": "Refusé",
  "bank.comite.refuseAide": "le dossier est clos",
  "bank.comite.ajourne": "Ajourné",
  "bank.comite.ajourneAide": "le dossier reste devant le comité",
  "bank.comite.conditions": "Conditions fixées par le comité",
  "bank.comite.conditionsAide":
    "Une condition par ligne. Chacune rejoint la liste des conditions préalables du dossier : l'analyste la lève sur pièce, et le versement reste bloqué tant qu'elle ne l'est pas.",
  "bank.comite.resume": "Résumé des échanges",
  "bank.comite.resumeAide":
    "Ce que le comité a retenu, en quelques phrases. Il est versé au journal d'audit.",
  "bank.comite.enregistrer": "Enregistrer la décision du comité",
  "bank.comite.enCours": "Enregistrement en cours.",
  "bank.comite.garde": "À renseigner : {liste}.",
  "bank.comite.gardeDate": "la date de la séance",
  "bank.comite.gardeIssue": "l'issue",
  "bank.comite.gardeConditions": "les conditions",
  "bank.comite.gardeResume": "le résumé des échanges",
  "bank.comite.dossier": "Le dossier soumis au comité",
  "bank.comite.dossierSous": "offre acceptée par le client",
  "bank.comite.client": "Client",
  "bank.comite.montant": "Montant financé",
  "bank.comite.duree": "Durée",
  "bank.comite.dureeMois": "{n} mois",
  "bank.comite.taux": "Taux nominal",
  "bank.comite.seuil": "Seuil de délégation",
  "bank.comite.enregistre": "Décision du comité enregistrée.",
  "bank.comite.enregistreSuite": "Elle est jointe au dossier et visible par l'analyste.",
  "bank.comite.decision": "Décision du comité",
  "bank.comite.decisionSeance": "Séance du",
  "bank.comite.decisionConditions": "Conditions fixées",
  "bank.comite.decisionResume": "Résumé",
  "bank.comite.decisionPar": "Enregistré par {nom}, le {date}",
  "bank.comite.noticeAccord": "Décision du comité enregistrée. Le dossier passe à la signature.",
  "bank.comite.noticeRefus": "Refus du comité enregistré. Le dossier est clos.",
  "bank.comite.noticeAjourne": "Ajournement enregistré : le dossier reste devant le comité.",
  // `COMITE-RECORD-3` (frames 43 and 44; D2-copy « Analyste »): the analyst's committee stage waits.
  "bank.comiteAttente.titre": "Le dossier est devant le comité de crédit.",
  "bank.comiteAttente.phraseMontant": "{montant} dépasse le seuil de délégation du chef du crédit ({seuil}).",
  "bank.comiteAttente.fin":
    "Le chef du crédit enregistre l'issue de la séance : une notification vous le dira. Aucune action de votre part à cette étape.",
  "bank.comiteAttente.devenirTitre": "Ce que devient le dossier",
  "bank.comiteAttente.devenirAccorde":
    "Accordé, ou accordé sous conditions : le dossier passe à la signature, la décision du comité est jointe au dossier.",
  "bank.comiteAttente.devenirRefuse": "Refusé : le dossier est clos, le motif est celui du résumé.",
  "bank.comiteAttente.devenirAjourne": "Ajourné : il reste ici, avec la date et le résumé.",
  "bank.comiteAttente.ajourneTitre": "Le comité a ajourné ce dossier",
  "bank.comiteAttente.ajourneSous": "le dossier reste devant le comité",
  "bank.comiteAttente.offreTitre": "Offre acceptée par le client",
  "bank.comiteAttente.offreSous": "soumise au comité",
  "bank.comiteAttente.issue": "Issue",
  "bank.comiteAttente.enregistrePar": "Enregistré par",
  // C-5: the offer's waiting views (validation, offre émise, the committee stage).
  "bank.validation.soumisLeObjectif": "Soumis le {quand} · {objectif}",
  "bank.validation.objectifDecision": "Objectif de décision du chef du crédit : 24 heures",
  "bank.validation.revueTerminee": "Revue terminée.",
  "bank.offre.attenteReponseClient": "En attente de la réponse du client.",
  "bank.offre.validiteAnneau": "Validité de l'offre",
  "bank.offre.joursRestants": "{n} j",
  "bank.etape.enMots": "Le dossier est à l'étape « {etape} ».",
  // ── end bank · lane C ──
};

/**
 * Looks a key up, and **throws when it is missing** rather than returning the
 * key itself.
 *
 * Returning the key is the common design and it is the wrong one here: a
 * screen would render `client.nav.profil` to a buyer in Bamako, it would look
 * like a bug nobody filed, and it would ship. A missing key is a programming
 * error and it fails where it is written.
 */
export function t(key: string): string {
  const found = FR_ML[key];
  if (found === undefined) throw new Error(`missing fr-ML catalogue key: ${key}`);
  return found;
}

/**
 * `CANON-1`: **« 2 sur 3 reçus », the count a pièce asking for several documents prints.**
 *
 * A function rather than a catalogue row, because the catalogue deliberately has no interpolation:
 * `t` looks a key up and throws when it is missing, and a row carrying a placeholder would start a
 * second, quieter templating language beside it that nothing checks. The words live here, once, and
 * the screen hands over two numbers it read from the door.
 *
 * `reçus` agrees with the masculine plural `fichiers`, which is the noun both pièces' files are, and
 * is fixed for that reason rather than passed in: there is one subject here, unlike `accord.ts`'s
 * pièces, whose gender varies row by row.
 */
export const receivedOfExpected = (received: number, expected: number): string =>
  `${String(received)} sur ${String(expected)} reçus`;

/**
 * `PIECES-COUNT` (pilot list A1, the architect's approved words): **« 3 mois reçus sur 3 »**, the count line of a pièce the door counts in MONTHS.
 * Both numbers are served (`coveredMonths`, `expectedFiles`) and only said here; nothing in it claims the months are recent. The participle takes the plural from two (`pl`, the French rule): « 1 mois reçu sur 3 », « 3 mois reçus sur 3 ».
 */
export const coveredMonthsOfExpected = (covered: number, expected: number): string =>
  `${pl(covered, "mois reçu", "mois reçus")} sur ${String(expected)}`;

/** True when the key exists, for a caller that legitimately wants to ask. */
export const hasKey = (key: string): boolean => Object.hasOwn(FR_ML, key);

/**
 * The vocabulary, re-exported through the catalogue's own door so a screen has
 * ONE import for the words it renders. The labels stay owned by
 * `@hakili/domain-types` , this is a doorway, not a copy.
 */
export const labelEtape = (etape: EtapeDossier): string => etapeLabel(etape);
export const labelQui = (etape: EtapeDossier): string => quiLabel(etape);
